// Boot: open saves → load/create a persistent world → wire renderer/input/loop/
// autosave (M0.4 + M1.4) and the survival player, inventory, HUD, and screens
// (M4). Defines the shared Game shape the client operates on.
import * as THREE from 'three';
import type { HangarClient } from './hangar/bridge';
import { shouldNotifyTimerFinished } from './hangar/timer';
import { PLAYER, REACH, VIEW, WORLD, AUTOSAVE_MS, SURVIVAL, MOB, PORTAL, WEATHER, CHARGED_CREEPER, RAID, RED, PROJECTILE, TNT, POTION, FIREWORK, AVI, FARMING } from '../sim/config';

/** Soft terrain blocks an enderman may pick up and carry (MOB-2, M13.2). */
const ENDERMAN_CARRY = new Set(['grass_block', 'dirt', 'sand', 'gravel', 'poppy', 'dandelion']);
import { World } from '../sim/engine/world';
import { DimensionManager, portalAccessOf } from '../sim/dimensions/manager';
import { ignitePortal, type DimId, type PortalBlocks } from '../sim/dimensions/portal';
import { resolveAtlas, parseAtlasParam } from './atlas';
import { resolveEarth, earthRangeSpec } from './earth';
import { parseEarthParam } from './earth/deeplink';
import { earthBadge } from '../sim/earth/badge';
import { OverlayController } from './earth/overlay';
import { openOverlayPanel } from './ui/screens/overlaypanel';
// World doors (Part VII / M41, DOR-1..5): the local world graph + world-session-swap
// traversal. The sim only stores the door's edge strings; all of this is client chrome.
import { openDoorScreen, type DoorScreenApi } from './ui/screens/door';
import { createChildWorld, bindExistingWorld, resizeWorld, homeWorldId, newId, type ChildOpts, type ResizeResult } from './world/doors';
import { takeHandoff, writeHandoff, type DoorHandoff } from './world/handoff';
import { openUsageScreen } from './ui/screens/usage';
import { computeUsage, type UsageReport } from './world/usage';
import type { EarthWorld } from '../sim/earth/earthgen';
import type { IntermediateCity } from '../sim/atlas/intermediate';
import type { AtlasElevation } from '../sim/atlas/elevation';
import type { AtlasSolar } from '../sim/atlas/solar';
import { makeBody, stepPlayer, collideAndMove, blockIntersectsBody, PLAYER_HALF, PLAYER_HEIGHT, type Body } from '../sim/engine/physics';
import { SHAPE, type Box } from '../sim/shapes';
import { STATE_AIR, makeRange } from '../sim/engine/section';
import { isBodyInFluid } from '../sim/engine/fluids';
import { runTick } from '../sim/engine/tick';
import { buildRenderTable, DYE_ORDER, FORGE, TILE_PX, type RenderTable } from '../sim/mesh/atlasmap';
import type { Registries } from '../sim/registry/load';
import type { LessonDef } from '../sim/registry/schemas';
import { raycastVoxel } from '../sim/engine/raycast';
import { Player } from '../sim/game/player';
import { modeCaps, type GameMode } from '../sim/game/modes';
import {
  PlayerInventory, INV, makeStack, emptyStack, isEmpty, cloneStack, type ItemStack, type ArmorSlot,
} from '../sim/game/inventory';
import {
  armorSlotOf as gearArmorSlotOf, armorStatsOf, armorCategory, armorEnchantability,
  armorTotals, absorbWithGear, resolveTotem, type ArmorStats,
} from '../sim/game/gear';
import { MapStore, buildStateMapColors, mapCenter, type MapSampler } from '../sim/game/cartography';
import { GROW_DIRS, rollBudGrowth } from '../sim/game/amethyst';
import { beaconTier as computeBeaconTier, resolveBeaconEffects, beaconDuration, beaconInRange } from '../sim/game/beacon';
import { conduitFrameCount, conduitCoreClear, conduitActive, conduitRange as computeConduitRange, conduitInRange, conduitAttacks } from '../sim/game/conduit';
import {
  nextWarningLevel, shouldSummonWarden, wardenEmerged, sonicBoomHits, sonicBoomDamage,
  addAnger, angerOf, decayAnger, wardenQuarry, wardenPacified, type AngerLedger,
} from '../sim/game/warden';
import { PLAYER_SOURCE, vibrationReaches, vibrationAnger, angersWarden } from '../sim/game/vibration';
import { dragonPhase, dragonKillable } from '../sim/game/ender_dragon';
import { generatedCrystalCells, buildExitPortal, endFightPending, dragonSpawnPoint } from '../sim/game/end_fight';
import {
  nextRingGateway, buildGateway, isRingGateway, gatewayIslandColumn, arriveAtOuterIsland,
} from '../sim/game/end_gateway';
import { chorusTeleportTarget, teleportsOnEat } from '../sim/game/chorus';
import { trialWaveTotal, trialSimultaneousCap, trialCanSpawn, nextTrialState, vaultUnlocks, type TrialState } from '../sim/game/trial';
import { stalagmiteFallDamage, dripstoneGrows, stalactiteFalls, stalactiteColumn } from '../sim/game/dripstone';
import { chargeAnchor, anchorAction, anchorExplosionPower } from '../sim/game/respawn_anchor';
import { grantsDolphinGrace, dolphinGraceTicks, pufferfishPuffed, pufferfishPoisons, pufferfishPoisonTicks } from '../sim/game/ocean_fauna';
import { beamCharge, beamFires, beamDamage, elderInflictsFatigue, fatigueAmplifier, fatigueTicks, fatigueReapplies } from '../sim/game/guardian';
import { hoglinZombifyStarts, hoglinZombifies, striderHurtByWater } from '../sim/game/nether_fauna';
import { turtleHelmetBreathes, turtleHelmetBreathTicks, eggNextCrack, eggHatches, scuteFromGrowUp } from '../sim/game/turtle';
import { allayDances, allayDuplicates, allayDupCooldownTicks } from '../sim/game/allay';
import { sculkDetects, sculkActiveTicks } from '../sim/game/sculk_sensor';
import { windBurstKnockback, windBurstRadius, windChargeActivates, windChargeDamage, breezeFireInterval, breezeReadyToFire, windSelfLaunchImpulse } from '../sim/game/breeze';
import { brushProgress, brushComplete, isSuspicious, suspiciousBase, suspiciousLoot } from '../sim/game/archaeology';
import { glowInkApplies, signGlowAfter } from '../sim/game/glow_ink';
import { potInsert } from '../sim/game/decorated_pot';
import { isBucketableFish, fishBucket, fishFromBucket } from '../sim/game/fish';
import { snifferDiggableGround, snifferDigInterval, snifferDigsSeed, snifferSeed } from '../sim/game/sniffer';
import { camelCanDash, camelDashImpulse, camelDashHop, camelDashCooldown } from '../sim/game/camel';
import { chargeJump, jumpImpulse, chargesJump } from '../sim/game/horse';
import { canAddPetal, nextPetalAmount, canPlacePetalsOn } from '../sim/game/pink_petals';
import { copperInfo, oxidationStageOf, canOxidize, weatherStep, waxStep, axeStep, shouldOxidize, type OxidationNeighbourhood } from '../sim/game/copper';
import { bucketDrain, bucketFill, rainAccumulate, CAULDRON_MAX } from '../sim/game/cauldron';
import { freezeStep, freezeDamage } from '../sim/game/powder_snow';
import { contactBurnDamage, ignitesOnContact, BURN_INTERVAL, CAMPFIRE_FIRE_TICKS } from '../sim/game/hot_block';
import { nextHoney, honeyFull, harvestYield, boostCells, DEPOSIT_INTERVAL } from '../sim/game/hive';
import { froglightFor, isFrogPrey, FROG_EAT_RANGE, frogVariantForBiome, tadpoleGrowTicks } from '../sim/game/frog';
import { eggDelay } from '../sim/game/egg';
import { isBreedFood, foodPoints, isWilling, canBreed } from '../sim/game/villager_breed';
import { professionFor } from '../sim/game/jobsite';
import { canSpawnIronGolem } from '../sim/game/golem_spawn';
import { canSummonVexes, vexesToSummon, VEX_SUMMON_COOLDOWN } from '../sim/game/illager';
import { dispenseAction, spawnEggMob, type DispenseAction } from '../sim/game/dispenser';
import { tickBrewing as tickBrewingContainer, BREW_INGREDIENT, BREW_TICKS } from '../sim/game/containers/brewing';
import { openBrewing } from './ui/screens/brewing';
import { screenTint, nightVisionFloor, effectHud } from '../sim/game/effect_visual';
import { normalizeSignText } from '../sim/game/sign';
import { signPixels as computeSignPixels, rasterizeSign, signTextureLitCount as computeSignLitCount, SIGN_COLS, SIGN_ROWS, type SignPixel } from '../sim/mesh/sign_glyph';
import { cycleNote, notePlayback, type NotePlayback } from '../sim/game/note_block';
import { AdvancementTracker, type Trigger } from '../sim/game/advancements';
import { AdvancementToasts, showAdvancements } from './ui/advancements';
import { StatsTracker, distanceCm, isJumpTakeoff, type StatCategory, type CustomStat } from '../sim/game/stats';
import { RecipeBook } from '../sim/game/recipe_book';
import { showStats } from './ui/stats';
import { rebind, type GameAction } from './keybinds';
import { AudioEngine, type AudioEvent } from './audio/engine';
import { Subtitles, subtitleDirection } from './ui/subtitles';
import { noteVoice } from './audio/synth/voices';
import {
  contextFor, composeContext, composeDisc, discComparatorOutput, discById, type MusicContext,
} from './audio/synth/music';
import { wallTorchFacing, wallSupport, attachmentFor, attachSupport, type Facing as AttachFacing } from '../sim/game/attach';
import { layersOf as snowLayersOf, stackPlace as snowStackPlace, snowballsFor, accumulateStep as snowAccumulate, shouldFormOnSurface as snowFormsOnSurface, shouldMelt as snowShouldMelt } from '../sim/game/snow';
import { concreteOf, solidifiesInWater, dyeColorIndex } from '../sim/game/dye';
import { isDoor, partnerOffsets, DOOR_HALVES, nextOpen as doorNextOpen } from '../sim/game/door';
import { FISHING_TABLE, cast as castRod, reel as reelRod, type FishingState } from '../sim/game/fishing';
import { activityAt as villagerActivityAt, type Activity as VillagerActivity } from '../sim/game/schedule';
import { isBarterCurrency, BARTER_TABLE } from '../sim/game/piglin';
import {
  isElytra, canStartGliding, shouldStopGliding, elytraAerodynamics, fireworkBoost,
  FIREWORK_BOOST_TICKS,
} from '../sim/game/elytra';
import {
  bloom, bloomOrigin, catalystNear, chargeFromXp, SCULK_BLOCK, type SculkView,
} from '../sim/game/sculk';
import { CARTO, SCR, STREAM, SAR, HELI, CLS, TALL, EARTH, DOOR, STO, TILE, BEACON, WITHER, CONDUIT, WARDEN, DRAGON, TRIAL, GUARDIAN, NETHER_FAUNA, ALLAY, END, STRUCTURE, BEE, EDU, COW } from '../sim/config';
import { insert } from '../sim/game/transaction';
import { blockDrops, type HeldTool } from '../sim/game/drops';
import { enchantLevelOf, consumesDurability, hasSilkTouch, blockBreakXp, powerDamageMultiplier, flameArrows, infiniteArrows } from '../sim/game/enchant_effects';
import { BlockEntityStore, tickBlockEntities, blockEntityDrops, HOPPER_SIZE, CRAFTER_SIZE, type BlockEntityType, type BlockEntityEnv, type DropperBE, type DispenserBE, type CrafterBE, type BrewingStandBE, type ScriptBE, type WorldDoorBE, type CropBE, type StickyNoteBE, type TaskBoardBE, type TimerBE } from '../sim/game/blockentity';
import { craftOnce, toggleDisabled } from '../sim/game/crafter';
import { CHEST_SIZE, chestSlots } from '../sim/game/containers/chest';
import { furnaceSlots, FURNACE_INPUT, FURNACE_FUEL, FURNACE_OUTPUT } from '../sim/game/containers/furnace';
import { arraySlots } from '../sim/game/containers/array';
import { addToCampfire, isCampfireCookable, CAMPFIRE_COOK_TICKS } from '../sim/game/containers/campfire';
import { RedstoneEngine } from '../sim/game/redstone/engine';
import { containerFullness } from '../sim/game/redstone/power';
import { cellKey, DIR_VEC, type Dir, type RedRole, type RedstoneWorld } from '../sim/game/redstone/types';
import { resolveExtend, resolveRetract, occupiedAfter, pistonPushBox, type Move, type MoveClass, type PistonView, type PushPlan } from '../sim/game/piston';
import {
  tickHopper, dropperFire, dispenserFire, fullTarget, type TransferTarget,
} from '../sim/game/logistics';
import { structureAnchorsInChunk, locateStructureType, structureTypeNames, insideStructure, type StructureEnv } from '../sim/gen/structures';
import type { StateResolver } from '../sim/gen/schematic-place';
import { breakTicks } from '../sim/game/breaktime';
import { fallDamage, tickAir, mitigate, tickFire } from '../sim/game/combat';
import { tickHunger } from '../sim/game/hunger';
import { evaluateSleep, wakeTime, sleepMessage } from '../sim/game/sleep';
import { facingFromYaw, partnerOffset, BED_FACING_OFFSET, type Facing } from '../sim/game/bed';
import { setWeather, isThunderstorm } from '../sim/engine/weather';
import { connectSides, isConnectingShape, type ConnectAccess } from '../sim/game/connect';
import { cropOf, SEED_TO_CROP, shouldGrow, growthChance, bonemealGrowth, nextMoisture, COMPOSTABLES, type CropDef } from '../sim/game/farming';
import { rangeOffsets } from '../sim/game/bee';
import { verticalGrow, stemGrow, netherWartGrow, agePlantGrow, VERTICAL_PLANTS, STEM_FRUIT, AGE_PLANTS, type GrowthView } from '../sim/game/growth';
import { isVine, vineGrow, vineBoneMeal } from '../sim/game/vines';
import { climbVy, CLIMBABLE_TAG } from '../sim/game/climb';
import { landSurface, bounceVelocity, surfaceSpeedFactor, honeyJumpCap, honeySlideVelocity } from '../sim/game/bounce';
import { biteCake, CAKE_SATURATION_PER_BITE } from '../sim/game/cake';
import { applyFood, canEat, foodEffects, EAT_TICKS } from '../sim/game/eat';
import { slotLevels, rollEnchants, withEnchants, ENCHANTABILITY, shelfPower } from '../sim/game/enchant';
import { openEnchanting } from './ui/screens/enchanting';
import { openSmithing } from './ui/screens/smithing';
import { insertEye, compassBearing, type PortalProbe } from '../sim/game/end_portal';
import {
  caravans, refuseJoin, join as caravanJoin, leave as caravanLeave, followPoint,
  tetherSnapped, carpetColour, dressesLlama, llamaDecorDrop,
} from '../sim/game/caravan';
import { heldUpgrade, forgeHeld, type SmithingRecipe, type SmithingItems } from '../sim/game/smithing';
import {
  addEffect, removeEffect, amplifierOf, applyInstant, tickEffects, speedMultiplier, jumpBoost, meleeDamageDelta, hasEffect,
  resistanceMultiplier, absorptionAmount, negatesFallDamage, levitationDelta, slowFallingActive, digSpeedMultiplier,
  POTIONS, isInstantEffect,
} from '../sim/game/effects';
import { checkGolem } from '../sim/game/golem';
import { checkWitherSummon, witherInvulnerable } from '../sim/game/wither';
import { Raid, wavesFor, type SavedRaid } from '../sim/game/raid';
import { applyTrade, canAfford, offerAvailable } from '../sim/game/trade';
import {
  type GossipLedger, type GossipType, addGossip, reputation, gossipDiscount, discountedBuyCount,
  shareGossip, decayGossip,
} from '../sim/game/gossip';
import { meetingPairs, isShareTick, isDecayTick } from '../sim/game/gossip_tick';
import { patrolDue, rollPatrol, patrolMembers, patrolSpawnPoint, PATROL_INTERVAL } from '../sim/game/patrol';
import { encodeVillagerEconomy, decodeGossip, decodeUses } from '../sim/game/villager_persist';
import { canStartCure, cureDuration, shouldDrown } from '../sim/game/monsters';
import { cureGossipGrants } from '../sim/game/cure';
import { openTrade } from './ui/screens/trade';
import { ItemDropSystem, type ItemDrop } from '../sim/entities/itemdrop';
import { FallingBlockSystem, type FallingWorld } from '../sim/entities/fallingblock';
import { PrimedTntSystem, type TntWorld, type PrimedTnt } from '../sim/entities/tnt';
import { MinecartSystem, type RailWorld, type MinecartKind, type CartSlot, type Minecart } from '../sim/entities/minecart';
import { BoatSystem, type BoatWorld, type BoatKind, type BoatInput, type Boat } from '../sim/entities/boat';
import { AircraftSystem, bodyAxes, type Aircraft, type AircraftInput, type AircraftWorld } from '../sim/aviation/aircraft';
import { HelicopterSystem, type Helicopter, type HeliInput, type HeliWorld } from '../sim/aviation/helicopter';
import { VanSystem, type Van, type VanInput, type VanWorld } from '../sim/vehicles/van';
import { Autopilot, type AutopilotTargets, type AutopilotPreset, autopilotFromPreset } from '../sim/aviation/autopilot';
import { isLanding, isRunwaySurface, holdStreak } from '../sim/aviation/lessontrigger';
import { vec3 as scriptVec3, type ScriptHost, type Vec3 as ScriptVec3 } from '../sim/script';
import {
  createScriptSystem,
  type BlockHook, type MobScriptSources, type ScriptCell, type ScriptDiagnostics,
  type SignalTarget,
} from '../sim/script/system';
import { scriptSource } from './scriptsources';
import { mobHostFor, scriptNameOf } from '../sim/entities/behaviors';
import { openScript } from './ui/screens/script';
import { synthRunway } from '../sim/atlas/aeroway';
import { BeaconField, SarObjective, beaconDist, terrainShadow, rssi as sarRssi, signalBars, bearingToBeacon, ledIndex, latLonToMgrs, type Beacon } from '../sim/education/sar';
import { ClassroomBoard, EditLog, compileBlueprint, diffBlueprint, progressOf, isComplete, attributeProgress, heatmap, type Blueprint, type CellRead } from '../sim/education/classroom';
import { readLightMeter, readSoilProbe, readSignalMeter, readAltimeter, readAirspeed, readSequencer } from '../sim/education/instruments';
import { parseSchematic } from '../sim/gen/schematic-place';
import { BlueprintRenderer } from './render/classroom';
import { openClassroom } from './ui/screens/classroom';
import { unprojectBlock } from '../sim/atlas/elevation';
import { passengerPosition, canMount, mountKindOf, mountWish, isSteerable, needsHeldControl, controlItem, SEAT_CAPACITY, type VehicleKind } from '../sim/game/mount';
import { resolveRailShape, type RailShape, type RailView } from '../sim/game/rail';
import { EntitySystem, type Entity, type MobContext, type MobWorld, type PlayerHandle, type MobFrame, type Difficulty } from '../sim/entities/entity';
import { trySpawn, type SpawnContext } from '../sim/entities/spawner';
import { rollLootByName } from '../sim/entities/loot';
import { Rng, defineSalt, forkRng, SALT_HER_ROLL, SALT_HER_POLLEN } from '../sim/engine/rng';
import { EntityRenderer } from './render/entities/renderer';
import { loadClientRegistries } from './data';
import { createRenderContext } from './render/renderer';
import { ChunkMeshes, makeMeshWorkers } from './render/chunkmeshes';
import { nextViewLod } from './streaming/priority';
import { nextRenderCap } from './streaming/adaptive';
import { ItemIcons } from './render/textures/itemicons';
import { iconToRgba, faceIcon, type BlockTexture, type IconData } from '../sim/registry/icon';
import type { MobDef } from '../sim/registry/schemas';
import { ItemDropRenderer } from './render/itemdrops';
import { SignTextRenderer } from './render/signs';
import { RemotePlayerRenderer } from './render/remoteplayers';
import type { NetSession } from './net/client';
import { followCamera, nextSpectatorTarget } from './net/spectate';
import { Btn } from '../netproto/protocol';
import { FallingBlockRenderer } from './render/fallingblocks';
import { PrimedTntRenderer } from './render/tnt';
import { MinecartRenderer } from './render/minecart';
import { BoatRenderer } from './render/boat';
import { AircraftRenderer } from './render/aircraft';
import { HelicopterRenderer } from './render/helicopter';
import { VanRenderer } from './render/van';
import { BeaconRenderer } from './render/sar';
import { AviationHud, SarHud, BeckerDial, type SarReadout } from './ui/hud/aviation';
import { SarDfHud } from './ui/hud/sar_df';
import { WeatherFX, type Precip } from './render/weather';
import { BreakOverlay, type MiningState } from './render/breakoverlay';
import { showDeath, isDeathShown } from './ui/screens/death';
import { attachInput } from './input';
import { startLoop } from './loop';
import { installDebugApi } from './debugapi';
import { ReplayController } from './replay/controller';
import { encodeReplay } from '../sim/replay/format';
import { ReplayOverlay } from './replay/overlay';
import { ReplayWorldView } from './render/replayview';
import { openReplayScreen } from './ui/screens/replay';
import { MiniMap } from './ui/minimap';
import { MapView } from './ui/hud/mapview';
import { Hotbar } from './ui/hud/hotbar';
import { Hearts } from './ui/hud/hearts';
import { Hunger } from './ui/hud/hunger';
import { Air } from './ui/hud/air';
import { Crosshair } from './ui/hud/crosshair';
import { BossBar } from './ui/hud/bossbar';
import { F3 } from './ui/hud/f3';
import { openInventory } from './ui/screens/inventory';
import { openCraftingTable } from './ui/screens/crafting';
import { openChest } from './ui/screens/chest';
import { openMountStorage as openPackWindow } from './ui/screens/mount_storage';
import {
  newPack, packContents, resizePack, type MountPack,
} from '../sim/game/containers/mount_storage';
import {
  carriesChest, chestSlotCount, attachesChest, storageOpens, chestDropsOnDeath,
  bardsWith, horseArmorPoints, bardingDropsOnDeath, isHorseArmor,
} from '../sim/game/equine';
import { openFurnace } from './ui/screens/furnace';
import { openHopper } from './ui/screens/hopper';
import { openDispenser } from './ui/screens/dispenser';
import { openSignEditor, openNoteEditor } from './ui/screens/sign';
import { openTaskBoard } from './ui/screens/taskboard';
import { openTimerScreen } from './ui/screens/timer';
import { openScreenModal } from './ui/screens/screen';
import { normalizeNote, normalizeBoard, toggleTask, type TaskLine } from '../sim/coworking/text';
import { startTimer, stopTimer, timerFace } from '../sim/coworking/timer';
import { ShareController, captureConstraints } from './net/webrtc';
import { ScreenQuad } from './render/screen';
import { openCreative } from './ui/screens/creative';
import { openForge } from './ui/screens/forge';
import { DEFAULT_FORGE_PARAMS, blitForgeTile, clampForgeParams, packSeed, type ForgeParams } from './render/textures/forge';
import { GenClient } from './genclient';
import { SaveManager, type WorldMeta, type BoardLink } from './storage/saves';
import { CURRENT_SAVE_VERSION } from './storage/migrate';
import { loadSettings, type Settings } from './settings';
import { clampFov, effectiveFov, approachFov } from './fov';
import { screenshotFilename } from './screenshot';
import { debugLines, boxEdges } from './ui/debug_overlay';
import { flatPresetBlocks } from './world_presets';
import { showMenu } from './ui/screens/menu';
import { showPause } from './ui/screens/pause';
import { showEditor } from './ui/screens/editor';
import { showOptions } from './ui/screens/options';
import { showHelp } from './ui/screens/help';
import { installChat } from './ui/screens/chat';
import { installPlayerList } from './ui/screens/playerlist';
import { executeCommand, KNOWN_GAMERULES, type CommandContext, type CommandResult } from '../sim/game/commands/core';
import './ui/style.css';
import { isScriptName } from '../sim/engine/saves';
import { HEREDITY_RULE, PACE_RULE, heredityOn, pace as heredityPaceOf } from '../sim/heredity/rule';
import { carryAcross, recombine } from '../sim/heredity/breed';
import { decodeGenome, encodeGenome, rollGenome, type Traits } from '../sim/heredity/genome';
import { express } from '../sim/heredity/express';
import { expressCrop, growthRoll, produceCount, pollenMate } from '../sim/heredity/crops';
import {
  emptyNotebook, hasAnimal, upsertAnimal, upsertCrop, markAnimalDeath, animalRecordOf,
  type Notebook, type AnimalRecord,
} from '../sim/heredity/notebook';
import { openNotebookScreen } from './ui/screens/notebook';
import { openLessonScreen } from './ui/screens/lesson';
import { ObjectiveTracker, regionCheck, emptyRegionState } from '../sim/education/objectives';
import { lessonByName } from '../sim/education/lessons';
import { emptyLessonResult, tickLessonResult, applyEduTrigger, type LessonResult } from '../sim/education/results';
import { gradeQuiz, type QuizAnswer } from '../sim/education/quiz';
import { soloRole, isRole, atLeast, type Role } from '../sim/education/roles';
import { buildRoster, type RosterRow } from './ui/roster';
import { LessonHud } from './ui/hud/lessonhud';
import { lessonCsv, resultsJson, type ExportInput } from '../sim/education/export';
import { ActionBar } from './ui/hud/actionbar';
import { PuffRenderer } from './render/puffs';
import { CropOverlay } from './render/heredity';
import { nameEntity } from '../sim/heredity/name';
import { openNameTagPrompt } from './ui/screens/nametag';

/** §28.8 — binding copy for a refused feed at carrying capacity. */
const CAPACITY_COPY = 'The flock is too crowded to breed here.';

export const DIM = 'overworld';

/** Active-raid snapshot for the bossbar HUD + debug API (RAID, M14.2). */
export interface RaidInfo {
  status: 'active' | 'victory' | 'defeat';
  /** 1-based wave number being fought. */
  wave: number;
  totalWaves: number;
  /** Raiders currently alive. */
  alive: number;
  /** Raiders spawned for the current wave (bossbar denominator). */
  waveTotal: number;
  center: { x: number; y: number; z: number };
  /** Live raider entity ids (E2E drives the fight through these). */
  raiderIds: number[];
}

/** Redstone state at a cell (debug/E2E, RED, M15.1). */
export interface RedstoneInfo {
  block: string;
  /** Dust power 0..15 (wire only). */
  power: number;
  /** Lit flag (torch/lamp). */
  lit: boolean;
  /** Powered flag (lever/button/plate/repeater/comparator). */
  powered: boolean;
  /** Repeater delay 1..4 (0 for others). */
  delay: number;
  /** Comparator mode ('' for others). */
  mode: string;
}

/** Fork salt for the entity/mob random stream (ENG-3, E-25). Declared once. */
const S_MOB = defineSalt('mobs');
/** §27.8's `mobSeed` — the parent `fork(mobSeed, entityId)` derives every scripted
 *  mob's per-dispatch rng from. Its own salt rather than `S_MOB`, so the script stream
 *  and the AI stream cannot correlate, and a `nextU32` because `entityScriptSeed` takes
 *  a NUMBER while a world seed may be a string or a bigint. */
const S_MOBSCRIPT = defineSalt('mob-scripts');
/** Fork salt for the block-spawner (dungeon) random stream (ENG-3, E-25). */
const S_SPAWNER = defineSalt('block-spawner');

/** Apply GUI scale to the CSS var used by overlays. */
function applyGuiScale(scale: number): void {
  document.documentElement.style.setProperty('--bb-scale', String(scale));
}

/** Mini-map radar surface the debug API reads (UI-8). */
export interface MiniMapApi {
  readonly radius: number;
  cycleZoom(): number;
  colorAt(wx: number, wz: number): readonly [number, number, number] | null;
}

export interface InputState {
  forward: boolean;
  back: boolean;
  left: boolean;
  right: boolean;
  jump: boolean;
  sprint: boolean;
  sneak: boolean;
}

/** A snapshot of the active guided-build blueprint's progress (M37, CLS-2). */
export interface ClassroomSnapshot {
  name: string;
  counts: { correct: number; wrong: number; missing: number; total: number };
  progress: number;
  complete: boolean;
  students: { student: string; correct: number; wrong: number }[];
  done: readonly string[];
}

export interface Game {
  registries: Registries;
  renderTable: RenderTable;
  world: World;
  save: SaveManager;
  meta: WorldMeta;
  settings: Settings;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  chunkMeshes: ChunkMeshes;
  icons: ItemIcons;
  /** The procedural block-texture atlas (content export renders it to PNG). */
  atlasCanvas: HTMLCanvasElement;
  /** Repaint a block's atlas tiles from an authored texture, live (content
   *  editor). `null` restores the procedural original. A block's WORLD texture
   *  lives in the atlas, not in its item icon — editing the icon alone changes
   *  the inventory sprite and nothing you can walk up to. */
  reskinBlock(block: string, texture: BlockTexture | null): void;
  /** Install an authored item icon live (content editor); null restores the
   *  procedural sprite. Inventory slots and dropped items repaint at once. */
  reskinItem(item: string, icon: IconData | null): void;
  /** Install authored rig parts for a mob live; rebuilds every visible rig of
   *  that type so the change shows without respawning or reloading. */
  reskinMob(mob: string, rig: MobDef['rig'] | null): void;
  player: { body: Body; yaw: number; pitch: number };
  /** Survival state + inventory (M4). Mode governs flight/break/hunger via caps. */
  playerState: Player;
  input: InputState;
  locked: boolean;
  /** Left mouse held — survival hold-to-break progresses while true. */
  breaking: boolean;
  /** Right mouse held — hold-to-eat progresses while true (M11.2). */
  usingItem: boolean;
  /** Block currently being mined + crack-overlay progress (M4.3). */
  mining: MiningState;
  /** Loose item entities (E-17). */
  drops: ItemDropSystem;
  /** Falling blocks — sand/gravel/concrete powder gravity (M10.1). */
  fallingBlocks: FallingBlockSystem;
  /** Primed TNT — falling + fusing + detonation (RED-8, M15.2.2). */
  primedTnt: PrimedTntSystem;
  /** Minecarts riding rails (RED-5/6, M16.2). */
  minecarts: MinecartSystem;
  /** Place a minecart onto the rail at a cell (debug/E2E + item use). */
  spawnMinecartAt(x: number, y: number, z: number): void;
  /** Boats floating on water (RED-6, M16.2). */
  boats: BoatSystem;
  /** Set a boat's steering input by id (debug/E2E; passenger binding is excluded). */
  setBoatInput(id: number, input: BoatInput): void;
  /** Aircraft flight model (M32, AVI-2); empty unless the world has aviation on. */
  aircraft: AircraftSystem;
  /** Enter the nearest parked aircraft (climb into the cockpit); false if none/off. */
  enterAircraft(): boolean;
  /** Leave the cockpit, stepping the player onto the ground beside the aircraft. */
  exitAircraft(): void;
  /** The aircraft the player is flying, or null (AVI-2). */
  flyingAircraft(): Aircraft | null;
  /** Override the aircraft controls (debug/E2E + autopilot); null → pilot's keys. */
  setAircraftInput(input: AircraftInput | null): void;
  /** Engage the PID autopilot with targets, or null to hand back to the pilot (AVI-3). */
  setAutopilot(targets: AutopilotTargets | null): void;
  /** Whether the autopilot is currently flying the aircraft. */
  autopilotEngaged(): boolean;
  /** SAR / ground-team trainer (M36): deploy an ELT beacon (returns its id),
   *  remove/clear beacons, list them, read the homed-beacon signal at a position,
   *  read the local search objective, and toggle the SAR instrument HUD. */
  deployBeacon(x: number, y: number, z: number): number;
  removeBeacon(id: number): boolean;
  clearBeacons(): void;
  beaconList(): { id: number; x: number; y: number; z: number }[];
  sarSignal(x: number, y: number, z: number): { dist: number | null; rssi: number; bars: number };
  /** Direction-finder reading (SAR-4): relative bearing (rad) + handheld LED index
   *  to the nearest in-range beacon from a position + facing yaw; nulls = no signal. */
  sarBearing(x: number, y: number, z: number, yaw: number): { relBearing: number | null; led: number | null };
  sarObjectiveState(searcher?: string): { dwell: number; found: boolean };
  toggleSarHud(v?: boolean): boolean;
  /** Classroom / guided-build (M37, CLS-1..3): compile + place ghost blueprint(s)
   *  (translucent phantom voxels, never real blocks), read the live placed-vs-plan
   *  diff, toggle the green/red heat overlay, and open the instructor screen.
   *  Instructor/ops-gated at the call sites (creative or cheats). */
  placeBlueprint(dsl: string, x: number, y: number, z: number, rot?: number): { ok: boolean; cells: number; error?: string };
  setBlueprintCourse(entries: { dsl: string; x: number; y: number; z: number; rot?: number }[]): { ok: boolean; cells: number; error?: string };
  clearBlueprint(): void;
  blueprintState(): ClassroomSnapshot | null;
  /** The world cell a placed blueprint's origin should sit at (aimed surface + 1). */
  blueprintAim(): { x: number; y: number; z: number };
  toggleClassroomHeat(v?: boolean): boolean;
  openClassroom(): void;
  /** Reference overlays (OVL-1, M40.4): the Earth-world ghost-plane controller +
   *  its panel. Null on non-Earth worlds (overlays are Earth-only in v1). */
  overlay: OverlayController | null;
  openOverlayPanel(): void;
  /** Imagery bake (OVL-4, M43): freeze the aligned overlays (+ any covering NAIP tiles)
   *  into the ground as deterministic generator input, pinned by sha-256 — a declared
   *  regen event (pristine chunks only; edited sections untouched). Un-bake removes the
   *  pins and regens without them. Earth+solo only; both reload the world. */
  bakeImagery(): Promise<void>;
  unbakeImagery(): Promise<void>;
  /** Whether this world currently has a pinned imagery bake (drives the panel + badge). */
  hasBakedImagery(): boolean;
  /** World doors (DOR-1..5, M41): open the door menu at (x,y,z) (create/link/enter/
   *  resize); traverse a bound door (world-session swap); resolve the escape-hatch home
   *  world; and read a door's edge data. Door graph/bounds logic lives in world/doors. */
  openDoorScreen(x: number, y: number, z: number): void;
  /** Net only (M42 DOR-2): the return door the player arrived at, so the UI/debug API
   *  can point back through it. Null in solo (arrival positioning is local). */
  doorArrival?: { x: number; y: number; z: number } | null;
  worldDoorAt(x: number, y: number, z: number): WorldDoorBE['data'] | null;
  traverseDoor(x: number, y: number, z: number): Promise<boolean>;
  createChildThroughDoor(x: number, y: number, z: number, opts: ChildOpts): Promise<string>;
  resizeDoorTarget(x: number, y: number, z: number, size: number): ReturnType<typeof resizeWorld>;
  /** DOR-5 escape hatch: flush + return to the highest reachable home world's spawn. */
  returnHome(): Promise<void>;
  /** Local storage read-out (STO-1/3, M41): open the usage screen, or read the report. */
  openUsageScreen(): void;
  storageUsage(): Promise<UsageReport>;
  /** SAR vehicles (M36.3, SAR-5): the helicopter + van systems and their piloting
   *  seams — spawn/enter/exit/board (mount caps), input override, and rider counts. */
  helicopters: HelicopterSystem;
  vans: VanSystem;
  spawnHeli(x: number, y: number, z: number, yaw?: number): number;
  enterHeli(id: number): boolean;
  exitHeli(): void;
  boardHeli(id: number): boolean;
  setHeliInput(input: HeliInput | null): void;
  pilotedHeliId(): number | null;
  heliRiderCount(id: number): number;
  spawnVan(x: number, y: number, z: number, yaw?: number): number;
  enterVan(id: number): boolean;
  exitVan(): void;
  boardVan(id: number): boolean;
  setVanInput(input: VanInput | null): void;
  pilotedVanId(): number | null;
  vanRiderCount(id: number): number;
  /** Streaming/perf snapshot: worker-pool sizes, current mesh LOD depth (0 = full),
   *  the adaptive render-distance cap, loaded chunk-mesh count, and the smoothed
   *  heading (blocks/tick) (perf E2E). */
  streamingDebug(): { lod: number; renderCap: number; meshWorkers: number; genWorkers: number; loadedChunks: number; vx: number; vz: number };
  /** Spectate a remote player id (chase cam), cycle to the next, or stop (NET C4). */
  spectate(id: number | null): void;
  /** Ask the server for a seat on a replicated entity id (0 = dismount). The seat is
   *  the lowest free one on the roster; the server arbitrates (mount replication). */
  requestMount(vehicleId: number): void;
  spectateNext(): void;
  /** The remote player id currently being spectated, or null. */
  spectatingId(): number | null;
  /** Mount a boat by id / dismount / query the ridden boat (passenger binding, B4). */
  mountBoat(id: number): boolean;
  dismountBoat(): void;
  ridingBoatId(): number | null;
  /** Mount/dismount/query a land mob the player rides (M19 land-mount riding). */
  mountEntity(id: number): boolean;
  dismountMount(): void;
  ridingMount(): number | null;
  /** Put a saddle on mob `id` (E2E, M19). */
  saddleMount(id: number): boolean;
  /** Per-position block entities — chests/furnaces/spawners (M6). */
  blockEntities: BlockEntityStore;
  /** Living entities — mobs, projectiles, XP orbs (MOB-1..5). */
  entities: EntitySystem;
  /** Player melee attack cooldown (ticks remaining) + its max (UI-4 indicator). */
  attackCooldown: number;
  attackCooldownMax: number;
  /** An inventory/crafting/creative screen is open — gameplay input suspended. */
  uiOpen: boolean;
  /** Hangar §39.4 (M104.3): tick/render stop while true — a minimized window's
   *  net connection stays open, only the loop pauses. Unset (never true) outside
   *  Hangar; `loop.ts` treats it as `false` either way. */
  renderPaused?: boolean;
  /** Hangar §39.4 (M104.4): set only under `?hangar=1`; `input.ts` forwards a
   *  reserved chord through it while the pointer is unlocked (§9.7, D-759 rule 2). */
  hangar?: HangarClient;
  /** Hangar §39.6 (M106.6): whether the bridged window currently has focus — unset (falsy)
   *  outside Hangar, where a timer's existing action-bar line is the only signal there is. */
  hangarFocused?: boolean;
  fps: number;
  worstFrameMs: number;
  cheats: boolean;
  chatOpen: boolean;
  cmdContext: CommandContext;
  /** The resolved Atlas city model (ATL-1), or undefined for non-atlas worlds. */
  atlasCity?: IntermediateCity;
  /** Active multiplayer session (NET-2), or undefined in solo (NET-7). */
  net?: NetSession;
  /** Mini-map radar (UI-8); assigned once the HUD is built. */
  minimap?: MiniMapApi;
  /** Live replay recorder (REP-1); assigned once the game is built (opt-in gamerule). */
  replay?: ReplayController;
  readonly eye: number;
  readonly reach: number;
  /** Raw block edit (commands/debug) — relights + remeshes, no inventory effect. */
  setBlock(x: number, y: number, z: number, state: number): void;
  /** Break a block (survival: drops into inventory; creative: nothing). */
  mineBlock(x: number, y: number, z: number): void;
  /** Place the held item's block (survival: consumes one). Returns success. */
  placeBlock(x: number, y: number, z: number): boolean;
  /** Melee-attack the mob under the crosshair (PLR-3). Returns true if one was hit. */
  attack(): boolean;
  /** Right-click interact with the mob under the crosshair (MOB-2): tame a wolf,
   *  sit/stand a pet, shear a sheep. Returns true if a mob was interacted with. */
  useEntity(): boolean;
  /** Right-click use a block (open container / toggle door). True if handled (M6). */
  useBlock(x: number, y: number, z: number): boolean;
  /** Use the held item on a block face (flint & steel → ignite a portal, M7.1). */
  useHeldItem(faceX: number, faceY: number, faceZ: number): boolean;
  /** Bucket interaction (M9.1): pick up / place a fluid source under the crosshair,
   *  waterlogging a target block where applicable. Returns true if handled. */
  useBucket(): boolean;
  /** Use the held item on the targeted block (M11.1): hoe→till, seed→plant,
   *  bonemeal→grow, compostable→composter. Returns true if handled. */
  useItemOnBlock(x: number, y: number, z: number, nx: number, ny: number, nz: number): boolean;
  /** Ignite a nether portal at an interior air cell directly (debug/E2E, M7.1). */
  ignitePortalAt(x: number, y: number, z: number): boolean;
  /** Prime the TNT block at (x,y,z) — replace it with a fusing entity. False if
   *  the block isn't TNT (RED-8, M15.2.2). */
  ignitePrimedTntAt(x: number, y: number, z: number): boolean;
  /** State the held item would place, or null (used by input + pick-block). */
  heldPlaceState(): number | null;
  /** Add items to the inventory (/give, pickups). */
  give(item: string, count?: number): void;
  giveStack(stack: ItemStack): void;
  /** Drop items that don't fit (item entities land in M4.3; currently discards). */
  dropItem?(stack: ItemStack): void;
  setGameMode(mode: GameMode): void;
  /** Open the mode-appropriate inventory (creative palette or survival grid). */
  openPlayerInventory(): void;
  openInventory(): void;
  openCraftingTable(): void;
  openCreative(): void;
  // ── Texture Forge (M34, FRG-1/2) ──
  /** Open the Texture Forge screen. */
  openForge(): void;
  /** The current forged-skin params (the forge screen seeds its UI from this). */
  forgeParams(): ForgeParams;
  /** The current forged skin as a shareable seed string. */
  forgeSeed(): string;
  /** Re-skin the `forged_block` variant from `params` + hand the player one (FRG-2). */
  applyForge(params: ForgeParams): void;
  /** The reserved forge tile's RGBA bytes off the atlas (E2E/golden proxy). */
  forgeTilePixels(): number[];
  // ── Script Blocks (M35, SCR-4) ──
  /** The script block's stored source + autopilot preset at (x,y,z), or null. */
  scriptAt(x: number, y: number, z: number): { source: string; autopilot: AutopilotPreset | null } | null;
  /** Save a script block's source (compiles-checks it; clears any autopilot preset).
   *  Returns the compile status so the editor can surface a positioned error. */
  setScriptSource(x: number, y: number, z: number, source: string): { ok: boolean; error?: string };
  /** Load an editable autopilot preset into a script block (AVI-3). */
  setScriptAutopilot(x: number, y: number, z: number, preset: AutopilotPreset): void;
  /** Manually dispatch a script block once (editor "Run", debug); returns a message. */
  runScriptBlock(x: number, y: number, z: number, event?: string): { ok: boolean; message: string };
  /** §27.16's diagnostics for the script block at a cell, or null if there is none:
   *  the error ring, the op read-out against its budget, and the last dispatch's
   *  effects. Read-only — the runtime hands out copies. */
  scriptDiagnostics(x: number, y: number, z: number): ScriptDiagnostics | null;
  /** Engage a script block's autopilot preset now; false if not currently flying. */
  engageScriptAutopilot(x: number, y: number, z: number): boolean;
  /** Open the enchanting table UI at (x,y,z) (PLR-6, M12.1). */
  openEnchanting(x: number, y: number, z: number): void;
  /** The three enchant offers for the held tool at a table, or [] (M12.1). */
  enchantOffers(x: number, y: number, z: number): Array<{ level: number; lapisCost: number; enchants: Array<{ id: string; lvl: number }> }>;
  /** Apply enchant slot `slot` (0..2) to the held tool. Returns success (M12.1). */
  applyEnchant(x: number, y: number, z: number, slot: number): boolean;
  // ── Smithing table (M59) ──
  /** Open the smithing-table UI (upgrades the item in hand). */
  openSmithing(): void;
  /** The upgrade the held item is eligible for, or null (M59). */
  smithingOffer(): { base: string; result: string; template?: string; addition: string; affordable: boolean } | null;
  /** Forge the held item's upgrade, consuming the template + addition (M59). */
  doSmith(): boolean;
  // ── Villager trading (VIL, M14.1) ──
  /** Open the trade UI for the villager with this entity id. */
  openTrade(entityId: number): void;
  /** A villager's profession + trade-table name, or null if it isn't a villager. */
  villagerInfo(entityId: number): { profession: string } | null;
  /** The villager's offers with live use counts + affordability. */
  villagerOffers(entityId: number): Array<{ buy: Array<{ name: string; count: number }>; sell: { name: string; count: number }; used: number; maxUses: number; affordable: boolean }>;
  /** The villager's scheduled activity at the current world time (MB.7), or null if not a villager. */
  villagerActivity(entityId: number): VillagerActivity | null;
  /** Execute trade `index` with the villager. Returns success (VIL, M14.1). */
  doTrade(entityId: number, index: number): boolean;
  /** The player's reputation with this villager (gossip weighted sum, MB.7). */
  villagerReputation(entityId: number): number;
  /** Grant a gossip event about the player to this villager (e.g. a cure grants
   *  major_positive). Test convenience for the reputation → discount path. */
  addVillagerGossip(entityId: number, type: GossipType, amount: number): void;
  /** Force one gossip share round now — nearby villagers swap reputation (MB.7). */
  shareVillagerGossip(): void;
  /** Force one gossip decay step now — every ledger nudges toward zero (MB.7). */
  decayVillagerGossip(): void;
  /** Mark a mob as under Weakness (as a splashed potion would); gates the cure
   *  of a zombie villager (debug/E2E, MB.6). */
  weakenMob(entityId: number): boolean;
  /** Drive the real right-click interaction on a mob (feed/cure/trade/tame/…),
   *  returning whether it did anything (debug/E2E). */
  interactMob(entityId: number): boolean;
  /** §27.13's "Attach to this entity": give the mob under the crosshair its own script
   *  (a script NAME), or clear it with `null` so it runs its type's again. Returns the
   *  status line the editor shows. Solo only — a server's entities are the server's, and
   *  a script change reaches them through a content push (§27.14). */
  attachScriptToTarget(script: string | null): string;
  /** §28.2's heredity switch: read it, or set it (`true`/`false`) exactly as
   *  `/gamerule heredity` would. Returns the value after the call. */
  heredityRule(on?: boolean): boolean;
  /** §28.2's pace: read it, or set it (1..HER.PACE_MAX; junk reads back as 1). */
  heredityPace(n?: number): number;
  /** The action bar's current line and a serial that changes on every show (so the same
   *  line shown twice re-displays). §28.8 / D-TF-191. */
  readonly actionBar: { text: string; serial: number };
  /** The instructor's controls on this player (§29.8): frozen, the eyes-up line (null
   *  when down), and whether an instructor is watching — set by the server's `Lesson`
   *  frames, rendered by `ui/hud/lessonhud.ts`, never sim state. */
  readonly eduControl: { frozen: boolean; eyesUp: string | null; watched: boolean };
  /** Show one line on the action bar. */
  showActionBar(text: string): void;
  /** The last line shown on the action bar ('' if none) — §28.16 `lastActionBar`. */
  lastActionBar(): string;
  /** Name a mob with the HELD name tag (§28.9): validates, consumes one tag, sets `name` +
   *  `persistent`. False (and nothing consumed) for a refused name, no tag, or no mob. */
  nameEntity(id: number, text: string): boolean;
  /** Set a name directly, no tag (debug/E2E — §28.16 `setName`). */
  setEntityName(id: number, text: string): boolean;
  /** A mob's rendered nametag text, or null (debug/E2E). */
  entityNametag(id: number): string | null;
  /** Set an entity's age in ticks (debug/E2E, §28.16 `ageTo`). */
  ageEntityTo(id: number, ticks: number): void;
  /** An entity's genome as base64, or null (no entity / no genome). §28.16. */
  heredityGenome(id: number): string | null;
  /** Replace an entity's genome from base64 (decoded against its table); false when the
   *  entity is missing, its species has no table, or the text does not decode. §28.16. */
  setHeredityGenome(id: number, b64: string): boolean;
  /** An entity's cached traits, or null. §28.16. */
  heredityTraits(id: number): Traits | null;
  /** A genome-painted rig part's colour (`#rrggbb`), or null. §28.16. */
  entityTint(id: number, part: string): string | null;
  /** Whether a rig part is visible, or null. */
  entityPartVisible(id: number, part: string): boolean | null;
  /** The rendered rig scale, or null. §28.16. */
  entityScale(id: number): number | null;
  /** A genetic crop cell's genome, or null (no `crop` block entity there). §28.16. */
  cropGenome(x: number, y: number, z: number): string | null;
  /** Force-place a genetic crop at age 0 — the given genome, or a fresh roll when
   *  omitted. False when the rule is off, the crop has no gene table, or the state
   *  cannot be placed. Debug/E2E — the real plant path is a held-seed interaction
   *  (§28.16). */
  plantGenetic(x: number, y: number, z: number, crop: string, b64?: string): boolean;
  /** Force-harvest the crop at (x,y,z) exactly as breaking it would (genetics,
   *  cross-pollination, block-entity removal) but with no player/tool involved —
   *  the produce count and each dropped seed's genome, in order (§28.16). */
  harvest(x: number, y: number, z: number): { produce: number; seeds: string[] };
  /** How many cells the crop stalk-colour overlay currently paints (§28.16). */
  overlayCount(): number;
  /** Right-click use of a held field notebook — record whatever is targeted
   *  (a mob is handled earlier, by `interactMob`), or open the screen if
   *  nothing recordable is. False if the held item is not a notebook (§28.11). */
  useFieldNotebook(): boolean;
  /** This player's notebook (§28.16 `notebook`), or an empty one if they have
   *  never recorded anything. */
  readonly notebook: Notebook;
  /** Record entity `id` by its id directly, bypassing the crosshair (debug/E2E,
   *  §28.16). False for no such entity or no table (§28.11's no-op). */
  recordAnimalById(id: number): boolean;
  /** Record the genetic crop at (x,y,z) directly (debug/E2E, §28.16). False for
   *  no block entity there. */
  recordCropAt(x: number, y: number, z: number): boolean;
  /** Every lesson in the loaded collections (§29.17 `voxel.edu.lessons`, M51.6). */
  eduLessons(): readonly LessonDef[];
  /** This world's active lesson name, or null (§29.17 `voxel.edu.activeLesson`, M51.6). */
  eduActiveLesson(): string | null;
  /** The active lesson's full definition, or undefined (M51.7, the lesson screen's seam). */
  eduActiveLessonDef(): LessonDef | undefined;
  /** This player's result on the active lesson, or undefined — no active lesson,
   *  or none yet (§29.17 `voxel.edu.result`, M51.7). */
  eduResult(): LessonResult | undefined;
  /** Inject a trigger into the active lesson's evaluator; returns the objective
   *  titles newly completed (§29.17 `voxel.edu.fire`, M51.7; E-59 with none active). */
  eduFire(t: Trigger): string[];
  /** The reading TEXT for any instrument, without needing it held/aimed (M53.1,
   *  §29.17 `voxel.edu.readInstrument`); `x`/`y`/`z` are a block target for
   *  `light_meter`/`soil_probe`, unused for `signal_meter`/`altimeter`/
   *  `airspeed_indicator` (nearest to the player), and `x` alone is an entity id
   *  for `gene_sequencer`. Never fires a trigger — `fire()` does that. */
  readInstrument(item: string, x?: number, y?: number, z?: number): string;
  /** Grade + record a quiz attempt against the active lesson (§29.17
   *  `voxel.edu.submitQuiz`, M51.7); throws with no active lesson or no quiz. */
  eduSubmitQuiz(answers: readonly QuizAnswer[]): { score: number; n: number };
  /** Open the lesson screen (`KeyJ`, §29.3). */
  openLessonScreen(): void;
  /** This player's role here (§29.7): solo = `instructor` whenever cheats are on; on a
   *  server, what the server's `Players` roster says. */
  eduRole(): Role;
  /** The instructor's roster (§29.8, `voxel.edu.roster`) — empty in solo (no students). */
  eduRoster(): RosterRow[];
  /** Send a console command to the server (Watch/Go to/Bring/Freeze…); solo prints why not. */
  eduCommand(text: string): void;
  /** Raise or lower this player's hand (`KeyH`, §29.8) — the server numbers the queue. */
  eduRaiseHand(up: boolean): void;
  /** Whether the server's roster shows this player's hand up. */
  eduHandUp(): boolean;
  /** The lesson export (§29.9): `results.json`, or the active lesson's CSV. On a server,
   *  pulled over HTTP (instructor-gated there); in solo, the same pure serializer over
   *  this world's own rows. */
  eduExport(format: 'json' | 'csv'): Promise<string>;
  /** The last `results.json` exported this session ('' before any) — `voxel.edu.exportJson`. */
  eduLastExport(): string;
  /** Fast-forward an in-progress zombie-villager cure to complete on the next tick
   *  (debug/E2E, MB.6). Returns true if the mob was curing. */
  finishCure(entityId: number): boolean;
  /** The player's powder-snow freeze counter, in ticks (debug/E2E, MB.1). */
  readonly frozenTicks: number;
  /** Honey level 0..5 of the beehive at (x,y,z), or -1 if not a hive (MB.5). */
  hiveHoney(x: number, y: number, z: number): number;
  /** Simulate a nectar delivery to the hive at (x,y,z): raise honey + nudge one
   *  nearby crop (debug/E2E, MB.5). Returns true if it deposited. */
  pollinateHive(x: number, y: number, z: number): boolean;
  /** Make the frog with this id eat a nearby slime/magma cube; returns the dropped
   *  froglight block name, or '' if nothing was in range (debug/E2E, MB.5). */
  frogEat(entityId: number): string;
  /** Make the chicken with this id lay an egg now (debug/E2E, MB.3). True if a
   *  chicken was there. */
  layEgg(entityId: number): boolean;
  /** Run one mob spawn cycle now; returns the number of mobs spawned (debug/E2E). */
  forceSpawnCycle(): number;
  /** Whether the villager with this id is willing to breed (fed enough, MB.7). */
  villagerWilling(entityId: number): boolean;
  /** Run one villager-breeding pass now; true if a baby was born (debug/E2E, MB.7). */
  tryBreedVillagers(): boolean;
  /** The bed cell a villager has claimed for night pathing, or null (MB.7). Assigns
   *  one immediately if the villager has none yet and a bed is in range. */
  villagerBed(entityId: number): { x: number; y: number; z: number } | null;
  /** Try to give a jobless villager a nearby workstation's profession now; returns
   *  the new profession (mob type) it took up, or null (debug/E2E, MB.7). */
  assignVillagerJob(entityId: number): string | null;
  /** Try to spawn a village iron golem now; true if one was summoned (debug/E2E, MB.7). */
  trySpawnIronGolem(): boolean;
  /** Spawn a pillager patrol near the player now; returns how many spawned (debug/E2E, MB.6). */
  forcePatrol(): number;
  /** Ring the bell at (x,y,z): reveal nearby raiders. Returns how many were revealed
   *  (debug/E2E, MB.6). */
  ringBell(x: number, y: number, z: number): number;
  /** Have the evoker with this id conjure vexes now; returns how many (debug/E2E, MB.6). */
  summonVexes(entityId: number): number;
  /** Fire the dispenser at (x,y,z) once (its exotic action or an ejection). Returns
   *  true if a dispenser was there (debug/E2E, MB.7). */
  fireDispenser(x: number, y: number, z: number): boolean;
  /** Load a brewing stand's 3 bottle potion keys ('' = empty), ingredient, and fuel
   *  charges (debug/E2E, MB.4). */
  loadBrewingStand(x: number, y: number, z: number, bottles: string[], ingredient: string, fuel: number): void;
  /** Fast-forward the brewing stand at (x,y,z) by one full batch; true if it brewed
   *  (debug/E2E, MB.4). */
  finishBrewing(x: number, y: number, z: number): boolean;
  /** The 3 bottle potion keys currently in the brewing stand at (x,y,z) (MB.4). */
  brewingStandBottles(x: number, y: number, z: number): string[];
  /** The current status-effect screen wash {r,g,b,a} (a=0 ⇒ none) (debug/E2E, MB.4). */
  readonly effectTint: { r: number; g: number; b: number; a: number };
  /** The render brightness floor from night vision (1 while active, else 0) (MB.4). */
  readonly nightVisionFloor: number;
  /** Write a sign's four text lines (normalized) at (x,y,z) (debug/E2E, MB.1). */
  setSignText(x: number, y: number, z: number, lines: string[]): void;
  /** Read a sign's four text lines at (x,y,z) (debug/E2E, MB.1). */
  signText(x: number, y: number, z: number): string[];
  /** Whether the sign at (x,y,z) has glowing text (glow-ink applied) (debug/E2E, M22). */
  signGlows(x: number, y: number, z: number): boolean;
  /** The lit board pixels the sign's text rasterizes to — the in-world glyph
   *  render data the client draws on the board (debug/E2E, MB.1). */
  signPixels(x: number, y: number, z: number): SignPixel[];
  /** The sign board's pixel grid size (cols × rows). */
  signBoardSize(): { cols: number; rows: number };
  /** Opaque pixel count of the sign's rendered board texture (MB.1) — proves the
   *  glyph rasterization the client uploads onto the board quad. */
  signTextureLitCount(x: number, y: number, z: number): number;
  // ── Co-working (M55.4, ARCH §30.12) ──
  /** Get (no arg) or set this player's presence status (§30.4, COW-5). Refuses
   *  an unknown value with the binding line, keeping the previous status. */
  cowStatus(v?: string): string;
  cowNoteText(x: number, y: number, z: number): string | null;
  cowSetNote(x: number, y: number, z: number, text: string): boolean;
  cowBoard(x: number, y: number, z: number): Array<{ text: string; done: boolean }> | null;
  cowToggleTask(x: number, y: number, z: number, i: number): boolean;
  cowTimer(x: number, y: number, z: number): { endTick: number | null; preset: string } | null;
  /** The timer's `mm:ss` face text right now (§30.3's own "reads 25:00" example). */
  cowTimerFace(x: number, y: number, z: number): string | null;
  cowStartTimer(x: number, y: number, z: number, preset: string): boolean;
  // ── The shared screen (M57, ARCH §30.6/§30.12) ──
  cowScreenState(x: number, y: number, z: number): { live: boolean; sharer: number; viewers: string[] } | null;
  cowCanShare(): boolean;
  cowViewers(x: number, y: number, z: number): string[];
  /** Test-only extension beyond §30.12's declared `viewers()` (which is names
   *  only, matching the wire's own name-free viewer list): per-viewer
   *  connection state, for asserting "the sharer's viewer list names the
   *  watcher as live" (M57.4's own Verify wording) without a pixel-level
   *  video assertion. */
  cowViewerStates(x: number, y: number, z: number): Array<{ id: string; state: string }>;
  /** Test-only canvas `captureStream` share (M57.4) — bypasses the browser's
   *  real `getDisplayMedia` consent picker entirely, so CI can drive
   *  share → watch → paint → stop with no display capture and no TURN. */
  cowFakeShare(x: number, y: number, z: number): boolean;
  /** Test-only (E-64 seam): force THIS client's own watching peer connection
   *  to fail, standing in for a real ICE failure a headless browser can't
   *  otherwise produce. */
  cowForceWatchFail(): void;
  // ── Note blocks (MB.1) ──
  /** Current note (0..24) of the note block at (x,y,z). */
  noteBlockNote(x: number, y: number, z: number): number;
  /** Advance the note block's note by one (right-click) and play it; returns the payload. */
  cycleNoteBlock(x: number, y: number, z: number): NotePlayback;
  /** Play the note block at (x,y,z) without cycling (a redstone pulse would call this). */
  playNoteBlock(x: number, y: number, z: number): NotePlayback;
  /** The most recent note-block play payload (also routed to the audio engine), or null. */
  readonly lastNotePlayed: NotePlayback | null;
  // ── Audio (AUD-1, M23) ──
  /** Resume the WebAudio context (call from a user gesture; autoplay policy). */
  resumeAudio(): void;
  /** The rolling audio-event log (subtitles + E2E read this). */
  audioEvents(): readonly AudioEvent[];
  /** The most recent audio event, or null. */
  lastAudio(): AudioEvent | null;
  /** Clear the audio-event log (test helper). */
  clearAudio(): void;
  /** Play a sound event by id at an optional world position (test/scripting). */
  playSound(id: string, x?: number, y?: number, z?: number, pitch?: number): void;
  /** Set an audio bus volume 0..1. */
  setAudioVolume(bus: 'master' | 'sound' | 'music', v: number): void;
  /** Current audio bus volumes. */
  audioVolumes(): { master: number; sound: number; music: number };
  // ── Jukebox / music (AUD-4) ──
  /** Insert a disc into the jukebox at (x,y,z); plays it. False if invalid/occupied. */
  insertDisc(x: number, y: number, z: number, disc: string): boolean;
  /** The disc currently in the jukebox at (x,y,z), or null. */
  jukeboxDisc(x: number, y: number, z: number): string | null;
  /** The comparator output (0..15) a jukebox at (x,y,z) emits (its disc index). */
  discComparator(x: number, y: number, z: number): number;
  /** The current ambient music context, or null before the first frame. */
  readonly musicContext: string | null;
  /** Re-apply audio settings (volumes + subtitles) from `settings` (options change). */
  applyAudioSettings(): void;
  /** Toggle the subtitles overlay directly (test/scripting). */
  setSubtitles(on: boolean): void;
  /** Active subtitle caption lines (test/E2E seam). */
  subtitleLines(): string[];
  // ── Advancements (M24) ──
  /** Completed advancement ids (test/E2E seam). */
  advancementsDone(): string[];
  /** Titles of advancement toasts shown so far (test/E2E seam). */
  advancementToasts(): string[];
  /** Open the advancement tree browser (L). */
  openAdvancements(): void;
  // ── Keybinds (M24) ──
  /** Rebind an action to a key code (clears any conflict), as the options UI does. */
  setKeybind(action: GameAction, code: string): void;
  /** The key code currently bound to an action ('' if unbound). */
  keybindFor(action: GameAction): string;
  // ── Statistics (M24) ──
  /** Open the statistics screen; onBack (from the pause menu) returns to it. */
  openStats(onBack?: () => void): void;
  /** A per-id category tally (test/E2E seam). */
  statCount(category: StatCategory, id: string): number;
  /** A general custom total (test/E2E seam). */
  statCustom(stat: CustomStat): number;
  /** Capture the current frame → a PNG data URL, download it, and confirm on
   *  screen (F2, M24). Returns the data URL (test/E2E seam). */
  takeScreenshot(): string;
  /** How many screenshots have been taken this session (test/E2E seam). */
  screenshotCount(): number;
  // ── F3 debug (M24): the text overlay (F3), chunk borders (F3+G), hitboxes (F3+B) ──
  toggleDebugOverlay(): void;
  toggleChunkBorders(): void;
  toggleHitboxes(): void;
  debugOverlayOn(): boolean;
  chunkBordersOn(): boolean;
  hitboxesOn(): boolean;
  // ── Recipe book (M24): unlocked-recipe tracking + the crafted-count hook ──
  readonly recipeBook: RecipeBook;
  onCraft?(item: string, count: number): void;
  // ── Crafter (MB) ──
  /** Set a crafter grid slot (creates the block entity); an unknown item clears it. */
  setCrafterSlot(x: number, y: number, z: number, i: number, item: string, count?: number): void;
  /** Toggle a crafter slot's disabled flag (forced-gap shaping; empty slots only). */
  toggleCrafterSlot(x: number, y: number, z: number, i: number): void;
  /** The crafter grid contents at (x,y,z) as {name,count}|null per slot. */
  crafterSlots(x: number, y: number, z: number): ({ name: string; count: number } | null)[];
  /** Fire the crafter at (x,y,z) once (as a redstone pulse would); true if one was there. */
  fireCrafter(x: number, y: number, z: number): boolean;
  /** The most recent crafter output {item,count}, or null. */
  readonly lastCrafted: { item: string; count: number } | null;
  /** Round-trip all entities through save/reload, rebuilding villager economy maps
   *  under new ids — proves gossip/uses persistence (debug/E2E, MB.7). */
  reloadEntitiesRoundTrip(): void;
  // ── Raids (RAID, M14.2) ──
  /** Active-raid summary (bossbar/debug), or null when no raid is running. */
  raidInfo(): RaidInfo | null;
  /** Force-start a raid at the nearest village (debug/E2E). Returns success. */
  forceRaid(): boolean;
  /** Serialize the active raid's progress, or null (MB.6 persistence). */
  raidState(): SavedRaid | null;
  /** Rebuild the active raid mid-flight from a saved snapshot (MB.6). */
  restoreRaid(state: SavedRaid): boolean;
  /** Damage a mob by id as if the player struck it (debug/E2E). True if it hit. */
  hurtMob(entityId: number, amount: number): boolean;
  /** Deal `amount` raw damage to the player through the real gear-mitigation path
   *  (armour/EPF/totem, M17). `fromX/fromZ` set the knockback source (debug/E2E). */
  hurtPlayer(amount: number, fromX?: number, fromZ?: number): void;
  /** Equip an item (debug/E2E): armour → its column, shield/totem → offhand (M17). */
  equip(item: string): boolean;
  /** Boost an active elytra glide with a held firework rocket (M21). Returns true
   *  if a rocket was consumed (only while gliding). */
  useFirework(): boolean;
  /** Force a fishing bite on the current cast and reel it, returning caught names (debug/E2E, MB.3). */
  fishCatch(): string[];
  /** Force-start an elytra glide, bypassing the jump edge (debug/E2E, M21). */
  glide(): void;
  /** Whether the player is currently elytra-gliding (debug/E2E, M21). */
  readonly gliding: boolean;
  /** Level-wide filled-map store (UI-8, M18). Held maps reference it by `mapId`. */
  mapStore: MapStore;
  /** Right-click use of an empty `map` → fill it into a `filled_map` (UI-8, M18). */
  useMapInHand(): boolean;
  /** Create a filled map at the player, put it on the hotbar, return its `mapId`
   *  (debug/E2E). */
  createFilledMap(scale?: number): number;
  /** Create a treasure map at the player, pin it to the nearest buried treasure, and
   *  return the mapId + the located target (or null coords if none) (E2E, M18). */
  createTreasureMap(scale?: number): { mapId: number; targetX: number | null; targetZ: number | null };
  /** The `explorer_map` loot seam (M18): mint a `filled_map` centred on and pinned to
   *  the nearest `destination` structure, or null (flat/atlas/earth worlds, or none in
   *  range). Passed into `fillChestLoot` so a shipwreck's map chest yields a real
   *  treasure map instead of a blank one. */
  explorerMap(destination: string): ItemStack | null;
  /** Re-sample a filled map around the player (the HUD + debug/E2E share this).
   *  Returns the number of pixels newly filled in. */
  refreshMap(mapId: number, radius?: number): number;
  /** Force one budding_amethyst growth step on face `dirIndex` (debug/E2E, M19).
   *  Returns true if a bud sprouted/advanced. */
  growAmethyst(x: number, y: number, z: number, dirIndex?: number): boolean;
  /** Bloom sculk from a death at `(x,y,z)` with `charge` XP (debug/E2E, M22).
   *  Returns the number of blocks converted (=== charge spent). */
  bloomSculk(x: number, y: number, z: number, charge: number): number;
  /** Pyramid tier (0..4) beneath the beacon at (x,y,z) (debug/E2E, M20.1). */
  beaconTier(x: number, y: number, z: number): number;
  /** Set the beacon at (x,y,z)'s chosen primary (+ optional secondary) effect (M20.1). */
  setBeaconEffect(x: number, y: number, z: number, primary: string, secondary?: string | null): void;
  /** Force one beacon application; returns the effects it applied (debug/E2E, M20.1). */
  pulseBeacon(x: number, y: number, z: number): { id: string; amplifier: number }[];
  /** Ignite a built wither summon frame centred at (x,y,z); true if a wither spawned (M20.1). */
  summonWither(x: number, y: number, z: number): boolean;
  /** Prismarine frame-block count around the conduit at (x,y,z) (debug/E2E, M18.1). */
  conduitFrame(x: number, y: number, z: number): number;
  /** Force one conduit application; returns its frame/range/powered state (M18.1). */
  pulseConduit(x: number, y: number, z: number): { frame: number; range: number; powered: boolean };
  /** Trigger the sculk shrieker at (x,y,z); returns the new warning level + whether a warden spawned (M22.1). */
  shriek(x: number, y: number, z: number): { level: number; summoned: boolean };
  /** Emit a sculk vibration on the shared bus — sensors wake, wardens take offence. */
  emitVibration(event: string, x: number, y: number, z: number, source: number): void;
  /** A warden's anger toward a suspect (0 if unknown), and its current quarry. */
  wardenAngerAt(wardenId: number, suspect: number): number;
  wardenQuarryOf(wardenId: number): { suspect: number; anger: number; hunting: boolean } | null;
  /** Make the warden `id` fire its sonic boom at the player; returns damage dealt (M22.1). */
  wardenSonicBoom(id: number): number;
  /** Intact end crystals shielding the ender dragon `id` (debug/E2E, M21.1). */
  dragonCrystals(id: number): number;
  /** The ender dragon `id`'s current flight phase, or null if not a dragon (M21.1). */
  dragonPhase(id: number): string | null;
  /** Total mobs a trial spawner releases for the current player count (M22.1). */
  trialWave(): number;
  /** Simultaneous-alive cap for the current player count (M22.1). */
  trialCap(): number;
  /** Force the trial spawner at (x,y,z) to complete its wave so it ejects the key (E2E, M22.1). */
  forceTrialEject(x: number, y: number, z: number): void;
  /** Force one deterministic dripstone growth step at the down-tip (x,y,z) (E2E, M19.1). */
  growDripstone(x: number, y: number, z: number): boolean;
  /** Right-click the respawn anchor at (x,y,z); returns the action taken (M20.1). */
  useAnchor(x: number, y: number, z: number): string;
  /** Apply dolphin-grace / pufferfish-poison to the player at current positions (E2E, M18.1). */
  pulseOceanFauna(): void;
  /** Fully charge + fire nearby guardian beams and apply the elder aura now (E2E, M18.1). */
  pulseGuardians(): void;
  /** Force the hoglin `id` past its overworld zombify timer; returns true if it became a zoglin (E2E, M17.1). */
  zombifyHoglin(id: number): boolean;
  /** Run one turtle-egg random tick at (x,y,z): cracks, or hatches a baby at night on sand (E2E, M18.1). */
  turtleEggTick(x: number, y: number, z: number): void;
  /** Emit a vibration at (x,y,z) → wake nearby sculk sensors + resolve redstone now (E2E, M22.1). */
  sculkVibrate(x: number, y: number, z: number): void;
  /** Detonate a wind-charge burst at (x,y,z): knock entities back + toggle activatable blocks (E2E, M22.1). */
  windCharge(x: number, y: number, z: number): void;
  /** Brush the suspicious block at (x,y,z) one stage; returns the revealed item name once dug out, else '' (E2E, M41). */
  brushBlock(x: number, y: number, z: number): string;
  /** Force sniffer `id` to dig now; returns the unearthed seed name, or '' if the ground isn't diggable (E2E, M19). */
  snifferDig(id: number): string;
  /** Make camel `id` dash (if off cooldown); returns true if it dashed (E2E, M19). */
  camelDash(id: number): boolean;
  /** Grow tadpole `id` into a frog now; returns the froglight its variant would make, or '' (E2E, M19). */
  growTadpole(id: number): string;
  /** Force one deterministic copper oxidation step at (x,y,z) (debug/E2E, MB.1).
   *  Returns the new block name, or null if it can't oxidize (waxed/oxidized). */
  oxidizeCopper(x: number, y: number, z: number): string | null;
  /** Number of occupied cooking slots in the campfire at (x,y,z) (debug/E2E, MB.3). */
  campfireCount(x: number, y: number, z: number): number;
  /** Fast-forward the campfire at (x,y,z) to completion, popping its cooked food
   *  through the real tick path (debug/E2E, MB.3). Returns slots completed. */
  cookCampfire(x: number, y: number, z: number): number;
  /** Run one rain-fill step on the cauldron at (x,y,z) through the real path
   *  (debug/E2E, MB.1); returns its water level afterwards. */
  rainCauldron(x: number, y: number, z: number): number;
  /** Force one deterministic growth step for the MB.2 plant at (x,y,z) (debug/E2E).
   *  Returns true if the plant grew / aged / fruited. */
  growPlant(x: number, y: number, z: number): boolean;
  /** Run the real random-tick handler on the cell at (x,y,z) (debug/E2E) — e.g.
   *  snow melt/accumulate/coat (MB.1). */
  randomTick(x: number, y: number, z: number): void;
  /** Place the currently-held item at (x,y,z) via the real placement interaction
   *  (debug/E2E) — e.g. snow-layer stacking (MB.1). Returns whether it placed. */
  placeHeld(x: number, y: number, z: number): boolean;
  /** Redstone state at a cell (debug/E2E), or null if not a redstone component. */
  redstoneAt(x: number, y: number, z: number): RedstoneInfo | null;
  /** Resolved block-state properties at a cell (debug/E2E) — e.g. a piston's
   *  `extended` / `facing`, an observer's `powered`. */
  blockPropsAt(x: number, y: number, z: number): Readonly<Record<string, string>>;
  runCommand(input: string): CommandResult;
  print(msg: string): void;
  tick(): void;
  render(alpha: number): void;
  /** Persist world meta + player + dirty sections. Resolves when committed. */
  saveNow(): Promise<void>;
}

export function defaultMeta(id: string, params: URLSearchParams): WorldMeta {
  const atlas = params.has('atlas');
  // `?earth=lat,lon[,scale]` boots directly into an Earth world (ERT-6, ATL-8 pattern);
  // a bare `?earth` = the offline KHFD default.
  const earth = params.has('earth');
  // `?tall=1` (TAL-6 proving ground): a flat world on the default Earth column
  // (672 sections). Dev-only — it exercises the tall-world engine at extreme Y.
  const tall = params.has('tall');
  const flat = id === 'test' || params.has('flat') || tall;
  const hardcore = params.has('hardcore');
  // Atlas/Earth worlds default to creative (flight on, ATL-6/ERT-6).
  const gameMode = !atlas && !earth && (hardcore || params.get('mode') === 'survival') ? 'survival' : 'creative';
  // `?atlas=lat,lon[,scale]` boots directly at those coords (ATL-8); bare `?atlas` = default KHFD.
  const atlasReq: WorldMeta['atlas'] = atlas ? parseAtlasParam(params.get('atlas')) : undefined;
  // Earth: resolve the deep-link into an EarthMeta (default = the KHFD point + 3 ft scale).
  const earthMeta: WorldMeta['earth'] = earth ? earthMetaFromParam(params.get('earth')) : undefined;
  // The Earth world's overworld Y-range is derived from its scale (ERT-5); the tall
  // proving ground uses the fixed default Earth column.
  const ranges = earthMeta
    ? { overworld: earthRangeSpec(earthMeta.scaleM) }
    : tall ? { overworld: { minY: TALL.RANGE_MIN_Y, height: TALL.RANGE_HEIGHT } } : undefined;
  return {
    id,
    name: id,
    seed: params.get('seed') ?? String(Date.now()),
    worldType: earth ? 'earth' : atlas ? 'atlas' : flat ? 'flat' : 'normal',
    gameMode,
    difficulty: hardcore ? 'hard' : 'normal',
    hardcore,
    // Cheats follow game mode by default (creative on, survival off); `?cheats` forces on.
    allowCommands: params.has('cheats') ? true : gameMode !== 'survival',
    atlas: atlasReq,
    earth: earthMeta,
    // Aviation layer (M32/AVI-2): on for every Atlas world and every Earth world (real
    // terrain + a runway) — no flag needed.
    aviation: atlas || earth ? true : undefined,
    ranges,
    createdAt: Date.now(),
    playedAt: Date.now(),
    version: 1,
  };
}

/** `?earth=lat,lon[,scale]` → an `EarthMeta` (default = the offline KHFD point). */
function earthMetaFromParam(val: string | null): NonNullable<WorldMeta['earth']> {
  const dl = parseEarthParam(val);
  const lat = dl.lat ?? EARTH.DEFAULT.lat, lon = dl.lon ?? EARTH.DEFAULT.lon;
  const isDefault = dl.lat === undefined;
  return {
    lat, lon,
    label: isDefault ? EARTH.DEFAULT.label : `${lat},${lon}`,
    scaleM: dl.scaleM,
    extentM: EARTH.EXTENT_M_DEFAULT,
    contract: { major: 1 },
  };
}

/** Derive a client WorldMeta from a server Welcome (NET-2). The replica is never
 *  persisted locally (autosave is skipped in net mode), so the id is namespaced and
 *  cheats are off — the server arbitrates everything. */
function metaFromWelcome(welcome: NetSession['welcome'], params: URLSearchParams): WorldMeta {
  const m = welcome.meta;
  return {
    id: `net:${m.id}`,
    name: m.name || 'Server',
    seed: m.seed,
    worldType: m.worldType,
    gameMode: m.gameMode,
    difficulty: m.difficulty,
    hardcore: m.hardcore,
    allowCommands: params.has('cheats'),
    flatPreset: m.flatPreset || undefined,
    createdAt: Date.now(),
    playedAt: Date.now(),
    version: 1,
  };
}

/** Build the running game for a world (loading its edits + player if returning). A door
 *  traversal passes `enter` (DOR-2): the carried inventory + the paired arrival door. */
export async function startGame(save: SaveManager, registries: Registries, settings: Settings, meta: WorldMeta, isNew: boolean, net?: NetSession, enter?: DoorHandoff): Promise<Game> {
  const app = document.getElementById('app');
  if (!app) throw new Error('#app missing');
  app.innerHTML = '';

  const renderTable = buildRenderTable(registries);
  const flat = meta.worldType === 'flat';

  // Atlas worlds (ATL-1): resolve the city model before building the world — the
  // default location renders offline from the bundled KHFD fixture (ATL-6).
  let atlasCity: IntermediateCity | undefined;
  let atlasElevation: AtlasElevation | undefined;
  let atlasSolar: AtlasSolar | undefined;
  // Earth worlds (ERT-1): resolve the TIL tiles into the serializable EarthWorld the
  // sim voxelizes — the default KHFD location boots the bundled fixture offline (ERT-6).
  let earthWorld: EarthWorld | undefined;
  if (meta.worldType === 'earth') {
    const em = meta.earth ?? { lat: EARTH.DEFAULT.lat, lon: EARTH.DEFAULT.lon, label: EARTH.DEFAULT.label, scaleM: EARTH.DEFAULT.scaleM, extentM: EARTH.EXTENT_M_DEFAULT, contract: { major: 1 } };
    const resolved = await resolveEarth(em);
    earthWorld = resolved.world;
    // Real solar (SOL-1 widened to Earth): the resolved centre drives the true sun,
    // calendar date fixed at create — Earth worlds get real day/night like Atlas.
    atlasSolar = { lat: resolved.center.lat, lon: resolved.center.lon, epochMs: meta.createdAt ?? Date.now() };
    // Stamp the resolved DEM provenance back onto the meta for the accuracy badge (ERT-3).
    meta.earth = { ...em, demSource: resolved.demSource, contract: { ...em.contract, major: resolved.contractMajor } };
    // Baked imagery (OVL-4, M43): a pinned bake becomes deterministic generator input —
    // the ENG-1 crossing. Re-fetch + re-hash the pinned sources; anything that drifted or
    // vanished drops (never a silent re-bake), and a fully-dropped bake ⇒ M40 parity.
    if (meta.imageryBake && earthWorld) {
      const { buildImageryPalette } = await import('../sim/earth/imagery');
      const cimg = await import('./earth/imagery');
      const { loadImageryTile } = await import('./earth/tiles');
      const baseUrl = meta.earth.contract?.url;
      const imagery = await cimg.resolveBakedImagery(meta.imageryBake, {
        center: resolved.center, scaleM: em.scaleM, palette: buildImageryPalette(registries),
        decode: cimg.canvasDecode, sha256: cimg.sha256Hex,
        loadOverlayBlob: (id) => save.getOverlayBlob(id),
        loadTilePng: (z, x, y) => loadImageryTile(baseUrl, z, x, y),
      });
      if (imagery) earthWorld = { ...earthWorld, imagery };
    }
  }
  if (meta.worldType === 'atlas') {
    const req = meta.atlas ?? { scale: 1 };
    const resolved = await resolveAtlas(req);
    atlasCity = resolved.city;
    // Real terrain relief (M30/ELV-1): the default location uses the bundled DEM
    // fixture (offline); the resample itself is pure and runs inside worldgen. With
    // no DEM (fetch failed, or the simulated fallback) the world stays flat at
    // SEA_LEVEL — byte-identical to v1.1 (parity), never labelled relief.
    atlasElevation = resolved.elevation.dem ? resolved.elevation : undefined;
    // Real solar position (M31/SOL-1): a known geographic centre drives the real sun,
    // with the calendar date fixed at world create. A pure ATL-2 fallback (no centre)
    // keeps the generic day/night cycle.
    atlasSolar = resolved.center
      ? { lat: resolved.center.lat, lon: resolved.center.lon, epochMs: meta.createdAt ?? Date.now() }
      : undefined;
    meta.atlas = { ...(meta.atlas ?? { scale: atlasCity.scale }), simulated: atlasCity.simulated };
    // Aviation (M32, AVI-1): every Atlas world is flyable; one with no OSM runway
    // gets a synthesized sandbox airstrip through the origin so you can always take
    // off (decisions.md). We're already inside the `worldType === 'atlas'` branch.
    if (atlasCity.aeroways.length === 0) {
      atlasCity = { ...atlasCity, aeroways: [synthRunway()] };
    }
  }

  // Dimensions (DIM-1): a manager holds one World per dimension, creating the
  // nether lazily on first travel. The overworld is the active dimension at boot.
  const portalBlocks: PortalBlocks = {
    air: STATE_AIR,
    obsidian: registries.states.defaultOf.get('obsidian') ?? -1,
    portalX: registries.states.idByKey.get('nether_portal[axis=x]') ?? -1,
    portalZ: registries.states.idByKey.get('nether_portal[axis=z]') ?? -1,
  };
  // Flat preset (M24 world-create v2): a flat world remaps its top/filler/base
  // blocks from the chosen preset; other world types ignore it.
  const flatPreset = flat && meta.flatPreset ? flatPresetBlocks(meta.flatPreset) : undefined;
  // Per-dimension Y-range (TAL-1): the overworld may be a tall/Earth column
  // (recorded in meta.ranges); the nether keeps the default range.
  const owRangeSpec = meta.ranges?.overworld;
  const overworldRange = owRangeSpec ? makeRange(owRangeSpec.minY, owRangeSpec.height) : undefined;
  // Bounded child world (DOR-3, M41): the overworld carries a square footprint; the
  // generator walls its edges. The nether stays unbounded (doors are keyed by worldId,
  // not dimId — §10 is untouched).
  const bounds = meta.bounds ? { half: meta.bounds.size / 2 } : undefined;
  const manager = new DimensionManager(
    (dim) => new World(registries, meta.seed, dim === 'nether'
      ? { dimension: 'nether' }
      : dim === 'end' ? { dimension: 'end' }
      : earthWorld ? { earth: earthWorld, solar: atlasSolar, range: overworldRange, bounds }
      : atlasCity ? { atlas: atlasCity, atlasElevation, solar: atlasSolar, bounds }
      : { flat, flatPreset, range: overworldRange, bounds }),
    portalBlocks,
  );
  let currentDim: DimId = 'overworld';
  let world = manager.world('overworld');
  // Atlas/Earth defaults (ATL-6/ERT-6): flight on (creative), mobs off, weather on (M9).
  if (atlasCity || earthWorld) world.gamerules.set('doMobSpawning', 'false');
  // Game rules chosen at creation (M24): re-applied on every load (they live in
  // the persisted world meta, so they survive reloads).
  if (meta.gamerules) for (const [k, v] of Object.entries(meta.gamerules)) world.gamerules.set(k, v);

  let loadedPlayer: Awaited<ReturnType<SaveManager['getPlayer']>>;
  let loadedEntities: Awaited<ReturnType<SaveManager['loadEntities']>> = [];
  let loadedBlockEntities: Awaited<ReturnType<SaveManager['loadBlockEntities']>> = [];
  if (!isNew) {
    const sections = await save.loadSections(meta.id, currentDim, world.range);
    const touched = new Set<string>();
    for (const s of sections) {
      world.overlaySection(s.cx, s.cz, s.sy, s.states);
      touched.add(`${s.cx},${s.cz}`);
    }
    for (const k of touched) {
      const [cx, cz] = k.split(',').map(Number) as [number, number];
      world.recomputeChunkHeightmaps(cx, cz);
      world.relightChunk(cx, cz); // saved sections carry blocks, not light — recompute
    }
    loadedPlayer = await save.getPlayer(meta.id);
    loadedEntities = await save.loadEntities(meta.id, currentDim);
    loadedBlockEntities = await save.loadBlockEntities(meta.id, currentDim);
  }

  const rc = createRenderContext(app);
  rc.camera.fov = clampFov(settings.fov); // start at the saved FOV, not the default (M24)
  rc.camera.updateProjectionMatrix();
  let chunkMeshes = new ChunkMeshes(world, renderTable, rc.scene, rc.material, rc.waterMaterial, makeMeshWorkers());
  const icons = new ItemIcons(rc.atlasCanvas, registries, renderTable);

  // ── Survival player + inventory (M4) ──
  const maxStackOf = (item: number): number =>
    registries.items.byName.get(registries.items.nameOf[item] ?? '')?.maxStack ?? 64;
  // Per-dimension sim state (world/entities/blockEntities/drops) is swapped on
  // travel; render objects are torn down + rebuilt (DIM-1). These `let` bindings
  // always point at the active dimension so every closure below tracks the swap.
  let drops = new ItemDropSystem(maxStackOf);
  let fallingBlocks = new FallingBlockSystem();
  let primedTnt = new PrimedTntSystem();
  let minecarts = new MinecartSystem();
  let boats = new BoatSystem();
  /** Debug/E2E steering per boat id (passenger binding is excluded per design). */
  const boatInputs = new Map<number, BoatInput>();
  /** Aircraft (M32, AVI-2): only ever populated on an aviation-enabled Atlas world. */
  const aircraft = new AircraftSystem();
  // ── SAR vehicles (M36.3, SAR-5): a 4-seat helicopter + an 8-seat van, driven by
  //    the pure sim models; the host binds pilot input, chase-cams, and seats riders
  //    (mount.ts caps). Only spawned in Atlas/aviation worlds (parity). ──
  const helicopters = new HelicopterSystem();
  const vans = new VanSystem();
  let pilotedHeliId: number | null = null;
  let pilotedVanId: number | null = null;
  /** Rider occupancy per vehicle (pilot + boarded passengers), capped by mount.ts. */
  const heliRiders = new Map<number, number>();
  const vanRiders = new Map<number, number>();
  const aviationEnabled = meta.worldType === 'atlas' || meta.worldType === 'earth';
  /** The aircraft the player is flying, or null; and an optional control override. */
  let flyingAircraftId: number | null = null;
  let aircraftInputOverride: AircraftInput | null = null;
  /** PID autopilot (AVI-3); when its targets are set it flies the aircraft. A `let`
   *  so the M35 script-block preset can swap in a controller with editable gains. */
  let autopilot = new Autopilot();
  let autopilotTargets: AutopilotTargets | null = null;
  /** M54.1 (§29.4, CNT-5 "tune-the-autopilot"): consecutive ticks the ALTITUDE
   *  error has stayed within `AUTOPILOT_HOLD_ERROR` while engaged — reset to 0 the
   *  moment it drifts out, or the autopilot disengages. Host-tracked derived state
   *  (like `sarObjective`/`lessonRegionState`), not sim state: it's a pure function
   *  of already-deterministic aircraft/target positions every tick, so replay and
   *  MP parity hold without it living in `sim/`. */
  let autopilotHoldTicks = 0;
  /** Landing-edge detection for `aircraft_landed` (E-28/M54.1): the aircraft's own
   *  `onGround` from the tick BEFORE this one. */
  let aircraftWasOnGround = true;
  /** Presence status (§30.4, COW-5): session state only, never sim state, never
   *  persisted, defaults to `available`. */
  let presenceStatus: 'available' | 'focus' | 'call' = 'available';
  /** Spectator-camera target: a remote player id to chase, or null for own view (C4). */
  let spectateId: number | null = null;
  /** Boat the player is riding, or null (passenger binding, B4 — activates mount.ts). */
  let ridingBoatId: number | null = null;
  /** Land mob (horse/pig/camel/…) the player is riding, or null (M19 mount riding). */
  let ridingMountId: number | null = null;
  /** Which SEAT we occupy, in net mode (solo is always seat 0 — there is nobody
   *  else to take the other one). Set from the server's authoritative roster. */
  let netRiderSeat = 0;
  // ── SAR / ground-team trainer (M36, SAR-1..3): deployed ELT beacons + the local
  //    search objective. Beacons are always-loaded objective entities (E-32); the
  //    signal model + objective are pure (sim/education/sar.ts). ──
  const beacons = new BeaconField();
  const sarObjective = new SarObjective();
  /** ELT signal-strength bar count on the SAR HUD (a display choice). */
  const SAR_BARS = 5;
  /** Ground height at a column for the coarse LOS terrain shadow (ELV heightmap
   *  proxy): the Atlas relief surface, or sea level for an ungenerated column. */
  const sarGroundY = (x: number, z: number): number =>
    world.surfaceY(Math.floor(x), Math.floor(z)) ?? WORLD.SEA_LEVEL;
  // ── Classroom / guided-build (M37, CLS-1..3): the instructor's ghost blueprint
  //    course + a per-position edit log for per-student attribution. The compile,
  //    diff, and completion/unlock are pure (sim/education/classroom.ts). ──
  const classroom = new ClassroomBoard();
  const classroomLog = new EditLog();
  let classroomHeat = false;         // false = ghost (cyan) overlay, true = green/red heat
  let classroomDiffAcc = 0;          // ticks since the last periodic placed-vs-plan diff
  const worldRead: CellRead = (x, y, z) => world.getBlockState(x, y, z);
  let blockEntities = new BlockEntityStore();
  blockEntities.loadJSON(loadedBlockEntities);
  /** The crop stalk-colour overlay (HER-8, ARCH §28.10). */
  const cropOverlay = new CropOverlay(rc.scene);
  /** Rebuild the overlay from the block-entity store (solo — the store IS the
   *  world here) after a heredity-rule flip: off ⇒ empty, on ⇒ every genetic crop
   *  the store still holds (§28.2 — the rule stops READING traits, it never
   *  deletes them). In multiplayer the overlay instead tracks `CropTint` frames;
   *  flipping off still clears the local view immediately rather than waiting on
   *  the server, which sends nothing for a rule it did not change. */
  const refreshCropOverlay = (): void => {
    cropOverlay.clearAll();
    if (!heredityOn(manager.world('overworld'))) return;
    for (const be of blockEntities.values()) {
      if (be.type !== 'crop') continue;
      const crop = cropOf(registries.states.blockName[world.getBlockState(be.x, be.y, be.z)] ?? '');
      const genome = crop?.genes ? decodeGenome(be.data.genome, crop.genes) : null;
      if (genome) cropOverlay.set(be.x, be.y, be.z, expressCrop(crop!.genes!, genome).stalkQ);
    }
  };
  // ── Cartography (UI-8, M18): level-wide filled-map store + shared UI-8 colour
  //    palette. The sampler tracks the active dimension's world (swapped on
  //    travel), so a map only fills from the dimension it was made in. ──
  const mapStore = new MapStore();
  const mapPalette = buildStateMapColors(registries);
  const mapSampler: MapSampler = {
    columnTopState: (x, z) => world.columnTopState(x, z),
    surfaceY: (x, z) => world.surfaceY(x, z),
  };
  const dropRenderer = new ItemDropRenderer(rc.scene, icons);
  const signRenderer = new SignTextRenderer(rc.scene);
  const fallingRenderer = new FallingBlockRenderer(rc.scene, registries);
  const primedTntRenderer = new PrimedTntRenderer(rc.scene);
  const minecartRenderer = new MinecartRenderer(rc.scene);
  const boatRenderer = new BoatRenderer(rc.scene);
  const aircraftRenderer = new AircraftRenderer(rc.scene);
  const helicopterRenderer = new HelicopterRenderer(rc.scene);
  const vanRenderer = new VanRenderer(rc.scene);
  const beaconRenderer = new BeaconRenderer(rc.scene);
  const blueprintRenderer = new BlueprintRenderer(rc.scene); // M37 ghost blueprint overlay
  const aviationHud = new AviationHud();
  app.appendChild(aviationHud.el);
  const sarHud = new SarHud();
  app.appendChild(sarHud.el);
  const beckerDial = new BeckerDial();   // airborne DF (SAR-4)
  app.appendChild(beckerDial.el);
  const sarDfHud = new SarDfHud();        // handheld 9-LED DF (SAR-4)
  app.appendChild(sarDfHud.el);
  // Replay 3D re-render (REP-3): its own scene/camera/sky/mesh set that draws the
  // read-only replay world while the scrubber overlay is open. Shares the atlas
  // material + daylight uniform (same registry), but never touches the live scene.
  const replayView = new ReplayWorldView(rc.renderer, renderTable, rc.material, rc.waterMaterial, rc.daylight);
  const weatherFX = new WeatherFX(rc.scene);
  // Precipitation kind at the player (client-only, ENG-1): no rain in the nether
  // or deserts; snow high in the mountains, rain elsewhere (PLR-10).
  const precipKind = (): Precip => {
    if (world.dimension !== 'overworld' || !world.weather.raining) return 'none';
    const biome = world.biomeNameAt(Math.floor(player.body.x), Math.floor(player.body.z));
    if (biome === 'desert') return 'none';
    return biome === 'mountains' && player.body.y > 90 ? 'snow' : 'rain';
  };

  // Structure block entities (M6.3): each time a chunk is generated, register the
  // loot chests / spawners its structures place. Idempotent, and persisted/edited
  // block entities loaded above take precedence (so an opened chest keeps its
  // realised contents). Loot rolls lazily on open from a per-position seed.
  const structResolver: StateResolver = {
    statesOf: (n) => registries.states.statesOf.get(n),
    propsOf: (id) => registries.states.props[id] ?? {},
    defaultOf: (n) => registries.states.defaultOf.get(n),
  };
  // `world` is the ACTIVE world, so this env follows the player across dimensions —
  // which is what the nether structure set (fortress / bastion) needs.
  const structEnv: StructureEnv = {
    terrainHeight: (x, z) => world.terrainHeight(x, z),
    biomeName: (x, z) => world.biomeNameAt(x, z),
    get dimension() { return world.dimension; },
  };
  const lootSeedAt = (x: number, y: number, z: number): number => {
    let h = 2166136261 >>> 0;
    for (const c of `${String(world.seed)}:${x}:${y}:${z}`) h = Math.imul(h ^ c.charCodeAt(0), 16777619) >>> 0;
    return h;
  };
  let beSeenChunks = new Set<string>();
  // Structure block-entities (loot chests / spawners) for the overworld set and the
  // nether set (fortress / bastion). Re-installed on each active world so it tracks
  // the dimension swap; the per-dimension `beSeenChunks` guard keeps a chunk from
  // being seeded twice, and `structEnv.dimension` picks the right structure set.
  const installChunkReady = (w: World): void => {
    if (flat || atlasCity || earthWorld) return; // no vanilla structures in atlas/earth worlds
    w.chunkReady = (cx, cz): void => {
      const k = `${cx},${cz}`;
      if (beSeenChunks.has(k)) return;
      beSeenChunks.add(k);
      for (const a of structureAnchorsInChunk(w.seed, cx, cz, structEnv, structResolver)) {
        if (blockEntities.get(a.x, a.y, a.z)) continue; // loaded/edited BE wins
        if (a.type === 'chest') {
          const be = blockEntities.create('chest', a.x, a.y, a.z);
          if (be.type === 'chest') { be.data.loot = a.data; be.data.lootSeed = lootSeedAt(a.x, a.y, a.z); }
        } else if (a.type === 'spawner') {
          const be = blockEntities.create('spawner', a.x, a.y, a.z);
          if (be.type === 'spawner') be.data.mob = a.data;
        }
      }
    };
  };
  installChunkReady(world);
  // Catch up any chunks already loaded from saved edits (chunkReady was unset).
  if (!flat) for (const ch of world.loadedChunks()) world.chunkReady?.(ch.cx, ch.cz);
  const breakOverlay = new BreakOverlay(rc.scene);
  const mining: MiningState = { active: false, x: 0, y: 0, z: 0, progress: 0 };
  // Armour items resolve their slot by name (M17 gear engine); the inventory uses
  // this to accept a piece only into its matching column (helmet=0 … boots=3).
  const armorSlotOf = (item: number): ArmorSlot | null => gearArmorSlotOf(registries.items.nameOf[item]);
  // Smithing table (M59): the registry's `smithing` recipes, plus the name→id and
  // durability lookups the re-forge needs to carry an item's wear across tiers.
  const smithingRecipes: SmithingRecipe[] = registries.recipes.list
    .filter((r): r is Extract<typeof r, { type: 'smithing' }> => r.type === 'smithing')
    .map((r) => ({ name: r.name, template: r.template, base: r.base, addition: r.addition, result: r.result.item }));
  const smithingItems: SmithingItems = {
    idOf: registries.items.idOf,
    nameOf: registries.items.nameOf,
    durabilityOf: (name) => registries.items.byName.get(name)?.durability,
  };
  const playerState = new Player(maxStackOf, meta.gameMode, armorSlotOf);
  playerState.loadJSON(loadedPlayer?.survival);
  // §28.11: the field notebook's records — row-level like `survival`/`progress`,
  // not part of `Player`'s own (M4) save shape, so it travels alongside them.
  let currentNotebook: Notebook = loadedPlayer?.notebook ?? emptyNotebook();
  // §29.6/§29.13 (M51.7): the world's active lesson, resolved once at boot — the
  // definition never changes mid-session (a collection swap needs a reload, same
  // as any other registry change). No active lesson ⇒ every one of these stays
  // undefined and `fireEduTrigger`/`tickEduLesson` are no-ops (D21 parity).
  const eduControl: Game['eduControl'] = { frozen: false, eyesUp: null, watched: false };
  let lastExport = '';
  const lessonDef: LessonDef | undefined = meta.lesson ? lessonByName(registries.lessons.byName, meta.lesson.name) : undefined;
  const lessonObjectives = lessonDef ? new ObjectiveTracker(lessonDef.objectives) : undefined;
  let lessonResult: LessonResult | undefined = lessonDef
    ? (loadedPlayer?.lessons?.[lessonDef.name] ?? emptyLessonResult(lessonDef, world.tickCount))
    : undefined;
  let lessonRegionState = emptyRegionState();
  if (lessonObjectives && lessonResult) {
    // A resumed lesson replays its saved progress into the live tracker — the
    // tracker is the evaluator of record; the save is only ever a mirror of it.
    lessonObjectives.load({
      done: Object.entries(lessonResult.objectives).filter(([, tick]) => tick !== null).map(([name]) => name),
      counts: lessonResult.counts,
    });
  }
  // M54.2 (§29.3, CNT-5): a lesson's declared ELT beacons deploy fresh every time
  // its world is entered — beacons have no persistence of their own (D-TF-277),
  // so this is the lesson's own re-creation of them, not a load.
  if (lessonDef?.beacons) for (const [bx, by, bz] of lessonDef.beacons) beacons.deploy(bx, by, bz);
  /** Apply a trigger to the active lesson (a no-op with none, E-59) — toasts the
   *  binding copy on an objective's, then the whole lesson's, completion. */
  const fireEduTrigger = (t: Trigger): string[] => {
    const wasComplete = lessonResult?.completed !== undefined;
    const step = applyEduTrigger(t, lessonObjectives, lessonDef, lessonResult, world.tickCount);
    lessonResult = step.result;
    for (const title of step.newlyTitles) advToasts.note('', `Objective complete: ${title}`);
    if (!wasComplete && lessonResult?.completed !== undefined && lessonDef) {
      advToasts.note('', `Lesson complete: ${lessonDef.title}`);
    }
    return step.newlyTitles;
  };
  /** §29.4's region check + `ticksOnTask` — run every tick, cheap when idle
   *  (`tickLessonResult`/`regionCheck` both short-circuit with no active lesson
   *  or no declared regions). */
  const tickEduLesson = (): void => {
    if (!lessonDef || !lessonResult) return;
    lessonResult = tickLessonResult(lessonResult, true); // solo: this world IS the one the lesson is active in
    if (lessonDef.regions?.length) {
      const rc = regionCheck(lessonDef.regions, { x: player.body.x, y: player.body.y, z: player.body.z }, lessonRegionState, world.tickCount);
      lessonRegionState = rc.state;
      for (const trig of rc.fired) fireEduTrigger(trig);
    }
    // `biology/population` (M53.5, CNT-5): "reach 20 voles" / "voles below 10" /
    // "release 2 owls" all read as the SAME fact — a live species count crossing a
    // bound — on the same cadence `regionCheck` self-throttles to (EDU.REGION_CHECK_TICKS).
    // Cheap and unconditional: with no `population_count` criterion in the active
    // lesson this simply never matches anything (E-59), same as any other trigger.
    if (world.tickCount % EDU.REGION_CHECK_TICKS === 0) {
      for (const species of ['meadow_vole', 'barn_owl']) {
        let n = 0;
        for (const e of entities.entities) if (!e.removed && e.type === species) n++;
        fireEduTrigger({ kind: 'population_count', species, value: n });
      }
    }
  };
  // World-door arrival (DOR-2): a traversal carries the player's INVENTORY (main + armor
  // + offhand + selected slot) into the target world, overriding that world's own saved
  // inventory. Vitals / game mode / spawn / position stay per-world (DOR-2) — only the
  // stuff in your pack comes with you.
  if (enter?.carry) {
    playerState.inventory.loadJSON(enter.carry.inventory);
    playerState.inventory.selected = enter.carry.selected ?? 0;
  }
  // No default items in any game mode — creative gives free palette access
  // instead of a pre-filled hotbar; survival players dig/craft everything.

  // Atlas/Earth worlds spawn at the city centre (0,0); others near the origin chunk.
  const sx0 = atlasCity || earthWorld ? 0.5 : 8.5, sz0 = atlasCity || earthWorld ? 0.5 : 8.5;
  const spawnY = flat ? World.FLAT_SURFACE + 3 : world.terrainHeight(Math.floor(sx0), Math.floor(sz0)) + 3;
  const player = {
    body: makeBody(loadedPlayer?.x ?? sx0, loadedPlayer?.y ?? spawnY, loadedPlayer?.z ?? sz0),
    yaw: loadedPlayer?.yaw ?? 0,
    pitch: loadedPlayer?.pitch ?? 0,
  };
  // Multiplayer (NET-2): the server dictates the spawn; the replica world is seeded
  // identically, so pristine terrain is already generated locally around it.
  if (net) {
    player.body.x = net.welcome.spawn.x;
    player.body.y = net.welcome.spawn.y;
    player.body.z = net.welcome.spawn.z;
    player.yaw = net.welcome.spawn.yaw;
    player.pitch = net.welcome.spawn.pitch;
  }
  if (!playerState.spawn) playerState.spawn = { x: player.body.x, y: player.body.y, z: player.body.z };

  chunkMeshes.primeAround(player.body.x, player.body.z);

  let prevX = player.body.x, prevY = player.body.y, prevZ = player.body.z;
  // Smoothed ground-plane heading (blocks/tick) driving speed-aware streaming: the
  // prefetch focus leads this vector so a fast flight loads the terrain ahead first.
  let streamVx = 0, streamVz = 0;
  // Current mesh LOD depth (0 = full detail); raised while flying high & fast.
  let viewLod = 0;
  // Adaptive render-distance cap (≤ the user's setting), eased when fps sags.
  let effectiveRenderDist = settings.renderDistance;
  let lastCapAdjust = performance.now();
  let wasOnGround = player.body.onGround;
  // Status-effect screen wash + a swatch row (MB.4). A fixed full-screen overlay
  // tinted by the active effects; night vision floors the brightness separately.
  let effectOverlayEl: HTMLDivElement | null = null;
  let effectSwatchEl: HTMLDivElement | null = null;
  const applyEffectOverlay = (tint: { r: number; g: number; b: number; a: number }): void => {
    if (!effectOverlayEl) {
      effectOverlayEl = document.createElement('div');
      effectOverlayEl.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:5;transition:background 0.2s;';
      effectSwatchEl = document.createElement('div');
      effectSwatchEl.style.cssText = 'position:fixed;top:8px;right:8px;display:flex;gap:4px;pointer-events:none;z-index:6;';
      document.body.append(effectOverlayEl, effectSwatchEl);
    }
    effectOverlayEl.style.background = tint.a > 0 ? `rgba(${tint.r},${tint.g},${tint.b},${tint.a.toFixed(3)})` : 'transparent';
    const hud = effectHud(playerState.effects);
    if (effectSwatchEl && effectSwatchEl.childElementCount !== hud.length) {
      effectSwatchEl.textContent = '';
      for (const e of hud) {
        const sw = document.createElement('div');
        sw.style.cssText = `width:14px;height:14px;border:1px solid #0008;background:rgb(${e.color[0]},${e.color[1]},${e.color[2]});`;
        sw.title = e.id;
        effectSwatchEl.append(sw);
      }
    }
  };
  // Powder-snow freeze counter (MB.1): climbs while submerged without leather
  // boots, thaws otherwise; drives freeze damage past the onset.
  let frozenTicks = 0;
  const hasLeatherBoots = (): boolean => {
    const st = playerState.inventory.get(INV.ARMOR_START + 3);
    return !isEmpty(st) && registries.items.nameOf[st.item] === 'leather_boots';
  };
  let breakElapsed = 0, breakNeeded = 0;
  // World-door post-arrival cooldown (DOR-2, E-36): a fresh traversal grants brief
  // immunity so the arrival door isn't immediately re-triggered. Wall-clock ms.
  let doorCooldownUntil = 0;
  // Elytra glide (M21): whether the player is gliding, the jump edge that starts
  // it (a jump press while airborne), and remaining firework-boost ticks.
  let gliding = false;
  let prevJump = false;
  let fireworkBoostTicks = 0;
  let statDistAccum = 0; // sub-centimetre carry for the Distance Walked stat (M24)
  // Portal travel (DIM-2): ticks the player has stood in a portal; immune after a
  // fresh arrival until they step out (so they don't bounce straight back).
  let portalTicks = 0, portalImmune = false;
  const netherPortalStates = new Set(
    (registries.states.statesOf.get('nether_portal') ?? []) as readonly number[],
  );
  // End portals (DIM-3) travel to/from the End. Unlike a nether portal there is no
  // coordinate mapping and no wait: you step in, you are gone (as in Minecraft).
  const endPortalStates = new Set(
    (registries.states.statesOf.get('end_portal') ?? []) as readonly number[],
  );
  // End gateways (DIM-3): the ring a slain dragon lights over the arena. Unlike an
  // end portal these do not change dimension at all — they throw you across the
  // void gap WITHIN the End, and the one they raise over your landing brings you
  // back, so the outer islands are reachable without flying the whole 640 blocks.
  const endGatewayStates = new Set(
    (registries.states.statesOf.get('end_gateway') ?? []) as readonly number[],
  );
  const boxesAt = (x: number, y: number, z: number): readonly Box[] => world.collisionBoxesAt(x, y, z);

  // Underwater tint overlay (M2.2): shown when the camera eye is in water.
  const waterTint = document.createElement('div');
  waterTint.className = 'bb-water-tint';
  waterTint.style.cssText = 'position:fixed;inset:0;background:rgba(40,96,180,0.34);pointer-events:none;z-index:30;display:none;';
  document.body.append(waterTint);

  // Sleep transition: a brief black fade when the night is skipped (PLR-10).
  const sleepFlash = document.createElement('div');
  sleepFlash.style.cssText = 'position:fixed;inset:0;background:#000;pointer-events:none;z-index:60;opacity:0;transition:opacity .35s;';
  document.body.append(sleepFlash);
  const showSleepFlash = (): void => {
    sleepFlash.style.opacity = '1';
    setTimeout(() => { sleepFlash.style.opacity = '0'; }, 400);
  };

  // Lightning (PLR-10): a white flash + a nearby strike that hurts/ignites the
  // player. Client-only rng (kept off the sim stream so it never desyncs NET).
  const lightningFlash = document.createElement('div');
  lightningFlash.style.cssText = 'position:fixed;inset:0;background:#eaf0ff;pointer-events:none;z-index:55;opacity:0;transition:opacity .12s;';
  document.body.append(lightningFlash);
  const strikeRng = new Rng(`${String(meta.seed)}#lightning`).fork(defineSalt('lightning'));
  const doLightning = (): void => {
    lightningFlash.style.opacity = '0.75';
    setTimeout(() => { lightningFlash.style.opacity = '0'; }, 120);
    const angle = (strikeRng.nextInt(360) * Math.PI) / 180;
    const dist = strikeRng.nextInt(70);
    const sx = player.body.x + Math.cos(angle) * dist, sz = player.body.z + Math.sin(angle) * dist;
    // A bolt charges any creeper it lands near (MOB-2 charged creeper, M13.2).
    for (const e of entities.entities) {
      if (e.type === 'creeper' && Math.hypot(e.x - sx, e.z - sz) <= CHARGED_CREEPER.CHARGE_RADIUS) e.variant = 1;
    }
    if (Math.hypot(player.body.x - sx, player.body.z - sz) <= 3 && modeCaps(playerState.gameMode).survivalHunger) {
      playerState.applyDamage(mitigate(WEATHER.LIGHTNING_DAMAGE));
      playerState.fireTicks = Math.max(playerState.fireTicks, WEATHER.LIGHTNING_FIRE_TICKS);
      if (playerState.dead) onDeath();
    }
  };

  const remeshChunks = (dirty: Iterable<string>): void => {
    for (const k of dirty) {
      const [cx, cz] = k.split(',').map(Number) as [number, number];
      chunkMeshes.markDirty(cx * WORLD.SECTION + 8, cz * WORLD.SECTION + 8);
    }
  };

  // ── Multiplayer (NET-2/3, M26.2/M27) ────────────────────────────────────────
  const remoteRenderer = net ? new RemotePlayerRenderer(rc.scene) : null;
  // ── The shared screen (M57, ARCH §30.6, COW-9) — client-only chrome; the sim
  //    never learns a share exists (D-TF-180), the server only authorizes and
  //    relays. Multiplayer-only: there is no server to authorize a share solo. ──
  const shareController = net ? new ShareController(net, () => net.welcome.playerId) : null;
  const screenQuad = new ScreenQuad(rc.scene);
  /** The one cell this client is sharing (its own share), or null. */
  let sharingAt: { x: number; y: number; z: number } | null = null;
  /** The one cell this client is watching (someone else's share), or null. */
  let watchingAt: { x: number; y: number; z: number; sharer: number } | null = null;
  /** Set the instant `net.sendShareJoin` fires, cleared once the matching
   *  `ShareState` reply arrives (or the share ends first) — the trigger that
   *  distinguishes OUR OWN join reply (which starts the peer connection) from
   *  every other client's broadcast confirmation of the same live state. */
  let pendingWatchAt: { x: number; y: number; z: number } | null = null;
  /** The latest known `ShareState` per screen cell (LIVE badge / `screenState`
   *  debug reads) — chrome-side cache, never sim state. */
  const shareStates = new Map<string, { sharer: number; live: boolean }>();
  const sameCell = (a: { x: number; y: number; z: number }, b: { x: number; y: number; z: number }): boolean =>
    a.x === b.x && a.y === b.y && a.z === b.z;
  if (net && shareController) {
    net.onShareState = (s) => {
      const key = cellKey(s.x, s.y, s.z);
      shareStates.set(key, { sharer: s.sharer, live: s.live });
      if (!s.live) {
        if (sharingAt && sameCell(sharingAt, s)) { sharingAt = null; shareController.stopSharing(); screenQuad.dispose(); }
        if (watchingAt && sameCell(watchingAt, s)) { watchingAt = null; shareController.unwatch(); screenQuad.dispose(); }
        return;
      }
      if (pendingWatchAt && sameCell(pendingWatchAt, s)) {
        pendingWatchAt = null;
        watchingAt = { x: s.x, y: s.y, z: s.z, sharer: s.sharer };
        shareController.watch(s.sharer, s.ice);
      }
    };
    shareController.onRemoteStream = () => {
      if (watchingAt && shareController.remoteStream) {
        screenQuad.show(watchingAt.x, watchingAt.y, watchingAt.z, screenNormalAt(watchingAt.x, watchingAt.y, watchingAt.z), shareController.remoteStream);
      }
    };
    // E-64: OUR OWN watching connection failing (real ICE failure, or the
    // `forceWatchFail` test seam) is told to us alone — the share continues
    // for every other viewer, who never hears about it.
    shareController.onViewerStateChanged = () => {
      if (watchingAt && shareController.watchState() === 'failed') {
        game.showActionBar('Could not connect to the shared screen.');
        watchingAt = null;
        screenQuad.dispose();
      }
    };
  }
  /** The block face a `shared_screen` at (x,y,z) presents to open air — the
   *  quad's normal (§30.6). Picks the first cardinal neighbor that is air;
   *  falls back to +Z (arbitrary but deterministic) if fully embedded. */
  const screenNormalAt = (x: number, y: number, z: number): { x: number; y: number; z: number } => {
    const dirs = [{ x: 1, y: 0, z: 0 }, { x: -1, y: 0, z: 0 }, { x: 0, y: 0, z: 1 }, { x: 0, y: 0, z: -1 }];
    for (const d of dirs) if (world.getBlockState(x + d.x, y + d.y, z + d.z) === STATE_AIR) return d;
    return { x: 0, y: 0, z: 1 };
  };
  /** Begin sharing `stream` at (x,y,z) — the one path both a real `Share`
   *  click and `voxel.cow.fakeShare` (a canvas `captureStream`, test-only)
   *  funnel through. Shows the local self-view optimistically; the server's
   *  own `ShareState` echo is the actual authorization (a refusal just never
   *  arrives and the quad quietly never gets confirmed by a peer). */
  const startSharingAt = (x: number, y: number, z: number, stream: MediaStream): void => {
    if (!net || !shareController) return;
    sharingAt = { x, y, z };
    shareController.share(stream);
    screenQuad.show(x, y, z, screenNormalAt(x, y, z), stream);
    net.sendShareStart(x, y, z);
  };
  // >0 while inside a local player block action (mine/place) — every `edit()` made
  // then is relayed to the server (NET-3 prediction); sim-driven edits (fluids,
  // redstone, growth) stay local until they become server-authoritative in M28.
  let netEditDepth = 0;
  /** Overlay server-pushed edited sections + apply server-arbitrated block patches
   *  onto the replica, then remesh (NET-2/3). A patch to a cell we predicted wrong
   *  overwrites the misprediction — that is the rollback. */
  const applyNetWorld = (): void => {
    if (!net) return;
    const touched = new Set<string>();
    for (const s of net.drainSections()) {
      world.overlaySection(s.cx, s.cz, s.sy, s.states);
      touched.add(`${s.cx},${s.cz}`);
    }
    for (const k of touched) {
      const [cx, cz] = k.split(',').map(Number) as [number, number];
      world.recomputeChunkHeightmaps(cx, cz);
      world.relightChunk(cx, cz);
    }
    remeshChunks(touched);
    // Patches are single-cell server edits — apply through the normal edit() path
    // (depth 0, so applying them never re-sends), which relights + remeshes.
    for (const p of net.drainPatches()) {
      const base = (p.sy - WORLD.SECTION_Y_OFFSET) * WORLD.SECTION;
      for (const op of p.ops) {
        const lx = op.li & 15, lz = (op.li >> 4) & 15, ly = (op.li >> 8) & 15;
        edit(p.cx * WORLD.SECTION + lx, base + ly, p.cz * WORLD.SECTION + lz, op.state);
      }
    }
  };
  /** Run a local player block action so its edits relay to the server (NET-3). */
  const playerEdit = <T>(fn: () => T): T => {
    netEditDepth++;
    try { return fn(); } finally { netEditDepth--; }
  };
  // Server-owned mobs (M28.1): in net mode the client runs NO mob AI/spawns — the
  // server simulates every entity and streams its pose. We mirror those poses into
  // the local `entities` system purely as a render shadow (never ticked), so the
  // existing EntityRenderer draws server mobs with the same rigs solo uses. Both
  // clients therefore see the identical server-authoritative entity (NET-2).
  const netEntityById = new Map<number, Entity>();
  // §28.9: a mob named after it was first seen gets the name onto its render shadow.
  if (net) net.onEntityName = (id, name) => { const e = netEntityById.get(id); if (e) e.name = name; };
  // §28.11: the server's answer to `Record` (an explicit ask) or an auto-record
  // push (a birth/death the server saw for this player's already-known lineage)
  // — both land here, since a client cannot tell them apart on arrival and
  // showing "Recorded: …" either way is accurate either way.
  if (net) {
    net.onNotebookUpdate = (update) => {
      for (const a of update.animals ?? []) applyAnimalRecord(a);
      for (const c of update.crops ?? []) applyCropRecord(c.x, c.y, c.z, c.crop, c.genome);
    };
  }
  // §28.10/D-TF-251: paint (or clear, q=5) whatever the server tells us — its own
  // `Plant` claims and its section catch-up on subscribe. Block entities never
  // replicate, so this is the ONLY way a remote genetic crop reaches this client.
  if (net) {
    net.onCropTint = (tint) => {
      const base = (tint.sy - WORLD.SECTION_Y_OFFSET) * WORLD.SECTION;
      for (const cell of tint.cells) {
        const lx = cell.li & 15, lz = (cell.li >> 4) & 15, ly = (cell.li >> 8) & 15;
        const x = tint.cx * WORLD.SECTION + lx, y = base + ly, z = tint.cz * WORLD.SECTION + lz;
        if (cell.q === 5) cropOverlay.clear(x, y, z); else cropOverlay.set(x, y, z, cell.q);
      }
    };
    // A cell's text payload arrived (§30.2, M55.3): `sign` feeds the existing
    // client-side map; the other three kinds are a real block entity's data.
    net.onBlockText = (x, y, z, kind, payload) => {
      if (kind === 'sign') { setSignText(x, y, z, JSON.parse(payload) as string[]); return; }
      const be = blockEntities.create(kind, x, y, z);
      Object.assign(be.data, JSON.parse(payload));
    };
  }
  const syncNetEntities = (): void => {
    if (!net) return;
    const seen = new Set<number>();
    for (const r of net.remoteEntities()) {
      seen.add(r.id);
      let e = netEntityById.get(r.id);
      if (!e) {
        e = entities.spawn(r.type, r.x, r.y, r.z, { variant: r.variant, baby: r.baby }) ?? undefined;
        if (!e) continue;
        e.data.netId = r.id; // map the shadow back to the server entity for attacks
        netEntityById.set(r.id, e);
        // §28.12/§28.6: the server sends a genome only under ITS rule, so a shadow that
        // receives one expresses it (tint, parts, scale) whatever this client's own rule
        // says — the same `express` a solo world runs. A table this client lacks leaves
        // it IDENTITY and untinted (§28.13's collection-mismatch row).
        const genes = registries.mobs.byName.get(r.type)?.genes;
        const genome = r.genome !== undefined && genes ? decodeGenome(r.genome, genes) : null;
        if (genome) { e.genome = genome; e.traits = express(genes, genome, true); }
        if (r.name !== undefined) e.name = r.name;
      }
      // Velocity delta drives the renderer's walk cycle; then snap to the server pose.
      e.vx = r.x - e.x; e.vz = r.z - e.z;
      e.x = r.x; e.y = r.y; e.z = r.z; e.yaw = r.yaw;
    }
    for (const [id, e] of netEntityById) {
      if (seen.has(id)) continue;
      e.removed = true;
      netEntityById.delete(id);
    }
    for (let i = entities.entities.length - 1; i >= 0; i--) if (entities.entities[i]!.removed) entities.entities.splice(i, 1);
  };
  // Prediction ring (NET-3): our predicted body pos after each sent input seq, so a
  // server Correction is compared like-for-like (the latency offset cancels) and
  // only genuine divergence is corrected — gently (capped per tick) so a steady walk
  // never snaps; a large desync (teleport/portal) hard-snaps to authority.
  const NET_RECON = { HARD: 4, GAIN: 0.35, CAP: 0.15 };
  const predictRing = new Map<number, { x: number; y: number; z: number }>();
  const reconcile = (b: Body): void => {
    if (!net) return;
    const c = net.takeCorrection();
    if (!c) return;
    const pred = predictRing.get(c.seq);
    for (const k of [...predictRing.keys()]) { if (k <= c.seq) predictRing.delete(k); }
    if (!pred) return;
    const ex = c.x - pred.x, ey = c.y - pred.y, ez = c.z - pred.z;
    const err = Math.hypot(ex, ey, ez);
    if (err < 0.001) return;
    if (err > NET_RECON.HARD) { // genuine desync (teleport/portal) — snap to authority
      b.x = c.x; b.y = c.y; b.z = c.z; b.vx = c.vx; b.vy = c.vy; b.vz = c.vz;
      net.maxCorrection = Math.max(net.maxCorrection, err);
      return;
    }
    const scale = Math.min(NET_RECON.GAIN, NET_RECON.CAP / err);
    b.x += ex * scale; b.y += ey * scale; b.z += ez * scale;
    net.maxCorrection = Math.max(net.maxCorrection, err * scale);
  };
  /** Pack the current input + mouse state into the wire button bitfield (NET-1). */
  const netButtons = (): number =>
    (game.input.forward ? Btn.Forward : 0) | (game.input.back ? Btn.Back : 0) |
    (game.input.left ? Btn.Left : 0) | (game.input.right ? Btn.Right : 0) |
    (game.input.jump ? Btn.Jump : 0) | (game.input.sneak ? Btn.Sneak : 0) |
    (game.input.sprint ? Btn.Sprint : 0) | (game.breaking ? Btn.Attack : 0) | (game.usingItem ? Btn.Use : 0);
  // A live index of placed TNT block cells (RED-8, M15.2.2). Maintained by `edit`
  // (every block change routes through it) so redstone ignition never scans the
  // world — it only revisits the handful of cells that hold TNT.
  const tntCells = new Set<string>();
  // Piston + observer cell indexes (RED-3, M16.1): maintained here so the per-
  // redstone-tick scans revisit only the cells that actually hold one.
  const pistonCells = new Set<string>();
  const observerCells = new Set<string>();
  const observerBaseline = new Map<string, number>(); // observer cell -> last-seen watched state
  const observerPulse = new Map<string, number>(); // observer cell -> redstone ticks left in its pulse
  // Beacon blocks (M20.1): the placed beacons + each one's chosen primary/secondary
  //  effect. The tier is recomputed from the pyramid below every APPLY_INTERVAL.
  const beaconBlockCells = new Set<string>();
  const beaconSel = new Map<string, { primary: string; secondary: string | null }>();
  // Conduit blocks (M18.1): placed conduits; the prismarine frame around each is
  //  recounted every APPLY_INTERVAL to project Conduit Power + zap a hostile.
  const conduitCells = new Set<string>();
  // End crystals (M21.1): while any stands near the ender dragon it is un-killable.
  const endCrystalCells = new Set<string>();
  // Sculk sensors (M22.1): placed sensors, and each active one's remaining powered
  //  ticks. A vibration in range wakes the sensor → it emits redstone power.
  const sculkSensorCells = new Set<string>();
  const sculkActive = new Map<string, number>();
  // Trial chambers (M22.1): each spawner's wave state; each vault's per-player unlocks.
  const trialSpawnerCells = new Set<string>();
  const trialData = new Map<string, { state: TrialState; spawned: number; mobs: number[]; cooldown: number }>();
  const vaultUnlockedBy = new Map<string, number[]>();
  // Note-block indexes (MB.1): cells holding a note block, their note value 0..24,
  // and the rising-edge power latch. See notePlay/tickNoteBlocks below.
  const noteBlockCells = new Set<string>();
  const noteBlocks = new Map<string, number>();
  const notePowered = new Set<string>();
  let lastNotePlayed: NotePlayback | null = null;
  // ── Audio engine (AUD-1, M23): the WebAudio host for every synthesized sound.
  //    The sim/synth stay pure; this plays their patches positionally + logs each
  //    event (subtitles + E2E read the log). `soundGroupOf` maps a block to its
  //    material voice; `playBlockSound` voices a dig/place/step gesture in-world.
  const audio = new AudioEngine();
  // Subtitles overlay (M23 accessibility): off unless the option enables it.
  const subtitles = new Subtitles();
  const applyAudioSettings = (): void => {
    audio.setMasterVolume(settings.masterVolume);
    audio.setSoundVolume(settings.soundVolume);
    audio.setMusicVolume(settings.musicVolume);
    subtitles.setEnabled(settings.subtitles);
  };
  // ── Advancements (M24): a pure tracker grants nodes as triggers fire off the
  //    game event flow; grants pop a toast (L opens the tree). Icons resolve through
  //    the shared ItemIcons cache by item name.
  const advancements = new AdvancementTracker();
  const advIcon = (item: string): string => {
    const id = registries.items.idOf.get(item);
    return id === undefined ? '' : icons.get(id);
  };
  const advToasts = new AdvancementToasts(advIcon);
  const fireAdvancement = (t: Trigger): void => {
    for (const id of advancements.fire(t)) advToasts.show(id);
  };
  const openAdvancementsScreen = (): void => {
    if (game.uiOpen) return;
    game.uiOpen = true;
    document.exitPointerLock();
    showAdvancements(advancements, advIcon, () => {
      game.uiOpen = false;
      void game.renderer.domElement.requestPointerLock();
    });
  };
  // ── Statistics (M24): a pure tracker counts blocks mined / items used / picked up
  //    / mobs killed + general totals (time played, mob kills, deaths) off the same
  //    event flow as advancements. Opened from the pause menu (returns to it) or via
  //    the openStats seam (does the uiOpen/pointer-lock dance like the tree screen).
  const stats = new StatsTracker();
  // Recipe book (M24): unlocks a crafting recipe when the player obtains one of
  // its ingredients (MC's default); the crafting table reads game.recipeBook.
  const recipeBook = new RecipeBook();
  const itemHasTag = (item: string, tag: string): boolean =>
    registries.items.byName.get(item)?.tags.includes(tag) ?? false;
  const unlockRecipesFor = (name: string): void => {
    recipeBook.unlockByObtaining(name, registries.recipes.list, itemHasTag);
  };
  // ── Restore saved progression (M25 ENG-11) ── statistics, advancements, and the
  //    recipe book round-trip through the save's `progress` bundle. Absent on any
  //    pre-M25 world → the migration harness supplied an empty bundle, so the
  //    trackers just stay empty. `load` populates without firing, so nothing re-toasts.
  {
    const p = loadedPlayer?.progress;
    stats.load(p?.stats);
    advancements.load(p?.advancements);
    recipeBook.load(p?.recipeBook);
  }
  const openStatsScreen = (onBack?: () => void): void => {
    if (onBack) {
      showStats(stats, advIcon, onBack); // from the pause menu — return to it, leave lock alone
      return;
    }
    if (game.uiOpen) return;
    game.uiOpen = true;
    document.exitPointerLock();
    showStats(stats, advIcon, () => {
      game.uiOpen = false;
      void game.renderer.domElement.requestPointerLock();
    });
  };
  // ── Screenshot (M24, F2): render one fresh frame (the renderer has no
  //    preserveDrawingBuffer, so the buffer must be read in the same task it was
  //    drawn), capture it as a PNG data URL, download it, and flash the name. ──
  let screenshotsTaken = 0;
  const takeScreenshot = (): string => {
    game.renderer.render(game.scene, game.camera);
    const url = game.renderer.domElement.toDataURL('image/png');
    const name = screenshotFilename(new Date());
    // Hangar §39.5 (M105.2): bridged, F2 writes to /Apps/terraforge/Screenshots/ and posts the
    // notification instead of downloading — "when unbridged, keeps TerraForge's current
    // download behaviour" is exactly the branch below.
    if (game.hangar?.bridged()) {
      const hangar = game.hangar;
      void fetch(url)
        .then((r) => r.arrayBuffer())
        .then(async (buf) => (await import('./hangar/files')).saveScreenshotBridged(hangar, new Uint8Array(buf)));
    } else {
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
    }
    screenshotsTaken++;
    const msg = document.createElement('div');
    msg.className = 'bb-screenshot-msg';
    msg.textContent = `Saved screenshot as ${name}`;
    app.append(msg);
    setTimeout(() => msg.remove(), 2500);
    return url;
  };

  // ── M34 Texture Forge (FRG-2): the current forged skin + a re-skin seam. Applying
  //    repaints the one reserved atlas tile in place, re-uploads the texture, drops
  //    the forged_block's cached icon so it re-renders, and hands the player one.
  //    Determinism/parity are untouched — only pixels change, never a block state. ──
  let forgeParams: ForgeParams = DEFAULT_FORGE_PARAMS;
  const forgedBlockItemId = registries.items.idOf.get('forged_block');
  const applyForge = (params: ForgeParams): void => {
    forgeParams = clampForgeParams(params);
    blitForgeTile(rc.atlasCanvas, FORGE.SKIN, forgeParams);
    if (rc.material.map) rc.material.map.needsUpdate = true;
    if (rc.waterMaterial.map) rc.waterMaterial.map.needsUpdate = true;
    if (forgedBlockItemId !== undefined) {
      icons.invalidate(forgedBlockItemId);
      game.give('forged_block', 1);
    }
  };
  // Pristine copy of the atlas, taken before any re-skin, so "reset texture"
  // can restore a block's original tiles without regenerating the whole sheet.
  const pristineAtlas = document.createElement('canvas');
  pristineAtlas.width = rc.atlasCanvas.width;
  pristineAtlas.height = rc.atlasCanvas.height;
  pristineAtlas.getContext('2d')!.drawImage(rc.atlasCanvas, 0, 0);

  /** The atlas tile each of a block's 6 faces draws with (−1 where unused).
   *  Faces often SHARE a tile (stone uses one for all six), which is why the
   *  editor groups faces by tile rather than pretending they are independent. */
  const faceTilesOf = (block: string): number[] => {
    const state = registries.states.defaultOf.get(block);
    if (state === undefined) return [];
    return Array.from({ length: 6 }, (_, f) => renderTable.tiles[state * 6 + f] ?? -1);
  };

  const reskinBlock = (block: string, texture: BlockTexture | null): void => {
    const faces = faceTilesOf(block);
    if (faces.length === 0) return;
    const ctx = rc.atlasCanvas.getContext('2d')!;
    const done = new Set<number>();
    for (let f = 0; f < 6; f++) {
      const tile = faces[f]!;
      if (tile < 0 || done.has(tile)) continue; // a shared tile is painted once
      done.add(tile);
      const icon = texture ? faceIcon(texture, f) : null;
      const x = tile * TILE_PX;
      if (icon) {
        const img = ctx.createImageData(TILE_PX, TILE_PX);
        img.data.set(iconToRgba(icon));
        ctx.putImageData(img, x, 0);
      } else {
        ctx.clearRect(x, 0, TILE_PX, TILE_PX);
        ctx.drawImage(pristineAtlas, x, 0, TILE_PX, TILE_PX, x, 0, TILE_PX, TILE_PX);
      }
    }
    if (rc.material.map) rc.material.map.needsUpdate = true;
    if (rc.waterMaterial.map) rc.waterMaterial.map.needsUpdate = true;
    // The block's item icon samples the atlas, so drop its cache too.
    const itemId = registries.items.idOf.get(block);
    if (itemId !== undefined) icons.invalidate(itemId);
  };

  const reskinItem = (item: string, icon: IconData | null): void => {
    const id = registries.items.idOf.get(item);
    if (id !== undefined) icons.setOverride(id, icon);
  };

  /** Swap a mob's rig description and drop the pooled rigs so they rebuild.
   *  `registries` is only shallow-frozen, so the def itself is writable — and
   *  every renderer reads the rig from the def, so one mutation covers them. */
  const reskinMob = (mob: string, rig: MobDef['rig'] | null): void => {
    const def = registries.mobs.byName.get(mob);
    if (!def || !rig) return;
    (def as { rig: MobDef['rig'] }).rig = rig;
    entityRenderer.clear(); // pooled rigs rebuild from the new def on next sync
  };

  // Apply any authored block skins the registry already carries (an overlay from
  // a previous session, or a collection version this server selected).
  for (const [name, def] of registries.blocks.byName) {
    if (def.texture) reskinBlock(name, def.texture);
  }

  /** Read the reserved forge tile's RGBA pixels off the atlas (E2E golden proxy). */
  const forgeTilePixels = (): number[] => {
    const ctx = rc.atlasCanvas.getContext('2d')!;
    return Array.from(ctx.getImageData(FORGE.SKIN * TILE_PX, 0, TILE_PX, TILE_PX).data);
  };

  // ── F3 debug (M24): a DOM text overlay plus two scene line-overlays for chunk
  //    borders (F3+G) and entity hitboxes (F3+B). The text is a pure function of a
  //    per-frame snapshot; the lines are rebuilt from world/entity state. ──
  const debugEl = document.createElement('pre');
  debugEl.className = 'bb-debug-overlay';
  debugEl.style.display = 'none';
  app.append(debugEl);
  let debugOverlayOn = false;
  const chunkBorderLines = new THREE.LineSegments(new THREE.BufferGeometry(), new THREE.LineBasicMaterial({ color: 0xffe14d }));
  const hitboxLines = new THREE.LineSegments(new THREE.BufferGeometry(), new THREE.LineBasicMaterial({ color: 0xffffff }));
  for (const l of [chunkBorderLines, hitboxLines]) { l.visible = false; l.frustumCulled = false; rc.scene.add(l); }
  const setEdges = (lines: THREE.LineSegments, pts: number[]): void => {
    lines.geometry.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
  };
  const updateChunkBorders = (): void => {
    const b = player.body;
    const cx = Math.floor(b.x / WORLD.SECTION) * WORLD.SECTION;
    const cz = Math.floor(b.z / WORLD.SECTION) * WORLD.SECTION;
    const y0 = Math.floor(b.y) - 48, y1 = Math.floor(b.y) + 48;
    const pts: number[] = [];
    boxEdges(cx, y0, cz, cx + WORLD.SECTION, y1, cz + WORLD.SECTION, pts); // the player's chunk
    for (let gx = 0; gx <= WORLD.SECTION; gx += WORLD.SECTION) // its two Z-running cell seams
      pts.push(cx + gx, y0, cz, cx + gx, y1, cz, cx + gx, y0, cz + WORLD.SECTION, cx + gx, y1, cz + WORLD.SECTION);
    setEdges(chunkBorderLines, pts);
  };
  const updateHitboxes = (): void => {
    const pts: number[] = [];
    for (const e of entities.entities) {
      if (e.removed) continue;
      const def = registries.mobs.byName.get(e.type);
      if (!def) continue;
      const s = e.baby ? 0.5 : 1;
      const hw = (def.width * s) / 2, hh = def.height * s;
      boxEdges(e.x - hw, e.y, e.z - hw, e.x + hw, e.y + hh, e.z + hw, pts);
    }
    setEdges(hitboxLines, pts);
  };

  const soundGroupOf = (name: string | null | undefined): string =>
    (name ? registries.blocks.byName.get(name)?.sounds : undefined) ?? 'stone';
  const playBlockSound = (gesture: string, name: string | null | undefined, x: number, y: number, z: number): void => {
    audio.playSound(`block.${soundGroupOf(name)}.${gesture}`, { x: x + 0.5, y: y + 0.5, z: z + 0.5 });
  };
  // ── Jukebox (AUD-4): cellKey → the disc item currently spinning. A comparator
  //    behind a jukebox reads the disc's fixed index; breaking it drops the disc.
  const jukeboxDiscs = new Map<string, string>();
  const insertDisc = (x: number, y: number, z: number, disc: string): boolean => {
    if (!discById(disc)) return false;
    const k = cellKey(x, y, z);
    if (jukeboxDiscs.has(k)) return false; // already loaded
    jukeboxDiscs.set(k, disc);
    audio.playMusic(`music_disc.${disc}`, composeDisc(disc), { x: x + 0.5, y: y + 0.5, z: z + 0.5 });
    redstone.markDirty(x, y, z); // comparators re-read the new signal
    return true;
  };
  const ejectDisc = (x: number, y: number, z: number): string | null => {
    const k = cellKey(x, y, z);
    const disc = jukeboxDiscs.get(k);
    if (!disc) return null;
    jukeboxDiscs.delete(k);
    audio.stopMusic();
    redstone.markDirty(x, y, z);
    return disc;
  };
  // ── Music context (AUD-4): the ambient composer switches per dimension/time/mode.
  let musicContext: MusicContext | null = null;
  const updateMusicContext = (): void => {
    const ctx = contextFor(world.dimension, world.dayTime, playerState.gameMode);
    if (ctx === musicContext) return;
    musicContext = ctx;
    audio.playMusic(`music.${ctx}`, composeContext(ctx, world.dimension), {});
  };
  // `edit` runs long before the vibration bus is built, so it calls through a
  // late-bound reference rather than forcing the whole bus earlier.
  let emitVibrationRef: ((event: string, x: number, y: number, z: number, source: number) => void) | null = null;
  const edit = (x: number, y: number, z: number, state: number): void => {
    world.setBlockState(x, y, z, state);
    // Replay (REP-1): a player-initiated edit (netEditDepth>0 ⇒ inside playerEdit)
    // is the direct block change to record; world-sim edits (redstone/fluids/mobs)
    // are reproduced by the replay session's own runTick, so they are NOT recorded.
    if (netEditDepth > 0) game.replay?.noteEdit(x, y, z, state, state !== STATE_AIR);
    // Classroom attribution (M37, CLS-2): record who last touched this cell so the
    // placed-vs-plan diff can credit a correct/wrong block to a student (PLR-1/NET-6).
    if (netEditDepth > 0) classroomLog.record(x, y, z, classroomStudent());
    // A player-initiated edit is a loud sculk vibration (M22.1): sensors hear it and
    // wardens blame the player for it. World-sim edits (redstone/fluids) are silent —
    // only the direct player action makes a noise, as in Minecraft.
    if (netEditDepth > 0) {
      emitVibrationRef?.(state === STATE_AIR ? 'block_destroy' : 'block_place', x + 0.5, y + 0.5, z + 0.5, PLAYER_SOURCE);
    }
    // Keep the TNT index in step with the new block (add if TNT, else drop the cell).
    const ck = cellKey(x, y, z);
    const nm = registries.states.blockName[state];
    if (nm === 'tnt') tntCells.add(ck); else tntCells.delete(ck);
    if (nm === 'piston' || nm === 'sticky_piston') pistonCells.add(ck); else pistonCells.delete(ck);
    // A note block joins the redstone-triggered index; replacing it clears its note.
    if (nm === 'note_block') noteBlockCells.add(ck);
    else { noteBlockCells.delete(ck); noteBlocks.delete(ck); notePowered.delete(ck); }
    if (nm === 'observer') {
      observerCells.add(ck);
      const [ox, oy, oz] = DIR_VEC[(registries.states.props[state]?.facing ?? 'south') as Dir];
      observerBaseline.set(ck, world.getBlockState(x + ox, y + oy, z + oz)); // arm to the current view
    } else { observerCells.delete(ck); observerBaseline.delete(ck); observerPulse.delete(ck); }
    // A beacon joins the projector index; replacing it drops its effect selection.
    if (nm === 'beacon') beaconBlockCells.add(ck); else { beaconBlockCells.delete(ck); beaconSel.delete(ck); }
    // A conduit joins its own index; replacing it removes it.
    if (nm === 'conduit') conduitCells.add(ck); else conduitCells.delete(ck);
    // An end crystal joins the dragon-shield index; destroying it removes it.
    if (nm === 'end_crystal') endCrystalCells.add(ck); else endCrystalCells.delete(ck);
    // A sculk sensor joins the vibration-listener index; replacing it clears it.
    if (nm === 'sculk_sensor' || nm === 'calibrated_sculk_sensor') sculkSensorCells.add(ck);
    else { sculkSensorCells.delete(ck); sculkActive.delete(ck); }
    // A trial spawner joins its index; replacing it drops its wave state.
    if (nm === 'trial_spawner') trialSpawnerCells.add(ck); else { trialSpawnerCells.delete(ck); trialData.delete(ck); }
    if (nm !== 'vault') vaultUnlockedBy.delete(ck);
    // Incremental relight (LIT-2/3) returns every chunk whose light changed —
    // remesh exactly those (a torch can light a 3×3 chunk neighbourhood).
    const dirty = world.relightAfterEdit(x, y, z);
    dirty.add(`${Math.floor(x / WORLD.SECTION)},${Math.floor(z / WORLD.SECTION)}`);
    remeshChunks(dirty);
    // Let any adjacent fluid flow into / react to the change (ENG-14, M9.1).
    world.scheduleFluidAround(x, y, z);
    // A gravity block here, or one resting on what was just removed, may now fall
    // (M10.1). Removing it recurses up the column via this same edit → maybeFall.
    maybeFall(x, y, z);
    maybeFall(x, y + 1, z);
    maybeDripstoneFall(x, y - 1, z); // a spike hanging off the block just broken
    // Re-resolve fence/wall/pane connections for the cell + its 4 neighbours (M10.2).
    updateConnections(x, y, z);
    // Re-resolve rail shapes for the cell + its 4 neighbours (RED-5, M16.2).
    updateRails(x, y, z);
    // Re-solve any redstone network touching this cell (RED, M15.1).
    redstone.markDirty(x, y, z);
    // Multiplayer (NET-3): relay a player-initiated edit to the server for
    // arbitration (it echoes a Patch to everyone, or a revert Patch on rejection).
    if (net && netEditDepth > 0) net.sendEdit(x, y, z, state, state !== STATE_AIR);
  };

  // ── Connecting shapes (M10.2): fence/wall/pane n/e/s/w state from neighbours. ──
  const materialAt = (x: number, y: number, z: number): string | undefined =>
    registries.blocks.byName.get(registries.states.blockName[world.getBlockState(x, y, z)] ?? '')?.material;
  const connectAccess: ConnectAccess = {
    shapeAt: (x, y, z) => world.shapeAt(x, y, z),
    // Anchor to opaque full cubes only (fences/walls skip glass + leaves).
    isSolidCube: (x, y, z) => world.isSolid(x, y, z) && materialAt(x, y, z) !== 'glass' && materialAt(x, y, z) !== 'leaves',
    isGlassLike: (x, y, z) => materialAt(x, y, z) === 'glass',
  };
  const resolveConnections = (x: number, y: number, z: number): void => {
    const st = world.getBlockState(x, y, z);
    const shape = world.shapeAt(x, y, z);
    if (!isConnectingShape(shape)) return;
    const sides = connectSides(connectAccess, shape, x, y, z);
    let key = registries.states.key[st]!;
    for (const [p, v] of Object.entries(sides)) key = key.replace(new RegExp(`\\b${p}=(?:true|false)`), `${p}=${v}`);
    const next = registries.states.idByKey.get(key);
    if (next !== undefined && next !== st) {
      world.setBlockState(x, y, z, next);
      chunkMeshes.markDirty(x, z);
    }
  };
  const updateConnections = (x: number, y: number, z: number): void => {
    resolveConnections(x, y, z);
    resolveConnections(x - 1, y, z);
    resolveConnections(x + 1, y, z);
    resolveConnections(x, y, z - 1);
    resolveConnections(x, y, z + 1);
  };

  // ── Redstone (RED-1/2/4, M15.1): the host adapter over the pure engine. The
  //    engine reads roles + writes derived block states; see redstone-semantics.md.
  //    Component geometry is state-invariant in this slice, so writes need no
  //    remesh (textures/glow are M15.1-remainder). ───────────────────────────────
  const rsConductor = (x: number, y: number, z: number): boolean => {
    if (world.shapeAt(x, y, z) !== SHAPE.CUBE) return false;
    const st = world.getBlockState(x, y, z);
    if (st === STATE_AIR) return false;
    if (registries.states.blockName[st] === 'observer') return false; // a mechanism, not a wire bridge
    const m = materialAt(x, y, z);
    return m !== 'glass' && m !== 'leaves' && m !== 'water' && m !== 'lava';
  };
  const rsRoleAt = (x: number, y: number, z: number): RedRole => {
    const st = world.getBlockState(x, y, z);
    if (st === STATE_AIR) return { kind: 'air' };
    const name = registries.states.blockName[st];
    const p = registries.states.props[st] ?? {};
    // A copper bulb (any oxidation/wax) is a redstone sink whose light follows its
    // input (MB.1). (Java toggles on the rising edge; lit-follows-power is the
    // approximation here — the toggle latch is a faithfulness remainder.)
    if (name?.endsWith('copper_bulb')) return { kind: 'lamp', lit: p.lit === 'true' };
    switch (name) {
      case 'redstone_wire': return { kind: 'wire', power: Number(p.power ?? '0') };
      case 'redstone_torch': return { kind: 'torch', lit: p.lit === 'true', attach: 'down' };
      case 'redstone_wall_torch': return { kind: 'torch', lit: p.lit === 'true', attach: wallSupport((p.facing ?? 'north') as AttachFacing) };
      case 'lever': return { kind: 'lever', on: p.powered === 'true', attach: attachSupport(p.face, p.facing) };
      case 'stone_button': return { kind: 'button', on: p.powered === 'true', attach: attachSupport(p.face, p.facing) };
      case 'stone_pressure_plate': return { kind: 'plate', on: p.powered === 'true' };
      case 'detector_rail': return { kind: 'plate', on: p.powered === 'true' }; // powers while a cart rides it (RED-5)
      case 'sculk_sensor': return { kind: 'plate', on: p.powered === 'true' }; // M22: emits while active
      case 'calibrated_sculk_sensor': return { kind: 'plate', on: p.powered === 'true' };
      case 'redstone_lamp': return { kind: 'lamp', lit: p.lit === 'true' };
      case 'redstone_block': return { kind: 'source' };
      case 'repeater': return { kind: 'repeater', facing: (p.facing ?? 'north') as Facing, delay: Number(p.delay ?? '1'), powered: p.powered === 'true', locked: false };
      case 'comparator': return { kind: 'comparator', facing: (p.facing ?? 'north') as Facing, mode: (p.mode ?? 'compare') as 'compare' | 'subtract', powered: p.powered === 'true' };
      case 'observer': return { kind: 'observer', facing: (p.facing ?? 'south') as Dir, powered: p.powered === 'true' };
      default: return rsConductor(x, y, z) ? { kind: 'block' } : { kind: 'air' };
    }
  };
  const rsContainerSignal = (x: number, y: number, z: number): number => {
    // A jukebox emits its disc's fixed comparator index (0 when empty) — AUD-4.
    if (registries.states.blockName[world.getBlockState(x, y, z)] === 'jukebox') {
      return discComparatorOutput(jukeboxDiscs.get(cellKey(x, y, z)));
    }
    const be = blockEntities.get(x, y, z);
    if (!be) return -1;
    const fill = (s: ItemStack | null): number => (s && !isEmpty(s) ? s.count / Math.max(1, maxStackOf(s.item)) : 0);
    if (be.type === 'chest') return containerFullness(be.data.items.map(fill), CHEST_SIZE);
    if (be.type === 'furnace' || be.type === 'smoker') return containerFullness([be.data.input, be.data.fuel, be.data.output].map(fill), 3);
    if (be.type === 'hopper' || be.type === 'dropper' || be.type === 'dispenser') return containerFullness(be.data.items.map(fill), be.data.items.length);
    if (be.type === 'decorated_pot') return containerFullness(be.data.items.map(fill), be.data.items.length);
    return -1;
  };
  /** Rewrite one block-state property in place (no remesh — geometry is invariant). */
  const rsSetProp = (x: number, y: number, z: number, prop: string, value: string): void => {
    const st = world.getBlockState(x, y, z);
    const key = registries.states.key[st]?.replace(new RegExp(`\\b${prop}=[a-z0-9]+`), `${prop}=${value}`);
    const next = key !== undefined ? registries.states.idByKey.get(key) : undefined;
    if (next !== undefined && next !== st) world.setBlockState(x, y, z, next);
  };
  const redstoneWorld: RedstoneWorld = {
    roleAt: rsRoleAt,
    isConductor: rsConductor,
    containerSignal: rsContainerSignal,
    setWire: (x, y, z, power) => rsSetProp(x, y, z, 'power', String(power)),
    setPowered: (x, y, z, on) => rsSetProp(x, y, z, 'powered', String(on)),
    setLit: (x, y, z, lit) => rsSetProp(x, y, z, 'lit', String(lit)),
  };
  const redstone = new RedstoneEngine(redstoneWorld);

  /** Toggle/cycle a redstone input the player right-clicks. Returns handled. */
  const useRedstone = (x: number, y: number, z: number, name: string | undefined): boolean => {
    const p = registries.states.props[world.getBlockState(x, y, z)] ?? {};
    if (name === 'lever') { rsSetProp(x, y, z, 'powered', p.powered === 'true' ? 'false' : 'true'); redstone.markDirty(x, y, z); return true; }
    if (name === 'stone_button') { if (p.powered !== 'true') redstone.pressButton(x, y, z); return true; }
    if (name === 'repeater') { rsSetProp(x, y, z, 'delay', String((Number(p.delay ?? '1') % 4) + 1)); redstone.markDirty(x, y, z); return true; }
    if (name === 'comparator') { rsSetProp(x, y, z, 'mode', p.mode === 'subtract' ? 'compare' : 'subtract'); redstone.markDirty(x, y, z); return true; }
    return false;
  };

  // Pressure plates: pressed while the player or a mob stands in the plate cell.
  let pressedPlates = new Set<string>();
  const tickPressurePlates = (): void => {
    const now = new Set<string>();
    const scan = (px: number, py: number, pz: number): void => {
      const bx = Math.floor(px), by = Math.floor(py), bz = Math.floor(pz);
      if (registries.states.blockName[world.getBlockState(bx, by, bz)] === 'stone_pressure_plate') now.add(cellKey(bx, by, bz));
    };
    if (!playerState.dead) scan(player.body.x, player.body.y, player.body.z);
    for (const e of entities.entities) scan(e.x, e.y, e.z);
    for (const k of now) if (!pressedPlates.has(k)) { const [x, y, z] = k.split(',').map(Number) as [number, number, number]; rsSetProp(x, y, z, 'powered', 'true'); redstone.markDirty(x, y, z); }
    for (const k of pressedPlates) if (!now.has(k)) { const [x, y, z] = k.split(',').map(Number) as [number, number, number]; rsSetProp(x, y, z, 'powered', 'false'); redstone.markDirty(x, y, z); }
    pressedPlates = now;
  };

  // ── Item logistics (RED-2, M15.2.1): hoppers pull/push each phase-6 tick;
  //    droppers/dispensers fire once on the rising edge of incoming redstone power.
  //    The pure engine (game/logistics.ts) moves items between `TransferTarget`s
  //    the host derives from block entities. ─────────────────────────────────────
  const dispenseRng = new Rng(`${String(meta.seed)}#dispense`).fork(defineSalt('dispense'));
  /** The dominant axis of a look vector as a 6-way facing (placement orientation). */
  const facing6FromDir = (d: { x: number; y: number; z: number }): Dir => {
    const ax = Math.abs(d.x), ay = Math.abs(d.y), az = Math.abs(d.z);
    if (ay >= ax && ay >= az) return d.y > 0 ? 'up' : 'down';
    if (ax >= az) return d.x > 0 ? 'east' : 'west';
    return d.z > 0 ? 'south' : 'north';
  };
  const facingProp = (x: number, y: number, z: number): Dir =>
    (registries.states.props[world.getBlockState(x, y, z)]?.facing ?? 'down') as Dir;
  /** The container a hopper/dropper can move items through at (x,y,z), or null. A
   *  furnace exposes only its output for extraction and routes inserts by entry
   *  face (top → smelt input, side → fuel); every other container is symmetric. */
  const targetAt = (x: number, y: number, z: number): TransferTarget | null => {
    const be = blockEntities.get(x, y, z);
    if (!be) return null;
    if (be.type === 'chest') return fullTarget(chestSlots(be.data, maxStackOf));
    if (be.type === 'hopper' || be.type === 'dropper' || be.type === 'dispenser') return fullTarget(arraySlots(be.data, maxStackOf));
    if (be.type === 'furnace' || be.type === 'smoker') {
      return {
        slots: furnaceSlots(be.data, registries, maxStackOf),
        extract: [FURNACE_OUTPUT],
        insertFrom: (face) => (face === 'up' ? [FURNACE_INPUT] : face === 'down' ? [] : [FURNACE_FUEL]),
      };
    }
    return null;
  };
  const hopperRange = Array.from({ length: HOPPER_SIZE }, (_, i) => i);
  /** Phase-6 hopper transfer: pull from above, push toward its facing; a powered
   *  hopper is locked. Marks redstone dirty on change (comparators read fill). */
  const tickHoppers = (): void => {
    for (const be of blockEntities.values()) {
      if (be.type !== 'hopper') continue;
      const { x, y, z } = be;
      const facing = facingProp(x, y, z);
      const [fx, fy, fz] = DIR_VEC[facing];
      const above = targetAt(x, y + 1, z);
      const faced = targetAt(x + fx, y + fy, z + fz);
      const locked = redstone.powerLevelAt(x, y, z) > 0;
      const slots = arraySlots(be.data, maxStackOf);
      if (tickHopper(be.data, slots, facing, above, faced, locked)) redstone.markDirty(x, y, z);
      // Vacuum loose item drops in the column above (RED-2); a locked hopper skips it.
      if (!locked && drops.vacuumInto(x, y, z, (s) => insert(slots, s, hopperRange)) > 0) redstone.markDirty(x, y, z);
    }
  };
  // Rising-edge activation for droppers/dispensers.
  const activatorPowered = new Map<string, boolean>();
  const emitPos = (x: number, y: number, z: number, d: readonly [number, number, number]): [number, number, number] =>
    [x + 0.5 + d[0] * 0.7, y + 0.5 + d[1] * 0.7, z + 0.5 + d[2] * 0.7];
  // Put one of `name` back into a dispenser's first free slot (empty bucket after
  // placing a fluid, etc.). MB.7.
  const stashInDispenser = (be: DispenserBE, name: string): void => {
    const id = registries.items.idOf.get(name);
    if (id === undefined) return;
    const empty = be.data.items.findIndex((s) => s == null || isEmpty(s));
    if (empty >= 0) be.data.items[empty] = makeStack(id, 1);
  };
  // Exotic dispenser behaviours (MB.7): act on the block in front rather than
  // ejecting (game/dispenser classifies the item).
  const doExoticDispense = (action: DispenseAction, be: DispenserBE, fx: number, fy: number, fz: number, itemName: string, dir: readonly [number, number, number]): void => {
    const target = world.getBlockState(fx, fy, fz);
    const openCell = target === STATE_AIR || world.isReplaceableByFluid(target);
    switch (action) {
      case 'grow': {
        const crop = cropOf(registries.states.blockName[target] ?? '');
        if (!crop) break;
        const age = ageOf(target);
        if (age >= crop.maxAge) break;
        const ns = cropStateOf(crop.block, Math.min(crop.maxAge, age + 1 + dispenseRng.nextInt(2)));
        if (ns >= 0) setBlockRemesh(fx, fy, fz, ns);
        break;
      }
      case 'place_water': if (openCell) { edit(fx, fy, fz, world.waterState); stashInDispenser(be, 'bucket'); } break;
      case 'place_lava': if (openCell) { edit(fx, fy, fz, world.lavaState); stashInDispenser(be, 'bucket'); } break;
      case 'scoop':
        if (target === world.waterState) { edit(fx, fy, fz, STATE_AIR); stashInDispenser(be, 'water_bucket'); }
        else if (target === world.lavaState) { edit(fx, fy, fz, STATE_AIR); stashInDispenser(be, 'lava_bucket'); }
        break;
      case 'ignite_tnt': {
        const tntState = registries.states.defaultOf.get('tnt');
        if (target === STATE_AIR && tntState !== undefined) { edit(fx, fy, fz, tntState); ignitePrimedTntAt(fx, fy, fz); }
        break;
      }
      case 'shear': {
        // Clip the first woolly sheep in the block in front; the shears persist.
        const sheep = entities.entities.find((e) =>
          !e.removed && e.type === 'sheep' && !e.sheared && !e.baby &&
          Math.floor(e.x) === fx && Math.floor(e.z) === fz && Math.abs(e.y - fy) < 1.5);
        if (sheep) {
          sheep.sheared = true;
          const id = registries.items.idOf.get(`${DYE_ORDER[sheep.variant] ?? 'white'}_wool`);
          if (id !== undefined) drops.spawn(makeStack(id, 1 + dispenseRng.nextInt(3)), sheep.x, sheep.y + 0.5, sheep.z, { vy: 0.2 });
        }
        stashInDispenser(be, 'shears'); // a tool, not consumed
        break;
      }
      case 'spawn_mob': {
        const mob = spawnEggMob(itemName);
        if (mob && registries.mobs.byName.has(mob) && openCell) entities.spawn(mob, fx + 0.5, fy, fz + 0.5, { persistent: true });
        break;
      }
      case 'equip_armor': {
        // Fit the piece onto a player standing in the block in front, if that slot
        // is empty; otherwise put it back (nothing to wear it).
        const id = registries.items.idOf.get(itemName);
        const slot = id !== undefined ? armorSlotOf(id) : null;
        const inFront = Math.floor(player.body.x) === fx && Math.floor(player.body.y) === fy && Math.floor(player.body.z) === fz;
        if (slot !== null && id !== undefined && inFront && isEmpty(playerState.inventory.get(INV.ARMOR_START + slot))) {
          playerState.inventory.set(INV.ARMOR_START + slot, makeStack(id, 1));
        } else {
          stashInDispenser(be, itemName);
        }
        break;
      }
      case 'launch_firework': {
        // Shoot a self-propelled firework rocket in the dispenser's facing direction.
        const s = FIREWORK.LAUNCH_SPEED;
        entities.spawnProjectile('firework', fx + 0.5, fy + 0.5, fz + 0.5, dir[0] * s, dir[1] * s, dir[2] * s, -1, 0);
        break;
      }
      default: break;
    }
  };
  const fireDispenser = (be: DispenserBE, x: number, y: number, z: number, facing: Dir): void => {
    const res = dispenserFire(be.data, arraySlots(be.data, maxStackOf), (item) => registries.items.nameOf[item], dispenseRng);
    if (!res) return;
    const d = DIR_VEC[facing];
    const [ox, oy, oz] = emitPos(x, y, z, d);
    const firedName = registries.items.nameOf[res.item] ?? '';
    const action = res.kind === 'shoot' ? 'default' : dispenseAction(firedName);
    if (action !== 'default') {
      doExoticDispense(action, be, x + d[0], y + d[1], z + d[2], firedName, d);
    } else if (res.kind === 'shoot') {
      const s = PROJECTILE.ARROW_SPEED;
      entities.spawnProjectile('arrow', ox, oy, oz, d[0] * s, d[1] * s, d[2] * s, -1, PROJECTILE.ARROW_SPEED * PROJECTILE.ARROW_DAMAGE_PER_SPEED);
    } else {
      drops.spawn(makeStack(res.item, 1), ox, oy, oz, { vx: d[0] * 0.2, vy: d[1] * 0.2 + 0.1, vz: d[2] * 0.2 });
    }
    redstone.markDirty(x, y, z);
  };
  const fireDropper = (be: DropperBE, x: number, y: number, z: number, facing: Dir): void => {
    const d = DIR_VEC[facing];
    const faced = targetAt(x + d[0], y + d[1], z + d[2]);
    const ejected = dropperFire(be.data, arraySlots(be.data, maxStackOf), facing, faced, dispenseRng);
    const [ox, oy, oz] = emitPos(x, y, z, d);
    if (ejected) drops.spawn(ejected, ox, oy, oz, { vx: d[0] * 0.2, vy: d[1] * 0.2 + 0.1, vz: d[2] * 0.2 });
    redstone.markDirty(x, y, z);
  };
  // A crafter auto-crafts one output from its grid on the rising edge, consuming
  // one of each ingredient and ejecting the result toward its facing (MB).
  let lastCrafted: { item: string; count: number } | null = null;
  const fireCrafter = (be: CrafterBE, x: number, y: number, z: number, facing: Dir): void => {
    const res = craftOnce(registries, be.data.items, be.data.disabled);
    if (!res) return;
    for (let i = 0; i < CRAFTER_SIZE; i++) be.data.items[i] = res.slots[i] ?? null;
    const d = DIR_VEC[facing];
    const [ox, oy, oz] = emitPos(x, y, z, d);
    drops.spawn(res.output, ox, oy, oz, { vx: d[0] * 0.2, vy: d[1] * 0.2 + 0.1, vz: d[2] * 0.2 });
    lastCrafted = { item: registries.items.nameOf[res.output.item] ?? '?', count: res.output.count };
    redstone.markDirty(x, y, z);
  };
  const tickActivators = (): void => {
    for (const be of blockEntities.values()) {
      if (be.type !== 'dropper' && be.type !== 'dispenser' && be.type !== 'crafter') continue;
      const { x, y, z } = be;
      const k = cellKey(x, y, z);
      const powered = redstone.powerLevelAt(x, y, z) > 0;
      if (powered && !(activatorPowered.get(k) ?? false)) {
        const facing = facingProp(x, y, z);
        if (be.type === 'dispenser') fireDispenser(be, x, y, z, facing);
        else if (be.type === 'crafter') fireCrafter(be, x, y, z, facing);
        else fireDropper(be, x, y, z, facing);
      }
      if (powered) activatorPowered.set(k, true); else activatorPowered.delete(k);
    }
  };

  // ── Primed TNT (RED-8, M15.2.2): a TNT block ignited by redstone power, flint &
  //    steel, or a neighbouring blast becomes a fusing entity that detonates via
  //    the shared explosion model (power 4). The pure system (entities/tnt.ts)
  //    owns the fall + fuse; the host owns ignition + the blast. ────────────────
  const tntRng = new Rng(`${String(meta.seed)}#tnt`).fork(defineSalt('tnt'));
  const tntWorld: TntWorld = { isSolid: (x, y, z) => world.isSolid(x, y, z) };
  /** Replace the TNT block at (x,y,z) with a fusing entity; false if it isn't TNT. */
  const ignitePrimedTntAt = (x: number, y: number, z: number, fuse: number = TNT.FUSE_GT): boolean => {
    if (registries.states.blockName[world.getBlockState(x, y, z)] !== 'tnt') return false;
    edit(x, y, z, STATE_AIR); // remove the block (also drops it from the TNT index)
    primedTnt.spawn(x, y, z, fuse, tntRng);
    return true;
  };
  /** Detonate a primed TNT through the shared explosion (breaks blocks + hurts
   *  entities; a TNT block caught in the blast re-primes via the mob-world's
   *  breakBlock → the chain reaction). */
  const detonateTnt = (t: { x: number; y: number; z: number }): void => {
    entities.explode(makeFrame(), t.x, t.y + 0.5, t.z, TNT.POWER);
  };
  /** Redstone-solved TNT cells ignite while powered (removing themselves from the
   *  index as they become air). Iterating the Set while deleting the current cell
   *  is safe; ignition never adds to it. */
  const tickTntIgnition = (): void => {
    if (tntCells.size === 0) return;
    for (const k of tntCells) {
      const [x, y, z] = k.split(',').map(Number) as [number, number, number];
      // Self-heal any stale entry (mined / dimension-swapped cell) before probing.
      if (registries.states.blockName[world.getBlockState(x, y, z)] !== 'tnt') { tntCells.delete(k); continue; }
      if (redstone.powerLevelAt(x, y, z) > 0) ignitePrimedTntAt(x, y, z);
    }
  };

  // ── Pistons + observers (RED-3, M16.1). A piston extends on a rising power edge
  //    and retracts on the falling edge; the pure resolver (game/piston.ts) owns
  //    the structure it pushes/pulls (12-block limit, immovable/destroy classes,
  //    slime/honey adhesion). An observer emits a 2-gt pulse when the block it
  //    faces changes. Both scan only their cell index, on the redstone tick. ─────
  const PISTON_IMMOVABLE = new Set(['obsidian', 'piston_head', 'spawner']);
  const PISTON_DESTROY_SHAPES = new Set<number>([SHAPE.CROSS, SHAPE.TORCH, SHAPE.LAYER, SHAPE.FLUID]);
  const pistonMoveClass = (x: number, y: number, z: number): MoveClass => {
    const st = world.getBlockState(x, y, z);
    if (st === STATE_AIR) return 'normal';
    const name = registries.states.blockName[st] ?? 'air';
    const def = registries.blocks.byName.get(name);
    if (!def || def.hardness < 0) return 'immovable'; // bedrock / unbreakable
    if (PISTON_IMMOVABLE.has(name)) return 'immovable';
    if (def.blockEntity) return 'immovable'; // containers: relocation unsupported (M16.1-remainder)
    if (name === 'piston' || name === 'sticky_piston') {
      return registries.states.props[st]?.extended === 'true' ? 'immovable' : 'normal';
    }
    if (def.material === 'water' || def.material === 'lava' || def.material === 'plant') return 'destroy';
    if (PISTON_DESTROY_SHAPES.has(world.shapeAt(x, y, z))) return 'destroy'; // torches/plants/flat components/snow
    return 'normal';
  };
  const pistonView: PistonView = {
    name: (x, y, z) => registries.states.blockName[world.getBlockState(x, y, z)] ?? 'air',
    moveClass: pistonMoveClass,
    adhesion: (x, y, z) => {
      const n = registries.states.blockName[world.getBlockState(x, y, z)];
      return n === 'slime_block' ? 'slime' : n === 'honey_block' ? 'honey' : 'none';
    },
  };
  const pistonHeadState = (facing: Dir): number =>
    registries.states.idByKey.get(`piston_head[facing=${facing}]`) ?? STATE_AIR;
  /** Drop a crushed block's items and clear it (piston destroy-class). */
  const breakToDrops = (x: number, y: number, z: number): void => {
    for (const drop of blockDrops(registries, world.getBlockState(x, y, z), null)) {
      drops.spawn(drop, x + 0.5, y + 0.55, z + 0.5, { vx: (Math.random() - 0.5) * 0.1, vy: 0.16, vz: (Math.random() - 0.5) * 0.1 });
    }
    edit(x, y, z, STATE_AIR);
  };
  /** Enact a resolved push/pull: crush destroyed cells, then relocate the movers.
   *  Every destination is written before any vacated source is cleared, so an
   *  overlapping structure (a block moving into another's old cell) is order-free. */
  const applyPistonPlan = (plan: { moves: Move[]; destroyed: Array<[number, number, number]> }): void => {
    for (const [dx, dy, dz] of plan.destroyed) breakToDrops(dx, dy, dz);
    const snap = plan.moves.map((m) => ({ ...m, state: world.getBlockState(m.fromX, m.fromY, m.fromZ) }));
    const dests = new Set(snap.map((m) => cellKey(m.toX, m.toY, m.toZ)));
    for (const m of snap) edit(m.toX, m.toY, m.toZ, m.state);
    for (const m of snap) if (!dests.has(cellKey(m.fromX, m.fromY, m.fromZ))) edit(m.fromX, m.fromY, m.fromZ, STATE_AIR);
  };
  /** Shove the player, mobs and item drops out of the cells a piston just filled
   *  (E-14). Each target slides the minimal distance along the push axis to clear
   *  the moved blocks + the head, and loses its velocity along that axis so it
   *  doesn't immediately fall/creep back into the arm. */
  const shovePistonTargets = (plan: PushPlan, hx: number, hy: number, hz: number, facing: Dir): void => {
    const occupied = occupiedAfter(plan, hx, hy, hz);
    const shove = (b: { x: number; y: number; z: number; vx: number; vy: number; vz: number }, half: number, height: number): void => {
      const d = pistonPushBox(occupied, facing, {
        minX: b.x - half, minY: b.y, minZ: b.z - half,
        maxX: b.x + half, maxY: b.y + height, maxZ: b.z + half,
      });
      if (d.x === 0 && d.y === 0 && d.z === 0) return;
      b.x += d.x; b.y += d.y; b.z += d.z;
      if (d.x !== 0) b.vx = 0;
      if (d.y !== 0) b.vy = 0;
      if (d.z !== 0) b.vz = 0;
    };
    if (!playerState.caps.noClip) shove(player.body, PLAYER_HALF, PLAYER_HEIGHT);
    for (const e of entities.entities) if (!e.removed) shove(e, 0.3, 0.9);
    for (const d of drops.drops) shove(d, 0.125, 0.25);
  };
  const extendPiston = (x: number, y: number, z: number, facing: Dir): void => {
    const plan = resolveExtend(pistonView, x, y, z, facing);
    if (!plan) return; // blocked — stay retracted; retry next tick if still powered
    applyPistonPlan(plan);
    const [fx, fy, fz] = DIR_VEC[facing];
    edit(x + fx, y + fy, z + fz, pistonHeadState(facing)); // the arm fills the front cell
    shovePistonTargets(plan, x + fx, y + fy, z + fz, facing); // push entities aside (E-14)
    rsSetProp(x, y, z, 'extended', 'true');
    chunkMeshes.markDirty(x, z);
    redstone.markDirty(x, y, z);
  };
  const retractPiston = (x: number, y: number, z: number, facing: Dir, sticky: boolean): void => {
    const [fx, fy, fz] = DIR_VEC[facing];
    if (registries.states.blockName[world.getBlockState(x + fx, y + fy, z + fz)] === 'piston_head') {
      edit(x + fx, y + fy, z + fz, STATE_AIR); // remove the arm
    }
    rsSetProp(x, y, z, 'extended', 'false');
    chunkMeshes.markDirty(x, z);
    redstone.markDirty(x, y, z);
    applyPistonPlan(resolveRetract(pistonView, x, y, z, facing, sticky)); // sticky pull-back
  };
  const tickPistons = (): void => {
    if (pistonCells.size === 0) return;
    for (const k of [...pistonCells]) {
      const [x, y, z] = k.split(',').map(Number) as [number, number, number];
      const st = world.getBlockState(x, y, z);
      const name = registries.states.blockName[st];
      if (name !== 'piston' && name !== 'sticky_piston') { pistonCells.delete(k); continue; }
      const facing = (registries.states.props[st]?.facing ?? 'up') as Dir;
      const extended = registries.states.props[st]?.extended === 'true';
      const powered = redstone.powerLevelAt(x, y, z) > 0;
      if (powered && !extended) extendPiston(x, y, z, facing);
      else if (!powered && extended) retractPiston(x, y, z, facing, name === 'sticky_piston');
    }
  };
  const OBSERVER_PULSE_RT = 1; // 2 gt = 1 redstone tick
  const tickObservers = (): void => {
    if (observerCells.size === 0) return;
    for (const k of [...observerCells]) {
      const [x, y, z] = k.split(',').map(Number) as [number, number, number];
      const st = world.getBlockState(x, y, z);
      if (registries.states.blockName[st] !== 'observer') { observerCells.delete(k); observerBaseline.delete(k); observerPulse.delete(k); continue; }
      const [dx, dy, dz] = DIR_VEC[(registries.states.props[st]?.facing ?? 'south') as Dir];
      const watched = world.getBlockState(x + dx, y + dy, z + dz);
      const pulse = observerPulse.get(k) ?? 0;
      if (pulse > 0) {
        observerBaseline.set(k, watched); // hold the baseline current while pulsing
        if (pulse <= OBSERVER_PULSE_RT) { observerPulse.delete(k); rsSetProp(x, y, z, 'powered', 'false'); redstone.markDirty(x, y, z); }
        else observerPulse.set(k, pulse - 1);
        continue;
      }
      const base = observerBaseline.get(k);
      if (base === undefined) { observerBaseline.set(k, watched); continue; } // arm on first sight
      if (watched !== base) {
        observerBaseline.set(k, watched);
        observerPulse.set(k, OBSERVER_PULSE_RT);
        rsSetProp(x, y, z, 'powered', 'true'); // emit out the back for one pulse
        redstone.markDirty(x, y, z);
      }
    }
  };

  // ── Beacon (M20.1). A pyramid of mineral blocks under a beacon, with clear sky
  //  above it, powers the effect the player selected over an area that scales with
  //  the pyramid's tier. Recomputed every BEACON.APPLY_INTERVAL ticks. ──
  const beaconNameAt = (x: number, y: number, z: number): string | null =>
    registries.states.blockName[world.getBlockState(x, y, z)] ?? null;
  const beaconSkyClear = (x: number, y: number, z: number): boolean =>
    world.getSkyLight(x, y + 1, z) >= 15; // full sky over the beacon = no opaque block between it and the sky
  const beaconTierAt = (x: number, y: number, z: number): number =>
    computeBeaconTier((dx, depth, dz) => beaconNameAt(x + dx, y - depth, z + dz));
  /** Compute + apply a single beacon's effects; returns what it applied (E2E seam). */
  const applyBeaconAt = (x: number, y: number, z: number): { id: string; amplifier: number }[] => {
    if (registries.states.blockName[world.getBlockState(x, y, z)] !== 'beacon' || !beaconSkyClear(x, y, z)) return [];
    const tier = beaconTierAt(x, y, z);
    const sel = beaconSel.get(cellKey(x, y, z)) ?? { primary: 'speed', secondary: null };
    const effects = resolveBeaconEffects(tier, sel.primary, sel.secondary);
    if (effects.length === 0) return [];
    const b = player.body;
    if (!beaconInRange(tier, b.x - x, b.y - y, b.z - z)) return [];
    const dur = beaconDuration(tier);
    for (const e of effects) addEffect(playerState.effects, e.id, e.amplifier, dur);
    return effects.map((e) => ({ id: e.id, amplifier: e.amplifier }));
  };
  const tickBeaconBlocks = (): void => {
    if (beaconBlockCells.size === 0 || world.tickCount % BEACON.APPLY_INTERVAL !== 0) return;
    for (const k of [...beaconBlockCells]) {
      const [x, y, z] = k.split(',').map(Number) as [number, number, number];
      if (registries.states.blockName[world.getBlockState(x, y, z)] !== 'beacon') { beaconBlockCells.delete(k); beaconSel.delete(k); continue; }
      applyBeaconAt(x, y, z);
    }
  };

  // ── Conduit (M18.1). A prismarine frame around a submerged conduit powers a
  //  Conduit Power area over the player and, when fully framed, zaps a hostile. ──
  const conduitFrameAt = (x: number, y: number, z: number): number =>
    conduitFrameCount((dx, dy, dz) => beaconNameAt(x + dx, y + dy, z + dz));
  /** Apply a single conduit's effect + hostile zap; returns its frame/range (E2E seam). */
  const applyConduitAt = (x: number, y: number, z: number): { frame: number; range: number; powered: boolean } => {
    if (registries.states.blockName[world.getBlockState(x, y, z)] !== 'conduit') return { frame: 0, range: 0, powered: false };
    const frame = conduitFrameAt(x, y, z);
    const coreOk = conduitCoreClear((dx, dy, dz) => beaconNameAt(x + dx, y + dy, z + dz));
    if (!coreOk || !conduitActive(frame)) return { frame, range: 0, powered: false };
    const b = player.body;
    if (conduitInRange(frame, b.x - x, b.y - y, b.z - z)) addEffect(playerState.effects, 'conduit_power', 0, CONDUIT.EFFECT_TICKS);
    if (conduitAttacks(frame)) {
      const hostile = entities.entities.find((e) => !e.removed && registries.mobs.byName.get(e.type)?.category === 'monster' &&
        Math.hypot(e.x - x, e.y - y, e.z - z) <= CONDUIT.ATTACK_RADIUS);
      if (hostile) entities.damage(hostile, CONDUIT.ATTACK_DAMAGE, makeFrame(), 0, 0, false);
    }
    return { frame, range: computeConduitRange(frame), powered: true };
  };
  const tickConduits = (): void => {
    if (conduitCells.size === 0 || world.tickCount % CONDUIT.APPLY_INTERVAL !== 0) return;
    for (const k of [...conduitCells]) {
      const [x, y, z] = k.split(',').map(Number) as [number, number, number];
      if (registries.states.blockName[world.getBlockState(x, y, z)] !== 'conduit') { conduitCells.delete(k); continue; }
      applyConduitAt(x, y, z);
    }
  };

  // ── Ender dragon (M21.1). Intact end crystals within HEAL_RADIUS shield the
  //  dragon (it is un-killable until they are all destroyed); on death it lays the
  //  dragon egg where it fell. ──
  const crystalsAliveNear = (ex: number, ey: number, ez: number): number => {
    let n = 0;
    for (const k of [...endCrystalCells]) {
      const [x, y, z] = k.split(',').map(Number) as [number, number, number];
      if (registries.states.blockName[world.getBlockState(x, y, z)] !== 'end_crystal') { endCrystalCells.delete(k); continue; }
      if (Math.hypot(x - ex, y - ey, z - ez) <= DRAGON.HEAL_RADIUS) n++;
    }
    return n;
  };

  /**
   * Arm the End's dragon fight (DIM-3). The generator stands the end crystals on the
   * pillars, but `endCrystalCells` is only fed by block EDITS — so without this the
   * boss would arrive already un-shielded. The cells come from the seed (the same
   * `endPillars` the generator uses), so no chunk scan is needed and it works before
   * those chunks have even loaded.
   *
   * Whether the fight is still owed is read off the WORLD — the exit portal's
   * presence — so re-entering or reloading an End you already beat does not respawn
   * the boss, and needs no extra saved state.
   */
  const armEndFight = (w: World, es: EntitySystem): void => {
    if (w.dimension !== 'end') return;
    const portalState = registries.states.defaultOf.get('end_portal');
    const access = { get: (x: number, y: number, z: number) => w.getBlockState(x, y, z), set: () => {} };
    if (portalState === undefined || !endFightPending(access, portalState)) return;
    for (const [x, y, z] of generatedCrystalCells(w.seed)) endCrystalCells.add(`${x},${y},${z}`);
    if (es.entities.some((e) => e.type === 'ender_dragon' && !e.removed)) return;
    const [dx, dy, dz] = dragonSpawnPoint();
    es.spawn('ender_dragon', dx, dy, dz, { persistent: true });
  };

  /** The slain dragon's parting gift: the bedrock exit portal home, egg on top. */
  const raiseEndExitPortal = (): void => {
    const bedrock = registries.states.defaultOf.get('bedrock');
    const endPortal = registries.states.defaultOf.get('end_portal');
    if (bedrock === undefined || endPortal === undefined) return;
    buildExitPortal(
      { get: (x, y, z) => world.getBlockState(x, y, z), set: (x, y, z, st) => edit(x, y, z, st) },
      { bedrock, endPortal, dragonEgg: registries.states.defaultOf.get('dragon_egg') },
    );
  };

  /** The end-portal probe (DIM-3): what a frame at a cell is, how to re-state it
   *  with its eye in, and how to fill the pool. Writes go through `edit` so the
   *  lit portal remeshes and relights like any other block change. */
  const portalProbe = (): PortalProbe | null => {
    const portalState = registries.states.defaultOf.get('end_portal');
    if (portalState === undefined) return null;
    return {
      portalState,
      frameAt: (x, y, z) => {
        const st = world.getBlockState(x, y, z);
        if (registries.states.blockName[st] !== 'end_portal_frame') return null;
        const props = registries.states.props[st] ?? {};
        return { facing: props.facing ?? 'north', eye: props.eye === 'true' };
      },
      frameState: (facing, eye) => registries.states.idByKey.get(`end_portal_frame[facing=${facing},eye=${eye}]`)
        ?? registries.states.defaultOf.get('end_portal_frame')!,
      set: (x, y, z, st) => edit(x, y, z, st),
    };
  };

  /** The gateway/landing block accessor — writes go through `edit` so the cage and
   *  the pad remesh and relight like any other block change. */
  const gatewayAccess = () => ({
    get: (x: number, y: number, z: number) => world.getBlockState(x, y, z),
    set: (x: number, y: number, z: number, st: number) => edit(x, y, z, st),
    isSolid: (x: number, y: number, z: number) => world.isSolid(x, y, z),
  });

  const gatewayIds = (): { bedrock: number; endGateway: number; endStone?: number } | null => {
    const bedrock = registries.states.defaultOf.get('bedrock');
    const endGateway = registries.states.defaultOf.get('end_gateway');
    if (bedrock === undefined || endGateway === undefined) return null;
    return { bedrock, endGateway, endStone: registries.states.defaultOf.get('end_stone') };
  };

  /** …and its other gift: the next gateway of the ring lights, opening the way out
   *  to the shards. Each dragon lights one more, so a respawned fight is worth it. */
  const raiseEndGateway = (): void => {
    const ids = gatewayIds();
    if (!ids) return;
    const cell = nextRingGateway(gatewayAccess(), ids.endGateway);
    if (!cell) return; // the whole ring is already lit
    buildGateway(gatewayAccess(), ids, cell);
    game.print('§5A gateway opens over the island.');
  };

  /** Move the player to a position without changing dimension (gateway / chorus). */
  const teleportPlayer = ([tx, ty, tz]: readonly [number, number, number]): void => {
    player.body.x = tx; player.body.y = ty; player.body.z = tz;
    player.body.vx = player.body.vy = player.body.vz = 0; player.body.onGround = false;
    playerState.fallPeak = ty; // a teleport is not a fall — don't bank the drop
    prevX = tx; prevY = ty; prevZ = tz;
    chunkMeshes.primeAround(tx, tz);
    portalImmune = true;
    portalTicks = 0;
  };

  /** Step into a gateway: outbound throws you across the void gap onto the shards
   *  (laying a pad and a return gateway there); a return gateway sends you back to
   *  the exit portal on the arena. Same dimension either way — no portal dwell. */
  const useEndGateway = (b: Body): void => {
    const ids = gatewayIds();
    if (!ids) return;
    const cx = Math.floor(b.x), cy = Math.floor(b.y + 0.9), cz = Math.floor(b.z);
    if (isRingGateway(cx, cz)) {
      // Land on a SPECIFIC island, not just a bearing: the gateway hunts around its
      // nominal column for real ground, and only falls back to a laid pad if the
      // whole neighbourhood is void.
      const target = gatewayIslandColumn(gatewayAccess(), [cx, cy, cz]);
      const { arrival } = arriveAtOuterIsland(gatewayAccess(), ids, target.x, target.z);
      teleportPlayer(arrival);
    } else {
      teleportPlayer([0.5, END.ISLAND_TOP + 1, 0.5]);
    }
  };

  // ── Trial chambers (M22.1). A trial spawner wakes when a player is near, releases
  //  a per-player-scaled wave (capped simultaneous), and — once every mob is
  //  spawned and slain — ejects a trial key + goes on cooldown. A vault opens once
  //  per player for the right key. ──
  const trialPlayerCount = (): number => 1; // single-player; MP scaling is a remainder
  const trialAlive = (st: { mobs: number[] }): number =>
    st.mobs.filter((id) => entities.entities.some((e) => e.id === id && !e.removed)).length;
  const tickTrialSpawners = (): void => {
    if (trialSpawnerCells.size === 0 || world.tickCount % TRIAL.SPAWN_INTERVAL !== 0) return;
    const players = trialPlayerCount();
    for (const k of [...trialSpawnerCells]) {
      const [x, y, z] = k.split(',').map(Number) as [number, number, number];
      if (registries.states.blockName[world.getBlockState(x, y, z)] !== 'trial_spawner') { trialSpawnerCells.delete(k); trialData.delete(k); continue; }
      const st = trialData.get(k) ?? { state: 'waiting' as TrialState, spawned: 0, mobs: [], cooldown: 0 };
      const near = Math.hypot(player.body.x - x, player.body.y - y, player.body.z - z) <= TRIAL.DETECT_RADIUS;
      const alive = trialAlive(st);
      const prev = st.state;
      st.state = nextTrialState(st.state, near, st.spawned, alive, players, st.cooldown);
      if (st.state === 'active' && trialCanSpawn(st.spawned, alive, players)) {
        const e = entities.spawn('zombie', x + 0.5, y + 1, z + 0.5, { persistent: false });
        if (e) { st.mobs.push(e.id); st.spawned += 1; }
      }
      if (prev === 'active' && st.state === 'ejecting') { // wave cleared → reward
        const key = registries.items.idOf.get('trial_key');
        if (key !== undefined) drops.spawn(makeStack(key, 1), x + 0.5, y + 1, z + 0.5, { vy: 0.2 });
        st.cooldown = TRIAL.COOLDOWN_TICKS;
      }
      if (st.state === 'cooldown') st.cooldown = Math.max(0, st.cooldown - TRIAL.SPAWN_INTERVAL);
      if (st.state === 'waiting' && prev === 'cooldown') { st.spawned = 0; st.mobs = []; } // reset for the next run
      trialData.set(k, st);
    }
  };
  /** Try to open the vault at (x,y,z) with a held trial key — once per player. */
  const tryOpenVault = (x: number, y: number, z: number): boolean => {
    if (registries.states.blockName[world.getBlockState(x, y, z)] !== 'vault') return false;
    const held = playerState.inventory.held();
    const heldName = isEmpty(held) ? '' : registries.items.nameOf[held.item] ?? '';
    const k = cellKey(x, y, z);
    const unlockedBy = vaultUnlockedBy.get(k) ?? [];
    if (!vaultUnlocks(heldName === 'trial_key' || heldName === 'ominous_trial_key', unlockedBy, 0)) return false;
    consumeHeld();
    unlockedBy.push(0);
    vaultUnlockedBy.set(k, unlockedBy);
    game.give('emerald', 3); // the vault reward (a representative loot grant)
    return true;
  };

  // ── Respawn anchor (M20.1). Glowstone charges it; in the Nether a charged anchor
  //  sets the player's spawn, but anywhere else it detonates like a bed. ──
  const anchorStateOf = (charges: number): number => registries.states.idByKey.get(`respawn_anchor[charges=${charges}]`) ?? -1;
  const useAnchorAt = (x: number, y: number, z: number): string => {
    const st = world.getBlockState(x, y, z);
    if (registries.states.blockName[st] !== 'respawn_anchor') return 'none';
    const charges = Number(registries.states.props[st]?.charges ?? '0');
    const held = playerState.inventory.held();
    const heldName = isEmpty(held) ? '' : registries.items.nameOf[held.item] ?? '';
    const action = anchorAction(world.dimension, charges, heldName === 'glowstone');
    if (action === 'charge') {
      const ns = anchorStateOf(chargeAnchor(charges));
      if (ns >= 0) { edit(x, y, z, ns); consumeHeld(); }
    } else if (action === 'set_spawn') {
      playerState.spawn = { x: x + 0.5, y: y + 1, z: z + 0.5 };
      game.print?.('Respawn point set');
    } else if (action === 'explode') {
      edit(x, y, z, STATE_AIR);
      entities.explode(makeFrame(), x + 0.5, y + 0.5, z + 0.5, anchorExplosionPower());
    }
    return action;
  };

  // ── Rails + minecarts (RED-5/6, M16.2). A rail's `shape` state auto-resolves
  //    from its neighbours (curves for a normal rail, straight-only for a powered
  //    rail); a minecart rides the rail centreline, launched/braked by powered
  //    rails. The pure system (entities/minecart.ts) owns the physics; the host
  //    adapts rail shapes + powered-rail power. ─────────────────────────────────
  const RAIL_NAMES = new Set(['rail', 'powered_rail', 'detector_rail']);
  const isRailBlock = (x: number, y: number, z: number): boolean =>
    RAIL_NAMES.has(registries.states.blockName[world.getBlockState(x, y, z)] ?? '');
  const railView: RailView = { isRail: isRailBlock };
  const setRailShape = (x: number, y: number, z: number, shape: RailShape): void => {
    const st = world.getBlockState(x, y, z);
    const key = registries.states.key[st]?.replace(/\bshape=[a-z_]+/, `shape=${shape}`);
    const next = key !== undefined ? registries.states.idByKey.get(key) : undefined;
    if (next !== undefined && next !== st) { world.setBlockState(x, y, z, next); chunkMeshes.markDirty(x, z); }
  };
  const resolveRail = (x: number, y: number, z: number): void => {
    const n = registries.states.blockName[world.getBlockState(x, y, z)];
    if (!RAIL_NAMES.has(n ?? '')) return;
    // Powered + detector rails never curve (straight/ascending only).
    setRailShape(x, y, z, resolveRailShape(railView, x, y, z, n !== 'rail'));
  };
  /** Re-resolve the rail at (x,y,z), its 4 neighbours, and their one-up/one-down
   *  cells after a block edit (a slope's shape depends on a rail a level away). */
  const updateRails = (x: number, y: number, z: number): void => {
    for (let dy = -1; dy <= 1; dy++) {
      resolveRail(x, y + dy, z);
      resolveRail(x - 1, y + dy, z); resolveRail(x + 1, y + dy, z);
      resolveRail(x, y + dy, z - 1); resolveRail(x, y + dy, z + 1);
    }
  };
  const railWorld: RailWorld = {
    railShapeAt: (x, y, z) => {
      const st = world.getBlockState(x, y, z);
      if (!RAIL_NAMES.has(registries.states.blockName[st] ?? '')) return null;
      return (registries.states.props[st]?.shape ?? 'north_south') as RailShape;
    },
    // A powered rail is active when power reaches the rail cell or the block it sits
    // on. Normal + detector rails apply rolling friction (null).
    poweredRailAt: (x, y, z) => {
      if (registries.states.blockName[world.getBlockState(x, y, z)] !== 'powered_rail') return null;
      return redstone.powerLevelAt(x, y, z) > 0 || redstone.powerLevelAt(x, y - 1, z) > 0;
    },
  };
  // Detector rails emit redstone power while a cart sits on them (RED-5). Track the
  // set of currently-powered detector cells so we can clear one when the cart leaves.
  const detectorPowered = new Set<string>();
  const tickDetectorRails = (): void => {
    const nowOccupied = new Set<string>();
    for (const cart of minecarts.carts) {
      if (registries.states.blockName[world.getBlockState(cart.cx, cart.cy, cart.cz)] === 'detector_rail') {
        nowOccupied.add(cellKey(cart.cx, cart.cy, cart.cz));
      }
    }
    for (const k of nowOccupied) {
      if (detectorPowered.has(k)) continue;
      const [x, y, z] = k.split(',').map(Number) as [number, number, number];
      rsSetProp(x, y, z, 'powered', 'true'); redstone.markDirty(x, y, z);
      detectorPowered.add(k);
    }
    for (const k of [...detectorPowered]) {
      if (nowOccupied.has(k)) continue;
      const [x, y, z] = k.split(',').map(Number) as [number, number, number];
      if (registries.states.blockName[world.getBlockState(x, y, z)] === 'detector_rail') {
        rsSetProp(x, y, z, 'powered', 'false'); redstone.markDirty(x, y, z);
      }
      detectorPowered.delete(k);
    }
  };
  const spawnMinecartAt = (x: number, y: number, z: number, dir: Facing = 'north', kind: MinecartKind = 'minecart'): void => {
    if (!isRailBlock(x, y, z)) return;
    // Net mode: the SERVER owns vehicles (M62), so we ask rather than spawn — one
    // cart for everyone instead of a private one per client.
    if (net) { net.sendVehicle(kind, x, y, z, 0); return; }
    minecarts.spawn(x, y, z, dir, 0, kind);
  };
  // ── Boats (RED-6): free-floating water transport (buoyancy + paddle; no rails). ──
  const boatWorld: BoatWorld = {
    isWater: (x, y, z) => world.isFluid(x, y, z) && !world.isLava(x, y, z),
    isSolid: (x, y, z) => world.isSolid(x, y, z),
  };
  const spawnBoatAt = (x: number, y: number, z: number, yaw = 0, kind: BoatKind = 'boat'): void => {
    if (net) { net.sendVehicle(kind, x, y, z, yaw); return; } // server-owned in net mode (M62)
    boats.spawn(x, y, z, yaw, kind);
  };
  const tickBoats = (): void => {
    if (boats.count === 0) return;
    boats.tick(boatWorld, (b) => boatInputs.get(b.id) ?? {});
  };
  // ── Passenger binding (B4): the player rides a boat — mount.ts seats them, their
  //    movement input paddles it, and their body is snapped to the seat each tick. ──
  const mountBoat = (id: number): boolean => {
    const boat = boats.boats.find((b) => b.id === id);
    if (!boat || !canMount('boat', 0)) return false; // one seat, occupied check (B4)
    ridingBoatId = id;
    return true;
  };
  const dismountBoat = (b: Body, boat: Boat): void => {
    ridingBoatId = null;
    boatInputs.set(boat.id, {}); // stop paddling
    b.x = boat.x + 0.9; b.y = boat.y + 0.4; b.z = boat.z; // step off to the side
    b.vx = b.vy = b.vz = 0; b.onGround = false;
    playerState.fallPeak = b.y;
  };
  /** One tick while riding: sneak dismounts, else steer + snap the body to the seat. */
  const rideBoatTick = (b: Body, boat: Boat): void => {
    if (game.locked && game.input.sneak) { dismountBoat(b, boat); return; }
    boatInputs.set(boat.id, { forward: game.input.forward, back: game.input.back, left: game.input.left, right: game.input.right });
    const seat = passengerPosition({ kind: 'boat', x: boat.x, y: boat.y, z: boat.z, yaw: boat.yaw });
    b.x = seat.x; b.y = seat.y; b.z = seat.z;
    b.vx = b.vy = b.vz = 0; b.onGround = false;
    playerState.fallPeak = b.y;
  };
  /** The lowest seat on `vehicleId` nobody is in yet, per the server's roster. The
   *  server re-checks it — this is only so we ASK for a plausible seat rather than
   *  always seat 0 and always losing the race on a camel. */
  const freeSeatOn = (vehicleId: number, kind: VehicleKind): number => {
    const taken = new Set((net?.riders ?? []).filter((r) => r.vehicleId === vehicleId).map((r) => r.seat));
    for (let s = 0; s < SEAT_CAPACITY[kind]; s++) if (!taken.has(s)) return s;
    return 0; // full — the server will refuse, which is the right answer
  };
  // ── Land-mount riding (M19): the player rides a saddled mob — its AI wish is
  //    replaced by the rider's input (es.controlled), and the body is snapped to the
  //    seat each tick. Sneak dismounts. ──
  const dismountMount = (b: Body, mount: Entity): void => {
    if (net) net.sendMount(0); // the server owns the roster; tell it we stepped off
    ridingMountId = null;
    netRiderSeat = 0;
    b.x = mount.x + 0.9; b.y = mount.y + 0.5; b.z = mount.z; // step off to the side
    b.vx = b.vy = b.vz = 0; b.onGround = false;
    playerState.fallPeak = b.y;
  };
  const rideMountTick = (b: Body, mount: Entity): void => {
    if (game.locked && game.input.sneak) { dismountMount(b, mount); return; }
    const kind = mountKindOf(mount.type);
    if (kind && game.locked) {
      // A horse winds up a charged jump: hold jump to build it, release to leap,
      // scaled by this horse's own rolled jump attribute (M19).
      if (chargesJump(kind)) {
        const held = game.input.jump;
        const charge = chargeJump(mount.data.jumpCharge ?? 0, held);
        if (!held && (mount.data.jumpCharge ?? 0) > 0 && mount.onGround) {
          const up = jumpImpulse(mount.data.jumpCharge!, mount.data.jumpMul ?? 1);
          if (up > 0) { mount.vy = up; mount.onGround = false; }
        }
        mount.data.jumpCharge = charge;
      }
      // A ridden camel dashes forward on the sprint key, on its own cooldown (M19).
      if (kind === 'camel') {
        mount.data.dashCd = Math.max(0, (mount.data.dashCd ?? 0) - 1);
        if (game.input.sprint && camelCanDash(mount.data.dashCd ?? 0)) {
          const imp = camelDashImpulse();
          mount.vx += -Math.sin(mount.yaw) * imp;
          mount.vz += -Math.cos(mount.yaw) * imp;
          mount.vy = Math.max(mount.vy, camelDashHop());
          mount.onGround = false;
          mount.data.dashCd = camelDashCooldown();
        }
      }
    }
    // Seat 0 solo; in net mode the server's roster says which seat is ours, so a
    // camel's second rider sits BEHIND the first instead of inside them.
    const seat = kind
      ? passengerPosition({ kind, x: mount.x, y: mount.y, z: mount.z, yaw: mount.yaw }, net ? netRiderSeat : 0)
      : { x: mount.x, y: mount.y + 1.0, z: mount.z };
    b.x = seat.x; b.y = seat.y; b.z = seat.z;
    b.vx = b.vy = b.vz = 0; b.onGround = mount.onGround; // ride the mount's ground state (no fall damage)
    playerState.fallPeak = b.y;
  };
  /** Remove one of item `name` from anywhere in the inventory (E2 bow → arrow). */
  const consumeOneItem = (name: string): boolean => {
    const id = registries.items.idOf.get(name);
    if (id === undefined) return false;
    const inv = playerState.inventory;
    for (let i = 0; i < inv.size; i++) {
      const s = inv.get(i);
      if (!isEmpty(s) && s.item === id) { inv.set(i, s.count <= 1 ? emptyStack() : { ...s, count: s.count - 1 }); return true; }
    }
    return false;
  };
  /** Fire an arrow from a bow (E2, M17): Power scales the damage, Infinity fires
   *  without spending an arrow, Flame lights it; the bow takes durability. Needs an
   *  arrow (unless creative / Infinity). */
  const shootBow = (): boolean => {
    const caps = modeCaps(playerState.gameMode);
    const ench = playerState.inventory.held().components?.enchants;
    const infinite = infiniteArrows(enchantLevelOf(ench, 'infinity'));
    if (!caps.infiniteResources && !infinite && !consumeOneItem('arrow')) return false;
    const power = enchantLevelOf(ench, 'power');
    const dmg = Math.max(1, Math.round(PROJECTILE.ARROW_SPEED * PROJECTILE.ARROW_DAMAGE_PER_SPEED * powerDamageMultiplier(power)));
    const dir = lookDir();
    const s = PROJECTILE.ARROW_SPEED;
    const ex = player.body.x, ey = player.body.y + PLAYER.EYE_HEIGHT, ez = player.body.z;
    const flame = flameArrows(enchantLevelOf(ench, 'flame'));
    entities.spawnProjectile('arrow', ex, ey, ez, dir.x * s, dir.y * s, dir.z * s, -1, dmg,
      flame ? { id: 'fire', amplifier: 0, ticks: 100 } : undefined);
    if (caps.survivalHunger) damageHeldTool(); // bow durability
    return true;
  };
  /** Mount the nearest boat within reach in front of the player (right-click, B4). */
  const tryMountBoat = (): boolean => {
    let best: Boat | null = null; let bestD = 3;
    for (const boat of boats.boats) {
      const d = Math.hypot(boat.x - player.body.x, boat.y - player.body.y, boat.z - player.body.z);
      if (d < bestD) { best = boat; bestD = d; }
    }
    return best ? mountBoat(best.id) : false;
  };

  // ── Aircraft (M32, AVI-2): an opt-in flyable vehicle on aviation Atlas worlds.
  //    The sim owns the flight model; the host binds pilot input, chase-cams it,
  //    and resets it to the runway on a crash (E-28). ──
  const aircraftWorld: AircraftWorld = { isSolid: (x, y, z) => world.isSolid(x, y, z) };
  /** A runway spawn: the midpoint of the first runway, on its flat surface, yawed
   *  down the strip; falls back to the player's column if there is no runway. */
  const runwaySpawn = (): { x: number; y: number; z: number; yaw: number } => {
    const rw = atlasCity?.aeroways.find((a) => a.kind === 'runway');
    if (rw) {
      // Spawn at the near threshold (a few blocks in so the wheels are on the strip),
      // nose pointed down the full length of the runway — a full takeoff roll ahead.
      const [ax, az] = rw.points[0]!, [bx, bz] = rw.points[rw.points.length - 1]!;
      const dx = bx - ax, dz = bz - az;
      const len = Math.hypot(dx, dz) || 1;
      const inset = Math.min(6, len * 0.25);
      const sx = Math.round(ax + (dx / len) * inset), sz = Math.round(az + (dz / len) * inset);
      // The threshold can be far from the loaded spawn chunk, so force-generate its
      // column (with the runway painted) before reading the surface — else surfaceY
      // returns null and the plane would spawn at sea level, underground.
      world.getChunk(Math.floor(sx / WORLD.SECTION), Math.floor(sz / WORLD.SECTION), true);
      const sy = world.surfaceY(sx, sz) ?? WORLD.SEA_LEVEL + 1;
      return { x: sx + 0.5, y: sy + AVI.HALF_H, z: sz + 0.5, yaw: Math.atan2(-dx, -dz) };
    }
    const b = player.body;
    return { x: b.x, y: (world.surfaceY(Math.floor(b.x), Math.floor(b.z)) ?? b.y) + AVI.HALF_H, z: b.z, yaw: player.yaw };
  };
  /** Ensure a parked aircraft exists on the runway (spawned lazily on first enter). */
  const ensureAircraft = (): Aircraft => {
    if (aircraft.count > 0) return aircraft.aircraft[0]!;
    const s = runwaySpawn();
    return aircraft.spawn(s.x, s.y, s.z, s.yaw);
  };
  const flyingAircraft = (): Aircraft | null =>
    flyingAircraftId !== null ? aircraft.aircraft.find((a) => a.id === flyingAircraftId) ?? null : null;
  const enterAircraft = (): boolean => {
    if (!aviationEnabled) return false;
    const a = ensureAircraft();
    flyingAircraftId = a.id;
    aviationHud.setVisible(true);
    return true;
  };
  const exitAircraft = (): void => {
    const a = flyingAircraft();
    flyingAircraftId = null;
    aircraftInputOverride = null;
    autopilotTargets = null; autopilot.reset();
    aviationHud.setVisible(false);
    if (a) { // step the player out beside the aircraft, on the ground
      const b = player.body;
      b.x = a.x + 1.5; b.z = a.z;
      b.y = (world.surfaceY(Math.floor(b.x), Math.floor(b.z)) ?? a.y) + 0.0;
      b.vx = b.vy = b.vz = 0; b.onGround = true; playerState.fallPeak = b.y;
    }
  };
  /** Map the pilot's movement keys to flight controls (or the E2E/autopilot override):
   *  pull back = nose up, A/D = roll, Space/Shift = throttle, sprint = wheel brake. */
  const aircraftInputFor = (): AircraftInput => {
    if (aircraftInputOverride) return aircraftInputOverride;
    if (!game.locked || game.uiOpen) return {};
    const i = game.input;
    return {
      pitch: (i.back ? 1 : 0) - (i.forward ? 1 : 0),
      roll: (i.right ? 1 : 0) - (i.left ? 1 : 0),
      throttleUp: i.jump, throttleDown: i.sneak, brake: i.sprint,
    };
  };
  /** One tick while flying: step the aircraft, bind the player to it, handle a crash
   *  (damage + reset to the runway, E-28), and fire the two aviation lesson triggers
   *  (M54.1, §29.4): `aircraft_landed` on a gentle touchdown (never a crash) with
   *  `runway` true only over pavement/centerline paint, and `autopilot_hold` every
   *  tick the autopilot is engaged, carrying the measured streak + current error. */
  const flyAircraftTick = (a: Aircraft): void => {
    const wasOnGround = aircraftWasOnGround;
    // Autopilot (AVI-3) flies when engaged; otherwise the pilot's keys / E2E override.
    const input = autopilotTargets ? autopilot.step(a, autopilotTargets) : aircraftInputFor();
    aircraft.tick(aircraftWorld, () => input);
    const b = player.body;
    b.x = a.x; b.y = a.y; b.z = a.z; b.vx = b.vy = b.vz = 0; b.onGround = a.onGround;
    playerState.fallPeak = b.y;
    if (a.crashed) {
      playerState.applyDamage(AVI.CRASH_DAMAGE); // E-28: a hard impact hurts the pilot
      const s = runwaySpawn();
      a.x = s.x; a.y = s.y; a.z = s.z; a.yaw = s.yaw; a.pitch = 0; a.roll = 0;
      a.vx = a.vy = a.vz = 0; a.throttle = 0; a.onGround = true; a.crashed = false;
      aircraftWasOnGround = true;
    } else {
      if (isLanding(wasOnGround, a)) {
        const groundName = registries.states.blockName[world.getBlockState(Math.floor(a.x), Math.floor(a.y) - 1, Math.floor(a.z))];
        fireEduTrigger({ kind: 'aircraft_landed', runway: isRunwaySurface(groundName) });
      }
      aircraftWasOnGround = a.onGround;
    }
    if (autopilotTargets) {
      const error = Math.abs(a.y - autopilotTargets.altitude);
      autopilotHoldTicks = holdStreak(autopilotHoldTicks, error, AVI.AUTOPILOT_HOLD_ERROR);
      fireEduTrigger({ kind: 'autopilot_hold', ticks: autopilotHoldTicks, error });
    } else {
      autopilotHoldTicks = 0;
    }
  };

  // ── SAR / ground-team trainer (M36, SAR-1..3): the ELT beacon a search crew
  //    homes on. Deploy is instructor/creative; the RSSI + terrain shadow + the
  //    find objective are pure (sim/education/sar.ts); the host renders + scores. ──
  /** Deploy an ELT beacon at a world position (creative/instructor, SAR-2). */
  const deployBeaconAt = (x: number, y: number, z: number): Beacon => beacons.deploy(x, y, z);
  /** Local player id for objective/DF scoring (net id in MP, else 'local'). */
  const sarSearcher = (): string => (net ? `p${net.welcome.playerId}` : 'local');
  /** The homed beacon (nearest in range) + its signal for a searcher position. */
  const sarSignalAt = (x: number, y: number, z: number): {
    beacon: Beacon | null; dist: number | null; rssi: number; bars: number; shadow: number;
  } => {
    const pos = { x, y, z };
    const beacon = beacons.nearestInRange(pos);
    if (!beacon) return { beacon: null, dist: null, rssi: -Infinity, bars: 0, shadow: 0 };
    const dist = beaconDist(pos, beacon);
    const shadow = terrainShadow(pos, beacon, sarGroundY);
    return { beacon, dist, rssi: sarRssi(dist, shadow), bars: signalBars(dist, shadow, SAR_BARS), shadow };
  };
  /** One SAR objective tick for the local searcher (dwell → find, SAR-3). */
  const tickSarObjective = (): void => {
    if (beacons.count === 0) { sarObjective.update(sarSearcher(), player.body, null); return; }
    const b = player.body;
    const homed = beacons.nearestInRange({ x: b.x, y: b.y, z: b.z });
    if (sarObjective.update(sarSearcher(), { x: b.x, y: b.y, z: b.z }, homed)) {
      game.print('§aBeacon located — find scored!'); // SAR-3
      fireEduTrigger({ kind: 'beacon_found' }); // M54.2 (§29.4) — fires once, at the dwell edge, not per tick
    }
  };
  /** Compass + coordinate readout + ELT bars for the SAR HUD (SAR-1). */
  const sarReadout = (): SarReadout => {
    const b = player.body;
    const headingDeg = ((player.yaw * 180 / Math.PI) % 360 + 360) % 360;
    const lines: string[] = [];
    if (world.solar && meta.atlas) {
      const ll = unprojectBlock(b.x, b.z, { lat: world.solar.lat, lon: world.solar.lon }, meta.atlas.scale);
      lines.push(latLonToMgrs(ll.lat, ll.lon, 5));
      lines.push(`${ll.lat.toFixed(5)}, ${ll.lon.toFixed(5)}`);
    } else {
      lines.push(`X ${b.x.toFixed(0)}  Y ${b.y.toFixed(0)}  Z ${b.z.toFixed(0)}`);
    }
    const sig = sarSignalAt(b.x, b.y, b.z);
    const searcher = sarSearcher();
    return {
      headingDeg, lines, bars: sig.bars, maxBars: SAR_BARS, dist: sig.dist,
      dwellFrac: Math.min(1, sarObjective.dwellOf(searcher) / SAR.DWELL), found: sarObjective.hasFound(searcher),
    };
  };
  /** Does the player carry `name` anywhere in the inventory (the Becker is an
   *  aircraft-mounted instrument — carried, not necessarily held)? */
  const hasItem = (name: string): boolean => {
    const id = registries.items.idOf.get(name);
    if (id === undefined) return false;
    for (let s = 0; s < INV.SIZE; s++) { const st = playerState.inventory.get(s); if (!isEmpty(st) && st.item === id) return true; }
    return false;
  };
  /** Direction-finder reading (SAR-4): relative bearing to the nearest in-range
   *  beacon (terrain-shadowed) from a position + facing yaw, and the handheld LED
   *  index; both null when there is no usable signal. */
  const sarBearingAt = (x: number, y: number, z: number, yaw: number): { relBearing: number | null; led: number | null } => {
    const pos = { x, y, z };
    const b = beacons.nearestInRange(pos);
    const shadow = b ? terrainShadow(pos, b, sarGroundY) : 0;
    const relBearing = bearingToBeacon(pos, yaw, b, shadow);
    return { relBearing, led: ledIndex(relBearing) };
  };
  /** One frame of the DF instruments: the airborne Becker dial while flying with a
   *  becker_df aboard, else the handheld 9-LED bar while holding a sar_df (SAR-4). */
  const updateDfInstruments = (): void => {
    const flying = flyingAircraft();
    if (flying && hasItem('becker_df')) {
      beckerDial.setVisible(true); sarDfHud.setVisible(false);
      beckerDial.update(sarBearingAt(flying.x, flying.y, flying.z, flying.yaw).relBearing);
    } else if (!flying && heldItemName() === 'sar_df') {
      sarDfHud.setVisible(true); beckerDial.setVisible(false);
      const b = player.body;
      sarDfHud.update(sarBearingAt(b.x, b.y, b.z, player.yaw).led);
    } else {
      beckerDial.setVisible(false); sarDfHud.setVisible(false);
    }
  };

  // ── Classroom / guided-build (M37, CLS-1..3): compile a schematic into a ghost
  //    blueprint, place it (translucent overlay, never real blocks), run the cheap
  //    periodic placed-vs-plan diff, and toast completion/unlock. Pure diff + overlay
  //    (sim/education/classroom.ts); the host renders + owns the edit-log student id. ──
  /** The acting student for classroom attribution (net id in MP, else 'local'). */
  const classroomStudent = (): string => (net ? `p${net.welcome.playerId}` : 'local');
  /** Parse + compile a lesson DSL to a blueprint pinned at (x,y,z)/rot, or an error. */
  const compileLesson = (dsl: string, x: number, y: number, z: number, rot: number): { bp: Blueprint | null; error?: string } => {
    let schem;
    try { schem = parseSchematic(dsl); } catch { return { bp: null, error: 'could not parse blueprint' }; }
    const bp = compileBlueprint(schem, [x, y, z], rot, structResolver, STATE_AIR);
    if (!bp) return { bp: null, error: `blueprint too large (over ${CLS.MAX_CELLS} blocks)` };
    if (bp.cells.length === 0) return { bp: null, error: 'blueprint is empty' };
    return { bp };
  };
  const placeBlueprintImpl = (dsl: string, x: number, y: number, z: number, rot: number): { ok: boolean; cells: number; error?: string } => {
    const { bp, error } = compileLesson(dsl, x, y, z, rot);
    if (!bp) return { ok: false, cells: 0, error };
    classroom.place(bp);
    blueprintRenderer.clear();
    return { ok: true, cells: bp.cells.length };
  };
  const setCourseImpl = (entries: { dsl: string; x: number; y: number; z: number; rot?: number }[]): { ok: boolean; cells: number; error?: string } => {
    const bps: Blueprint[] = [];
    for (const e of entries) {
      const { bp, error } = compileLesson(e.dsl, e.x, e.y, e.z, e.rot ?? 0);
      if (!bp) return { ok: false, cells: 0, error };
      bps.push(bp);
    }
    if (bps.length === 0) return { ok: false, cells: 0, error: 'no lessons' };
    classroom.setCourse(bps);
    blueprintRenderer.clear();
    return { ok: true, cells: bps[0]!.cells.length };
  };
  /** Where a placed blueprint's origin sits: the aimed surface + 1, else a few
   *  blocks in front of the player at foot level (CLS-1 instructor placement). */
  const blueprintAimImpl = (): { x: number; y: number; z: number } => {
    const eye = { x: player.body.x, y: player.body.y + PLAYER.EYE_HEIGHT, z: player.body.z };
    const hit = raycastVoxel((x, y, z) => world.isRaycastTarget(x, y, z), eye, lookDir(), REACH.BLOCK);
    if (hit) return { x: hit.x, y: hit.y + 1, z: hit.z };
    const d = lookDir();
    return { x: Math.floor(player.body.x + d.x * 4), y: Math.floor(player.body.y), z: Math.floor(player.body.z + d.z * 4) };
  };
  const blueprintStateImpl = (): ClassroomSnapshot | null => {
    const bp = classroom.active();
    if (!bp) return null;
    const counts = diffBlueprint(bp, worldRead, STATE_AIR);
    return {
      name: bp.name, counts, progress: progressOf(counts), complete: isComplete(counts),
      students: attributeProgress(bp, worldRead, STATE_AIR, classroomLog), done: classroom.done,
    };
  };
  /** The cheap periodic placed-vs-plan pass (CLS-2/3): advance the completion latch
   *  every CLS.DIFF_INTERVAL_TICKS and toast the 100% edge + any unlock (UI-7). */
  const tickClassroom = (): void => {
    if (classroom.length === 0) return;
    if (++classroomDiffAcc < CLS.DIFF_INTERVAL_TICKS) return;
    classroomDiffAcc = 0;
    const activeName = classroom.active()?.name ?? '';
    const u = classroom.update(worldRead, STATE_AIR);
    if (u.justCompleted) {
      advToasts.note('Blueprint Complete!', activeName);
      game.print(`§aBlueprint “${activeName}” complete!`); // CLS-3
      if (u.unlocked) { blueprintRenderer.clear(); game.print(`§eNext blueprint unlocked: ${u.unlocked.name}`); }
    }
  };

  // ── SAR vehicles (M36.3, SAR-5): the crew's 4-seat helicopter + 8-seat van. The
  //    pure models fly/drive; the host binds the pilot, seats riders (mount caps),
  //    chase-cams, and resets a crashed helicopter to its spawn (E-28). ──
  const heliWorld: HeliWorld = { isSolid: (x, y, z) => world.isSolid(x, y, z) };
  // Atlas free-drives (no `drivable`); Earth mode (M40) adds the road-raster gate (E-35).
  const vanWorld: VanWorld = { groundTop: (x, z) => world.surfaceY(Math.floor(x), Math.floor(z)) ?? WORLD.SEA_LEVEL };
  const pilotedHeli = (): Helicopter | null => pilotedHeliId !== null ? helicopters.helicopters.find((h) => h.id === pilotedHeliId) ?? null : null;
  const pilotedVan = (): Van | null => pilotedVanId !== null ? vans.vans.find((v) => v.id === pilotedVanId) ?? null : null;
  /** A parked spawn on the ground under the player (helis/vans start beside you). */
  const groundSpawn = (dx: number, dz: number): { x: number; y: number; z: number } => {
    const b = player.body, x = b.x + dx, z = b.z + dz;
    return { x, y: (world.surfaceY(Math.floor(x), Math.floor(z)) ?? b.y) + 0.5, z };
  };
  const spawnHeli = (x: number, y: number, z: number, yaw = 0): Helicopter => helicopters.spawn(x, y, z, yaw);
  const spawnVan = (x: number, y: number, z: number, yaw = 0): Van => vans.spawn(x, y, z, yaw);
  /** Board an already-spawned vehicle as pilot (seat 0); false if it is full. */
  const enterHeli = (id: number): boolean => {
    const h = helicopters.helicopters.find((k) => k.id === id); if (!h) return false;
    if (!canMount('helicopter', heliRiders.get(id) ?? 0)) return false;
    pilotedHeliId = id; heliRiders.set(id, (heliRiders.get(id) ?? 0) + 1); aviationHud.setVisible(true); return true;
  };
  const enterVan = (id: number): boolean => {
    const v = vans.vans.find((k) => k.id === id); if (!v) return false;
    if (!canMount('van', vanRiders.get(id) ?? 0)) return false;
    pilotedVanId = id; vanRiders.set(id, (vanRiders.get(id) ?? 0) + 1); return true;
  };
  /** Seat one more passenger; false when the vehicle is full (mount caps, SAR-5). */
  const boardHeli = (id: number): boolean => {
    if (!helicopters.helicopters.some((k) => k.id === id) || !canMount('helicopter', heliRiders.get(id) ?? 0)) return false;
    heliRiders.set(id, (heliRiders.get(id) ?? 0) + 1); return true;
  };
  const boardVan = (id: number): boolean => {
    if (!vans.vans.some((k) => k.id === id) || !canMount('van', vanRiders.get(id) ?? 0)) return false;
    vanRiders.set(id, (vanRiders.get(id) ?? 0) + 1); return true;
  };
  const stepOutBeside = (x: number, y: number, z: number): void => {
    const b = player.body; b.x = x + 1.5; b.z = z;
    b.y = (world.surfaceY(Math.floor(b.x), Math.floor(b.z)) ?? y); b.vx = b.vy = b.vz = 0; b.onGround = true; playerState.fallPeak = b.y;
  };
  const exitHeli = (): void => {
    const h = pilotedHeli(); const id = pilotedHeliId; pilotedHeliId = null; aviationHud.setVisible(false);
    if (id !== null) heliRiders.set(id, Math.max(0, (heliRiders.get(id) ?? 1) - 1));
    if (h) stepOutBeside(h.x, h.y, h.z);
  };
  const exitVan = (): void => {
    const v = pilotedVan(); const id = pilotedVanId; pilotedVanId = null;
    if (id !== null) vanRiders.set(id, Math.max(0, (vanRiders.get(id) ?? 1) - 1));
    if (v) stepOutBeside(v.x, v.y, v.z);
  };
  /** Pilot input for the helicopter from the movement keys (or an E2E override). */
  let heliInputOverride: HeliInput | null = null;
  let vanInputOverride: VanInput | null = null;
  const heliInputFor = (): HeliInput => {
    if (heliInputOverride) return heliInputOverride;
    if (!game.locked || game.uiOpen) return {};
    const i = game.input;
    return {
      collectiveUp: i.jump, collectiveDown: i.sneak,
      pitch: (i.forward ? 1 : 0) - (i.back ? 1 : 0), // W = nose-down = forward
      roll: (i.right ? 1 : 0) - (i.left ? 1 : 0), yaw: i.sprint ? 1 : 0,
    };
  };
  const vanInputFor = (): VanInput => {
    if (vanInputOverride) return vanInputOverride;
    if (!game.locked || game.uiOpen) return {};
    const i = game.input;
    return { throttle: i.forward || i.back, reverse: i.back, brake: i.sneak, steer: (i.right ? 1 : 0) - (i.left ? 1 : 0) };
  };
  const pilotHeliTick = (h: Helicopter): void => {
    helicopters.tick(heliWorld, () => heliInputFor());
    const b = player.body; b.x = h.x; b.y = h.y; b.z = h.z; b.vx = b.vy = b.vz = 0; b.onGround = h.onGround; playerState.fallPeak = b.y;
    if (h.crashed) {
      playerState.applyDamage(HELI.CRASH_DAMAGE);
      const s = groundSpawn(0, 0); h.x = s.x; h.y = s.y; h.z = s.z; h.vx = h.vy = h.vz = 0;
      h.collective = HELI.HOVER_COLLECTIVE; h.pitchTilt = h.rollTilt = 0; h.onGround = true; h.crashed = false;
    }
  };
  const pilotVanTick = (v: Van): void => {
    vans.tick(vanWorld, () => vanInputFor());
    const b = player.body; b.x = v.x; b.y = v.y + 1; b.z = v.z; b.vx = b.vy = b.vz = 0; b.onGround = true; playerState.fallPeak = b.y;
  };
  /** Adapt a helicopter to the aircraft-shaped PFD (M32 AviationHud reuse). */
  const heliAsAircraft = (h: Helicopter): Aircraft => ({
    id: h.id, x: h.x, y: h.y, z: h.z, vx: h.vx, vy: h.vy, vz: h.vz,
    yaw: h.yaw, pitch: h.pitchTilt, roll: h.rollTilt,
    throttle: h.collective / HELI.COLLECTIVE_MAX, onGround: h.onGround, crashed: h.crashed,
  });

  // ── Special-cart hazards + automation (RED-6): a primed tnt cart's fuse burns
  //    down to an explosion; a hopper cart vacuums loose drops off the track. ──
  const tickMinecartHazards = (): void => {
    for (const cart of [...minecarts.carts]) {
      if (cart.kind === 'tnt_minecart' && (cart.fuse ?? 0) > 0) {
        cart.fuse = (cart.fuse ?? 0) - 1;
        if (cart.fuse! <= 0) { entities.explode(makeFrame(), cart.x, cart.y + 0.5, cart.z, TNT.POWER); minecarts.remove(cart); }
      } else if (cart.kind === 'hopper_minecart' && cart.items) {
        vacuumIntoCart(cart);
      }
    }
  };
  /** Pull loose item drops within the hopper cart's cell into its inventory (RED-6). */
  const vacuumIntoCart = (cart: { x: number; y: number; z: number; items?: (CartSlot | null)[] }): void => {
    if (!cart.items) return;
    for (let i = drops.drops.length - 1; i >= 0; i--) {
      const d = drops.drops[i]!;
      if (Math.abs(d.x - cart.x) > 0.75 || Math.abs(d.z - cart.z) > 0.75 || Math.abs(d.y - cart.y) > 1) continue;
      if (addToCart(cart.items, d.stack.item, d.stack.count)) drops.drops.splice(i, 1);
    }
  };
  /** Merge a stack into a cart inventory (stacking then first-empty); returns true
   *  if all of it fit. */
  const addToCart = (slots: (CartSlot | null)[], item: number, count: number): boolean => {
    const max = maxStackOf(item);
    let left = count;
    for (const s of slots) { if (s && s.item === item && s.count < max) { const add = Math.min(max - s.count, left); s.count += add; left -= add; if (left <= 0) return true; } }
    for (let i = 0; i < slots.length && left > 0; i++) { if (!slots[i]) { const add = Math.min(max, left); slots[i] = { item, count: add }; left -= add; } }
    return left <= 0;
  };

  // ── Farming (M11.1): farmland moisture, crop growth, hoe/seed/bonemeal use. ──
  const cropStateOf = (block: string, age: number): number => registries.states.idByKey.get(`${block}[age=${age}]`) ?? -1;
  const farmlandStateOf = (moisture: number): number => registries.states.idByKey.get(`farmland[moisture=${moisture}]`) ?? -1;
  const composterStateOf = (level: number): number => registries.states.idByKey.get(`composter[level=${level}]`) ?? -1;
  const cauldronStateOf = (level: number): number => registries.states.idByKey.get(`cauldron[level=${level}]`) ?? -1;
  const ageOf = (state: number): number => Number(registries.states.props[state]?.age ?? '0');
  const consumeHeld = (): void => {
    if (modeCaps(playerState.gameMode).infiniteResources) return;
    const slot = playerState.inventory.heldSlot();
    const s = playerState.inventory.get(slot);
    playerState.inventory.set(slot, s.count <= 1 ? emptyStack() : { ...s, count: s.count - 1 });
  };
  const setBlockRemesh = (x: number, y: number, z: number, state: number): void => {
    world.setBlockState(x, y, z, state);
    chunkMeshes.markDirty(x, z);
  };
  const waterWithin = (x: number, y: number, z: number, r: number): boolean => {
    for (let dx = -r; dx <= r; dx++) for (let dz = -r; dz <= r; dz++) for (let dy = 0; dy <= 1; dy++) {
      if (world.isFluid(x + dx, y + dy, z + dz)) return true;
    }
    return false;
  };

  const budStateOf = (block: string, facing: string): number => registries.states.idByKey.get(`${block}[facing=${facing}]`) ?? -1;
  /** One budding_amethyst growth attempt on face `dirIndex` (M19.1): probe the
   *  neighbour there and sprout/advance a bud per the pure rule. Returns success. */
  const growBudding = (x: number, y: number, z: number, roll01: number, dirIndex: number): boolean => {
    const dir = GROW_DIRS[dirIndex];
    if (!dir) return false;
    const nx = x + dir.dx, ny = y + dir.dy, nz = z + dir.dz;
    const nState = world.getBlockState(nx, ny, nz);
    const nName = registries.states.blockName[nState] ?? null;
    const nFacing = registries.states.props[nState]?.facing ?? null;
    const g = rollBudGrowth(roll01, dirIndex, nName, nFacing);
    if (!g) return false;
    const ns = budStateOf(g.block, dir.facing);
    if (ns < 0) return false;
    setBlockRemesh(nx, ny, nz, ns);
    return true;
  };

  // Dripstone growth (M19.1): a downward stalactite tip fed by a water-capped
  //  dripstone block grows one segment down (its tip descends, leaving a frustum).
  const dripstoneStateOf = (dir: string, thickness: string): number =>
    registries.states.idByKey.get(`pointed_dripstone[vertical_direction=${dir},dripstone_thickness=${thickness}]`) ?? -1;
  const dripCapFed = (x: number, y: number, z: number): boolean => {
    for (let yy = y; yy < y + 8; yy++) {
      const nm = registries.states.blockName[world.getBlockState(x, yy + 1, z)];
      if (nm === 'pointed_dripstone') continue; // climb the stalactite
      if (nm === 'dripstone_block') return registries.states.blockName[world.getBlockState(x, yy + 2, z)] === 'water'; // water over the cap
      return false;
    }
    return false;
  };
  const growDripstoneTip = (x: number, y: number, z: number, roll01: number): boolean => {
    const st = world.getBlockState(x, y, z);
    if (registries.states.blockName[st] !== 'pointed_dripstone') return false;
    const p = registries.states.props[st];
    if (p?.vertical_direction !== 'down' || p?.dripstone_thickness !== 'tip') return false;
    const belowName = registries.states.blockName[world.getBlockState(x, y - 1, z)] ?? null;
    if (!dripstoneGrows(roll01, 'down', dripCapFed(x, y, z), belowName)) return false;
    const newTip = dripstoneStateOf('down', 'tip'), frustum = dripstoneStateOf('down', 'frustum');
    if (newTip < 0 || frustum < 0) return false;
    edit(x, y, z, frustum); // the old tip hardens into a frustum
    edit(x, y - 1, z, newTip); // and a fresh tip drips below
    return true;
  };

  /** Replace a copper block in place across an oxidation/wax/scrape transform,
   *  PRESERVING its props (a bulb's `lit`, a door's `facing`/`open`) — only the
   *  leading block-name in the state key is swapped (MB.1). Plain-cube copper has
   *  no props, so this is identical to a default swap for them. */
  const setCopperBlock = (x: number, y: number, z: number, name: string): void => {
    const st = world.getBlockState(x, y, z);
    const curKey = registries.states.key[st];
    const curName = registries.states.blockName[st];
    const nextKey = curKey && curName ? name + curKey.slice(curName.length) : undefined;
    const ns = (nextKey !== undefined ? registries.states.idByKey.get(nextKey) : undefined) ?? registries.states.defaultOf.get(name);
    if (ns !== undefined) edit(x, y, z, ns);
  };
  /** Scan copper within taxicab distance 4 of (x,y,z) and bucket each *other*
   *  copper block by oxidation relative to `selfStage` (Minecraft's model). */
  const scanCopperNeighbourhood = (x: number, y: number, z: number, selfStage: number): OxidationNeighbourhood => {
    let hasLesser = false, greater = 0, same = 0;
    for (let dx = -4; dx <= 4 && !hasLesser; dx++) {
      for (let dy = -4; dy <= 4 && !hasLesser; dy++) {
        for (let dz = -4; dz <= 4; dz++) {
          if (Math.abs(dx) + Math.abs(dy) + Math.abs(dz) > 4 || (dx === 0 && dy === 0 && dz === 0)) continue;
          const st = oxidationStageOf(registries.states.blockName[world.getBlockState(x + dx, y + dy, z + dz)]);
          if (st < 0) continue; // not copper
          if (st < selfStage) { hasLesser = true; break; }
          else if (st > selfStage) greater++;
          else same++;
        }
      }
    }
    return { hasLesserNeighbour: hasLesser, greaterCount: greater, sameCount: same };
  };
  /** One random-tick weathering attempt on an oxidizable copper block (MB.1). */
  const oxidizeCopperTick = (x: number, y: number, z: number, name: string): void => {
    const stage = oxidationStageOf(name);
    const nb = scanCopperNeighbourhood(x, y, z, stage);
    if (!shouldOxidize(world.tickRng.nextInt(100000) / 100000, world.tickRng.nextInt(100000) / 100000, stage, nb)) return;
    const next = weatherStep(name);
    if (next) setCopperBlock(x, y, z, next);
  };

  /** A sky-exposed, un-full cauldron gains a water level while it's raining (MB.1). */
  const rainFillCauldron = (x: number, y: number, z: number, state: number): boolean => {
    if (world.dimension !== 'overworld' || !world.weather.raining) return false;
    const level = Number(registries.states.props[state]?.level ?? '0');
    if (level >= CAULDRON_MAX || world.getSkyLight(x, y + 1, z) < 15) return false; // roofed → no rain
    const ns = cauldronStateOf(rainAccumulate(level));
    if (ns < 0) return false;
    setBlockRemesh(x, y, z, ns);
    return true;
  };

  // ── Snow layers (MB.1) ──────────────────────────────────────────────────────
  const snowStateOf = (layers: number): number => registries.states.idByKey.get(`snow[layers=${layers}]`) ?? -1;
  /** A cell is cold enough for snow when it sits at/above its biome's snow line
   *  (the only cold terrain in this world; matches where gen paints snow caps). */
  const isColdAt = (x: number, y: number, z: number): boolean => {
    const biome = registries.biomes?.byName.get(world.biomeNameAt(x, z));
    return biome?.snowLine != null && y >= biome.snowLine;
  };
  /** Whether a solid block can hold a snow coat on its top face. */
  const canHoldSnow = (name: string | undefined): boolean => {
    const def = name ? registries.blocks.byName.get(name) : undefined;
    if (!def || def.shape !== 'cube') return false;
    return def.material !== 'air' && def.material !== 'water' && def.material !== 'lava';
  };
  /** Snowballs a broken snow layer drops (one per layer, shovel only, MB.1). */
  const snowLayerDrops = (state: number, tool: HeldTool | null): ItemStack[] => {
    const n = snowballsFor(snowLayersOf(registries.states.props[state]), tool?.toolClass === 'shovel');
    const id = registries.items.idOf.get('snowball');
    return n > 0 && id !== undefined ? [makeStack(id, n)] : [];
  };
  /** One random-tick step for an existing snow layer: melt under bright block
   *  light, else accumulate a layer while it is snowing on this cold column. */
  const snowRandomTick = (x: number, y: number, z: number, state: number): void => {
    if (snowShouldMelt(world.getBlockLight(x, y, z))) { setBlockRemesh(x, y, z, STATE_AIR); return; }
    if (world.dimension !== 'overworld' || !world.weather.raining) return;
    const nl = snowAccumulate(snowLayersOf(registries.states.props[state]),
      { snowing: true, seesSky: world.getSkyLight(x, y, z) === 15, cold: isColdAt(x, y, z) });
    if (nl !== null) { const ns = snowStateOf(nl); if (ns >= 0) setBlockRemesh(x, y, z, ns); }
  };
  /** Coat a bare, sky-exposed cold surface with a first snow layer under snowfall. */
  const trySnowfallCoat = (x: number, y: number, z: number, name: string | undefined): boolean => {
    if (registries.states.blockName[world.getBlockState(x, y + 1, z)] !== 'air' || !canHoldSnow(name)) return false;
    if (!snowFormsOnSurface({ snowing: true, seesSky: world.getSkyLight(x, y + 1, z) === 15, cold: isColdAt(x, y + 1, z) })) return false;
    const ns = snowStateOf(1);
    if (ns < 0) return false;
    setBlockRemesh(x, y + 1, z, ns);
    return true;
  };

  /** Concrete powder solidifies into concrete the moment a neighbour is water (MB.1). */
  const trySolidifyConcrete = (x: number, y: number, z: number, name: string | undefined): boolean => {
    const concrete = name ? concreteOf(name) : null;
    if (concrete === null) return false;
    const neighbours = [[1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1]]
      .map(([dx, dy, dz]) => registries.states.blockName[world.getBlockState(x + dx!, y + dy!, z + dz!)] ?? 'air');
    if (!solidifiesInWater(neighbours)) return false;
    const ns = registries.states.defaultOf.get(concrete);
    if (ns === undefined) return false;
    setBlockRemesh(x, y, z, ns);
    return true;
  };

  /** Block-name view for MB.2 growth (vertical plants / stems / nether wart). */
  const growthView: GrowthView = { name: (x, y, z) => registries.states.blockName[world.getBlockState(x, y, z)] ?? null };

  /** A random-tick handler (§5.5 phase 3): farmland hydration + crop growth. */
  const onRandomTick = (x: number, y: number, z: number, state: number): void => {
    const name = registries.states.blockName[state];
    // §27.4's tick placement: `randomTicks` -> block `on random_tick` (§27.9). Queued
    // like every other lifecycle hook, so it runs in the next cascade drain rather than
    // inside the random-tick sweep.
    //
    // GATED ON THE BLOCK NAME FIRST, and that is not a micro-optimisation: this handler
    // runs `TICK.RANDOM_TICK_SPEED` times per section per tick across every loaded
    // chunk, so reaching the block-entity store here — which builds a `"x,y,z"` key per
    // call — allocates tens of thousands of strings a tick and visibly stalls the boot.
    // `blockName` is an array index, and only a script_block ever gets past it.
    if (name === 'script_block') enqueueBlockHook(x, y, z, 'random_tick');
    if (name === 'budding_amethyst') {
      growBudding(x, y, z, world.tickRng.nextInt(1000) / 1000, world.tickRng.nextInt(6));
      return;
    }
    if (name === 'pointed_dripstone') { growDripstoneTip(x, y, z, world.tickRng.nextInt(1000) / 1000); return; }
    if (name === 'turtle_egg') { turtleEggRandomTick(x, y, z, state); return; } // crack → hatch (M18.1)
    if (name !== undefined && canOxidize(name)) { oxidizeCopperTick(x, y, z, name); return; }
    if (name === 'cauldron') { rainFillCauldron(x, y, z, state); return; }
    if (name === 'snow') { snowRandomTick(x, y, z, state); return; } // melt / accumulate (MB.1)
    if (trySolidifyConcrete(x, y, z, name)) return; // powder + water → concrete (MB.1)
    // A storm coats bare cold surfaces with a first snow layer (MB.1).
    if (world.dimension === 'overworld' && world.weather.raining && trySnowfallCoat(x, y, z, name)) return;
    // Nether vines: the tip extends one segment in the species' own direction.
    if (isVine(name)) {
      const cell = vineGrow(growthView, x, y, z, name!, world.tickRng.nextInt(1000) / 1000);
      if (cell) { const ns = registries.states.defaultOf.get(name!); if (ns !== undefined) setBlockRemesh(cell.x, cell.y, cell.z, ns); }
      return;
    }
    // MB.2 — plant styles beyond the age-crop table.
    if (name !== undefined && name in VERTICAL_PLANTS) {
      const cell = verticalGrow(growthView, x, y, z, name, world.tickRng.nextInt(1000) / 1000);
      if (cell) { const ns = registries.states.defaultOf.get(name); if (ns !== undefined) setBlockRemesh(cell.x, cell.y, cell.z, ns); }
      return;
    }
    if (name !== undefined && name in STEM_FRUIT) {
      const res = stemGrow(growthView, x, y, z, name, ageOf(state), world.tickRng.nextInt(1000) / 1000);
      if (res?.kind === 'age') { const ns = cropStateOf(name, res.age); if (ns >= 0) setBlockRemesh(x, y, z, ns); }
      else if (res?.kind === 'fruit') { const ns = registries.states.defaultOf.get(res.fruit); if (ns !== undefined) setBlockRemesh(res.cell.x, res.cell.y, res.cell.z, ns); }
      return;
    }
    if (name === 'nether_wart') {
      const na = netherWartGrow(growthView, x, y, z, ageOf(state), world.tickRng.nextInt(1000) / 1000);
      if (na !== null) { const ns = cropStateOf('nether_wart', na); if (ns >= 0) setBlockRemesh(x, y, z, ns); }
      return;
    }
    if (name !== undefined && name in AGE_PLANTS) {
      const na = agePlantGrow(name, ageOf(state), world.tickRng.nextInt(1000) / 1000);
      if (na !== null) { const ns = cropStateOf(name, na); if (ns >= 0) setBlockRemesh(x, y, z, ns); }
      return;
    }
    if (name === 'farmland') {
      const moisture = Number(registries.states.props[state]?.moisture ?? '0');
      const nm = nextMoisture(moisture, waterWithin(x, y, z, 4));
      if (nm !== moisture) { const ns = farmlandStateOf(nm); if (ns >= 0) setBlockRemesh(x, y, z, ns); }
      return;
    }
    const crop = cropOf(name ?? '');
    if (!crop) return;
    const age = ageOf(state);
    if (age >= crop.maxAge) return;
    if (world.getSkyLight(x, y, z) < 9 && world.getBlockLight(x, y, z) < 9) return; // crops need light
    const below = world.getBlockState(x, y - 1, z);
    const belowName = registries.states.blockName[below];
    // A hydroponic tray holds its own nutrient water: always fully moist, so it
    // grows at the wet-farmland rate without a water source anywhere near it.
    const moisture = belowName === 'hydroponic_tray' ? FARMING.MOISTURE_MAX
      : belowName === 'farmland' ? Number(registries.states.props[below]?.moisture ?? '0') : 0;
    const roll = world.tickRng.nextInt(1000) / 1000;
    // Heredity (HER-8, ARCH §28.10): a genetic plant's growth/drought genes scale the
    // same chance `shouldGrow` reads — only for a cell carrying a genetic crop's block
    // entity (created at plant time under the rule, E-55) while the rule is STILL on
    // (§28.2: off reads the table value even with genomes left in place). Every other
    // crop — no table, no entity, or the rule off — takes the untouched `shouldGrow`
    // path below with the SAME single draw either way, so D21 costs this tick nothing.
    const cropBE = heredityOn(manager.world('overworld')) && crop.genes ? blockEntities.get(x, y, z) : undefined;
    const genome = cropBE?.type === 'crop' ? decodeGenome(cropBE.data.genome, crop.genes!) : null;
    const traits = genome ? expressCrop(crop.genes!, genome) : null;
    const grows = traits ? growthRoll(moisture, traits.droughtTol, traits.growthMul, growthChance, roll) : shouldGrow(moisture, roll);
    if (grows) {
      const ns = cropStateOf(crop.block, age + 1);
      if (ns >= 0) setBlockRemesh(x, y, z, ns);
    }
  };

  /** Create a genetic crop's block entity at (x,y,z) — the seed's own genome, or a
   *  fresh roll keyed by cell so a genome-less seed (pre-rule chest, creative give)
   *  is not a dead end (HER-8, ARCH §28.10). Rule off ⇒ no block entity at all
   *  (E-55) — the genome, if any, is discarded with the seed. Shared by the real
   *  plant path and the `plantGenetic` debug/E2E seam. */
  const plantGenetics = (x: number, y: number, z: number, cropBlock: string, genome?: string): void => {
    const crop = cropOf(cropBlock);
    if (!heredityOn(manager.world('overworld')) || !crop?.genes) return;
    const bytes = genome ? decodeGenome(genome, crop.genes) : null;
    const finalGenome = bytes ?? rollGenome(crop.genes, forkRng(world.seed, SALT_HER_ROLL, x, y, z));
    const g = genome && bytes ? genome : encodeGenome(finalGenome);
    blockEntities.set({ type: 'crop', x, y, z, data: { genome: g } });
    // §28.10/D-TF-251: paint this client's own overlay immediately (solo needs no
    // round trip) and, in multiplayer, claim the quintile so every other client's
    // overlay paints it too — block entities never replicate (D-TF-136).
    const q = expressCrop(crop.genes, finalGenome).stalkQ;
    cropOverlay.set(x, y, z, q);
    net?.sendPlant(x, y, z, q);
  };

  /** CNT-4 pollination geometry, computed once (BEE.POLLINATION_RANGE never
   *  changes at runtime — sim/game/bee.ts `rangeOffsets`). */
  const pollenOffsets = rangeOffsets(BEE.POLLINATION_RANGE);
  /** Nearest `beehive` block within `range` of (x,y,z) — "an active hive", M53.4. */
  const nearestHiveWithin = (x: number, y: number, z: number, range: number): { x: number; z: number } | null => {
    let best: { x: number; z: number } | null = null, bestD2 = range * range;
    for (const [dx, dz] of pollenOffsets) {
      if (registries.states.blockName[world.getBlockState(x + dx, y, z + dz)] !== 'beehive') continue;
      const d2 = dx * dx + dz * dz;
      if (d2 < bestD2) { bestD2 = d2; best = { x: x + dx, z: z + dz }; }
    }
    return best;
  };

  /** Roll a crop's harvest by age: mature → produce + bonus seeds, else 1 seed.
   *  Heredity (HER-8, HER-9, ARCH §28.10): with the rule on and a `crop` block
   *  entity at (x,y,z), yield/seed counts are genetic and every dropped seed
   *  carries a recombined genome — self-pollinated when no mature same-crop
   *  orthogonal neighbour exists, cross-pollinated with one chosen at random
   *  otherwise. An immature break drops one seed with the plant's OWN genome, no
   *  recombination. `be` is whatever the CALLER already removed from the store at
   *  (x,y,z) — `mineBlock`'s own block-entity capture runs before this, for every
   *  block type, so this never removes it a second time. */
  const cropDrops = (crop: CropDef, age: number, x: number, y: number, z: number, be: CropBE | undefined, hydroponic = false): ItemStack[] => {
    const out: ItemStack[] = [];
    const push = (item: string, n: number, genome?: string): void => {
      const id = registries.items.idOf.get(item);
      if (id !== undefined && n > 0) out.push(makeStack(id, n, genome ? { genome } : undefined));
    };
    // §28.10/D-TF-251: the cell's tint dies with the block entity either way — a
    // no-op if it was never tinted (no entry to remove). In multiplayer, other
    // clients learn of the clear from the ordinary `Edit` this break already sends
    // (`applyEdit`'s own `clearCropTint`); this client's own view updates now.
    if (be) cropOverlay.clear(x, y, z);
    const genome = heredityOn(manager.world('overworld')) && crop.genes && be ? decodeGenome(be.data.genome, crop.genes) : null;
    if (!genome) {
      if (age >= crop.maxAge) {
        push(crop.produce, crop.produceMax + (hydroponic ? FARMING.HYDRO_YIELD_BONUS : 0));
        if (crop.seedBonus > 0) push(crop.seed, 1 + Math.floor(Math.random() * (crop.seedBonus + 1)));
      } else {
        push(crop.seed, 1);
      }
      return out;
    }
    if (age < crop.maxAge) { push(crop.seed, 1, encodeGenome(genome)); return out; }
    const traits = expressCrop(crop.genes!, genome);
    // Draw order (fixed, documented — the determinism contract): produce, then the
    // non-genetic seed-count dice (now seeded instead of `Math.random`, since a
    // genetic harvest must be reproducible), then one `recombine` per seed.
    const pollenRng = forkRng(world.seed, SALT_HER_POLLEN, x, y, z, world.tickCount);
    push(crop.produce, produceCount(crop.produceMax, traits.yieldMul, pollenRng.nextFloat()) + (hydroponic ? FARMING.HYDRO_YIELD_BONUS : 0));
    const nSeeds = (crop.seedBonus > 0 ? 1 + pollenRng.nextInt(crop.seedBonus + 1) : 0) + traits.seedBonus;
    if (nSeeds > 0) {
      const neighbors: Uint8Array[] = [];
      // CNT-4 (M53.4, §29.11): an active hive within BEE.POLLINATION_RANGE of this
      // cell widens the search to every mature same-crop cell within that same range
      // OF THE HIVE (a bee's own reach, not the harvested cell's) — with no hive
      // nearby this is exactly the M50 four-orthogonal-neighbour loop, unchanged.
      const hive = nearestHiveWithin(x, y, z, BEE.POLLINATION_RANGE);
      const offsets: ReadonlyArray<readonly [number, number]> = hive
        ? pollenOffsets.map(([odx, odz]) => [hive.x + odx - x, hive.z + odz - z])
        : ([[1, 0], [-1, 0], [0, 1], [0, -1]] as const);
      for (const [dx, dz] of offsets) {
        const nb = blockEntities.get(x + dx, y, z + dz);
        if (nb?.type !== 'crop') continue;
        if (ageOf(world.getBlockState(x + dx, y, z + dz)) < crop.maxAge) continue;
        const ng = decodeGenome(nb.data.genome, crop.genes!);
        if (ng) neighbors.push(ng);
      }
      for (let i = 0; i < nSeeds; i++) {
        const mate = pollenMate(neighbors, pollenRng) ?? genome;
        push(crop.seed, 1, encodeGenome(recombine(crop.genes!, genome, mate, pollenRng)));
      }
    }
    return out;
  };

  // ── The field notebook (HER-7, ARCH §28.11) ──────────────────────────────────
  //    Records live on `currentNotebook` (D-TF-135; row-level like `progress`,
  //    not part of `Player`'s own save shape). Crop records are entirely local
  //    — the server has no crop genome to help with (D-TF-251); animal records
  //    go through `Record`/`NotebookUpdate` in multiplayer because only the
  //    SERVER's `ServerEntities` knows a remote entity's `parents` (never
  //    replicated, §28.12's `Spawn` carries genome/name only) — solo/MP
  //    therefore share `applyAnimalRecord` but not the path that builds the
  //    record.
  const capitalize = (s: string): string => s.replace(/^./, (c) => c.toUpperCase());
  const applyAnimalRecord = (rec: AnimalRecord): void => {
    const { notebook, ok } = upsertAnimal(currentNotebook, rec);
    currentNotebook = notebook;
    if (!ok) { game.showActionBar('Notebook full.'); return; }
    game.showActionBar(rec.name !== undefined
      ? `Recorded: ${rec.name} (${capitalize(rec.species)} #${rec.id})`
      : `Recorded: ${capitalize(rec.species)} #${rec.id}`);
  };
  const applyCropRecord = (x: number, y: number, z: number, crop: string, genome: string): void => {
    const { notebook, ok } = upsertCrop(currentNotebook, x, y, z, crop, genome);
    currentNotebook = notebook;
    if (!ok) { game.showActionBar('Notebook full.'); return; }
    game.showActionBar(`Recorded: ${capitalize(crop)} at ${x}, ${y}, ${z}`);
  };
  /** Right-click a table-species mob with the notebook in hand (or the
   *  `record` debug/E2E seam, by id). A mob with no table is a silent no-op
   *  (§28.11's binding); returns whether a table was found at all. */
  const recordAnimal = (e: Entity): boolean => {
    if (net) {
      const netId = e.data.netId;
      if (typeof netId === 'number') net.sendRecord({ id: netId });
      return typeof netId === 'number'; // the record + action bar land via `onNotebookUpdate`
    }
    if (!e.genome) return false;
    const rec = animalRecordOf(e, world.tickCount);
    if (rec) { applyAnimalRecord(rec); fireEduTrigger({ kind: 'notebook_record', species: e.type }); }
    return rec !== null;
  };
  /** Right-click a genetic crop with the notebook in hand (or the `recordCrop`
   *  debug/E2E seam). A plain crop (no block entity) is a silent no-op. Local
   *  always — see the header note. */
  const recordCropAt = (x: number, y: number, z: number): boolean => {
    const be = blockEntities.get(x, y, z);
    if (be?.type !== 'crop') return false;
    const cropBlock = registries.states.blockName[world.getBlockState(x, y, z)] ?? '';
    applyCropRecord(x, y, z, cropBlock, be.data.genome);
    if (net) net.sendRecord({ x, y, z }); // best-effort: lets the server's session copy agree
    return true;
  };
  /** Right-click with the notebook and nothing recordable under the crosshair
   *  opens the screen (§28.11). A mob is handled earlier, in
   *  `defaultInteractMob` — this only ever sees a block or open air. */
  const useFieldNotebook = (): boolean => {
    if (heldItemName() !== 'field_notebook') return false;
    const dir = lookDir();
    const ex = player.body.x, ey = player.body.y + PLAYER.EYE_HEIGHT, ez = player.body.z;
    const hit = raycastVoxel((x, y, z) => world.isSolid(x, y, z), { x: ex, y: ey, z: ez }, dir, REACH.BLOCK);
    if (hit) { recordCropAt(hit.x, hit.y, hit.z); return true; }
    openNotebookScreen(game);
    return true;
  };

  // ── Beehives (MB.5): a resident bee delivering nectar raises the honey level
  //    and bonemeal-nudges one nearby crop (game/hive). A full hive is worked with
  //    a bottle (→ honey) or shears (→ comb), resetting it. ──
  const honeyLevelOf = (state: number): number => Number(registries.states.props[state]?.honey_level ?? '0');
  const beehiveStateOf = (level: number): number => registries.states.idByKey.get(`beehive[honey_level=${level}]`) ?? -1;
  const advanceCropAt = (x: number, y: number, z: number): boolean => {
    const st = world.getBlockState(x, y, z);
    const crop = cropOf(registries.states.blockName[st] ?? '');
    if (!crop) return false;
    const age = ageOf(st);
    if (age >= crop.maxAge) return false;
    const ns = cropStateOf(crop.block, age + 1);
    if (ns < 0) return false;
    setBlockRemesh(x, y, z, ns);
    return true;
  };
  const depositAtHive = (x: number, y: number, z: number): boolean => {
    const st = world.getBlockState(x, y, z);
    if (registries.states.blockName[st] !== 'beehive') return false;
    const level = honeyLevelOf(st);
    if (honeyFull(level)) return false;
    const ns = beehiveStateOf(nextHoney(level));
    if (ns < 0) return false;
    edit(x, y, z, ns);
    for (const c of boostCells(x, y, z)) if (advanceCropAt(c.x, c.y, c.z)) break; // nudge one crop
    return true;
  };
  const depositNearBee = (e: Entity): boolean => {
    const bx = Math.floor(e.x), by = Math.floor(e.y), bz = Math.floor(e.z);
    for (let dx = -2; dx <= 2; dx++)
      for (let dy = -2; dy <= 2; dy++)
        for (let dz = -2; dz <= 2; dz++)
          if (depositAtHive(bx + dx, by + dy, bz + dz)) return true;
    return false;
  };
  const tickHives = (): void => {
    if (world.tickCount % DEPOSIT_INTERVAL !== 0) return;
    for (const e of entities.entities) if (!e.removed && e.type === 'bee') depositNearBee(e);
  };

  // ── Frogs (MB.5): a frog eats an adjacent small slime / magma cube and drops a
  //    froglight coloured by its variant (game/frog). ──
  const frogEat = (frog: Entity): string => {
    let prey: Entity | null = null;
    let best = FROG_EAT_RANGE;
    for (const o of entities.entities) {
      if (o.removed || !isFrogPrey(o.type)) continue;
      const d = Math.hypot(o.x - frog.x, o.y - frog.y, o.z - frog.z);
      if (d <= best) { best = d; prey = o; }
    }
    if (!prey) return '';
    prey.removed = true;
    const light = froglightFor(frog.variant);
    const id = registries.items.idOf.get(light);
    if (id !== undefined) drops.spawn(makeStack(id, 1), frog.x, frog.y + 0.3, frog.z, { vy: 0.2 });
    return light;
  };
  const tickFrogs = (): void => {
    for (const e of entities.entities) if (!e.removed && e.type === 'frog') frogEat(e);
  };

  // ── Chicken egg-laying (MB.3): an adult chicken drops an egg on the game/egg
  //    timer (6000–12000 gt). ──
  const eggRng = new Rng(`${String(meta.seed)}#egg`);
  const layEgg = (chicken: Entity): void => {
    const id = registries.items.idOf.get('egg');
    if (id !== undefined) drops.spawn(makeStack(id, 1), chicken.x, chicken.y + 0.2, chicken.z, { vy: 0.15 });
    chicken.data.eggTimer = eggDelay(eggRng.nextFloat(), chicken.traits.eggMul);
  };
  const tickEggLaying = (): void => {
    for (const e of entities.entities) {
      if (e.removed || e.type !== 'chicken' || e.baby) continue;
      if (!e.data.eggTimer) e.data.eggTimer = eggDelay(eggRng.nextFloat(), e.traits.eggMul);
      else if (--e.data.eggTimer <= 0) layEgg(e);
    }
  };

  /** Fill a composter with a compostable (chance to raise its level, M11.1). */
  const fillComposter = (x: number, y: number, z: number, chance: number): boolean => {
    const st = world.getBlockState(x, y, z);
    const level = Number(registries.states.props[st]?.level ?? '0');
    if (level >= 8) return false; // full → collect via useBlock instead
    consumeHeld();
    if (level < 7 ? Math.random() < chance : true) {
      const ns = composterStateOf(level + 1);
      if (ns >= 0) edit(x, y, z, ns);
    }
    return true;
  };

  /** Use the held item on the targeted block: hoe→till, seed→plant, bonemeal→grow,
   *  compostable→composter. Returns true if it handled the interaction (M11.1). */
  const useItemOnBlock = (x: number, y: number, z: number, nx: number, ny: number, nz: number): boolean => {
    if (!modeCaps(playerState.gameMode).canBuild) return false;
    const held = playerState.inventory.held();
    if (isEmpty(held)) return false;
    const item = registries.items.nameOf[held.item] ?? '';
    const target = registries.states.blockName[world.getBlockState(x, y, z)] ?? '';

    // A torch placed against a vertical face mounts as a wall torch, pointing out
    // from the wall (MB.1). Clicking a top/bottom face falls through to a floor torch.
    if (item === 'torch' || item === 'redstone_torch') {
      const facing = wallTorchFacing(nx, ny, nz);
      if (facing) {
        const px = x + nx, py = y + ny, pz = z + nz;
        const ex = world.getBlockState(px, py, pz);
        if (ex === STATE_AIR || world.isReplaceableByFluid(ex)) {
          const wallName = item === 'torch' ? 'wall_torch' : 'redstone_wall_torch';
          const def = registries.states.defaultOf.get(wallName);
          if (def !== undefined) {
            let key = registries.states.key[def]!.replace(/facing=(?:north|east|south|west)/, `facing=${facing}`);
            if (wallName === 'redstone_wall_torch') key = key.replace(/lit=(?:true|false)/, 'lit=true');
            edit(px, py, pz, registries.states.idByKey.get(key) ?? def);
            consumeHeld();
            return true;
          }
        }
      }
    }

    // A lever/button mounts on whichever face was clicked (MB.1): a side click walls
    // it (powering the block behind), a top click floors it (powering below), a bottom
    // click ceilings it (powering above).
    if (item === 'lever' || item === 'stone_button') {
      const px = x + nx, py = y + ny, pz = z + nz;
      const ex = world.getBlockState(px, py, pz);
      if (ex === STATE_AIR || world.isReplaceableByFluid(ex)) {
        const att = attachmentFor(nx, ny, nz, facingFromYaw(player.yaw) as AttachFacing);
        const def = registries.states.defaultOf.get(item);
        if (def !== undefined) {
          const key = registries.states.key[def]!
            .replace(/face=(?:floor|wall|ceiling)/, `face=${att.face}`)
            .replace(/facing=(?:north|east|south|west)/, `facing=${att.facing ?? 'north'}`);
          edit(px, py, pz, registries.states.idByKey.get(key) ?? def);
          consumeHeld();
          return true;
        }
      }
    }

    // An eye of ender fed to a stronghold's portal frame (DIM-3). The twelfth one
    // lights the pool — the only way into The End that isn't starting a world there.
    if (item === 'ender_eye' && target === 'end_portal_frame') {
      const probe = portalProbe();
      const res = probe ? insertEye(probe, x, y, z) : 'none';
      if (res === 'none') return false; // already has an eye — don't eat the item
      if (!modeCaps(playerState.gameMode).infiniteResources) consumeHeld();
      if (res === 'lit') game.print('§5The portal opens.');
      return true;
    }

    // A wind charge thrown at a block bursts on it — knocking things back + toggling
    // any activatable block it catches (M22.1).
    if (item === 'wind_charge') {
      detonateWindCharge(x + 0.5, y + 0.5, z + 0.5);
      if (!modeCaps(playerState.gameMode).infiniteResources) consumeHeld();
      return true;
    }

    // Pink petals (M45): add a petal to an existing cell (up to 4), or lay a new
    // cover on a valid ground top.
    if (item === 'pink_petals') {
      if (target === 'pink_petals') {
        const amt = Number(registries.states.props[world.getBlockState(x, y, z)]?.flower_amount ?? '1');
        if (!canAddPetal(amt)) return false;
        const ns = registries.states.idByKey.get(`pink_petals[flower_amount=${nextPetalAmount(amt)}]`);
        if (ns === undefined) return false;
        edit(x, y, z, ns); consumeHeld(); return true;
      }
      if (ny === 1 && canPlacePetalsOn(target) && world.getBlockState(x, y + 1, z) === STATE_AIR) {
        const ns = registries.states.idByKey.get('pink_petals[flower_amount=1]');
        if (ns === undefined) return false;
        edit(x, y + 1, z, ns); consumeHeld(); return true;
      }
      return false;
    }

    // A brush dusts a suspicious block; the last stage reveals its buried find (M41).
    if (item === 'brush' && isSuspicious(target)) {
      brushSuspicious(x, y, z, target);
      if (modeCaps(playerState.gameMode).survivalHunger) damageHeldTool();
      return true;
    }

    // Flint & steel primes a TNT block (RED-8, M15.2.2) — consumes durability.
    // In net mode the server owns the primed entity (M62): we ASK, and the fuse,
    // the fall, the blast and the chain it sets off all arrive as replication.
    if (item === 'flint_and_steel' && target === 'tnt') {
      if (net) { net.sendIgnite(x, y, z); } else if (!ignitePrimedTntAt(x, y, z)) return false;
      if (modeCaps(playerState.gameMode).survivalHunger) damageHeldTool();
      return true;
    }

    // A minecart (any variant) placed onto a rail spawns a rolling cart (RED-5/6).
    if (item.endsWith('minecart') && (target === 'rail' || target === 'powered_rail' || target === 'detector_rail')) {
      spawnMinecartAt(x, y, z, facingFromYaw(player.yaw), item as MinecartKind);
      consumeHeld();
      return true;
    }

    // Copper (MB.1): an axe un-waxes then scrapes off oxidation; a honeycomb waxes.
    if (copperInfo(target)) {
      if (item.endsWith('_axe')) {
        const step = axeStep(target);
        if (!step) return false;
        setCopperBlock(x, y, z, step.result);
        if (modeCaps(playerState.gameMode).survivalHunger) damageHeldTool();
        return true;
      }
      if (item === 'honeycomb') {
        const waxed = waxStep(target);
        if (!waxed) return false;
        setCopperBlock(x, y, z, waxed);
        consumeHeld();
        return true;
      }
    }

    // Beehive (MB.5): a bottle or shears harvests a full hive, resetting it.
    if (target === 'beehive' && honeyFull(honeyLevelOf(world.getBlockState(x, y, z)))) {
      const yld = harvestYield(item);
      if (!yld) return false;
      const id = registries.items.idOf.get(yld.item);
      if (id === undefined) return false;
      if (item === 'shears') { if (modeCaps(playerState.gameMode).survivalHunger) damageHeldTool(); }
      else if (!modeCaps(playerState.gameMode).infiniteResources) consumeHeld();
      giveStack(makeStack(id, yld.count));
      const ns = beehiveStateOf(0);
      if (ns >= 0) edit(x, y, z, ns);
      return true;
    }

    // Campfire (MB.3): drop a raw food onto it to start cooking a free slot.
    if (blockEntityOf(target) === 'campfire' && isCampfireCookable(registries, held)) {
      const be = blockEntities.create('campfire', x, y, z);
      if (be.type !== 'campfire' || !addToCampfire(be.data, registries, held)) return false;
      consumeHeld();
      return true;
    }

    if (item.endsWith('_hoe')) {
      if ((target === 'dirt' || target === 'grass_block') && world.getBlockState(x, y + 1, z) === STATE_AIR) {
        const fs = farmlandStateOf(0);
        if (fs < 0) return false;
        edit(x, y, z, fs);
        if (modeCaps(playerState.gameMode).survivalHunger) damageHeldTool();
        return true;
      }
      return false;
    }
    const cropBlock = SEED_TO_CROP[item];
    if (cropBlock) {
      if ((target === 'farmland' || target === 'hydroponic_tray') && ny === 1 && world.getBlockState(x, y + 1, z) === STATE_AIR) {
        const cs = cropStateOf(cropBlock, 0);
        if (cs < 0) return false;
        edit(x, y + 1, z, cs);
        plantGenetics(x, y + 1, z, cropBlock, held.components?.genome);
        consumeHeld();
        return true;
      }
      return false;
    }
    // MB.2 — stems plant on farmland; nether wart on soul sand; bamboo on soil.
    const stemBlock = item === 'melon_seeds' ? 'melon_stem' : item === 'pumpkin_seeds' ? 'pumpkin_stem' : null;
    if (stemBlock) {
      if (target !== 'farmland' || ny !== 1 || world.getBlockState(x, y + 1, z) !== STATE_AIR) return false;
      const cs = cropStateOf(stemBlock, 0);
      if (cs < 0) return false;
      edit(x, y + 1, z, cs); consumeHeld(); return true;
    }
    if (item === 'nether_wart') {
      if (target !== 'soul_sand' || ny !== 1 || world.getBlockState(x, y + 1, z) !== STATE_AIR) return false;
      const cs = cropStateOf('nether_wart', 0);
      if (cs < 0) return false;
      edit(x, y + 1, z, cs); consumeHeld(); return true;
    }
    if (item === 'bamboo') {
      if (ny !== 1 || world.getBlockState(x, y + 1, z) !== STATE_AIR || !VERTICAL_PLANTS.bamboo!.ground.has(target)) return false;
      const ns = registries.states.defaultOf.get('bamboo');
      if (ns === undefined) return false;
      edit(x, y + 1, z, ns); consumeHeld(); return true;
    }
    if (item === 'bone_meal') {
      // A vine takes a whole burst rather than one segment, so bone meal reads as
      // fertiliser instead of a hurried random tick.
      if (isVine(target)) {
        const cells = vineBoneMeal(growthView, x, y, z, target, Math.random());
        if (cells.length === 0) return false;
        const vs = world.getBlockState(x, y, z);
        for (const c of cells) edit(c.x, c.y, c.z, vs);
        consumeHeld();
        return true;
      }
      const crop = cropOf(target);
      if (!crop) return false;
      const age = ageOf(world.getBlockState(x, y, z));
      if (age >= crop.maxAge) return false;
      const cs = cropStateOf(crop.block, Math.min(crop.maxAge, age + bonemealGrowth(Math.random())));
      if (cs < 0) return false;
      edit(x, y, z, cs);
      consumeHeld();
      return true;
    }
    if (target === 'composter' && COMPOSTABLES[item] !== undefined) return fillComposter(x, y, z, COMPOSTABLES[item]!);
    return false;
  };

  // ── Enchanting (PLR-6, M12.1). Offers are seeded per (table, xp, item) so they
  //    are stable across re-opens and change as the player levels up. ──
  const isOpaqueCube = (x: number, y: number, z: number): boolean => world.isSolid(x, y, z) && materialAt(x, y, z) !== 'glass';
  const countBookshelves = (tx: number, ty: number, tz: number): number => {
    let n = 0;
    for (const dy of [0, 1]) {
      for (let dz = -2; dz <= 2; dz++) for (let dx = -2; dx <= 2; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dz)) !== 2) continue; // outer ring only
        if (registries.states.blockName[world.getBlockState(tx + dx, ty + dy, tz + dz)] !== 'bookshelf') continue;
        // The block between the shelf and the table must be transparent (air gap).
        const mx = tx + Math.sign(dx), mz = tz + Math.sign(dz);
        if (!isOpaqueCube(mx, ty + dy, mz)) n++;
      }
    }
    return shelfPower(n);
  };
  interface EnchantOffer { level: number; lapisCost: number; enchants: Array<{ id: string; lvl: number }>; }
  const enchantOffersAt = (tx: number, ty: number, tz: number): EnchantOffer[] => {
    const held = playerState.inventory.held();
    if (isEmpty(held)) return [];
    const name = registries.items.nameOf[held.item] ?? '';
    const def = registries.items.byName.get(name);
    // Enchantable: a tool (its tool class) or a piece of armour (its slot). Armour
    // enchant reach uses the armour enchantability table (M17 gear).
    const category = def?.toolClass ?? armorCategory(name);
    if (!category || (held.components?.enchants?.length ?? 0) > 0) return [];
    const ench = def?.toolClass ? (ENCHANTABILITY[def.toolTier ?? 'wood'] ?? 1) : (armorEnchantability(name) ?? 1);
    const shelves = countBookshelves(tx, ty, tz);
    const levels = slotLevels(shelves, new Rng(`${String(world.seed)}:ench:${tx}:${ty}:${tz}:${playerState.xpTotal}`));
    return levels.map((level, i) => ({
      level,
      lapisCost: i + 1,
      enchants: rollEnchants(registries.enchantments, category, ench, level, new Rng(`${String(world.seed)}:ench:${tx}:${ty}:${tz}:${playerState.xpTotal}:${i}`)),
    }));
  };
  const consumeItem = (name: string, count: number): boolean => {
    const id = registries.items.idOf.get(name);
    if (id === undefined || playerState.inventory.countOf(id) < count) return false;
    let left = count;
    for (let s = 0; s < playerState.inventory.slots.length && left > 0; s++) {
      const st = playerState.inventory.get(s);
      if (st.item !== id || isEmpty(st)) continue;
      const take = Math.min(left, st.count);
      playerState.inventory.set(s, st.count <= take ? emptyStack() : { ...st, count: st.count - take });
      left -= take;
    }
    return true;
  };
  const applyEnchant = (tx: number, ty: number, tz: number, slot: number): boolean => {
    const offers = enchantOffersAt(tx, ty, tz);
    const offer = offers[slot];
    if (!offer || offer.enchants.length === 0) return false;
    const creative = modeCaps(playerState.gameMode).infiniteResources;
    const lapisId = registries.items.idOf.get('lapis_lazuli');
    const haveLapis = creative || (lapisId !== undefined && playerState.inventory.countOf(lapisId) >= offer.lapisCost);
    if ((playerState.xpLevel < offer.level && !creative) || !haveLapis) return false;
    const slotIdx = playerState.inventory.heldSlot();
    playerState.inventory.set(slotIdx, withEnchants(playerState.inventory.get(slotIdx), offer.enchants));
    if (!creative) {
      consumeItem('lapis_lazuli', offer.lapisCost);
      playerState.takeLevels(slot + 1);
    }
    return true;
  };

  // ── Falling blocks (M10.1): sand/gravel/concrete powder gravity. ──
  const passableForFall = (x: number, y: number, z: number): boolean => {
    const s = world.getBlockState(x, y, z);
    return s === STATE_AIR || world.isReplaceableByFluid(s) || world.isFluid(x, y, z);
  };
  const maybeFall = (x: number, y: number, z: number): void => {
    const s = world.getBlockState(x, y, z);
    if (!world.isGravityState(s) || !passableForFall(x, y - 1, z)) return;
    edit(x, y, z, STATE_AIR); // remove (this recurses to the block above)
    fallingBlocks.spawn(s, x, y, z);
  };
  /**
   * A stalactite hangs from what is above it (M19.1 remainder): break the support
   * and the whole connected run of down-pointing spikes drops as falling blocks.
   * Stalagmites rest on the floor and are untouched. Called from the same
   * neighbour-update the sand/gravel fall uses.
   */
  const maybeDripstoneFall = (x: number, y: number, z: number): void => {
    const nameAt = (yy: number): string => registries.states.blockName[world.getBlockState(x, yy, z)] ?? 'air';
    if (nameAt(y) !== 'pointed_dripstone') return;
    const dirAt = (yy: number): string =>
      registries.states.props[world.getBlockState(x, yy, z)]?.vertical_direction ?? 'up';
    if (!stalactiteFalls(dirAt(y), world.isSolid(x, y + 1, z))) return;
    const len = stalactiteColumn((dy) => nameAt(y + dy) === 'pointed_dripstone' && dirAt(y + dy) === 'down');
    for (let i = 0; i < len; i++) {
      const yy = y - i;
      const st = world.getBlockState(x, yy, z);
      edit(x, yy, z, STATE_AIR);
      fallingBlocks.spawn(st, x, yy, z);
    }
  };
  const fallingWorld: FallingWorld = {
    isPassable: (x, y, z) => passableForFall(x, y, z),
    place: (x, y, z, state) => {
      if (!passableForFall(x, y, z)) return false;
      edit(x, y, z, state);
      return true;
    },
    dropItem: (state, x, y, z) => {
      const id = registries.items.idOf.get(registries.states.blockName[state] ?? '');
      if (id !== undefined) drops.spawn(makeStack(id, 1), x + 0.5, y + 0.5, z + 0.5, {});
    },
  };
  const resolveBlock = (name: string): number =>
    registries.states.idByKey.get(`${name}[axis=y]`) ?? registries.states.defaultOf.get(name)!;

  // Ignite a nether portal at an interior air cell (DIM-2): detect+fill the frame,
  // register it for return-trip linking, and relight/remesh the lit interior.
  const ignitePortalAt = (fx: number, fy: number, fz: number): boolean => {
    const frame = ignitePortal(portalAccessOf(world), portalBlocks, fx, fy, fz);
    if (!frame) return false;
    manager.registerPortal(currentDim, [frame.a0, frame.y0, frame.fixed]);
    for (const [cx, cy, cz] of frame.cells) {
      const dirty = world.relightAfterEdit(cx, cy, cz);
      dirty.add(`${Math.floor(cx / WORLD.SECTION)},${Math.floor(cz / WORLD.SECTION)}`);
      for (const k of dirty) {
        const [dcx, dcz] = k.split(',').map(Number) as [number, number];
        chunkMeshes.markDirty(dcx * WORLD.SECTION + 8, dcz * WORLD.SECTION + 8);
      }
    }
    return true;
  };

  const blockEntityOf = (name: string | undefined): BlockEntityType | undefined =>
    name ? registries.blocks.byName.get(name)?.blockEntity : undefined;

  // Toggle a door open/closed (any wood species), carrying both tall halves and
  // swinging a side-by-side double-door partner to match (MB.1).
  const setDoorOpen = (name: string, x: number, y: number, z: number, open: string): void => {
    for (const dy of DOOR_HALVES) {
      const st = world.getBlockState(x, y + dy, z);
      if (registries.states.blockName[st] !== name) continue;
      const target = registries.states.idByKey.get(`${name}[facing=${registries.states.props[st]?.facing},open=${open}]`);
      if (target !== undefined && target !== st) edit(x, y + dy, z, target);
    }
  };
  const toggleDoor = (x: number, y: number, z: number): void => {
    const base = world.getBlockState(x, y, z);
    const name = registries.states.blockName[base];
    if (!isDoor(name)) return;
    const facing = registries.states.props[base]?.facing ?? 'north';
    const newOpen = doorNextOpen(registries.states.props[base]?.open);
    setDoorOpen(name, x, y, z, newOpen);
    // A same-name, same-facing door directly to either side is the pair's other
    // leaf — swing it to the same state so a double door opens together.
    for (const [dx, dz] of partnerOffsets(facing)) {
      const nx = x + dx, nz = z + dz;
      for (const dy of DOOR_HALVES) {
        const nSt = world.getBlockState(nx, y + dy, nz);
        if (registries.states.blockName[nSt] === name && registries.states.props[nSt]?.facing === facing) {
          setDoorOpen(name, nx, y + dy, nz, newOpen);
          break;
        }
      }
    }
  };

  // ── Beds & sleep (PLR-10) ──
  const bedState = (facing: Facing, part: 'foot' | 'head'): number =>
    registries.states.idByKey.get(`bed[facing=${facing},part=${part},occupied=false]`) ?? -1;

  /** Place a two-block bed with its foot at (x,y,z), head one cell toward `facing`. */
  const placeBed = (x: number, y: number, z: number): boolean => {
    const facing = facingFromYaw(player.yaw);
    const [ox, oz] = BED_FACING_OFFSET[facing];
    const hx = x + ox, hz = z + oz;
    if (world.getBlockState(hx, y, hz) !== STATE_AIR) return false;
    if (blockIntersectsBody(player.body, x, y, z) || blockIntersectsBody(player.body, hx, y, hz)) return false;
    const foot = bedState(facing, 'foot'), head = bedState(facing, 'head');
    if (foot < 0 || head < 0) return false;
    edit(x, y, z, foot);
    edit(hx, y, hz, head);
    return true;
  };

  /** The partner half of the bed at (x,y,z), or null if not a bed. */
  const bedPartner = (x: number, y: number, z: number): { x: number; y: number; z: number } | null => {
    const st = world.getBlockState(x, y, z);
    if (registries.states.blockName[st] !== 'bed') return null;
    const p = registries.states.props[st]!;
    const [ox, oz] = partnerOffset(p.facing as Facing, p.part as 'foot' | 'head');
    return { x: x + ox, y, z: z + oz };
  };

  /** Distance from (x,y,z) to the nearest hostile mob (Infinity if none). */
  const nearestMonsterDist = (x: number, y: number, z: number): number => {
    let best = Infinity;
    for (const e of entities.entities) {
      if (registries.mobs.byName.get(e.type)?.category !== 'monster') continue;
      const d = Math.hypot(e.x - x, e.y - y, e.z - z);
      if (d < best) best = d;
    }
    return best;
  };

  /** Right-click a bed to sleep (PLR-10): advances to morning, clears rain, sets spawn. */
  const trySleep = (x: number, y: number, z: number): boolean => {
    const st = world.getBlockState(x, y, z);
    if (registries.states.blockName[st] !== 'bed') return false;
    // The foot half owns the spawn; resolve it whichever half was clicked.
    const p = registries.states.props[st]!;
    const foot = p.part === 'foot' ? { x, y, z } : (bedPartner(x, y, z) ?? { x, y, z });
    const result = evaluateSleep({
      timeOfDay: world.dayTime % 24_000,
      thunderstorm: isThunderstorm(world.weather),
      occupied: p.occupied === 'true',
      nearestMonster: nearestMonsterDist(x, y, z),
    });
    if (result !== 'ok') { game.print(sleepMessage(result)); return true; }
    // Multiplayer (NET C3): the server owns the clock + the majority vote, so ask it
    // to sleep (it sets our shared bed spawn and skips only when enough players sleep).
    if (net) {
      net.sendCommand('/sleep');
      game.print('You are in bed — the night skips when enough players sleep');
      showSleepFlash();
      return true;
    }
    playerState.spawn = { x: foot.x + 0.5, y: foot.y, z: foot.z + 0.5 };
    playerState.timeSinceRest = 0;
    world.dayTime = wakeTime(world.dayTime);
    if (world.gamerules.get('doWeatherCycle') !== 'false') setWeather(world.weather, 'clear');
    game.print('Good morning');
    showSleepFlash();
    return true;
  };

  // Toggle an `open`-property block (fence gate / trapdoor) — M10.2.
  const toggleOpenable = (x: number, y: number, z: number): boolean => {
    const st = world.getBlockState(x, y, z);
    const props = registries.states.props[st];
    if (!props || props.open === undefined) return false;
    const key = registries.states.key[st]!.replace(/open=(?:true|false)/, `open=${props.open === 'true' ? 'false' : 'true'}`);
    const next = registries.states.idByKey.get(key);
    if (next === undefined) return false;
    edit(x, y, z, next);
    return true;
  };

  const heldTool = (): HeldTool | null => {
    const held = playerState.inventory.held();
    if (isEmpty(held)) return null;
    const def = registries.items.byName.get(registries.items.nameOf[held.item] ?? '');
    return def?.toolClass ? { toolClass: def.toolClass, toolTier: def.toolTier, enchants: held.components?.enchants } : null;
  };

  /** Consume one point of durability from the held tool; it breaks at 0. */
  const damageHeldTool = (): void => {
    const inv = playerState.inventory;
    const slot = inv.heldSlot();
    const s = inv.get(slot);
    if (isEmpty(s)) return;
    const def = registries.items.byName.get(registries.items.nameOf[s.item] ?? '');
    if (!def?.durability) return;
    // Unbreaking: a use may spare the durability point entirely (Java 1/(lvl+1)).
    const unbreaking = enchantLevelOf(s.components?.enchants, 'unbreaking');
    if (!consumesDurability(unbreaking, Math.random())) return;
    const dmg = (s.components?.damage ?? 0) + 1;
    if (dmg >= def.durability) {
      const brokenName = registries.items.nameOf[s.item];
      if (brokenName) stats.bump('broken', brokenName); // M24: Tools Broken stat — the use that snaps it
      inv.set(slot, emptyStack());
    } else inv.set(slot, { ...s, components: { ...s.components, damage: dmg } });
  };

  // Raycast from the eye along the look direction (survival hold-to-break).
  const lookDir = (): { x: number; y: number; z: number } => {
    const { yaw, pitch } = player;
    return { x: -Math.cos(pitch) * Math.sin(yaw), y: Math.sin(pitch), z: -Math.cos(pitch) * Math.cos(yaw) };
  };
  const raycastTarget = () => raycastVoxel(
    (x, y, z) => world.isRaycastTarget(x, y, z),
    { x: player.body.x, y: player.body.y + PLAYER.EYE_HEIGHT, z: player.body.z },
    lookDir(),
    REACH.BLOCK,
  );

  const heldPlaceState = (): number | null => {
    const held = playerState.inventory.held();
    if (isEmpty(held)) return null;
    const def = registries.items.byName.get(registries.items.nameOf[held.item] ?? '');
    if (!def?.block) return null;
    return resolveBlock(def.block);
  };

  const giveStack = (stack: ItemStack): void => {
    if (isEmpty(stack)) return;
    const name = registries.items.nameOf[stack.item];
    if (name) { fireAdvancement({ kind: 'obtain', item: name }); unlockRecipesFor(name); } // M24 obtain trigger + recipe unlock
    const rem = insert(playerState.inventory, stack, [...PlayerInventory.PICKUP_RANGE]);
    if (!isEmpty(rem)) game.dropItem?.(rem);
  };

  // ── Cartography (UI-8, M18): fill a fresh world-locked snapshot centred on the
  //    player and hand back a `filled_map` that references it by id. ──
  const makeFilledMapAtPlayer = (scale: number = CARTO.DEFAULT_SCALE): number => {
    const { cx, cz } = mapCenter(scale, Math.floor(player.body.x), Math.floor(player.body.z));
    const snap = mapStore.create(scale, cx, cz);
    snap.refresh(mapSampler, mapPalette, Math.floor(player.body.x), Math.floor(player.body.z), CARTO.UPDATE_RADIUS);
    return snap.id;
  };
  const filledMapStack = (mapId: number): ItemStack => {
    const id = registries.items.idOf.get('filled_map');
    return makeStack(id ?? 0, 1, { mapId });
  };
  /** The `explorer_map` loot function's minter: locate the nearest structure of a
   *  type and hand back a filled map framed on it with its "X" already pinned. The
   *  face starts blank and fills in as the player sails toward it (a chest map is
   *  a lead, not a survey). */
  const explorerMapStack = (destination: string): ItemStack | null => {
    if (flat || atlasCity || earthWorld) return null;
    const b = player.body;
    const found = locateStructureType(
      destination, world.seed, Math.floor(b.x / WORLD.SECTION), Math.floor(b.z / WORLD.SECTION), structEnv,
    );
    if (!found) return null;
    const scale = CARTO.DEFAULT_SCALE;
    const { cx, cz } = mapCenter(scale, found.x, found.z);
    const snap = mapStore.create(scale, cx, cz);
    snap.target = { x: found.x, z: found.z };
    return filledMapStack(snap.id);
  };
  /** Right-click with an empty `map` → consume it and add a filled map (E-map). */
  const useMapInHand = (): boolean => {
    const held = playerState.inventory.held();
    if (isEmpty(held) || registries.items.nameOf[held.item] !== 'map') return false;
    const mapId = makeFilledMapAtPlayer();
    consumeHeld();
    giveStack(filledMapStack(mapId));
    return true;
  };

  /** True if the body is pressed against a honey block on any horizontal side
   *  across its height — the honey wall-slide condition (E-14). */
  const touchingHoneyWall = (b: Body): boolean => {
    const cy0 = Math.floor(b.y + 0.1), cy1 = Math.floor(b.y + PLAYER_HEIGHT - 0.2);
    const reach = PLAYER_HALF + 0.05;
    for (const [dx, dz] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
      const nx = Math.floor(b.x + dx * reach), nz = Math.floor(b.z + dz * reach);
      for (let cy = cy0; cy <= cy1; cy++) {
        if (registries.states.blockName[world.getBlockState(nx, cy, nz)] === 'honey_block') return true;
      }
    }
    return false;
  };

  // ── Survival systems ticked each game tick (M4.3) ──
  const survivalTick = (b: Body): void => {
    // Fall damage on landing (PLR-9, E-8): track the peak Y while airborne.
    if (b.onGround) {
      if (!wasOnGround) {
        const dist = playerState.fallPeak - b.y;
        if (dist > 0) {
          const inWater = world.isFluid(Math.floor(b.x), Math.floor(b.y), Math.floor(b.z));
          const fx = Math.floor(b.x), fz = Math.floor(b.z), fy = Math.floor(b.y - 0.1);
          const landState = world.getBlockState(fx, fy, fz);
          const landName = registries.states.blockName[landState];
          // Slow Falling / Levitation cancel fall damage entirely (PLR-8).
          let dmg = negatesFallDamage(playerState.effects) ? 0 : fallDamage(dist, { inWater, landBlock: landName });
          // Landing on an upward stalagmite spike amplifies the fall damage (M19.1).
          if (dmg > 0 && landName === 'pointed_dripstone' && registries.states.props[landState]?.vertical_direction === 'up') dmg = stalagmiteFallDamage(dmg);
          if (dmg > 0) { playerState.applyDamage(dmg); playerState.addExhaustion(SURVIVAL.EXHAUST_DAMAGE); }
          // Trample farmland when jumped/fallen onto (E), destroying the crop above.
          if (landName === 'farmland' && dist > 0.6) {
            if (cropOf(registries.states.blockName[world.getBlockState(fx, fy + 1, fz)] ?? '')) edit(fx, fy + 1, fz, STATE_AIR);
            const dirt = registries.states.defaultOf.get('dirt');
            if (dirt !== undefined) edit(fx, fy, fz, dirt);
          }
        }
      }
      playerState.fallPeak = b.y;
    } else {
      playerState.fallPeak = Math.max(playerState.fallPeak, b.y);
    }
    wasOnGround = b.onGround;

    // Sprint exhaustion (PLR-4) proportional to ground distance covered.
    if (game.input.sprint && b.onGround) {
      const moved = Math.hypot(b.x - prevX, b.z - prevZ);
      if (moved > 0) playerState.addExhaustion(SURVIVAL.EXHAUST_SPRINT_PER_M * moved);
    }

    // Air / drowning (E-8 adjacent): head submerged drains the air bar.
    const headInWater = world.isFluid(Math.floor(b.x), Math.floor(b.y + PLAYER.EYE_HEIGHT), Math.floor(b.z));
    // A worn turtle helmet grants Water Breathing while the head is submerged (M18.1
    // → the "world-side" bonus gear.ts leaves to the host); it lingers a few seconds.
    const headSlot = playerState.inventory.get(INV.ARMOR_START + 0);
    const headItem = isEmpty(headSlot) ? '' : registries.items.nameOf[headSlot.item] ?? '';
    if (turtleHelmetBreathes(headItem, headInWater)) addEffect(playerState.effects, 'water_breathing', 0, turtleHelmetBreathTicks());
    const drown = tickAir(playerState, headInWater);
    if (drown > 0 && !hasEffect(playerState.effects, 'water_breathing') && !hasEffect(playerState.effects, 'conduit_power')) playerState.applyDamage(drown); // conduit power lets you breathe underwater (M18.1)

    // Lava contact + on-fire burn (M7.2): feet in lava sets a burn that persists.
    const feetInLava = world.isLava(Math.floor(b.x), Math.floor(b.y + 0.2), Math.floor(b.z));
    const inWater = world.isFluid(Math.floor(b.x), Math.floor(b.y), Math.floor(b.z)) || headInWater;
    const burn = tickFire(playerState, feetInLava, inWater);
    if (burn > 0 && !hasEffect(playerState.effects, 'fire_resistance')) playerState.applyDamage(burn);

    // Powder snow (MB.1): submerged without leather boots freezes you; once fully
    // frozen, freeze damage lands on a cadence. Leather boots never freeze.
    const feetBlk = registries.states.blockName[world.getBlockState(Math.floor(b.x), Math.floor(b.y), Math.floor(b.z))];
    const bodyBlk = registries.states.blockName[world.getBlockState(Math.floor(b.x), Math.floor(b.y + 1), Math.floor(b.z))];
    const inPowder = feetBlk === 'powder_snow' || bodyBlk === 'powder_snow';
    frozenTicks = freezeStep(frozenTicks, inPowder, hasLeatherBoots());
    const freeze = freezeDamage(frozenTicks);
    if (freeze > 0) playerState.applyDamage(freeze);

    // Contact burns (MB.3): standing on a lit campfire (burns + ignites), a soul
    // campfire (hotter), or a magma block (unless sneaking) hurts on a cadence.
    if (b.onGround && world.tickCount % BURN_INTERVAL === 0 && !hasEffect(playerState.effects, 'fire_resistance')) {
      const under = registries.states.blockName[world.getBlockState(Math.floor(b.x), Math.floor(b.y - 0.1), Math.floor(b.z))];
      const contact = contactBurnDamage(under, game.input.sneak);
      if (contact > 0) {
        playerState.applyDamage(contact);
        if (ignitesOnContact(under)) playerState.fireTicks = Math.max(playerState.fireTicks, CAMPFIRE_FIRE_TICKS);
      }
    }

    tickHunger(playerState, meta.difficulty);
    tickEffects(playerState.effects, playerState, world.tickCount); // status effects (PLR-8)
    // Absorption's pool vanishes when the effect ends (its only source, PLR-8).
    if (!hasEffect(playerState.effects, 'absorption') && playerState.absorption > 0) playerState.absorption = 0;
    playerState.timeSinceRest++; // insomnia → phantoms (PLR-10, M13)
    if (playerState.dead) onDeath();
  };

  // Hold-to-break with the PLR-2 timing; drives the crack overlay (creative
  // breaks instantly through the mousedown path in input.ts).
  const tickMining = (): void => {
    const caps = modeCaps(playerState.gameMode);
    if (!game.breaking || game.uiOpen || !game.locked || caps.instantBreak || !caps.canBuild || playerState.dead) {
      mining.active = false; breakElapsed = 0; return;
    }
    const hit = raycastTarget();
    if (!hit) { mining.active = false; breakElapsed = 0; return; }
    if (!mining.active || mining.x !== hit.x || mining.y !== hit.y || mining.z !== hit.z) {
      mining.active = true; mining.x = hit.x; mining.y = hit.y; mining.z = hit.z; mining.progress = 0;
      breakElapsed = 0;
      breakNeeded = breakTicks(registries, world.getBlockState(hit.x, hit.y, hit.z), heldTool(), { onGround: player.body.onGround, digSpeedMultiplier: digSpeedMultiplier(playerState.effects) });
    }
    breakElapsed++;
    mining.progress = Number.isFinite(breakNeeded) && breakNeeded > 0 ? Math.min(1, breakElapsed / breakNeeded) : 0;
    if (breakElapsed >= breakNeeded) { game.mineBlock(mining.x, mining.y, mining.z); breakElapsed = 0; }
  };

  // Drink a potion (PLR-8): apply its effect, leaving a glass bottle in survival.
  const drinkPotion = (name: string): void => {
    const p = POTIONS[name];
    if (!p) return;
    if (isInstantEffect(p.effect)) applyInstant(playerState, p.effect, p.amplifier);
    else addEffect(playerState.effects, p.effect, p.amplifier, p.ticks);
    if (modeCaps(playerState.gameMode).infiniteResources) return;
    const slot = playerState.inventory.heldSlot();
    const bottle = registries.items.idOf.get('glass_bottle');
    playerState.inventory.set(slot, bottle !== undefined ? makeStack(bottle, 1) : emptyStack());
  };

  // Hold right-click to eat food (PLR-4) or drink a potion (PLR-8) over EAT_TICKS.
  let eatTicks = 0;
  const tickEating = (): void => {
    if (!game.usingItem || game.uiOpen || !game.locked || playerState.dead) { eatTicks = 0; return; }
    const held = playerState.inventory.held();
    if (isEmpty(held)) { eatTicks = 0; return; }
    const def = registries.items.byName.get(registries.items.nameOf[held.item] ?? '');
    const potion = held.components?.potion;
    const eating = def?.food && modeCaps(playerState.gameMode).survivalHunger && canEat(playerState.hunger, def.food);
    const drinking = potion !== undefined && POTIONS[potion] !== undefined;
    const milk = registries.items.nameOf[held.item] === 'milk_bucket'; // clears all effects (MB.3)
    if (!eating && !drinking && !milk) { eatTicks = 0; return; }
    if (++eatTicks >= EAT_TICKS) {
      if (milk) {
        playerState.effects.length = 0; // milk washes out every status effect
        playerState.absorption = 0;
        const bucketId = registries.items.idOf.get('bucket');
        const slot = playerState.inventory.heldSlot();
        if (!modeCaps(playerState.gameMode).infiniteResources) {
          playerState.inventory.set(slot, bucketId !== undefined ? makeStack(bucketId, 1) : emptyStack());
        }
      } else if (eating) {
        applyFood(playerState, def!.food!);
        const eatenName = registries.items.nameOf[held.item];
        if (eatenName) { fireAdvancement({ kind: 'consume', item: eatenName }); stats.bump('used', eatenName); } // M24: "Husbandry" + Items Used stat (eating counts as a use)
        // Food-borne effects (golden apple regen/absorption, suspicious stew, …).
        for (const g of foodEffects(def!.food!, () => Math.random())) {
          if (isInstantEffect(g.id)) applyInstant(playerState, g.id, g.amplifier);
          else addEffect(playerState.effects, g.id, g.amplifier, g.ticks);
        }
        // Absorption grants its (yellow-heart) pool once when applied (PLR-8).
        const absorb = absorptionAmount(playerState.effects);
        if (absorb > playerState.absorption) playerState.absorption = absorb;
        // Chorus fruit blinks you a short way (DIM-3) — what makes the End's one
        // renewable crop a way OFF a shard you got stranded on. A roll that finds
        // nowhere standable just feeds you, exactly as in Minecraft.
        if (eatenName && teleportsOnEat(eatenName)) {
          const to = chorusTeleportTarget(
            { isSolid: (x, y, z) => world.isSolid(x, y, z) },
            player.body.x, player.body.y, player.body.z, () => Math.random(),
          );
          if (to) { teleportPlayer(to); audio.playSound('entity.enderman.teleport', { x: to[0], y: to[1], z: to[2] }); }
        }
        consumeHeld();
      } else if (drinking) drinkPotion(potion!);
      eatTicks = 0;
    }
  };

  const onDeath = (): void => {
    if (isDeathShown()) return;
    stats.add('deaths'); // M24: Deaths stat — once per death (guarded by the death screen)
    document.exitPointerLock();
    showDeath({
      score: playerState.xpTotal,
      hardcore: !!meta.hardcore,
      onRespawn: respawn,
      onQuit: () => { void game.saveNow().finally(() => { location.href = location.pathname; }); },
    });
  };

  const respawn = (): void => {
    // Survival death scatters the inventory at the death site (unless keepInventory).
    const keep = world.gamerules.get('keepInventory') === 'true';
    if (!keep && modeCaps(playerState.gameMode).dropsOnDeath) {
      for (const s of playerState.inventory.slots) {
        if (isEmpty(s)) continue;
        drops.spawn(s, player.body.x, player.body.y + 0.5, player.body.z, {
          vx: (Math.random() - 0.5) * 0.3, vy: 0.2, vz: (Math.random() - 0.5) * 0.3, pickupDelay: 60,
        });
      }
      playerState.inventory.clear();
    }
    playerState.resetForRespawn();
    // Bed respawn (PLR-10): use the bed spawn if it is still clear (E-18) — a 2-tall
    // air pocket on solid ground near it; otherwise fall back to world spawn.
    const worldSpawn = { x: 8.5, y: spawnY, z: 8.5 };
    let sp = worldSpawn;
    if (playerState.spawn) {
      const bx = Math.floor(playerState.spawn.x), bz = Math.floor(playerState.spawn.z), by = Math.floor(playerState.spawn.y);
      let safe: { x: number; y: number; z: number } | null = null;
      for (let dy = 1; dy >= -2 && !safe; dy--) {
        const y = by + dy;
        if (!world.isSolid(bx, y, bz) && !world.isSolid(bx, y + 1, bz) && world.isSolid(bx, y - 1, bz)) safe = { x: playerState.spawn.x, y, z: playerState.spawn.z };
      }
      if (safe) sp = safe;
      else { playerState.spawn = null; game.print('Your home bed was missing or obstructed'); }
    }
    player.body.x = sp.x; player.body.y = sp.y; player.body.z = sp.z;
    player.body.vx = player.body.vy = player.body.vz = 0; player.body.onGround = false;
    playerState.fallPeak = sp.y;
    wasOnGround = false;
    if (game.locked) void game.renderer.domElement.requestPointerLock();
  };

  // ── Sculk catalyst blooms (Deep Dark, M22.1). A mob dying within range of a
  //    sculk_catalyst feeds it the death's XP as spread charge; sculk creeps out
  //    over the exposed surfaces and no orbs drop (the catalyst ate them). ──
  const sculkView: SculkView = {
    block: (x, y, z) => registries.states.blockName[world.getBlockState(x, y, z)] ?? null,
  };
  const bloomCatalystOnDeath = (dx: number, dy: number, dz: number, xp: number): boolean => {
    const death = { x: Math.floor(dx), y: Math.floor(dy), z: Math.floor(dz) };
    if (!catalystNear(death, sculkView)) return false; // no catalyst → orbs drop as usual
    const sculkState = registries.states.defaultOf.get(SCULK_BLOCK);
    const origin = chargeFromXp(xp) > 0 ? bloomOrigin(death, sculkView) : null;
    if (origin && sculkState !== undefined) {
      for (const c of bloom(origin, xp, sculkView).convert) edit(c.x, c.y, c.z, sculkState);
    }
    return true; // the catalyst absorbed the death's XP
  };

  // ── Living entities (MOB-1..5), one system per dimension. ──
  const makeEntities = (dim: DimId): EntitySystem => {
    const rng = new Rng(dim === 'overworld' ? world.seed : `${String(world.seed)}#${dim}`).fork(S_MOB);
    const es = new EntitySystem(registries.mobs.byName, rng);
    es.lootFn = (table) => rollLootByName(registries, table, rng, { killedByPlayer: true });
    // §28.2: heredity is a WORLD rule, so every dimension's animals read the overworld's
    // gamerules — the map `WorldMeta.gamerules` seeds (D-TF-241).
    es.rules = manager.world('overworld');
    es.warn = (message) => { console.warn(`[heredity] ${message}`); };
    // §28.8/§28.11: the flock's notices, plus the notebook's two automatic hooks —
    // an old-age death marks a RECORDED animal's record (a no-op otherwise); a
    // genetic birth is auto-recorded when this player already has both parents.
    es.onHeredity = (ev) => {
      if (ev.kind === 'her:capacity') { game.showActionBar(CAPACITY_COPY); fireEduTrigger({ kind: 'capacity_message' }); return; }
      if (ev.kind === 'her:oldAge') {
        puffs.spawn(ev.x, ev.y, ev.z);
        currentNotebook = markAnimalDeath(currentNotebook, ev.id, world.tickCount, 'old_age');
        return;
      }
      if (!hasAnimal(currentNotebook, ev.parents[0]) || !hasAnimal(currentNotebook, ev.parents[1])) return;
      const child = entities.byId(ev.id);
      const rec = child ? animalRecordOf(child, world.tickCount) : null;
      if (rec) {
        applyAnimalRecord(rec);
        // §29.12's own example ("breed a chick from two recorded parents") is
        // exactly this branch — both parents already recorded (M51.7).
        fireEduTrigger({ kind: 'mob_bred', species: child!.type, recordedParents: true });
      }
    };
    // E-54: ridden, or leashed into a caravan.
    es.held = (e) => e.id === ridingMountId || caravanLinks.has(e.id) || [...caravanLinks.values()].includes(e.id);
    // Killing a raid captain (variant-1 raider) brands the player with Bad Omen.
    es.onPlayerKill = (e) => {
      const def = registries.mobs.byName.get(e.type);
      if (def?.raider && e.variant === 1) addEffect(playerState.effects, 'bad_omen', 0, RAID.BAD_OMEN_TICKS);
      fireAdvancement({ kind: 'kill', entity: e.type }); stats.bump('killed', e.type); stats.add('mob_kills'); // M24: "Monster Hunter" + Mobs Killed / Mob Kills stats
    };
    es.onPlayerDamage = (_e, amount) => stats.add('damage_dealt', Math.round(amount * 10)); // M24: Damage Dealt (tenths of a heart)
    // Barding softens a horse's hits through the SAME curve a player's armour uses
    // (M61) — which is what makes a diamond-barded horse worth the diamonds.
    es.mitigateDamage = (e, amount) => {
      const points = horseArmorPoints(bardingOf(e));
      return points > 0 ? mitigate(amount, points, 0, 0) : amount;
    };
    es.onMobHurt = (e) => audio.playSound(`entity.${e.type}.hurt`, { x: e.x, y: e.y + 0.5, z: e.z }); // AUD-2
    es.onMobDeath = (e, def) => {
      // §28.11: any death (not just old age, which has its own path through
      // `onHeredity`'s `her:oldAge`) marks a RECORDED animal's record — a no-op
      // for one this player never recorded.
      currentNotebook = markAnimalDeath(currentNotebook, e.id, world.tickCount, 'other');
      audio.playSound(`entity.${e.type}.death`, { x: e.x, y: e.y + 0.5, z: e.z }); // AUD-2 death voice
      // A chested pack animal gives everything back — the chest and its contents —
      // so losing a donkey costs you the donkey, not your cargo (M19 remainder).
      if (e.data.chest === 1) {
        for (const name of chestDropsOnDeath(true)) {
          const id = registries.items.idOf.get(name);
          if (id !== undefined) drops.spawn(makeStack(id, 1), e.x, e.y + 0.5, e.z, { pickupDelay: 10 });
        }
        if (e.pack) for (const s of packContents(e.pack)) drops.spawn(s, e.x, e.y + 0.5, e.z, { pickupDelay: 10 });
      }
      // A llama's carpet and a horse's barding come back too (M61) — you lose the
      // animal, not the diamonds you put on it.
      for (const name of [...llamaDecorDrop(llamaCarpetOf(e)), ...bardingDropsOnDeath(bardingOf(e))]) {
        const id = registries.items.idOf.get(name);
        if (id !== undefined) drops.spawn(makeStack(id, 1), e.x, e.y + 0.5, e.z, { pickupDelay: 10 });
      }
      // A slain llama's caravan breaks at the cut, as Minecraft's does.
      if (caravanLinks.has(e.id) || [...caravanLinks.values()].includes(e.id)) {
        caravanLinks = caravanLeave(caravanLinks, e.id);
      }
      // The slain ender dragon raises the exit portal home on the arena, egg on its
      // plinth (DIM-3). Outside the End — a dragon summoned in the overworld by
      // ritual (M21.1) — it just lays the egg where it fell, as before.
      if (e.type === 'ender_dragon') {
        if (world.dimension === 'end') { raiseEndExitPortal(); raiseEndGateway(); }
        else { const eg = registries.states.defaultOf.get('dragon_egg'); if (eg !== undefined) edit(Math.floor(e.x), Math.floor(e.y), Math.floor(e.z), eg); }
      }
      return bloomCatalystOnDeath(e.x, e.y, e.z, def.xp);
    };
    // The wither shrugs off all damage while it charges up on spawn (M20.1); the
    // ender dragon is shielded while any end crystal still heals it (M21.1).
    es.invulnerable = (e) =>
      (e.type === 'wither' && witherInvulnerable(e.data.summonTicks ?? 0)) ||
      (e.type === 'ender_dragon' && !dragonKillable(crystalsAliveNear(e.x, e.y, e.z)));
    // A saddled mount the player rides steers with the player's movement input (M19);
    // a warden hunting a MOB quarry is driven at it the same way (M22.1) — this seam
    // is what beats the mob's own AI wish, which would otherwise send it at the player.
    es.controlled = (e) => {
      // A llama in a caravan walks to its leader's heel (M61).
      const leaderId = caravanLinks.get(e.id);
      if (leaderId !== undefined) {
        const leader = entities.entities.find((m) => m.id === leaderId && !m.removed);
        if (!leader) { caravanLinks = caravanLeave(caravanLinks, e.id); return null; }
        if (tetherSnapped(leader.x, leader.z, e.x, e.z)) { caravanLinks = caravanLeave(caravanLinks, e.id); return null; }
        const want = followPoint(leader.x, leader.z, e.x, e.z);
        if (!want) return { wishX: 0, wishZ: 0, jump: false }; // close enough to graze
        const sp = (registries.mobs.byName.get(e.type)?.speed ?? 6) / 20;
        const dx = want.x - e.x, dz = want.z - e.z;
        const d = Math.hypot(dx, dz) || 1;
        return { wishX: (dx / d) * sp, wishZ: (dz / d) * sp, jump: false };
      }
      if (e.type === 'warden') {
        const quarry = e.data.quarry ?? -1;
        if (quarry <= 0 || !wardenEmerged(e.data.wardenEmerge ?? 0)) return null;
        const prey = entities.entities.find((m) => m.id === quarry && !m.removed);
        if (!prey) return null;
        const dist = Math.hypot(prey.x - e.x, prey.z - e.z);
        if (dist <= WARDEN.MOB_REACH) return { wishX: 0, wishZ: 0, jump: false };
        const sp = (registries.mobs.byName.get('warden')?.speed ?? 5) / 20;
        const inv = sp / dist;
        return { wishX: (prey.x - e.x) * inv, wishZ: (prey.z - e.z) * inv, jump: false };
      }
      if (e.id !== ridingMountId || !game.locked) return e.id === ridingMountId ? { wishX: 0, wishZ: 0, jump: false } : null;
      const kind = mountKindOf(e.type);
      if (!kind || !isSteerable(kind, e.data.saddled === 1)) return { wishX: 0, wishZ: 0, jump: false };
      // A pig/strider only goes where its stick points — without it held, you sit.
      if (needsHeldControl(kind) && heldItemName() !== controlItem(kind)) return { wishX: 0, wishZ: 0, jump: false };
      // This horse's own rolled speed attribute scales how fast it carries you.
      const base = (registries.mobs.byName.get(e.type)?.speed ?? 4) / 20; // blocks/tick
      const fwd = (game.input.forward ? 1 : 0) - (game.input.back ? 1 : 0);
      const { wishX, wishZ } = mountWish(fwd, player.yaw, base * (e.data.speedMul ?? 1));
      // A horse's jump key winds up its CHARGED jump (rideMountTick) — passing it
      // through here as well would make it hop flatly at the same time.
      return { wishX, wishZ, jump: chargesJump(kind) ? false : game.input.jump };
    };
    return es;
  };
  // ── Llama caravans + horse barding (M61) ──
  /** Follower entity id → the llama it trails. Rebuilt from nothing on load: a
   *  caravan is a walk, not a possession, and Minecraft drops it on reload too. */
  let caravanLinks = new Map<number, number>();
  /** The llama currently in hand on a lead, if any. */
  let ledLlamaId: number | null = null;
  /** Barding item names, indexed by the `e.data.barding` value minus one. */
  const HORSE_ARMOR_ITEMS: readonly string[] = ['leather_horse_armor', 'iron_horse_armor', 'golden_horse_armor', 'diamond_horse_armor'];
  const bardingOf = (e: Entity): string | null => HORSE_ARMOR_ITEMS[(e.data.barding ?? 0) - 1] ?? null;
  const llamaCarpetOf = (e: Entity): string | null => DYE_ORDER[(e.data.decor ?? 0) - 1] ?? null;

  let entities = makeEntities('overworld');
  entities.loadJSON(loadedEntities);
  const entityRenderer = new EntityRenderer(rc.scene, registries.mobs.byName);
  const puffs = new PuffRenderer(rc.scene); // §28.8 old-age puffs
  refreshCropOverlay(); // solo: paint whatever the loaded save already carries
  const actionBarState = { text: '', serial: 0 };
  const difficulty = meta.difficulty as Difficulty;

  // The world as the entity system sees it: block probes delegate to `world`;
  // block-breaking (creeper craters) routes through `edit` so it relights/remeshes.
  const mobWorld: MobWorld = {
    isSolid: (x, y, z) => world.isSolid(x, y, z),
    collisionBoxesAt: (x, y, z) => world.collisionBoxesAt(x, y, z),
    getBlockLight: (x, y, z) => world.getBlockLight(x, y, z),
    getSkyLight: (x, y, z) => world.getSkyLight(x, y, z),
    isFluid: (x, y, z) => world.isFluid(x, y, z),
    isGrass: (x, y, z) => {
      const n = registries.states.blockName[world.getBlockState(x, y, z)];
      return n === 'grass_block' || n === 'tall_grass';
    },
    getBlockState: (x, y, z) => world.getBlockState(x, y, z),
    blastResistance: (x, y, z) => {
      const st = world.getBlockState(x, y, z);
      if (st === STATE_AIR) return 0;
      const def = registries.blocks.byName.get(registries.states.blockName[st] ?? '');
      if (!def || def.material === 'air') return 0;
      return def.hardness < 0 ? Infinity : def.hardness;
    },
    breakBlock: (x, y, z) => {
      // A TNT block caught in a blast re-primes with a short random fuse instead
      // of vanishing — the chain reaction (RED-8, M15.2.2).
      if (registries.states.blockName[world.getBlockState(x, y, z)] === 'tnt') {
        ignitePrimedTntAt(x, y, z, TNT.CHAIN_FUSE_MIN + tntRng.nextInt(TNT.CHAIN_FUSE_SPREAD));
      } else {
        edit(x, y, z, STATE_AIR);
      }
    },
    // Enderman block carry (M13.2): a small whitelist of soft terrain blocks.
    isCarryable: (state) => ENDERMAN_CARRY.has(registries.states.blockName[state] ?? ''),
    placeBlock: (x, y, z, state) => edit(x, y, z, state),
  };

  // Equipped-armour readers (M17 gear): the four armour columns' defence stats +
  // enchant lists drive the PLR-3 mitigation the player takes.
  const armorPieces = (): (ArmorStats | null)[] => {
    const out: (ArmorStats | null)[] = [];
    for (let s = INV.ARMOR_START; s < INV.ARMOR_END; s++) {
      const st = playerState.inventory.get(s);
      out.push(isEmpty(st) ? null : armorStatsOf(registries.items.nameOf[st.item]));
    }
    return out;
  };
  const armorEnchants = (): (ReadonlyArray<{ id: string; lvl: number }> | undefined)[] => {
    const out: (ReadonlyArray<{ id: string; lvl: number }> | undefined)[] = [];
    for (let s = INV.ARMOR_START; s < INV.ARMOR_END; s++) out.push(playerState.inventory.get(s).components?.enchants);
    return out;
  };
  // Totem of undying: on a lethal hit, if a totem is in hand or offhand, consume
  // it and revive to 1 HP with the totem effects (PLR-3). Returns true if saved.
  const totemSave = (): boolean => {
    const inv = playerState.inventory;
    const totemId = registries.items.idOf.get('totem_of_undying');
    if (totemId === undefined) return false;
    const slot = [inv.heldSlot(), INV.OFFHAND].find((s) => { const st = inv.get(s); return !isEmpty(st) && st.item === totemId; });
    const res = resolveTotem(playerState.health, slot !== undefined);
    if (!res.activated || slot === undefined) return false;
    const st = inv.get(slot);
    inv.set(slot, st.count <= 1 ? emptyStack() : { ...st, count: st.count - 1 });
    playerState.health = res.newHealth;
    playerState.absorption = 0;
    for (const e of res.effects) addEffect(playerState.effects, e.id, e.amplifier, e.ticks);
    return true;
  };

  // The player as the entity system sees it (getters track the live body).
  const playerHandle: PlayerHandle = {
    get x() { return player.body.x; },
    get y() { return player.body.y; },
    get z() { return player.body.z; },
    eyeY: PLAYER.EYE_HEIGHT,
    get invulnerable() { return modeCaps(playerState.gameMode).invulnerable || playerState.dead; },
    hurt: (amount, kbX, kbZ) => {
      if (modeCaps(playerState.gameMode).invulnerable || playerState.dead) return;
      const totals = armorTotals(armorPieces());
      // Armour EPF, then Resistance, then the absorption/health pool (PLR-8).
      const afterArmor = absorbWithGear(amount, totals, armorEnchants(), 'generic');
      const applied = afterArmor * resistanceMultiplier(playerState.effects);
      playerState.applyDamage(applied);
      if (applied > 0) stats.add('damage_taken', Math.round(applied * 10)); // M24: Damage Taken (tenths of a heart)
      playerState.addExhaustion(SURVIVAL.EXHAUST_DAMAGE);
      const kb = Math.hypot(kbX, kbZ);
      if (kb > 0) {
        const resist = Math.max(0, 1 - totals.knockback); // netherite armour resists knockback
        player.body.vx += (kbX / kb) * MOB.MELEE.KNOCKBACK * resist;
        player.body.vz += (kbZ / kb) * MOB.MELEE.KNOCKBACK * resist;
        player.body.vy = Math.max(player.body.vy, 0.36);
        player.body.onGround = false;
      }
      if (playerState.dead && !totemSave()) onDeath();
    },
    addXp: (n) => playerState.addExperience(n),
    setFire: (t) => { if (!modeCaps(playerState.gameMode).invulnerable) playerState.fireTicks = Math.max(playerState.fireTicks, t); },
    addEffect: (id, amplifier, ticks) => { if (!modeCaps(playerState.gameMode).invulnerable && !playerState.dead) addEffect(playerState.effects, id, amplifier, ticks); },
    heal: (amount) => { if (!playerState.dead) playerState.heal(amount); },
    lookDir: () => lookDir(),
    offering: (item) => {
      const held = playerState.inventory.held();
      return !isEmpty(held) && registries.items.nameOf[held.item] === item;
    },
    consumeOffered: (item) => {
      const inv = playerState.inventory;
      const slot = inv.heldSlot();
      const s = inv.get(slot);
      if (!isEmpty(s) && registries.items.nameOf[s.item] === item) {
        inv.set(slot, s.count <= 1 ? emptyStack() : { ...s, count: s.count - 1 });
      }
    },
  };

  const makeFrame = (): MobFrame => ({
    world: mobWorld,
    // §27.8/§27.19's INJECTED TICK SOURCE. Without it the mob cadence, the frame
    // ceiling and the per-dispatch rng fork are all inactive — every scripted mob
    // dispatches every tick against an unforked stream.
    tick: world.tickCount,
    dayTime: world.dayTime,
    difficulty,
    mobGriefing: world.gamerules.get('mobGriefing') !== 'false',
    raining: world.dimension === 'overworld' && world.weather.raining,
    players: [playerHandle],
    dropLoot: (stack, x, y, z) => {
      drops.spawn(stack, x, y, z, { vx: (Math.random() - 0.5) * 0.15, vy: 0.2, vz: (Math.random() - 0.5) * 0.15 });
    },
  });

  // Nether standable Y: scan a loaded column for a 2-air gap on solid netherrack
  // (the surface heightmap points at the ceiling in the nether, not the floor).
  //
  // A FLUID floor counts too: a strider walks on the lava sea, and its `spawn.ground`
  // list is what actually admits it — every other nether mob still needs the floor
  // below to be solid, which `canSpawnAt` enforces.
  const netherStandY = (x: number, z: number): number | null => {
    if (!world.hasChunk(Math.floor(x / WORLD.SECTION), Math.floor(z / WORLD.SECTION))) return null;
    for (let y = 118; y > 5; y--) {
      const footing = world.isSolid(x, y - 1, z) || world.isFluid(x, y - 1, z);
      if (footing && !world.isSolid(x, y, z) && !world.isSolid(x, y + 1, z)) return y;
    }
    return null;
  };
  const makeSpawnCtx = (): SpawnContext => ({
    world: mobWorld,
    dayTime: world.dayTime,
    difficulty,
    dimension: world.dimension,
    seed: world.seed,
    doMobSpawning: world.gamerules.get('doMobSpawning') !== 'false',
    players: [{ x: player.body.x, y: player.body.y, z: player.body.z }],
    surfaceY: (x, z) => (world.dimension === 'nether' ? netherStandY(x, z) : world.surfaceY(x, z)),
    groundName: (x, y, z) => registries.states.blockName[world.getBlockState(x, y, z)] ?? null,
    biomeName: (x, z) => world.biomeNameAt(x, z), // MOB-4: gate spawns by biome
    // …and by structure, for the mobs that belong to one (the elder guardian).
    inStructure: (type, x, z) => insideStructure(type, world.seed, x, z, structEnv),
  });

  // Golem construction (MOB-2): placing a carved pumpkin on a valid iron/snow
  // frame consumes the frame and spawns a persistent golem in its place.
  const blockNameAt = (x: number, y: number, z: number): string =>
    registries.states.blockName[world.getBlockState(x, y, z)] ?? 'air';
  const tryBuildGolem = (x: number, y: number, z: number): boolean => {
    const build = checkGolem(blockNameAt, x, y, z);
    if (!build) return false;
    for (const [cx, cy, cz] of build.clear) edit(cx, cy, cz, STATE_AIR);
    return entities.spawn(build.type, build.spawn[0] + 0.5, build.spawn[1], build.spawn[2] + 0.5, { persistent: true }) !== null;
  };
  // Wither summon (MOB-6): three wither skeleton skulls atop a soul-block T ignite
  // the boss. Any of the three top skulls may be the one just placed, so each is
  // tested as the frame's centre.
  const tryBuildWither = (x: number, y: number, z: number): boolean => {
    for (const [cx, cz] of [[x, z], [x - 1, z], [x + 1, z], [x, z - 1], [x, z + 1]] as const) {
      const build = checkWitherSummon(blockNameAt, cx, y, cz);
      if (!build) continue;
      for (const [bx, by, bz] of build.clear) edit(bx, by, bz, STATE_AIR);
      const e = entities.spawn('wither', build.spawn[0] + 0.5, build.spawn[1], build.spawn[2] + 0.5, { persistent: true });
      if (e) e.data.summonTicks = WITHER.SUMMON_TICKS;
      return e !== null;
    }
    return false;
  };
  // Sculk shrieker → warden (M22.1). Each shriek raises the player's warning level
  //  + inflicts Darkness; a shriek at the top of the ladder summons a warden nearby.
  let sculkWarning = 0;
  const triggerShriek = (x: number, y: number, z: number): { level: number; summoned: boolean } => {
    if (registries.states.blockName[world.getBlockState(x, y, z)] !== 'sculk_shrieker') return { level: sculkWarning, summoned: false };
    sculkWarning = nextWarningLevel(sculkWarning);
    addEffect(playerState.effects, 'darkness', 0, WARDEN.DARKNESS_TICKS);
    if (!shouldSummonWarden(sculkWarning)) return { level: sculkWarning, summoned: false };
    const e = entities.spawn('warden', x + 2.5, y + 1, z + 0.5, { persistent: true });
    if (e) e.data.wardenEmerge = WARDEN.EMERGE_TICKS;
    sculkWarning = 0; // the ladder resets after a summon
    return { level: WARDEN.SUMMON_LEVEL, summoned: e !== null };
  };
  // A warden's armour-piercing sonic boom (needs the host's true-damage player path).
  const fireWardenSonicBoom = (e: Entity): number => {
    if (e.type !== 'warden' || !wardenEmerged(e.data.wardenEmerge ?? 0)) return 0;
    const b = player.body;
    if (!sonicBoomHits(e.x, e.y, e.z, b.x, b.y, b.z)) return 0;
    const dmg = sonicBoomDamage();
    playerState.applyDamage(dmg); // bypasses armour + shields (WARDEN.SONIC_BYPASS)
    e.data.sonicCd = WARDEN.SONIC_COOLDOWN;
    return dmg;
  };
  // Ocean fauna (M18.1): a dolphin near a swimmer grants Dolphin's Grace; a puffed
  //  pufferfish poisons a player who touches it.
  const tickOceanFauna = (): void => {
    const b = player.body;
    const inWater = world.isFluid(Math.floor(b.x), Math.floor(b.y), Math.floor(b.z));
    for (const e of entities.entities) {
      if (e.removed) continue;
      const dist = Math.hypot(b.x - e.x, b.y - e.y, b.z - e.z);
      if (e.type === 'dolphin') {
        if (grantsDolphinGrace(dist, inWater)) addEffect(playerState.effects, 'dolphins_grace', 0, dolphinGraceTicks());
      } else if (e.type === 'pufferfish') {
        if (pufferfishPoisons(dist, pufferfishPuffed(dist))) addEffect(playerState.effects, 'poison', 0, pufferfishPoisonTicks());
      }
    }
  };
  /**
   * The warden's in-world hunt (M22.1 extension). Its anger ledger — which the pure
   * module has had since M22.1 but nothing ever fed — now decides what it goes
   * after. Vibrations fill the ledger; here it decays, picks its quarry, and acts:
   *
   *  - quarry is the PLAYER → leave it to the melee override + the sonic boom.
   *  - quarry is a MOB → drive the warden at it and strike on cooldown. A blind
   *    hunter tracking sound has no reason to prefer the player, and this is the
   *    only way that shows in the world.
   *  - nothing left to be angry about → it digs away and despawns, as in Minecraft.
   */
  const tickWardens = (): void => {
    const decayDue = world.tickCount % WARDEN.DECAY_INTERVAL === 0;
    for (const e of entities.entities) {
      if (e.removed || e.type !== 'warden') continue;
      e.data.sonicCd = Math.max(0, (e.data.sonicCd ?? 0) - 1);
      e.data.mobStrikeCd = Math.max(0, (e.data.mobStrikeCd ?? 0) - 1);
      if (!wardenEmerged(e.data.wardenEmerge ?? 0)) continue;

      const ledger = ledgerFor(e.id);
      if (decayDue) decayAnger(ledger);
      const quarry = wardenQuarry(ledger);
      // A pacified warden burrows away rather than standing around forever.
      if (!quarry && wardenPacified(ledger)) {
        e.data.calmTicks = (e.data.calmTicks ?? 0) + 1;
        if (e.data.calmTicks >= WARDEN.PACIFIED_DESPAWN_TICKS) { e.removed = true; wardenAnger.delete(e.id); }
        continue;
      }
      e.data.calmTicks = 0;
      e.data.angerTop = quarry?.anger ?? 0;
      e.data.quarry = quarry?.suspect ?? -1;

      // Hunting a MOB: steer at it and strike. (The player case falls through to
      // the melee override, which already chases and hits.)
      if (quarry && quarry.hunting && quarry.suspect !== PLAYER_SOURCE) {
        const prey = entities.entities.find((m) => m.id === quarry.suspect && !m.removed);
        if (prey) {
          // Steering goes through `es.controlled` (below), the engine seam that
          // OVERRIDES a mob's AI wish — setting e.wishX/wishZ here would just be
          // overwritten by the warden's own melee AI a moment later.
          const dist = Math.hypot(prey.x - e.x, prey.z - e.z);
          e.yaw = Math.atan2(-(prey.x - e.x), -(prey.z - e.z));
          if (dist <= WARDEN.MOB_REACH && e.data.mobStrikeCd === 0) {
            const dmg = registries.mobs.byName.get('warden')?.attackDamage ?? 0;
            if (dmg > 0) entities.damage(prey, dmg, makeFrame(), prey.x - e.x, prey.z - e.z, false);
            e.data.mobStrikeCd = WARDEN.MOB_STRIKE_COOLDOWN;
          }
          continue; // it is busy with the mob; no sonic boom at the player
        }
        ledger.delete(quarry.suspect); // the suspect is gone — forget it
      }

      if (e.data.sonicCd > 0) continue;
      const b = player.body;
      const d = Math.hypot(b.x - e.x, b.y - e.y, b.z - e.z);
      if (d >= WARDEN.SONIC_MIN_RANGE && d <= WARDEN.SONIC_RANGE) fireWardenSonicBoom(e);
    }
  };
  // Guardian (M18.1): a charge-up beam (charge holds only while in range/LOS, fires
  // its hit at full charge) and the elder guardian's Mining Fatigue aura.
  // A solid block between the two points breaks line of sight.
  const losBlocked = (ax: number, ay: number, az: number, bx: number, by: number, bz: number): boolean => {
    const d = Math.hypot(bx - ax, by - ay, bz - az);
    if (d === 0) return false;
    const hit = raycastVoxel((x, y, z) => world.isSolid(x, y, z), { x: ax, y: ay, z: az }, { x: bx - ax, y: by - ay, z: bz - az }, d);
    return hit !== null && hit.distance < d - 0.5;
  };
  const tickGuardians = (): void => {
    const b = player.body;
    for (const e of entities.entities) {
      if (e.removed) continue;
      if (e.type !== 'guardian' && e.type !== 'elder_guardian') continue;
      const elder = e.type === 'elder_guardian';
      const dist = Math.hypot(b.x - e.x, b.y - e.y, b.z - e.z);
      if (elder && elderInflictsFatigue(dist) && fatigueReapplies(world.tickCount)) {
        addEffect(playerState.effects, 'mining_fatigue', fatigueAmplifier(), fatigueTicks());
      }
      // Beam: line of sight approximated by a clear straight ray to the player.
      const los = !losBlocked(e.x, e.y + 0.5, e.z, b.x, b.y + PLAYER.EYE_HEIGHT, b.z);
      const charge = beamCharge((e.data.beam as number) ?? 0, los, dist);
      e.data.beam = charge;
      if (beamFires(charge)) { playerHandle.hurt(beamDamage(elder), 0, 0); e.data.beam = 0; }
    }
  };
  // Convert a hoglin into a zoglin in place (overworld zombify — MOB, M17.1).
  const convertHoglinToZoglin = (e: Entity): void => {
    entities.spawn('zoglin', e.x, e.y, e.z);
    e.removed = true;
  };
  // Nether fauna (M17.1): a hoglin dragged into the overworld zombifies to a zoglin
  // after a timer; a strider standing in water takes damage.
  const tickNetherFauna = (): void => {
    const overworld = hoglinZombifyStarts(world.dimension);
    for (const e of entities.entities) {
      if (e.removed) continue;
      if (e.type === 'hoglin') {
        if (overworld) {
          e.data.owTicks = ((e.data.owTicks as number) ?? 0) + 1;
          if (hoglinZombifies(e.data.owTicks as number)) convertHoglinToZoglin(e);
        } else e.data.owTicks = 0;
      } else if (e.type === 'strider') {
        const inWater = registries.states.blockName[world.getBlockState(Math.floor(e.x), Math.floor(e.y), Math.floor(e.z))] === 'water';
        if (striderHurtByWater(inWater, world.tickCount)) entities.damage(e, 1, makeFrame(), 0, 0, false);
      }
    }
  };
  // Turtle (M18.1): a baby turtle that grows into an adult drops one scute.
  const tickTurtles = (): void => {
    for (const e of entities.entities) {
      if (e.removed || e.type !== 'turtle') continue;
      if (e.baby) { e.data.wasBaby = 1; continue; }
      if (e.data.wasBaby === 1) {
        e.data.wasBaby = 0;
        const id = registries.items.idOf.get(scuteFromGrowUp());
        if (id !== undefined) drops.spawn(makeStack(id, 1), e.x, e.y + 0.2, e.z, { vy: 0.15 });
      }
    }
  };
  // Allay (M18.1-adjacent): its duplication cooldown ticks down each tick.
  const tickAllays = (): void => {
    for (const e of entities.entities) {
      if (e.removed || e.type !== 'allay') continue;
      if ((e.data.dupCd as number) > 0) e.data.dupCd = (e.data.dupCd as number) - 1;
    }
  };
  // Sculk sensor (M22.1): wake every idle sensor within range of a vibration at
  // (vx,vy,vz) — set it powered, mark redstone dirty, and start its active timer.
  const wakeSculkSensorsNear = (vx: number, vy: number, vz: number): void => {
    for (const k of sculkSensorCells) {
      if (sculkActive.has(k)) continue; // already active
      const [x, y, z] = k.split(',').map(Number) as [number, number, number];
      if (!sculkDetects(Math.hypot(vx - x, vy - y, vz - z))) continue;
      rsSetProp(x, y, z, 'powered', 'true');
      redstone.markDirty(x, y, z);
      sculkActive.set(k, sculkActiveTicks());
    }
  };
  /**
   * Emit a sculk vibration (M22.1 extension). Before this, the ONLY thing in the
   * world that made a vibration was the player moving, and the only thing that
   * heard one was a sculk sensor. Now every source routes through here, and both
   * listeners get it: sensors wake, and every warden in earshot blames whoever
   * caused it. A warden ignores its own noise, so a pair never hunt each other.
   */
  // Per-warden anger ledgers. Entity.data only holds numbers, so the tables live
  // here, keyed by warden id and dropped when that warden goes.
  const wardenAnger = new Map<number, AngerLedger>();
  const ledgerFor = (id: number): AngerLedger => {
    let l = wardenAnger.get(id);
    if (!l) { l = new Map(); wardenAnger.set(id, l); }
    return l;
  };
  const emitVibration = (event: string, x: number, y: number, z: number, source: number): void => {
    if (sculkSensorCells.size > 0) wakeSculkSensorsNear(x, y, z);
    for (const e of entities.entities) {
      if (e.removed || e.type !== 'warden' || e.id === source) continue;
      if (!vibrationReaches(Math.hypot(x - e.x, y - e.y, z - e.z))) continue;
      const culprit = source === PLAYER_SOURCE ? null : entities.entities.find((m) => m.id === source);
      if (!angersWarden(culprit?.type ?? null)) continue;
      addAnger(ledgerFor(e.id), source, vibrationAnger(event));
    }
  };
  emitVibrationRef = emitVibration;
  /** True when anything is listening — lets the per-mob step scan stay free otherwise. */
  const anythingListens = (): boolean =>
    sculkSensorCells.size > 0 || entities.entities.some((e) => !e.removed && e.type === 'warden');
  const mobPrevPos = new Map<number, { x: number; y: number; z: number }>();

  let sculkPrevPos = { x: player.body.x, y: player.body.y, z: player.body.z };
  const tickSculkSensors = (): void => {
    // A moving player is a vibration; wake any sensors that can hear it.
    const b = player.body;
    if (Math.hypot(b.x - sculkPrevPos.x, b.y - sculkPrevPos.y, b.z - sculkPrevPos.z) > 0.01) {
      emitVibration('step', b.x, b.y, b.z, PLAYER_SOURCE);
      sculkPrevPos = { x: b.x, y: b.y, z: b.z };
    }
    // …and so is a moving MOB. This is what lets a warden hunt something other than
    // the player: the vibration carries the mob's id, so the mob enters its ledger.
    if (anythingListens()) {
      for (const e of entities.entities) {
        if (e.removed) continue;
        const prev = mobPrevPos.get(e.id);
        mobPrevPos.set(e.id, { x: e.x, y: e.y, z: e.z });
        if (!prev) continue;
        if (Math.hypot(e.x - prev.x, e.y - prev.y, e.z - prev.z) <= WARDEN.STEP_EPSILON) continue;
        emitVibration('step', e.x, e.y, e.z, e.id);
      }
    }
    if (mobPrevPos.size > entities.entities.length * 2) {
      const live = new Set(entities.entities.filter((e) => !e.removed).map((e) => e.id));
      for (const id of [...mobPrevPos.keys()]) if (!live.has(id)) mobPrevPos.delete(id);
    }
    // Active sensors count down, then power off.
    for (const [k, left] of [...sculkActive]) {
      if (left > 1) { sculkActive.set(k, left - 1); continue; }
      sculkActive.delete(k);
      const [x, y, z] = k.split(',').map(Number) as [number, number, number];
      rsSetProp(x, y, z, 'powered', 'false');
      redstone.markDirty(x, y, z);
    }
  };
  // Wind charge (M22.1): a burst at (cx,cy,cz) pushes the player + nearby mobs away
  // and toggles any activatable block it catches.
  const toggleWindActivatable = (x: number, y: number, z: number, name: string): void => {
    if (name === 'lever') {
      const p = registries.states.props[world.getBlockState(x, y, z)] ?? {};
      rsSetProp(x, y, z, 'powered', p.powered === 'true' ? 'false' : 'true');
      redstone.markDirty(x, y, z);
    } else if (name === 'stone_button' || name === 'oak_button') {
      redstone.pressButton(x, y, z);
    }
  };
  const detonateWindCharge = (cx: number, cy: number, cz: number): void => {
    const b = player.body;
    if (windBurstKnockback(Math.hypot(b.x - cx, b.y - cy, b.z - cz)) > 0) playerHandle.hurt(windChargeDamage(), cx, cz);
    for (const e of entities.entities) {
      if (e.removed) continue;
      const kb = windBurstKnockback(Math.hypot(e.x - cx, e.y - cy, e.z - cz));
      if (kb <= 0) continue;
      const dx = e.x - cx, dz = e.z - cz, len = Math.hypot(dx, dz) || 1;
      e.vx += (dx / len) * kb; e.vz += (dz / len) * kb; e.vy = Math.max(e.vy, kb * 0.5); e.onGround = false;
    }
    const r = Math.ceil(windBurstRadius());
    const ix = Math.floor(cx), iy = Math.floor(cy), iz = Math.floor(cz);
    for (let dx = -r; dx <= r; dx++) for (let dy = -r; dy <= r; dy++) for (let dz = -r; dz <= r; dz++) {
      if (Math.hypot(dx, dy, dz) >= windBurstRadius()) continue;
      const name = registries.states.blockName[world.getBlockState(ix + dx, iy + dy, iz + dz)];
      if (name && windChargeActivates(name)) toggleWindActivatable(ix + dx, iy + dy, iz + dz, name);
    }
  };
  // Breeze (M22.1): each breeze fires a wind charge at the player on its cadence
  // (the projectile travel is elided — the burst lands at the player).
  const tickBreezes = (): void => {
    const b = player.body;
    for (const e of entities.entities) {
      if (e.removed || e.type !== 'breeze') continue;
      const cd = ((e.data.fireCd as number) ?? breezeFireInterval()) - 1;
      if (!breezeReadyToFire(cd)) { e.data.fireCd = cd; continue; }
      // Ready: fire only when the player is in sight-range; else stay primed.
      if (Math.hypot(b.x - e.x, b.y - e.y, b.z - e.z) <= 16) { detonateWindCharge(b.x, b.y, b.z); e.data.fireCd = breezeFireInterval(); }
      else e.data.fireCd = 0;
    }
  };
  // Sniffer (M19): a sniffer over diggable ground unearths a torchflower seed on its
  // dig cadence. `digSniffer` takes the roll so a debug seam can force a find.
  const snifferRng = new Rng(`${String(meta.seed)}#sniffer`);
  const digSniffer = (e: Entity, digRoll: number, seedRoll = 0): string => {
    const gx = Math.floor(e.x), gy = Math.floor(e.y) - 1, gz = Math.floor(e.z);
    const ground = registries.states.blockName[world.getBlockState(gx, gy, gz)] ?? '';
    if (!snifferDiggableGround(ground) || !snifferDigsSeed(digRoll)) return '';
    const seed = snifferSeed(seedRoll); // torchflower seeds or a pitcher pod
    const id = registries.items.idOf.get(seed);
    if (id === undefined) return '';
    drops.spawn(makeStack(id, 1), e.x, e.y + 0.2, e.z, { vy: 0.15 });
    return seed;
  };
  const tickSniffers = (): void => {
    for (const e of entities.entities) {
      if (e.removed || e.type !== 'sniffer' || e.baby) continue;
      const cd = ((e.data.digCd as number) ?? snifferDigInterval()) - 1;
      if (cd > 0) { e.data.digCd = cd; continue; }
      e.data.digCd = snifferDigInterval();
      digSniffer(e, snifferRng.nextFloat(), snifferRng.nextFloat());
    }
  };
  // Camel (M19): its dash cooldown ticks down each tick. `dashCamel` applies the
  // dash impulse (toward the camel's facing) if the cooldown is ready.
  const tickCamels = (): void => {
    for (const e of entities.entities) {
      if (e.removed || e.type !== 'camel') continue;
      if ((e.data.dashCd as number) > 0) e.data.dashCd = (e.data.dashCd as number) - 1;
    }
  };
  // Tadpole (M19): a tadpole (laid when frogs breed) grows into a frog after its
  // timer, taking the frog variant of the biome it matures in (→ froglight colour).
  const convertTadpoleToFrog = (e: Entity): void => {
    const variant = frogVariantForBiome(world.biomeNameAt(Math.floor(e.x), Math.floor(e.z)) ?? '');
    // E-52: a tadpole's genome and parents carry into its frog when the two share a table.
    const carried = carryAcross(e, registries.mobs.byName.get('tadpole')?.genes, registries.mobs.byName.get('frog')?.genes);
    entities.spawn('frog', e.x, e.y, e.z, { variant, ...carried });
    e.removed = true;
  };
  const tickTadpoles = (): void => {
    for (const e of entities.entities) {
      if (e.removed || e.type !== 'tadpole') continue;
      e.data.tadAge = ((e.data.tadAge as number) ?? 0) + 1;
      if ((e.data.tadAge as number) >= tadpoleGrowTicks()) convertTadpoleToFrog(e);
    }
  };
  const dashCamel = (e: Entity): boolean => {
    if (!camelCanDash((e.data.dashCd as number) ?? 0)) return false;
    const imp = camelDashImpulse();
    e.vx += Math.sin(e.yaw) * imp;
    e.vz += Math.cos(e.yaw) * imp;
    e.vy = Math.max(e.vy, camelDashHop());
    e.onGround = false;
    e.data.dashCd = camelDashCooldown();
    return true;
  };
  // Archaeology (M41): brushing a suspicious block advances it through its dusting
  // stages; the final brush reveals a buried find (rolled from the block's loot
  // table) and collapses it into plain sand/gravel. Returns the revealed item, or ''.
  const brushProgressMap = new Map<string, number>();
  const archaeoRng = new Rng(`${String(meta.seed)}#archaeology`);
  const brushSuspicious = (x: number, y: number, z: number, name: string): string => {
    const table = suspiciousLoot(name);
    if (!table) return '';
    const ck = cellKey(x, y, z);
    const prog = brushProgress(brushProgressMap.get(ck) ?? 0);
    brushProgressMap.set(ck, prog);
    if (!brushComplete(prog)) return '';
    brushProgressMap.delete(ck);
    let revealed = '';
    for (const st of rollLootByName(registries, table, archaeoRng, { killedByPlayer: true })) {
      drops.spawn(st, x + 0.5, y + 1, z + 0.5, { vy: 0.15 });
      revealed = registries.items.nameOf[st.item] ?? revealed;
    }
    const base = suspiciousBase(name);
    const bs = base ? registries.states.defaultOf.get(base) : undefined;
    if (bs !== undefined) edit(x, y, z, bs);
    return revealed;
  };
  // Decorated pot (M42): right-click stows the held stack (topping up a match),
  // right-click empty-handed pulls the stored stack back out. Returns handled.
  const usePot = (x: number, y: number, z: number): boolean => {
    if (registries.states.blockName[world.getBlockState(x, y, z)] !== 'decorated_pot') return false;
    // Normal placement creates the store; lazily create it for a debug-placed pot.
    const be = blockEntities.get(x, y, z) ?? blockEntities.create('decorated_pot', x, y, z);
    if (be.type !== 'decorated_pot') return false;
    const slot = be.data.items[0];
    const held = playerState.inventory.held();
    if (isEmpty(held)) {
      if (!slot || isEmpty(slot)) return false;
      giveStack(cloneStack(slot));
      be.data.items[0] = null;
      redstone.markDirty(x, y, z);
      return true;
    }
    const cur = slot && !isEmpty(slot) ? { item: slot.item, count: slot.count } : null;
    const res = potInsert(cur, { item: held.item, count: held.count }, maxStackOf(held.item));
    if (res.leftover === held.count) return false; // nothing fit (full or mismatched)
    be.data.items[0] = makeStack(res.stored.item, res.stored.count);
    if (!modeCaps(playerState.gameMode).infiniteResources) {
      playerState.inventory.set(playerState.inventory.heldSlot(), res.leftover > 0 ? makeStack(held.item, res.leftover) : emptyStack());
    }
    redstone.markDirty(x, y, z);
    return true;
  };
  // Is a note block within `r` blocks of (x,y,z)? (an allay dances near one).
  const noteBlockNear = (x: number, y: number, z: number, r: number): boolean => {
    const cx = Math.floor(x), cy = Math.floor(y), cz = Math.floor(z);
    for (let dx = -r; dx <= r; dx++) for (let dy = -r; dy <= r; dy++) for (let dz = -r; dz <= r; dz++) {
      if (registries.states.blockName[world.getBlockState(cx + dx, cy + dy, cz + dz)] === 'note_block') return true;
    }
    return false;
  };
  // Turtle egg (M18.1): cracks over random ticks, then hatches a baby turtle at
  // night on sand. `hatchTurtleEgg` removes the egg and spawns the hatchling.
  const turtleEggStateOf = (hatch: number): number => registries.states.idByKey.get(`turtle_egg[hatch=${hatch}]`) ?? -1;
  const hatchTurtleEgg = (x: number, y: number, z: number): boolean => {
    if (registries.states.blockName[world.getBlockState(x, y, z)] !== 'turtle_egg') return false;
    edit(x, y, z, STATE_AIR);
    return entities.spawn('turtle', x + 0.5, y, z + 0.5, { baby: true }) !== null;
  };
  const turtleEggRandomTick = (x: number, y: number, z: number, state: number): void => {
    const stage = Number(registries.states.props[state]?.hatch ?? '0');
    const onSand = registries.states.blockName[world.getBlockState(x, y - 1, z)] === 'sand';
    const night = (world.dayTime % 24_000) >= 13_000; // MC: eggs hatch faster/only at night
    if (eggHatches(stage, night, onSand)) { hatchTurtleEgg(x, y, z); return; }
    const ns = turtleEggStateOf(eggNextCrack(stage));
    if (ns >= 0) setBlockRemesh(x, y, z, ns);
  };

  // Right-click mob interactions (MOB-2): tame, sit/stand, shear. Taming rolls a
  // dedicated seeded stream so an E2E can feed food until it succeeds.
  const petRng = new Rng(`${String(meta.seed)}#tame`);
  // Piglin barter rolls draw a dedicated seeded stream (M20).
  const barterRng = new Rng(`${String(meta.seed)}#barter`);
  // …and the eye of ender's 1-in-N shatter draws its own (DIM-3).
  const eyeRng = new Rng(`${String(meta.seed)}#ender-eye`);
  const fishRng = new Rng(`${String(meta.seed)}#fishing`);
  // Zombie-villager cure conversion timer draws its own seeded stream (MB.6).
  const cureRng = new Rng(`${String(meta.seed)}#cure`);
  // A cure feeds a golden apple to a weakened zombie villager, starting the 3–5
  // min conversion (game/monsters); `e.data.weak` marks a Weakness splash landing.
  const startCure = (e: Entity, held: string): boolean => {
    if (e.type !== 'zombie_villager' || e.data.curing) return false;
    if (!canStartCure(e.data.weak === 1, held)) return false;
    e.data.curing = cureDuration(cureRng.nextFloat());
    return true;
  };
  // Cast-rod state (MB.3). The bobber is virtual; a bite is a scheduled tick.
  let fishing: FishingState | null = null;
  /** Reel in the current cast, rolling the fishing loot on a bite. Returns caught names. */
  const reelFishingRod = (): string[] => {
    const caught: string[] = [];
    if (reelRod(fishing, world.tickCount) === 'catch') {
      for (const st of rollLootByName(registries, FISHING_TABLE, fishRng, { killedByPlayer: true })) {
        giveStack(st);
        const n = registries.items.nameOf[st.item];
        if (n) caught.push(n);
      }
      if (modeCaps(playerState.gameMode).survivalHunger) damageHeldTool();
    }
    fishing = null;
    return caught;
  };
  const heldItemName = (): string => {
    const h = playerState.inventory.held();
    return isEmpty(h) ? '' : registries.items.nameOf[h.item] ?? '';
  };
  // The item worn in the chest armour slot (elytra grants glide, M21).
  const chestItemName = (): string => {
    const s = playerState.inventory.get(INV.ARMOR_START + 1); // 1 = chest slot
    return isEmpty(s) ? '' : registries.items.nameOf[s.item] ?? '';
  };
  // Per-villager trade-offer use counts (client-side; single player, VIL M14.1).
  const tradeUses = new Map<number, number[]>();
  const usesFor = (e: Entity, table: { offers: unknown[] }): number[] => {
    let u = tradeUses.get(e.id);
    if (!u) { u = new Array<number>(table.offers.length).fill(0); tradeUses.set(e.id, u); }
    return u;
  };

  // Villager gossip (MB.7): reputation the player has earned with each villager
  // (curing raises it a lot, trading a little). Positive reputation discounts the
  // villager's offers; a fresh villager has none, so prices are unchanged by default.
  const villagerGossip = new Map<number, GossipLedger>();
  const gossipFor = (id: number): GossipLedger => villagerGossip.get(id) ?? {};
  const earnGossip = (id: number, type: GossipType, amount: number): void => {
    villagerGossip.set(id, addGossip(gossipFor(id), type, amount));
  };
  /** A villager's trade table (offer list), or undefined for a non-trader. */
  const tradeTableOf = (e: Entity): { offers: unknown[] } | undefined => {
    const t = registries.mobs.byName.get(e.type)?.trades;
    return t ? registries.trades.get(t) : undefined;
  };
  // Persistence (MB.7): fold each trader's reputation + use counts into its entity
  // `data` before a save (they round-trip with the entity), and rebuild the maps
  // from that data after a load — entity ids are reassigned on load, so the maps
  // can't survive on their own.
  const syncVillagerEconomy = (): void => {
    for (const e of entities.entities) {
      if (e.removed || !tradeTableOf(e)) continue;
      Object.assign(e.data, encodeVillagerEconomy(gossipFor(e.id), tradeUses.get(e.id) ?? []));
    }
  };
  const restoreVillagerEconomy = (): void => {
    villagerGossip.clear();
    tradeUses.clear();
    for (const e of entities.entities) {
      if (e.removed) continue;
      const tbl = tradeTableOf(e);
      if (!tbl) continue;
      const g = decodeGossip(e.data);
      if (Object.keys(g).length > 0) villagerGossip.set(e.id, g);
      const uses = decodeUses(e.data, tbl.offers.length);
      if (uses.some((u) => u > 0)) tradeUses.set(e.id, uses);
    }
  };
  restoreVillagerEconomy(); // seed the maps from the entities loaded at boot
  // Reputation dynamics (MB.7): villagers who meet share gossip, and all gossip
  // decays toward zero — the pieces existed since Pass 11/13, now ticked.
  const shareGossipRound = (): void => {
    const vills = entities.entities.filter((e) => !e.removed && tradeTableOf(e));
    for (const [i, j] of meetingPairs(vills.map((e) => ({ x: e.x, z: e.z })))) {
      const a = vills[i]!, b = vills[j]!;
      const [ga, gb] = shareGossip(gossipFor(a.id), gossipFor(b.id));
      if (Object.keys(ga).length > 0) villagerGossip.set(a.id, ga);
      if (Object.keys(gb).length > 0) villagerGossip.set(b.id, gb);
    }
  };
  const decayGossipRound = (): void => {
    for (const [id, g] of villagerGossip) {
      const next = decayGossip(g);
      if (Object.keys(next).length > 0) villagerGossip.set(id, next);
      else villagerGossip.delete(id);
    }
  };
  const tickGossip = (): void => {
    if (isDecayTick(world.tickCount)) decayGossipRound();
    if (isShareTick(world.tickCount)) shareGossipRound();
  };
  /** An offer with its first ingredient discounted by the player's reputation. */
  const discountOffer = <T extends { buy: ReadonlyArray<{ item: string; count: number }> }>(o: T, discount: number): T => {
    if (discount <= 0 || o.buy.length === 0) return o;
    return { ...o, buy: o.buy.map((b, i) => (i === 0 ? { ...b, count: discountedBuyCount(b.count, discount) } : b)) };
  };

  /** Everything a right-click on a mob does WITHOUT a script: feed, trade, mount,
   *  shear, and the rest. §27.6's fall-through target — `interactMob` runs the script's
   *  `on interact` first and only reaches this if the script did not call `handled()`. */
  /** §28.9's prompt over mob `e`: solo names it through the Game seam; multiplayer sends
   *  `Name` and lets the server validate and broadcast `EntityName` (the close is
   *  optimistic — a refusal leaves the tag where it was). */
  const openNamePrompt = (e: Entity): void => {
    game.uiOpen = true;
    document.exitPointerLock();
    openNameTagPrompt(
      (text) => {
        const netId = e.data.netId;
        if (net && typeof netId === 'number') net.sendName(netId, text, registries.items.idOf.get(heldItemName()) ?? 0);
        else game.nameEntity(e.id, text);
      },
      () => { game.uiOpen = false; void game.renderer.domElement.requestPointerLock(); },
    );
  };
  const defaultInteractMob = (e: Entity): boolean => {
    const def = registries.mobs.byName.get(e.type);
    if (!def) return false;
    // §28.9: a held name tag names ANY living mob, in any rule state — the first rung, so
    // naming a cow never feeds or shears it. The prompt consumes the tag only on Enter.
    if (heldItemName() === 'name_tag') { openNamePrompt(e); return true; }
    // §28.11: a held field notebook records ANY mob (a no-op for one with no table) —
    // same rung shape as the name tag, so it never also feeds/trades/shears.
    if (heldItemName() === 'field_notebook') { recordAnimal(e); return true; }
    // Gene sequencer (M53.1, ARCH §29.10): the on-sight genotype reveal, ANY mob —
    // "No genetics in this world." with the rule off or no gene table/genome.
    if (heldItemName() === 'gene_sequencer') {
      const r = readSequencer(e.type, e.id, def.genes, e.genome, heredityOn(world));
      game.showActionBar(r.text);
      for (const trig of r.triggers) fireEduTrigger({ kind: 'instrument_read', instrument: 'gene_sequencer', metric: trig.metric, value: trig.value });
      return true;
    }
    // Feed a villager a breeding food → adds to its willingness (MB.7). This wins
    // over opening the trade UI when the hand holds bread/carrot/potato/beetroot.
    if (def.archetype === 'villager' && !e.baby && isBreedFood(heldItemName())) {
      e.data.food = (e.data.food ?? 0) + foodPoints(heldItemName());
      consumeHeld();
      return true;
    }
    // A villager with a trade table opens the trade UI.
    if (def.trades && registries.trades.has(def.trades)) { openTrade(game, e.id); return true; }
    const held = heldItemName();
    // Feed a golden apple to a weakened zombie villager → start the cure (MB.6).
    if (e.type === 'zombie_villager' && held && startCure(e, held)) {
      if (!modeCaps(playerState.gameMode).infiniteResources) consumeHeld();
      return true;
    }
    // Scoop an axolotl with a water bucket → an axolotl bucket, removing it (MB.5).
    if (held === 'water_bucket' && e.type === 'axolotl') {
      const id = registries.items.idOf.get('axolotl_bucket');
      if (id === undefined) return false;
      if (!modeCaps(playerState.gameMode).infiniteResources) consumeHeld();
      giveStack(makeStack(id, 1));
      e.removed = true;
      return true;
    }
    // Land-mount riding (M19): saddle a rideable mob, or mount a (saddled) one — a
    // saddled mount steers with movement input; an unsaddled one you just sit on.
    // The animal's saddlebags, created on first use and resized if the def's slot
    // count ever changes (overflow is dropped at its feet rather than swallowed).
    const packFor = (m: Entity, size: number): MountPack => {
      if (!m.pack) { m.pack = newPack(size); return m.pack; }
      if (m.pack.items.length === size) return m.pack;
      const { pack, overflow } = resizePack(m.pack, size);
      m.pack = pack;
      for (const s of overflow) drops.spawn(s, m.x, m.y + 0.5, m.z, { pickupDelay: 10 });
      return pack;
    };
    // A pack animal takes a chest — once, permanently — and opens it when you
    // right-click it empty-handed (M19 remainder). Checked before the ride branch
    // so an empty hand on a chested donkey opens its bags rather than mounting it;
    // sneaking still mounts.
    const mobDef = registries.mobs.byName.get(e.type);
    if (carriesChest(mobDef)) {
      if (attachesChest(mobDef, e.data.chest === 1, !!e.baby, held)) {
        e.data.chest = 1;
        if (!modeCaps(playerState.gameMode).infiniteResources) consumeHeld();
        audio.playSound('entity.donkey.chest', { x: e.x, y: e.y + 0.5, z: e.z });
        return true;
      }
      if (held === '' && !game.input.sneak && storageOpens(mobDef, e.data.chest === 1)) {
        openPackWindow(game, packFor(e, chestSlotCount(mobDef)), `${e.type.replace(/^./, (c) => c.toUpperCase())} bags`);
        return true;
      }
    }
    // Dress a llama in a carpet (M61). A llama wears exactly one; it comes back
    // when the animal dies. Checked before the mount branch so a held carpet
    // decorates rather than doing nothing.
    if (caravans(e.type)) {
      const colour = carpetColour(held, DYE_ORDER as unknown as string[]);
      if (dressesLlama(e.type, (e.data.decor ?? 0) > 0, !!e.baby, colour)) {
        e.data.decor = DYE_ORDER.indexOf(colour as never) + 1;
        if (!modeCaps(playerState.gameMode).infiniteResources) consumeHeld();
        return true;
      }
      // A lead: take this llama in hand, or — if one is already in hand — hitch it
      // behind this one, which is what turns two llamas into a caravan.
      if (held === 'lead') {
        if (ledLlamaId === null || ledLlamaId === e.id) {
          ledLlamaId = e.id;
          game.print('§7You take the llama by the lead.');
          return true;
        }
        const led = entities.entities.find((m) => m.id === ledLlamaId && !m.removed);
        const refusal = led ? refuseJoin(caravanLinks, led.type, led.id, e.id) : 'not-a-llama';
        if (refusal === null) {
          caravanLinks = caravanJoin(caravanLinks, led!.type, led!.id, e.id);
          game.print('§7The llama falls in behind.');
        } else {
          game.print(`§7It will not follow (${refusal.replace(/-/g, ' ')}).`);
        }
        ledLlamaId = null;
        return true;
      }
    }
    // Bard a horse (M61): a barded horse takes the same mitigation curve a player's
    // armour does. A donkey or mule carries a chest instead and takes no barding.
    if (isHorseArmor(held) && bardsWith(e.type, bardingOf(e), !!e.baby, held)) {
      e.data.barding = HORSE_ARMOR_ITEMS.indexOf(held) + 1;
      if (!modeCaps(playerState.gameMode).infiniteResources) consumeHeld();
      return true;
    }
    if (mountKindOf(e.type) && !e.baby) {
      if (held === 'saddle' && e.data.saddled !== 1) {
        e.data.saddled = 1;
        if (!modeCaps(playerState.gameMode).infiniteResources) consumeHeld();
        return true;
      }
      if (held === '' && ridingMountId === null) {
        // Net mode: ASK. The server may hand us seat 1 of a camel someone is already
        // on, or refuse outright if it is full — either way the Riders snapshot is
        // what sets `ridingMountId`, never this call site.
        if (net) { const nid = e.data.netId; if (nid !== undefined) net.sendMount(nid, freeSeatOn(nid, mountKindOf(e.type)!)); return true; }
        ridingMountId = e.id;
        return true;
      }
    }
    // Scoop a fish with a water bucket → a fish bucket, removing it (M18-adjacent).
    if (held === 'water_bucket' && isBucketableFish(e.type)) {
      const bucket = fishBucket(e.type);
      const id = bucket ? registries.items.idOf.get(bucket) : undefined;
      if (id === undefined) return false;
      if (!modeCaps(playerState.gameMode).infiniteResources) consumeHeld();
      // The bucket carries the fish's livery, so releasing it gives back the same
      // fish you caught rather than a freshly rolled one (M18).
      giveStack(makeStack(id, 1, e.variant > 0 ? { fishVariant: e.variant } : undefined));
      e.removed = true;
      return true;
    }
    // Milk an adult cow / mooshroom with an empty bucket → a milk bucket (MB.3).
    if (held === 'bucket' && !e.baby && (e.type === 'cow' || e.type === 'mooshroom')) {
      const milkId = registries.items.idOf.get('milk_bucket');
      if (milkId !== undefined) {
        if (!modeCaps(playerState.gameMode).infiniteResources) consumeHeld();
        giveStack(makeStack(milkId, 1));
      }
      return true;
    }
    // Barter with an adult piglin: hand it a gold ingot → it barters a nether
    // trinket back (rolled through the shared loot table), consuming the ingot.
    if (def.barters && !e.baby && isBarterCurrency(held)) {
      consumeHeld();
      for (const st of rollLootByName(registries, BARTER_TABLE, barterRng)) {
        drops.spawn(st, e.x, e.y + 0.8, e.z,
          { vx: (Math.random() - 0.5) * 0.2, vy: 0.25, vz: (Math.random() - 0.5) * 0.2 });
      }
      return true;
    }
    // Hand an amethyst shard to an allay dancing by a note block → it duplicates
    // (once per long cooldown), consuming the shard (M18.1-adjacent).
    if (e.type === 'allay' && held === 'amethyst_shard') {
      const dancing = allayDances(noteBlockNear(e.x, e.y, e.z, ALLAY.NOTE_DANCE_RANGE));
      const cd = (e.data.dupCd as number) ?? 0;
      if (allayDuplicates(dancing, true, cd <= 0)) {
        if (!modeCaps(playerState.gameMode).infiniteResources) consumeHeld();
        const dup = entities.spawn('allay', e.x, e.y, e.z);
        if (dup) dup.data.dupCd = allayDupCooldownTicks();
        e.data.dupCd = allayDupCooldownTicks();
        return true;
      }
    }
    // Tame a wild tamable animal with its food (wolf ← bone).
    if (def.tameItem && !e.tamed && held === def.tameItem) {
      consumeHeld();
      if (petRng.nextFloat() < def.tameChance) {
        e.tamed = true; e.persistent = true; e.sitting = false; e.angerTimer = 0;
        e.health = e.maxHealth;
      }
      return true;
    }
    // Recolour a sheep's wool with a dye (MB.5) — the new colour applies to the
    // wool it grows back and drops when sheared or killed.
    if (e.type === 'sheep') {
      const ci = dyeColorIndex(held);
      if (ci >= 0) { e.variant = ci; consumeHeld(); return true; }
    }
    // Dye a tamed wolf's collar (MB.5, cosmetic — kept in variant).
    if (e.type === 'wolf' && e.tamed) {
      const ci = dyeColorIndex(held);
      if (ci >= 0) { e.variant = ci; consumeHeld(); return true; }
    }
    // Toggle a tamed pet between sitting and following (empty hand / non-food).
    if (e.tamed && held !== def.tameItem) { e.sitting = !e.sitting; return true; }
    // Shear a woolly sheep → drops its wool and bares it (regrows by grazing).
    if (e.type === 'sheep' && held === 'shears' && !e.sheared && !e.baby) {
      e.sheared = true;
      const id = registries.items.idOf.get(`${DYE_ORDER[e.variant] ?? 'white'}_wool`);
      if (id !== undefined) {
        drops.spawn(makeStack(id, 1 + petRng.nextInt(3)), e.x, e.y + 0.5, e.z,
          { vx: (Math.random() - 0.5) * 0.2, vy: 0.2, vz: (Math.random() - 0.5) * 0.2 });
      }
      if (modeCaps(playerState.gameMode).survivalHunger) damageHeldTool();
      return true;
    }
    return false;
  };
  /** A right-click on a mob, through §27.6's hook (M46.7, closing M45's gap).
   *
   *  `EntitySystem.interact` dispatches `on interact` FIRST and then falls through to
   *  the default behaviour unless the script called `handled()` — which, per §27.6, is
   *  the ONE hook `handled()` may cancel. Before this, `interactMob` went straight to
   *  the default, so a scripted mob's `on interact` could never fire at all. */
  const interactMob = (e: Entity): boolean =>
    entities.interact(e, makeFrame(), () => defaultInteractMob(e));

  // ── Raids (RAID, M14.2): a Bad-Omen player entering a village triggers a wave
  //    assault; the pure state machine (game/raid.ts) sequences the waves while
  //    the host spawns/counts the raiders and pays out Hero of the Village. ──
  const raidTable = registries.raids.get('default') ?? null;
  let raid: Raid | null = null;
  const isVillager = (e: Entity): boolean => registries.mobs.byName.get(e.type)?.archetype === 'villager';
  const raidRaidersAlive = (): number => {
    let n = 0;
    for (const e of entities.entities) if (!e.removed && e.data.raid === 1) n++;
    return n;
  };
  const villagersWithin = (x: number, z: number, r: number): number => {
    let n = 0;
    for (const e of entities.entities) if (!e.removed && isVillager(e) && Math.hypot(e.x - x, e.z - z) <= r) n++;
    return n;
  };

  // ── Villager breeding (MB.7): two fed (willing) adult villagers near a spare
  //    bed produce a baby (game/villager_breed). Beds are counted by their foot
  //    half within range; a free bed = beds outnumber villagers. ──
  const BREED_RANGE = 8;
  const BREED_COOLDOWN = 6000; // ticks before a villager can breed again (Java 5 min)
  const bedFeetWithin = (cx: number, cy: number, cz: number, r: number): number => {
    let n = 0;
    for (let dx = -r; dx <= r; dx++)
      for (let dy = -2; dy <= 2; dy++)
        for (let dz = -r; dz <= r; dz++) {
          const st = world.getBlockState(cx + dx, cy + dy, cz + dz);
          if (registries.states.blockName[st] === 'bed' && registries.states.props[st]?.part === 'foot') n++;
        }
    return n;
  };
  const tryBreedVillagers = (): boolean => {
    const adults = entities.entities.filter((e) => !e.removed && isVillager(e) && !e.baby);
    for (let i = 0; i < adults.length; i++) {
      for (let j = i + 1; j < adults.length; j++) {
        const a = adults[i]!, b = adults[j]!;
        if (a.breedCooldown > 0 || b.breedCooldown > 0) continue;
        if (Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z) > BREED_RANGE) continue;
        const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2, mz = (a.z + b.z) / 2;
        const beds = bedFeetWithin(Math.floor(mx), Math.floor(my), Math.floor(mz), BREED_RANGE);
        const localVillagers = villagersWithin(mx, mz, BREED_RANGE);
        if (!canBreed(a.data.food ?? 0, b.data.food ?? 0, beds, localVillagers)) continue;
        const baby = entities.spawn(a.type, mx, my, mz, { baby: true, persistent: true });
        if (!baby) continue;
        a.data.food = 0; b.data.food = 0;
        a.breedCooldown = BREED_COOLDOWN; b.breedCooldown = BREED_COOLDOWN;
        return true;
      }
    }
    return false;
  };
  const tickVillagerBreeding = (): void => {
    if (world.tickCount % 20 === 0) tryBreedVillagers();
  };

  // ── Villager schedule (MB.7): cache the nearest bed for each villager so the
  //    archetype can path to it at night (game/schedule drives the clock). ──
  const nearestBedFoot = (cx: number, cy: number, cz: number, r: number): { x: number; y: number; z: number } | null => {
    let best: { x: number; y: number; z: number } | null = null, bestD = Infinity;
    for (let dx = -r; dx <= r; dx++)
      for (let dy = -3; dy <= 3; dy++)
        for (let dz = -r; dz <= r; dz++) {
          const st = world.getBlockState(cx + dx, cy + dy, cz + dz);
          if (registries.states.blockName[st] !== 'bed' || registries.states.props[st]?.part !== 'foot') continue;
          const d = dx * dx + dy * dy + dz * dz;
          if (d < bestD) { bestD = d; best = { x: cx + dx, y: cy + dy, z: cz + dz }; }
        }
    return best;
  };
  const assignBed = (e: Entity): boolean => {
    const bed = nearestBedFoot(Math.floor(e.x), Math.floor(e.y), Math.floor(e.z), 12);
    if (!bed) return false;
    e.data.bedX = bed.x; e.data.bedY = bed.y; e.data.bedZ = bed.z; e.data.bedSet = 1;
    return true;
  };
  const nearestNamedBlock = (name: string, cx: number, cy: number, cz: number, r: number): { x: number; y: number; z: number } | null => {
    let best: { x: number; y: number; z: number } | null = null, bestD = Infinity;
    for (let dx = -r; dx <= r; dx++)
      for (let dy = -4; dy <= 4; dy++)
        for (let dz = -r; dz <= r; dz++) {
          if (registries.states.blockName[world.getBlockState(cx + dx, cy + dy, cz + dz)] !== name) continue;
          const d = dx * dx + dy * dy + dz * dz;
          if (d < bestD) { bestD = d; best = { x: cx + dx, y: cy + dy, z: cz + dz }; }
        }
    return best;
  };
  const assignBell = (e: Entity): boolean => {
    const bell = nearestNamedBlock('bell', Math.floor(e.x), Math.floor(e.y), Math.floor(e.z), 16);
    if (!bell) return false;
    e.data.bellX = bell.x; e.data.bellY = bell.y; e.data.bellZ = bell.z; e.data.bellSet = 1;
    return true;
  };
  // A jobless villager reaching an unclaimed workstation takes up its profession
  // (game/jobsite); one villager per site, tracked in a claim set.
  const claimedJobsites = new Set<string>();
  const assignJobsite = (e: Entity): boolean => {
    if (e.type !== 'villager' || e.baby) return false; // only unemployed adults
    const cx = Math.floor(e.x), cy = Math.floor(e.y), cz = Math.floor(e.z), r = 6;
    for (let dx = -r; dx <= r; dx++)
      for (let dy = -2; dy <= 2; dy++)
        for (let dz = -r; dz <= r; dz++) {
          const wx = cx + dx, wy = cy + dy, wz = cz + dz;
          const prof = professionFor(registries.states.blockName[world.getBlockState(wx, wy, wz)] ?? '');
          const key = `${wx},${wy},${wz}`;
          if (!prof || claimedJobsites.has(key)) continue;
          claimedJobsites.add(key);
          e.data.jobX = wx; e.data.jobY = wy; e.data.jobZ = wz; e.data.jobSet = 1;
          const def = registries.mobs.byName.get(prof);
          if (def) e.maxHealth = def.maxHealth;
          e.type = prof; // gains the profession's trade table
          return true;
        }
    return false;
  };
  const tickVillagerSchedule = (): void => {
    if (world.tickCount % 40 !== 0) return;
    for (const e of entities.entities) {
      if (e.removed || !isVillager(e)) continue;
      if (e.data.bedSet !== 1) assignBed(e);
      if (e.data.bellSet !== 1) assignBell(e);
      if (e.type === 'villager' && !e.baby) assignJobsite(e);
    }
  };

  // ── Iron golems (MB.7): a village (enough adult villagers + beds, no golem yet)
  //    summons an iron golem to guard it (game/golem_spawn). ──
  const GOLEM_RADIUS = 12;
  const golemsWithin = (x: number, z: number, r: number): number => {
    let n = 0;
    for (const e of entities.entities) if (!e.removed && e.type === 'iron_golem' && Math.hypot(e.x - x, e.z - z) <= r) n++;
    return n;
  };
  const adultVillagersWithin = (x: number, z: number, r: number): number => {
    let n = 0;
    for (const e of entities.entities) if (!e.removed && isVillager(e) && !e.baby && Math.hypot(e.x - x, e.z - z) <= r) n++;
    return n;
  };
  const trySpawnIronGolem = (): boolean => {
    for (const e of entities.entities) {
      if (e.removed || !isVillager(e) || e.baby) continue;
      const villagers = adultVillagersWithin(e.x, e.z, GOLEM_RADIUS);
      const beds = bedFeetWithin(Math.floor(e.x), Math.floor(e.y), Math.floor(e.z), GOLEM_RADIUS);
      const golems = golemsWithin(e.x, e.z, GOLEM_RADIUS);
      if (!canSpawnIronGolem(villagers, beds, golems)) continue;
      if (entities.spawn('iron_golem', e.x + 1.5, e.y, e.z, { persistent: true })) return true;
    }
    return false;
  };
  const tickGolemSpawn = (): void => {
    if (world.tickCount % 200 === 0) trySpawnIronGolem();
  };

  // Pillager patrols (MB.6): a captain-led band appears periodically once the early
  // game is past. The captain (variant 1) carries the ominous banner — the same
  // flag the raid trigger reads, so killing it grants Bad Omen → a raid.
  const patrolRng = new Rng(`${String(meta.seed)}#patrol`).fork(defineSalt('patrol'));
  let lastPatrolAttempt = -PATROL_INTERVAL;
  const spawnPatrol = (): number => {
    const c = patrolSpawnPoint(player.body.x, player.body.z, patrolRng.nextFloat() * Math.PI * 2);
    let spawned = 0;
    for (const m of patrolMembers()) {
      const ox = c.x + patrolRng.nextInt(5) - 2;
      const oz = c.z + patrolRng.nextInt(5) - 2;
      const oy = world.surfaceY(Math.floor(ox), Math.floor(oz)) ?? player.body.y;
      if (entities.spawn('pillager', ox + 0.5, oy, oz + 0.5, { persistent: true, variant: m.captain ? 1 : 0 })) spawned++;
    }
    return spawned;
  };
  const tickPatrol = (): void => {
    if (!patrolDue(world.tickCount, lastPatrolAttempt)) return;
    lastPatrolAttempt = world.tickCount;
    if (rollPatrol(patrolRng.nextFloat())) spawnPatrol();
  };

  // ── Bell (MB.6): ringing it reveals nearby raiders (a glow) for a spell, so a
  //    village can spot its attackers. ──
  const BELL_REVEAL_RANGE = 32;
  const BELL_REVEAL_TICKS = 60 * 20;
  const ringBell = (x: number, y: number, z: number): number => {
    if (registries.states.blockName[world.getBlockState(x, y, z)] !== 'bell') return 0;
    let n = 0;
    for (const e of entities.entities) {
      if (e.removed) continue;
      const isRaider = e.data.raid === 1 || registries.mobs.byName.get(e.type)?.raider === true;
      if (isRaider && Math.hypot(e.x - x, e.z - z) <= BELL_REVEAL_RANGE) { e.data.revealed = BELL_REVEAL_TICKS; n++; }
    }
    return n;
  };

  // ── Vex summon (MB.6): an evoker near the player conjures vexes on the illager
  //    cadence, up to the nearby cap (game/illager). ──
  const vexesNear = (x: number, z: number, r: number): number => {
    let n = 0;
    for (const e of entities.entities) if (!e.removed && e.type === 'vex' && Math.hypot(e.x - x, e.z - z) <= r) n++;
    return n;
  };
  const summonVexesFrom = (evoker: Entity): number => {
    const nearby = vexesNear(evoker.x, evoker.z, 16);
    if (!canSummonVexes(evoker.data.vexCd ?? 0, nearby)) return 0;
    const n = vexesToSummon(nearby);
    for (let i = 0; i < n; i++) {
      const ox = evoker.x + (petRng.nextFloat() - 0.5) * 2, oz = evoker.z + (petRng.nextFloat() - 0.5) * 2;
      const vex = entities.spawn('vex', ox, evoker.y + 1, oz, {});
      if (vex) vex.data.raid = evoker.data.raid ?? 0; // vexes join their evoker's raid
    }
    evoker.data.vexCd = VEX_SUMMON_COOLDOWN;
    return n;
  };
  const tickVexSummon = (): void => {
    for (const e of entities.entities) {
      if (e.removed || e.type !== 'evoker') continue;
      if ((e.data.vexCd ?? 0) > 0) e.data.vexCd = (e.data.vexCd ?? 0) - 1;
      if (Math.hypot(e.x - player.body.x, e.z - player.body.z) < 24) summonVexesFrom(e);
    }
  };

  // ── Brewing stand (MB.4): a per-cell brew batch. Bottles carry a potion key; an
  //    ingredient + blaze-powder fuel brews every brewable bottle over BREW_TICKS
  //    (pure game/brewing). Full inventory UI + persistence are a remainder. ──
  //    Now an ItemStack-backed block entity (containers/brewing) driving the same
  //    pure brewBatch — ticked in phase 6, filled through the window (openBrewing).
  const brewingBEAt = (x: number, y: number, z: number): BrewingStandBE =>
    blockEntities.create('brewing_stand', x, y, z) as BrewingStandBE;

  // ── Signs (MB.1): four editable text lines per sign cell (a lightweight
  //    block-entity). The client draws the text + the edit screen. ──
  const signBlocks = new Set(['oak_sign']);
  const signText = new Map<string, string[]>();
  const setSignText = (x: number, y: number, z: number, lines: readonly string[]): void => {
    signText.set(cellKey(x, y, z), normalizeSignText(lines));
  };
  const getSignText = (x: number, y: number, z: number): string[] =>
    signText.get(cellKey(x, y, z)) ?? ['', '', '', ''];
  // Glow ink (M22): a per-cell flag toggled by a glow/plain ink sac used on a sign.
  const signGlow = new Set<string>();
  const signGlows = (x: number, y: number, z: number): boolean => signGlow.has(cellKey(x, y, z));
  const setSignGlow = (x: number, y: number, z: number, glow: boolean): void => {
    const ck = cellKey(x, y, z);
    if (glow) signGlow.add(ck); else signGlow.delete(ck);
  };
  // ── Note blocks (MB.1) ──────────────────────────────────────────────────────
  // The instrument is chosen by the block underneath; the note (0..24) lives in
  // the per-cell store. A right-click cycles the note; a redstone rising edge (or
  // the interact) plays it. AUD-1 (M23) will route the play payload to the sound
  // engine — until then `playNote` records the last {instrument, pitch, hue}.
  const noteBelow = (x: number, y: number, z: number): { name: string | null; material?: string } => {
    const st = world.getBlockState(x, y - 1, z);
    const name = registries.states.blockName[st] ?? null;
    return { name, material: name ? registries.blocks.byName.get(name)?.material : undefined };
  };
  const playNote = (x: number, y: number, z: number): NotePlayback => {
    const b = noteBelow(x, y, z);
    const pb = notePlayback(noteBlocks.get(cellKey(x, y, z)) ?? 0, b.name, b.material);
    lastNotePlayed = pb;
    // AUD-3: voice the instrument chosen by the block below, pitched by the note.
    audio.playPatch('sound', `note_block.${pb.instrument}`, noteVoice(pb.instrument),
      { x: x + 0.5, y: y + 0.5, z: z + 0.5, pitch: pb.pitch });
    return pb;
  };
  const cycleNoteBlock = (x: number, y: number, z: number): NotePlayback => {
    const k = cellKey(x, y, z);
    noteBlocks.set(k, cycleNote(noteBlocks.get(k) ?? 0));
    noteBlockCells.add(k);
    return playNote(x, y, z);
  };
  /** Redstone rising-edge: a note block newly powered plays once (RED-like). */
  const tickNoteBlocks = (): void => {
    for (const k of noteBlockCells) {
      const [x, y, z] = k.split(',').map(Number) as [number, number, number];
      const powered = redstone.powerLevelAt(x, y, z) > 0;
      if (powered && !notePowered.has(k)) playNote(x, y, z);
      if (powered) notePowered.add(k); else notePowered.delete(k);
    }
  };
  // ── Script blocks (SCR-4, M35): the creative/ops command-block successor. A block
  //    entity holds the source; a redstone rising edge (or the editor's Run) dispatches
  //    the `redstone` event through the pure sandboxed VM (sim/script) against a host
  //    that delegates world edits to the command context. A block carrying an autopilot
  //    preset instead engages the aircraft autopilot (AVI-3) with its editable gains. ──
  //    The ONE script runtime (§27.4, SCR-6): the compile cache, the rising-edge latch
  //    and dispatch all live in `sim/script/system.ts` now, so a script_block runs on a
  //    dedicated server too. boot keeps only the ScriptHost below (client effects) plus
  //    the AVI-3 preset, which is client state the sim cannot hold.
  /** §27.6's `MobDef.script` resolver: a script NAME to its source. The sim cannot read
   *  a file (ENG-1), so the browser resolves names against the bundled collection
   *  sources — the same texts the editor's "Load example" picker offers. A name nothing
   *  provides returns undefined, which leaves that mob on its archetype (E-50). */
  const mobScriptSources: MobScriptSources = { sourceFor: scriptSource };
  const scripts = createScriptSystem(mobScriptSources);
  /** The whitelisted world/effect binding scripts run against (SCR-2), over the
   *  command context + a read-only entity/aircraft view. */
  const scriptHost: ScriptHost = {
    getBlock: (x, y, z) => cmdContext.getBlock(x, y, z),
    setBlock: (x, y, z, name) => {
      if (!registries.states.defaultOf.has(name)) return false;
      edit(x, y, z, resolveBlock(name));
      return true;
    },
    fill: (x1, y1, z1, x2, y2, z2, name) => {
      if (!registries.states.defaultOf.has(name)) return 0;
      return cmdContext.fillBlocks(x1, y1, z1, x2, y2, z2, name);
    },
    // §27.13: a 5th argument attaches a per-instance script. Without one this is the
    // unchanged `/summon` path; with one it is the same persistent spawn, overridden.
    summon: (mob, x, y, z, script) => (script === undefined
      ? cmdContext.summon(mob, x, y, z)
      : entities.spawn(mob, x, y, z, { persistent: true, scriptOverride: script }) !== null),
    give: (item, count) => cmdContext.giveItem(item, count),
    say: (message) => { game.print(message); },
    detect: (x, y, z, name) => cmdContext.getBlock(x, y, z) === name,
    nearestEntity: (x, y, z, radius): ScriptVec3 | null => {
      let best: (typeof entities.entities)[number] | null = null;
      let bestD = radius * radius;
      for (const e of entities.entities) {
        if (e.removed) continue;
        const d = (e.x - x) ** 2 + (e.y - y) ** 2 + (e.z - z) ** 2;
        if (d <= bestD) { bestD = d; best = e; }
      }
      return best ? scriptVec3(best.x, best.y, best.z) : null;
    },
    setAircraftControl: (pitch, roll, throttle) => {
      if (!flyingAircraft()) return false;
      aircraftInputOverride = { pitch, roll, throttleUp: throttle > 0, throttleDown: throttle < 0 };
      return true;
    },
    // §27.10's signal path (M46.3). The origin is the DISPATCH's own position, handed
    // down by the verb layer, and the depth is the runtime's — so a signal raised from
    // inside a handler is genuinely one level deeper and the cascade terminates.
    // Delivery is the runtime's job; this only queues.
    emit: (name, payload, x, y, z) => scripts.enqueueSignal({
      name, payload, x, y, z, depth: scripts.dispatchDepth,
    }),
  };
  /** Queue a §27.9 lifecycle dispatch for the script block at a cell, if one is there.
   *
   *  Silent when the cell holds no script block or an empty source — the bus notifies
   *  EVERY edit's neighbours, and the overwhelming majority of them are ordinary blocks
   *  with nothing to run. Filtering here rather than in the runtime is what keeps a
   *  world with no script blocks from queuing anything at all, which is the §27.19
   *  parity position. */
  const enqueueBlockHook = (
    x: number, y: number, z: number, hook: BlockHook,
    depth = 0, from?: { x: number; y: number; z: number },
  ): void => {
    const be = blockEntities.get(x, y, z);
    if (be?.type !== 'script' || be.data.source === '') return;
    scripts.enqueueLifecycle({ key: cellKey(x, y, z), x, y, z, source: be.data.source, hook, depth, from });
  };
  /** Give an `EntitySystem` the §27.6 mob-hook runtime. Called for the system built at
   *  boot and again on every dimension switch — a nether `EntitySystem` is a different
   *  object, and one that never got this runs every mob on its archetype. */
  const attachMobScripts = (es: EntitySystem): void => {
    // Per-DIMENSION, matching `makeEntities`' own rng derivation: the nether's mobs
    // must not replay the overworld's script randomness.
    const dimSeed = world.dimension === 'overworld' ? world.seed : `${String(world.seed)}#${world.dimension}`;
    es.mobScripts = {
      system: scripts,
      host: scriptHost,
      seed: new Rng(dimSeed).fork(S_MOBSCRIPT).nextU32(),
      // E-50: a per-instance override naming a script that no longer exists falls back
      // to the type's script; the author reads why in the console, not in chat.
      warn: (message) => { console.warn(`[script] ${message}`); },
    };
  };
  attachMobScripts(entities);
  /** §27.10's recipients: the scripted entities within `radius` of a raise point.
   *
   *  Each gets its own `ScriptMobHost` (one per delivery, like one per dispatch — a
   *  shared one would let `handled()` leak between entities). Yield order does not
   *  matter: the runtime sorts by entity id then depth before dispatching, which is
   *  what keeps a clipped cascade clipping identically everywhere. */
  const signalTargetsNear = (x: number, y: number, z: number, radius: number): SignalTarget[] => {
    const out: SignalTarget[] = [];
    const r2 = radius * radius;
    const ctx: MobContext = { world: mobWorld, rng: entities.rng, frame: makeFrame(), sys: entities };
    for (const e of entities.entities) {
      if (e.removed) continue;
      const def = registries.mobs.byName.get(e.type);
      // §27.13: an entity's own override wins over its type's script.
      const script = def ? scriptNameOf(e, def) : undefined;
      if (!def || script === undefined) continue; // unscripted mobs cannot hear a signal
      if ((e.x - x) ** 2 + (e.y - y) ** 2 + (e.z - z) ** 2 > r2) continue;
      out.push({
        entityId: e.id, script, type: e.type, hz: def.scriptHz,
        mob: mobHostFor(e, def, ctx),
      });
    }
    return out;
  };
  /** Run one dispatch for a script cell; returns an editor/chat message. The runtime
   *  owns the compile cache and the VM (§27.4); this only maps its result to copy.
   *  `power` is the caller's freshly-solved reading, so `self.powered` is the same
   *  value the pre-extraction code re-probed for. */
  const dispatchScript = (be: ScriptBE, event: string, power: number): { ok: boolean; message: string } => {
    const k = cellKey(be.x, be.y, be.z);
    scripts.install(k, be.data.source, 'block');
    const res = scripts.dispatch(k, { name: event, power }, {
      host: scriptHost,
      self: { x: be.x, y: be.y, z: be.z, powered: power > 0 },
    });
    return { ok: res.ok, message: res.ok ? `ran (${res.ops} ops)` : (res.error?.message ?? 'error') };
  };
  /** Engage a script block's autopilot preset now (AVI-3) — swap in a controller
   *  with the preset's editable gains. Returns whether an aircraft is being flown. */
  const engageScriptAutopilotAt = (be: ScriptBE): boolean => {
    if (!be.data.autopilot) return false;
    const built = autopilotFromPreset(be.data.autopilot);
    autopilot = built.autopilot;
    autopilotTargets = built.targets;
    return flyingAircraft() !== null;
  };
  /** Every script cell for the §27.4 sweep, in block-entity insertion order — the same
   *  order the pre-extraction sweep used (it is part of the determinism contract). */
  const scriptCells = function* (): Generator<ScriptCell> {
    for (const be of blockEntities.values()) {
      if (be.type !== 'script') continue;
      yield { key: cellKey(be.x, be.y, be.z), x: be.x, y: be.y, z: be.z, source: be.data.source };
    }
  };
  /** Redstone rising-edge sweep: a newly-powered script block runs its script once
   *  (or engages its autopilot preset). Runs on the redstone tick, like activators.
   *  The latch + dispatch live in the sim (§27.4); this supplies the cells, the power
   *  probe and the host. */
  const tickScriptBlocks = (): void => {
    scripts.tick({
      cells: scriptCells(),
      powerAt: (x, y, z) => redstone.powerLevelAt(x, y, z),
      host: scriptHost,
      // §27.8's injected tick + mob seed: the cadence, the frame ceiling, the spawn
      // budget and the per-dispatch rng fork all derive from these two.
      tick: world.tickCount,
      seed: entities.mobScripts?.seed,
      // §27.10's signal delivery. The runtime has no entity index, so the host answers
      // "who is in range" — and the runtime sorts by entity id afterwards, so the order
      // this yields in cannot reach the dispatch order.
      signalTargets: (x, y, z, radius) => signalTargetsNear(x, y, z, radius),
      // An autopilot preset claims the edge instead: the aircraft controller is client
      // state (AVI-3), so the sim never sees it.
      claim: (cell) => {
        const be = blockEntities.get(cell.x, cell.y, cell.z);
        if (be?.type !== 'script' || !be.data.autopilot) return false;
        engageScriptAutopilotAt(be);
        return true;
      },
    });
  };
  /** How many script blocks already exist in the chunk containing (x,z) (SCR-3 cap). */
  const scriptBlocksInChunk = (x: number, z: number): number => {
    const cx = Math.floor(x / WORLD.SECTION), cz = Math.floor(z / WORLD.SECTION);
    let n = 0;
    for (const be of blockEntities.values())
      if (be.type === 'script' && Math.floor(be.x / WORLD.SECTION) === cx && Math.floor(be.z / WORLD.SECTION) === cz) n++;
    return n;
  };
  /** The script block entity at (x,y,z) — lazily created for a `script_block` cell
   *  that has none yet (e.g. placed by `/setblock`), so scripting always has a home. */
  const scriptBEAt = (x: number, y: number, z: number): ScriptBE | null => {
    const existing = blockEntities.get(x, y, z);
    if (existing) return existing.type === 'script' ? existing : null;
    if (registries.states.blockName[world.getBlockState(x, y, z)] === 'script_block') {
      return blockEntities.create('script', x, y, z) as ScriptBE;
    }
    return null;
  };
  // A cure completes: the zombie villager becomes a (farmer) villager, and the
  // freshly-cured villager + witnesses in range gain major_positive gossip about
  // the curer (game/cure) — closing the loop the Pass-13 gossip slice simulated.
  const completeCure = (e: Entity): void => {
    e.type = 'farmer';
    const def = registries.mobs.byName.get('farmer');
    if (def) e.maxHealth = def.maxHealth;
    e.health = e.maxHealth;
    delete e.data.curing;
    delete e.data.weak;
    const nearby: Array<{ id: number; dist: number }> = [];
    for (const o of entities.entities) {
      if (o.removed || o.id === e.id || !isVillager(o)) continue;
      nearby.push({ id: o.id, dist: Math.hypot(o.x - e.x, o.z - e.z) });
    }
    for (const g of cureGossipGrants(e.id, nearby)) earnGossip(g.id, g.type, g.amount);
  };
  const tickCures = (): void => {
    for (const e of entities.entities) {
      if (e.removed || !e.data.curing) continue;
      if (--e.data.curing <= 0) completeCure(e);
    }
  };
  /** A zombie converts to a drowned in place (`e.type`) once its head has been under
   *  water long enough (E1, M18). Leaving the water resets the timer. */
  const completeDrown = (e: Entity): void => {
    e.type = 'drowned';
    const def = registries.mobs.byName.get('drowned');
    if (def) { e.maxHealth = def.maxHealth; e.health = Math.min(e.health, e.maxHealth); }
    delete e.data.drowning;
  };
  const tickDrownConversion = (): void => {
    for (const e of entities.entities) {
      if (e.removed || e.type !== 'zombie') continue;
      const hx = Math.floor(e.x), hy = Math.floor(e.y + 1.7), hz = Math.floor(e.z);
      if (!world.isFluid(hx, hy, hz) || world.isLava(hx, hy, hz)) { if (e.data.drowning) delete e.data.drowning; continue; }
      e.data.drowning = (e.data.drowning ?? 0) + 1;
      if (shouldDrown(e.data.drowning)) completeDrown(e);
    }
  };
  const nearestVillager = (x: number, z: number, r: number): Entity | null => {
    let best: Entity | null = null, bestD = r * r;
    for (const e of entities.entities) {
      if (e.removed || !isVillager(e)) continue;
      const d = (e.x - x) ** 2 + (e.z - z) ** 2;
      if (d < bestD) { bestD = d; best = e; }
    }
    return best;
  };
  // A 2-air gap on solid ground near a column — where a raider can stand.
  const raidStandY = (x: number, z: number, aroundY: number): number => {
    const ix = Math.floor(x), iz = Math.floor(z);
    for (let y = Math.floor(aroundY) + 4; y > Math.floor(aroundY) - 8; y--) {
      if (world.isSolid(ix, y - 1, iz) && !world.isSolid(ix, y, iz) && !world.isSolid(ix, y + 1, iz)) return y;
    }
    return Math.floor(aroundY);
  };
  const spawnRaider = (mob: string, index: number, count: number): void => {
    if (!raid) return;
    const angle = (index / Math.max(1, count)) * Math.PI * 2;
    const sx = raid.center.x + Math.cos(angle) * RAID.SPAWN_RING;
    const sz = raid.center.z + Math.sin(angle) * RAID.SPAWN_RING;
    const e = entities.spawn(mob, sx + 0.5, raidStandY(sx, sz, raid.center.y), sz + 0.5, { persistent: true });
    if (e) e.data.raid = 1;
  };
  const applyRaidVictory = (): void => {
    const lvl = raid ? raid.badOmen : 1;
    for (const p of [playerState]) addEffect(p.effects, 'hero_of_the_village', Math.max(0, lvl - 1), RAID.HERO_TICKS);
  };
  const raidInfoSnapshot = (): RaidInfo | null => {
    if (!raid) return null;
    const raiderIds: number[] = [];
    for (const e of entities.entities) if (!e.removed && e.data.raid === 1) raiderIds.push(e.id);
    return {
      status: raid.status, wave: raid.waveNumber, totalWaves: raid.totalWaves,
      alive: raiderIds.length, waveTotal: raid.waveTotal, center: { ...raid.center }, raiderIds,
    };
  };
  const startRaidAt = (center: { x: number; y: number; z: number }, badOmen: number): void => {
    if (raid || !raidTable) return;
    raid = new Raid(center, wavesFor(raidTable, difficulty), Math.max(1, badOmen));
  };
  const tickRaids = (): void => {
    if (!raidTable || world.dimension !== 'overworld') return;
    const px = player.body.x, pz = player.body.z;
    if (!raid && hasEffect(playerState.effects, 'bad_omen')) {
      const v = nearestVillager(px, pz, RAID.TRIGGER_RADIUS);
      if (v) {
        const lvl = amplifierOf(playerState.effects, 'bad_omen') + 1;
        removeEffect(playerState.effects, 'bad_omen');
        startRaidAt({ x: v.x, y: v.y, z: v.z }, lvl);
      }
    }
    if (!raid) return;
    // Defeat: the village is wiped, or the player abandons the fight.
    if (villagersWithin(raid.center.x, raid.center.z, RAID.LEASH) === 0 ||
        Math.hypot(px - raid.center.x, pz - raid.center.z) > RAID.ABANDON_RADIUS) {
      raid.defeat();
    } else {
      raid.tick(raidRaidersAlive(), spawnRaider);
    }
    if (raid.status === 'victory') { applyRaidVictory(); raid = null; }
    else if (raid.status === 'defeat') { raid = null; }
  };

  // Block-entity tick environment (§5.5 phase 6): furnaces smelt against the
  // registry; dungeon spawners fire near the player from a dedicated rng stream.
  const spawnerRng = new Rng(world.seed).fork(S_SPAWNER);
  const beEnv: BlockEntityEnv = {
    reg: registries,
    maxStackOf,
    dropItem: (x, y, z, stack) => { drops.spawn(stack, x + 0.5, y + 1, z + 0.5, { vy: 0.15 }); }, // campfire pops cooked food upward
    // M55.4 (§30.3): a running timer's own `env.tick` is set fresh before every
    // `tickBlockEntities` call below; `onTimerFinished` tells every player within
    // COW.TIMER_RADIUS — no chat, no toast storm, exactly the binding line.
    onTimerFinished: (x, y, z) => {
      const d = Math.hypot(player.body.x - (x + 0.5), player.body.y - (y + 0.5), player.body.z - (z + 0.5));
      if (d > COW.TIMER_RADIUS) return;
      game.showActionBar('Timer finished.');
      // Hangar §39.6 (M106.6) — only when there is somewhere else to be told: unfocused means
      // the action-bar line above is one the player cannot see.
      if (shouldNotifyTimerFinished(d, COW.TIMER_RADIUS, game.hangar, game.hangarFocused)) {
        void game.hangar!.api.notify.post({ title: 'Timer finished.', actions: ['Show'] });
      }
    },
    spawner: {
      nearestPlayerDist: (x, y, z) => Math.hypot(player.body.x - (x + 0.5), player.body.y - y, player.body.z - (z + 0.5)),
      countMobsNear: (type, x, y, z, r) => {
        let n = 0;
        for (const e of entities.entities) {
          if (e.type !== type) continue;
          if ((e.x - (x + 0.5)) ** 2 + (e.y - y) ** 2 + (e.z - (z + 0.5)) ** 2 <= r * r) n++;
        }
        return n;
      },
      spawnMob: (type, x, y, z) => entities.spawn(type, x, y, z) !== null,
      canSpawnAt: (x, y, z) => !world.isSolid(x, y, z) && !world.isSolid(x, y + 1, z) && world.isSolid(x, y - 1, z),
      rng: spawnerRng,
    },
  };

  const cmdContext: CommandContext = {
    get cheats() { return game.cheats; },
    hasBlock: (n) => registries.states.defaultOf.has(n),
    blockNames: () => [...registries.blocks.byName.keys()].filter((n) => n !== 'air'),
    itemNames: () => registries.items.nameOf.filter((n) => n !== 'empty'),
    getBlock: (x, y, z) => registries.states.blockName[world.getBlockState(x, y, z)]!,
    setBlock: (x, y, z, b) => edit(x, y, z, resolveBlock(b)),
    fillBlocks: (x1, y1, z1, x2, y2, z2, b) => {
      const state = resolveBlock(b);
      const ax = Math.min(x1, x2), bx = Math.max(x1, x2);
      const ay = Math.min(y1, y2), by = Math.max(y1, y2);
      const az = Math.min(z1, z2), bz = Math.max(z1, z2);
      let count = 0;
      for (let x = ax; x <= bx; x++) for (let y = ay; y <= by; y++) for (let z = az; z <= bz; z++) {
        world.setBlockState(x, y, z, state);
        count++;
      }
      for (let cx = Math.floor(ax / WORLD.SECTION) - 1; cx <= Math.floor(bx / WORLD.SECTION) + 1; cx++) {
        for (let cz = Math.floor(az / WORLD.SECTION) - 1; cz <= Math.floor(bz / WORLD.SECTION) + 1; cz++) {
          world.relightChunk(cx, cz);
        }
      }
      for (let cx = Math.floor(ax / WORLD.SECTION) - 1; cx <= Math.floor(bx / WORLD.SECTION) + 1; cx++) {
        for (let cz = Math.floor(az / WORLD.SECTION) - 1; cz <= Math.floor(bz / WORLD.SECTION) + 1; cz++) {
          chunkMeshes.markDirty(cx * WORLD.SECTION + 8, cz * WORLD.SECTION + 8);
        }
      }
      return count;
    },
    giveItem: (item, count) => {
      if (!registries.items.idOf.has(item)) return false;
      game.give(item, count);
      return true;
    },
    getPos: () => ({ x: player.body.x, y: player.body.y, z: player.body.z }),
    tp: (x, y, z) => {
      player.body.x = x; player.body.y = y; player.body.z = z;
      player.body.vx = player.body.vy = player.body.vz = 0;
      player.body.onGround = false;
    },
    getGamemode: () => playerState.gameMode,
    setGamemode: (m) => { game.setGameMode(m as GameMode); },
    getSeed: () => meta.seed,
    getTime: () => world.dayTime,
    setTime: (t) => { world.dayTime = t; },
    setWeather: (kind, seconds) => setWeather(world.weather, kind, seconds !== undefined ? seconds * 20 : undefined),
    getGamerule: (n) => world.gamerules.get(n),
    setGamerule: (n, v) => {
      world.gamerules.set(n, v);
      if (n === HEREDITY_RULE) refreshCropOverlay(); // §28.10 — off clears the overlay, on repaints it
    },
    listGamerules: () => [
      ...new Set([...KNOWN_GAMERULES, ...world.gamerules.keys()]),
    ],
    mobNames: () => [...registries.mobs.byName.keys()],
    summon: (mob, x, y, z) => entities.spawn(mob, x, y, z, { persistent: true }) !== null,
    killEntities: () => { const n = entities.entities.length; entities.clear(); return n; },
    structureTypes: () => (flat || atlasCity || earthWorld ? [] : structureTypeNames()),
    locate: (type, fromX, fromZ) => {
      if (flat || atlasCity || earthWorld) return null;
      const found = locateStructureType(type, world.seed, Math.floor(fromX / WORLD.SECTION), Math.floor(fromZ / WORLD.SECTION), structEnv);
      return found ? { x: found.x, z: found.z } : null;
    },
  };

  const game: Game = {
    registries,
    renderTable,
    // Per-dimension fields are getters so consumers always see the active
    // dimension after a portal swap (DIM-1) without holding a stale reference.
    get world() { return world; },
    save,
    meta,
    settings,
    scene: rc.scene,
    camera: rc.camera,
    renderer: rc.renderer,
    get chunkMeshes() { return chunkMeshes; },
    icons,
    atlasCanvas: rc.atlasCanvas,
    reskinBlock,
    reskinItem,
    reskinMob,
    player,
    playerState,
    net,
    input: { forward: false, back: false, left: false, right: false, jump: false, sprint: false, sneak: false },
    locked: false,
    breaking: false,
    usingItem: false,
    mining,
    get drops() { return drops; },
    get fallingBlocks() { return fallingBlocks; },
    get primedTnt() { return primedTnt; },
    get minecarts() { return minecarts; },
    spawnMinecartAt: (x, y, z) => spawnMinecartAt(x, y, z, facingFromYaw(player.yaw)),
    get boats() { return boats; },
    setBoatInput: (id, input) => { boatInputs.set(id, input); },
    get aircraft() { return aircraft; },
    enterAircraft: () => enterAircraft(),
    exitAircraft: () => exitAircraft(),
    flyingAircraft: () => flyingAircraft(),
    setAircraftInput: (input) => { aircraftInputOverride = input; },
    setAutopilot: (targets) => { autopilotTargets = targets; if (!targets) autopilot.reset(); },
    autopilotEngaged: () => autopilotTargets !== null,
    deployBeacon: (x, y, z) => deployBeaconAt(x, y, z).id,
    removeBeacon: (id) => { const b = beacons.beacons.find((k) => k.id === id); if (!b) return false; beacons.remove(b); return true; },
    clearBeacons: () => beacons.clear(),
    beaconList: () => beacons.beacons.map((b) => ({ id: b.id, x: b.x, y: b.y, z: b.z })),
    sarSignal: (x, y, z) => { const s = sarSignalAt(x, y, z); return { dist: s.dist, rssi: s.rssi, bars: s.bars }; },
    sarBearing: (x, y, z, yaw) => sarBearingAt(x, y, z, yaw),
    sarObjectiveState: (searcher) => { const s = searcher ?? sarSearcher(); return { dwell: sarObjective.dwellOf(s), found: sarObjective.hasFound(s) }; },
    toggleSarHud: (v) => { sarHud.setVisible(v ?? !sarHud.visible); return sarHud.visible; },
    placeBlueprint: (dsl, x, y, z, rot) => placeBlueprintImpl(dsl, x, y, z, rot ?? 0),
    setBlueprintCourse: (entries) => setCourseImpl(entries),
    clearBlueprint: () => { classroom.clear(); blueprintRenderer.clear(); },
    blueprintState: () => blueprintStateImpl(),
    blueprintAim: () => blueprintAimImpl(),
    toggleClassroomHeat: (v) => { classroomHeat = v ?? !classroomHeat; return classroomHeat; },
    openClassroom: () => openClassroom(game),
    overlay: null, // wired below once game.saveNow + toasts exist (Earth worlds only)
    openOverlayPanel: () => { if (game.overlay) openOverlayPanel(game, game.overlay); },
    bakeImagery: () => bakeImageryImpl(),
    unbakeImagery: () => unbakeImageryImpl(),
    hasBakedImagery: () => !!meta.imageryBake,
    // ── World doors (DOR-1..6, M41/M42): the menu + traversal + resize + escape hatch.
    //    In net mode the graph is server-side (M42), so each seam hits the control plane
    //    keyed off the door's block state; solo uses the local IDB block-entity graph. ──
    doorArrival: null,
    openDoorScreen: (x, y, z) => openDoorScreenFor(x, y, z),
    worldDoorAt: (x, y, z) => {
      // Net: server doors carry no local block entity — the world_door block state is the
      // door (its edge is fetched over the control plane; here we only report presence).
      if (net) return registries.states.blockName[world.getBlockState(x, y, z)] === 'world_door' ? { doorId: `${x},${y},${z}`, targetWorldId: 'server', targetDoorId: '' } : null;
      const be = blockEntities.get(x, y, z);
      return be?.type === 'world_door' ? { ...be.data } : null;
    },
    traverseDoor: async (x, y, z) => {
      if (net) return traverseServer(x, y, z); // resolve + reconnect; false ⇒ denied/inert (E-37/E-34)
      const be = blockEntities.get(x, y, z);
      if (be?.type !== 'world_door' || !be.data.targetWorldId) return false;
      return traverseTo(be.data.targetWorldId, be.data.targetDoorId); // false ⇒ inert (E-34)
    },
    createChildThroughDoor: async (x, y, z, opts) => {
      if (net?.ctl && net.guest) return net.ctl.createServerChild(net.controlOrigin, net.guest, net.welcome.meta.id, `${x},${y},${z}`, { worldType: opts.worldType, size: opts.size, name: opts.name, public: opts.public });
      const be = blockEntities.create('world_door', x, y, z) as WorldDoorBE;
      const r = await createChildWorld(save, meta, be.data, opts);
      Object.assign(be.data, r.door);
      await game.saveNow();
      return r.childId;
    },
    resizeDoorTarget: (x, y, z, size) => {
      if (net?.ctl && net.guest) {
        return net.ctl.resizeServerWorld(net.controlOrigin, net.guest, net.welcome.meta.id, `${x},${y},${z}`, size).then(
          (r): ResizeResult => (r.ok ? { ok: true, size: r.size ?? size } : { ok: false, size, blockers: (r.blockers ?? []).map((b) => ({ kind: 'section', x: b.cx * WORLD.SECTION, z: b.cz * WORLD.SECTION })) }),
        );
      }
      const be = blockEntities.get(x, y, z) as WorldDoorBE | undefined;
      return resizeWorld(save, be?.data.targetWorldId ?? '', size);
    },
    returnHome: () => returnHomeImpl(),
    openUsageScreen: () => openUsageScreen(game),
    storageUsage: () => (net?.ctl && net.guest ? serverUsageReport(net) : computeUsage(save)),
    helicopters, vans,
    spawnHeli: (x, y, z, yaw = 0) => spawnHeli(x, y, z, yaw).id,
    enterHeli: (id) => enterHeli(id),
    exitHeli: () => exitHeli(),
    boardHeli: (id) => boardHeli(id),
    setHeliInput: (input) => { heliInputOverride = input; },
    pilotedHeliId: () => pilotedHeliId,
    heliRiderCount: (id) => heliRiders.get(id) ?? 0,
    spawnVan: (x, y, z, yaw = 0) => spawnVan(x, y, z, yaw).id,
    enterVan: (id) => enterVan(id),
    exitVan: () => exitVan(),
    boardVan: (id) => boardVan(id),
    setVanInput: (input) => { vanInputOverride = input; },
    pilotedVanId: () => pilotedVanId,
    vanRiderCount: (id) => vanRiders.get(id) ?? 0,
    streamingDebug: () => ({ lod: viewLod, renderCap: effectiveRenderDist, meshWorkers: chunkMeshes.workerCount, genWorkers: genClient.workerCount, loadedChunks: chunkMeshes.loadedCount, vx: streamVx, vz: streamVz }),
    spectate: (id) => { spectateId = id; },
    // Mount replication (M19 remainder / E2E): ask the server for a free seat on a
    // server entity id, or dismount with 0. The server decides.
    requestMount: (vehicleId) => {
      if (!net) return;
      if (vehicleId === 0) { net.sendMount(0); return; }
      const shadow = netEntityById.get(vehicleId);
      const kind = shadow ? mountKindOf(shadow.type) : null;
      net.sendMount(vehicleId, kind ? freeSeatOn(vehicleId, kind) : 0);
    },
    spectateNext: () => { spectateId = net ? nextSpectatorTarget(spectateId, net.remotePlayers().map((r) => r.id)) : null; },
    spectatingId: () => spectateId,
    mountBoat: (id) => mountBoat(id),
    dismountBoat: () => { const rb = boats.boats.find((x) => x.id === ridingBoatId); if (rb) dismountBoat(player.body, rb); else ridingBoatId = null; },
    ridingBoatId: () => ridingBoatId,
    mountEntity: (id) => {
      const e = entities.entities.find((m) => m.id === id && !m.removed);
      if (!e || !mountKindOf(e.type) || e.baby || ridingMountId !== null) return false;
      ridingMountId = id;
      return true;
    },
    dismountMount: () => { const rm = entities.entities.find((m) => m.id === ridingMountId && !m.removed); if (rm) dismountMount(player.body, rm); else ridingMountId = null; },
    ridingMount: () => ridingMountId,
    saddleMount: (id) => {
      const e = entities.entities.find((m) => m.id === id && !m.removed && !!mountKindOf(m.type));
      if (!e) return false;
      e.data.saddled = 1;
      return true;
    },
    get blockEntities() { return blockEntities; },
    get entities() { return entities; },
    attackCooldown: 0,
    attackCooldownMax: MOB.MELEE.COOLDOWN,
    uiOpen: false,
    fps: 0,
    worstFrameMs: 0,
    // Cheats = the world's explicit Allow-Commands flag; older saves without it
    // fall back to the mode-based default (creative on / survival off). Debug API bypasses.
    cheats: meta.allowCommands ?? (meta.gameMode !== 'survival'),
    chatOpen: false,
    cmdContext,
    atlasCity,
    eye: PLAYER.EYE_HEIGHT,
    reach: REACH.BLOCK,
    setBlock(x, y, z, state) { edit(x, y, z, state); },
    heldPlaceState,
    mineBlock(x, y, z) { playerEdit(() => {
      const oldState = world.getBlockState(x, y, z);
      if (oldState === STATE_AIR) return;
      const caps = modeCaps(playerState.gameMode);
      if (!caps.canBuild) return;
      // Capture and remove any block entity before the block is cleared, so its
      // stored contents (chest/furnace) scatter with the block's own drop (M6).
      const be = blockEntities.remove(x, y, z);
      // §27.9 `on broken`, from the block entity JUST removed — `enqueueBlockHook`
      // reads the store and the store no longer has it, which is the whole reason
      // `LifecycleDispatch` carries the source text instead of a cell to look up.
      if (be?.type === 'script' && be.data.source !== '') {
        scripts.enqueueLifecycle({
          key: cellKey(x, y, z), x, y, z, source: be.data.source, hook: 'broken', depth: 0,
        });
      }
      // A broken jukebox stops its music + drops the spinning disc (added below).
      const jukeboxDisc = ejectDisc(x, y, z);
      // A bed is two blocks — breaking one removes its partner (which drops nothing).
      const bedOther = bedPartner(x, y, z);
      const oldName = registries.states.blockName[oldState] ?? '';
      playBlockSound('dig', oldName, x, y, z); // material break voice (AUD-2)
      if (oldName) { fireAdvancement({ kind: 'mine', block: oldName }); stats.bump('mined', oldName); } // M24: "Diamonds!" + Blocks Mined stat
      // Breaking a crop's support (farmland/dirt) also breaks the crop above it.
      // Its block entity is a SEPARATE cell from the one this function was called
      // for, so it needs its own capture — `be` above only ever covers (x,y,z).
      const aboveCrop = cropOf(registries.states.blockName[world.getBlockState(x, y + 1, z)] ?? '');
      const aboveState = world.getBlockState(x, y + 1, z);
      const aboveBE = aboveCrop ? blockEntities.remove(x, y + 1, z) : undefined;
      // Breaking a waterlogged block leaves the water it held behind (M9.1).
      edit(x, y, z, world.isWaterlogged(oldState) ? world.waterState : STATE_AIR);
      if (bedOther) edit(bedOther.x, bedOther.y, bedOther.z, STATE_AIR);
      if (aboveCrop) edit(x, y + 1, z, STATE_AIR);
      mining.active = false;
      if (!caps.infiniteResources) {
        // Age-based crop yield; farmland drops dirt; else the block's own drops
        // (Fortune multiplies ore drops, Silk Touch collects the ore block, MB.4).
        const crop = cropOf(oldName);
        const dirtId = registries.items.idOf.get('dirt');
        const tool = heldTool();
        const grownHydroponically = registries.states.blockName[world.getBlockState(x, y - 1, z)] === 'hydroponic_tray';
        const yield_ = crop ? cropDrops(crop, ageOf(oldState), x, y, z, be?.type === 'crop' ? be : undefined, grownHydroponically)
          : oldName === 'farmland' && dirtId !== undefined ? [makeStack(dirtId, 1)]
          : oldName === 'snow' ? snowLayerDrops(oldState, tool) // snowballs per layer, shovel only (MB.1)
          : blockDrops(registries, oldState, tool, { fortuneRoll: Math.random() });
        if (aboveCrop) {
          yield_.push(...cropDrops(aboveCrop, ageOf(aboveState), x, y + 1, z, aboveBE?.type === 'crop' ? aboveBE : undefined, oldName === 'hydroponic_tray'));
        }
        if (be) yield_.push(...blockEntityDrops(be));
        if (jukeboxDisc) { // the jukebox drops its disc alongside itself
          const did = registries.items.idOf.get(jukeboxDisc);
          if (did !== undefined) yield_.push(makeStack(did, 1));
        }
        for (const drop of yield_) {
          drops.spawn(drop, x + 0.5, y + 0.55, z + 0.5, {
            vx: (Math.random() - 0.5) * 0.1, vy: 0.16, vz: (Math.random() - 0.5) * 0.1,
          });
        }
        // XP orbs from ores/sculk (none when Silk-Touched — the block was collected).
        const xp = blockBreakXp(oldName, hasSilkTouch(tool?.enchants), Math.random());
        if (xp > 0) entities.spawnXp(xp, x + 0.5, y + 0.5, z + 0.5);
        damageHeldTool();
      }
    }); },
    placeBlock(x, y, z) { return playerEdit(() => {
      const caps = modeCaps(playerState.gameMode);
      if (!caps.canBuild) return false;
      const state = heldPlaceState();
      if (state === null) return false;
      // Beds occupy two blocks — place the foot + head together (PLR-10).
      if (registries.states.blockName[state] === 'bed') {
        const at = world.getBlockState(x, y, z);
        if (at !== STATE_AIR && !world.isReplaceableByFluid(at)) return false;
        if (!placeBed(x, y, z)) return false;
        consumeHeld();
        playBlockSound('place', 'bed', x, y, z);
        fireAdvancement({ kind: 'place', block: 'bed' }); stats.bump('used', 'bed'); // M24: "Sweet Dreams" + Items Used stat
        return true;
      }
      // Snow layers stack in-place: a snow item used on a <8-layer snow block
      // adds a layer rather than failing on the occupied cell (MB.1).
      if (registries.states.blockName[state] === 'snow') {
        const at = world.getBlockState(x, y, z);
        if (registries.states.blockName[at] === 'snow') {
          const nl = snowStackPlace(snowLayersOf(registries.states.props[at]));
          if (nl === null) return false; // already full height
          const ns = snowStateOf(nl);
          if (ns < 0) return false;
          edit(x, y, z, ns);
          consumeHeld();
          playBlockSound('place', 'snow', x, y, z);
          return true;
        }
      }
      const existing = world.getBlockState(x, y, z);
      const intoWater = existing === world.waterState;
      // Build into air, water, or a washable plant; waterlog if placed in a source.
      if (existing !== STATE_AIR && !intoWater && !world.isReplaceableByFluid(existing)) return false;
      if (blockIntersectsBody(player.body, x, y, z)) return false;
      // Orient a fence gate / trapdoor / carved pumpkin to the player's facing.
      let placeState = state;
      const placedName = registries.states.blockName[state] ?? '';
      // Script blocks are capped per chunk (SCR-3) — refuse over the limit.
      if (placedName === 'script_block' && scriptBlocksInChunk(x, z) >= SCR.BLOCKS_PER_CHUNK) {
        game.print(`§ too many script blocks in this chunk (max ${SCR.BLOCKS_PER_CHUNK})`);
        return false;
      }
      const def = registries.blocks.byName.get(placedName);
      // Hoppers/droppers/dispensers face along the player's look (6-way); a hopper
      // can't point up, so a skyward look collapses to `down` (RED-2).
      if (placedName === 'hopper' || placedName === 'dropper' || placedName === 'dispenser' || placedName === 'crafter') {
        let f = facing6FromDir(lookDir());
        if (placedName === 'hopper' && f === 'up') f = 'down';
        const key = registries.states.key[state]!.replace(/facing=(?:north|east|south|west|up|down)/, `facing=${f}`);
        placeState = registries.states.idByKey.get(key) ?? state;
      } else if ((def?.shape === 'fence_gate' || def?.shape === 'trapdoor' || placedName === 'carved_pumpkin' || placedName === 'repeater' || placedName === 'comparator' || placedName.endsWith('_glazed_terracotta')) && registries.states.props[state]?.facing !== undefined) {
        const key = registries.states.key[state]!.replace(/facing=(?:north|east|south|west)/, `facing=${facingFromYaw(player.yaw)}`);
        placeState = registries.states.idByKey.get(key) ?? state;
      }
      const wl = intoWater ? world.waterloggedVariant(placeState, true) : -1;
      edit(x, y, z, wl >= 0 ? wl : placeState);
      // Attach a block entity for chests/furnaces/spawners (M6).
      const beType = blockEntityOf(placedName);
      if (beType) blockEntities.create(beType, x, y, z);
      // §27.9 `on placed`, AFTER the block entity exists — a script block placed with a
      // source already in it (pick-blocked, or pasted from a schematic) must be able to
      // read that source on the hook that announces its own placement.
      enqueueBlockHook(x, y, z, 'placed');
      consumeHeld();
      playBlockSound('place', placedName, x, y, z); // material place voice (AUD-2)
      if (placedName) { fireAdvancement({ kind: 'place', block: placedName }); stats.bump('used', placedName); } // M24 place trigger + Items Used stat
      // A carved pumpkin may complete a golem (MOB-2): the frame becomes the mob.
      if (placedName === 'carved_pumpkin') tryBuildGolem(x, y, z);
      // A wither skeleton skull may complete the wither summon frame (MOB-6).
      if (placedName === 'wither_skeleton_skull') tryBuildWither(x, y, z);
      return true;
    }); },
    useBlock(x, y, z) {
      const name = registries.states.blockName[world.getBlockState(x, y, z)];
      if (signBlocks.has(name ?? '')) {
        // A glow/plain ink sac paints the sign's glow instead of opening the editor (M22).
        const held = heldItemName();
        if (glowInkApplies(held)) {
          setSignGlow(x, y, z, signGlowAfter(signGlows(x, y, z), held));
          if (!modeCaps(playerState.gameMode).infiniteResources) consumeHeld();
          return true;
        }
        openSignEditor(getSignText(x, y, z), (lines) => {
          setSignText(x, y, z, lines); // applied locally at once — MP reconciles via the server's own BlockText echo
          net?.sendBlockTextEdit(x, y, z, 'sign', JSON.stringify(normalizeSignText(lines)));
        });
        return true;
      }
      // Co-working blocks (M55.4, ARCH §30.3): sticky_note / task_board / timer.
      if (name === 'sticky_note') {
        const be = blockEntities.create('sticky_note', x, y, z) as StickyNoteBE;
        openNoteEditor(be.data.text, (text) => {
          be.data.text = normalizeNote(text);
          net?.sendBlockTextEdit(x, y, z, 'sticky_note', JSON.stringify(be.data));
        });
        return true;
      }
      if (name === 'task_board') {
        const be = blockEntities.create('task_board', x, y, z) as TaskBoardBE;
        const hangar = game.hangar;
        const net_ = game.net;
        const linkOf = (): BoardLink | undefined =>
          game.meta.boardLinks?.find((l) => l.boardCell.x === x && l.boardCell.y === y && l.boardCell.z === z);
        const persistLink = (link: BoardLink | undefined): void => {
          const links = (game.meta.boardLinks ?? []).filter(
            (l) => !(l.boardCell.x === x && l.boardCell.y === y && l.boardCell.z === z),
          );
          game.meta.boardLinks = link ? [...links, link] : links;
          void game.saveNow();
        };

        const onChange = (lines: TaskLine[]): void => {
          be.data.lines = normalizeBoard(lines);
          net?.sendBlockTextEdit(x, y, z, 'task_board', JSON.stringify(be.data));
          // §39.7 (M107.3) — Board → list: diff the edit against the mapping and send
          // whatever `write` ops result. No fresh `read`: the board is authoritative for
          // its own edits, and the mapping's own last-known content stands in for "what
          // the list still thinks," so only this edit itself can produce an op.
          const link = linkOf();
          if (hangar && link) {
            void import('./hangar/boardlink').then(({ reconcileWithoutRead }) => {
              const result = reconcileWithoutRead(link, be.data.lines, {
                mintLineId: () => newId('line'),
                maxLines: COW.BOARD_LINES,
                maxChars: COW.LINE_MAX,
              });
              persistLink({ ...link, mapping: result.mapping });
              if (result.writeOps.length > 0) void hangar.api.reminders.write(link.listId, result.writeOps);
            });
          }
        };

        const linkOptions =
          hangar && net_
            ? {
                isLinked: linkOf() !== undefined,
                onToggleLink: async (): Promise<void> => {
                  const existing = linkOf();
                  const { pickBoardLink, unlinkBoard, reconcileWithRead } = await import('./hangar/boardlink');
                  const hooks = { worldId: () => net_.welcome.meta.id, mintLineId: () => newId('line'), maxLines: COW.BOARD_LINES, maxChars: COW.LINE_MAX };
                  if (existing) {
                    if (await unlinkBoard(hooks, existing)) persistLink(undefined);
                    return;
                  }
                  const link = await pickBoardLink(hangar, hooks, { x, y, z });
                  if (!link) return;
                  // §39.7 — "on window open: one full reconcile before any incremental op."
                  // A fresh link has nothing to reconcile FROM yet, but this still settles
                  // the board against whatever the picked list already held.
                  const result = await reconcileWithRead(hangar, link, be.data.lines, hooks);
                  be.data.lines = result.boardLines;
                  net_.sendBlockTextEdit(x, y, z, 'task_board', JSON.stringify(be.data), true);
                  persistLink({ ...link, mapping: result.mapping });
                },
              }
            : undefined;

        if (hangar && linkOf()) {
          // The screen's own on-open reconcile (the other half of "on window open: one
          // full reconcile before any incremental op" — `attachBoardSync`'s background
          // pass covers the session-wide half of the same rule).
          const hooks = { mintLineId: () => newId('line'), maxLines: COW.BOARD_LINES, maxChars: COW.LINE_MAX };
          void import('./hangar/boardlink').then(({ reconcileWithRead }) =>
            reconcileWithRead(hangar, linkOf()!, be.data.lines, hooks).then((result) => {
              be.data.lines = result.boardLines;
              net?.sendBlockTextEdit(x, y, z, 'task_board', JSON.stringify(be.data), true);
              persistLink({ ...linkOf()!, mapping: result.mapping });
              openTaskBoard(be.data.lines, onChange, linkOptions);
            }),
          );
        } else {
          openTaskBoard(be.data.lines, onChange, linkOptions);
        }
        return true;
      }
      if (name === 'timer') {
        const be = blockEntities.create('timer', x, y, z) as TimerBE;
        openTimerScreen(be.data,
          (preset, customTicks) => {
            be.data = startTimer(world.tickCount, preset, customTicks);
            net?.sendBlockTextEdit(x, y, z, 'timer', JSON.stringify(be.data));
          },
          () => {
            be.data = stopTimer(be.data);
            net?.sendBlockTextEdit(x, y, z, 'timer', JSON.stringify(be.data));
          });
        return true;
      }
      if (name === 'shared_screen') {
        const known = shareStates.get(cellKey(x, y, z));
        const isSharer = !!sharingAt && sameCell(sharingAt, { x, y, z });
        const isWatching = !!watchingAt && sameCell(watchingAt, { x, y, z });
        openScreenModal(
          {
            live: known?.live ?? false, isSharer, isWatching,
            viewers: isSharer && shareController ? shareController.viewers().map((v) => ({ name: `p${v.id}`, state: v.state })) : [],
          },
          () => { // Share — the browser's OWN getDisplayMedia picker IS the consent gate (D-TF-179); TerraForge adds none of its own.
            void navigator.mediaDevices.getDisplayMedia(captureConstraints()).then((stream) => startSharingAt(x, y, z, stream));
          },
          () => { // Watch
            pendingWatchAt = { x, y, z };
            net?.sendShareJoin(x, y, z);
          },
          () => { // Stop
            if (isSharer) net?.sendShareStop(x, y, z);
            else if (isWatching) net?.sendShareLeave(x, y, z);
          },
        );
        return true;
      }
      if (name === 'note_block') { cycleNoteBlock(x, y, z); return true; } // cycle + play
      if (name === 'jukebox') { // insert the held disc, or eject the spinning one (AUD-4)
        const loaded = jukeboxDiscs.get(cellKey(x, y, z));
        if (loaded) { ejectDisc(x, y, z); game.give(loaded, 1); return true; }
        const held = playerState.inventory.held();
        const heldName = isEmpty(held) ? '' : registries.items.nameOf[held.item] ?? '';
        if (discById(heldName) && insertDisc(x, y, z, heldName)) { consumeHeld(); return true; }
        return false;
      }
      if (name === 'crafting_table') { game.openCraftingTable(); return true; }
      if (name === 'enchanting_table') { game.openEnchanting(x, y, z); return true; }
      if (name === 'smithing_table') { game.openSmithing(); return true; }
      if (name === 'decorated_pot') return usePot(x, y, z); // M42: stow / retrieve one stack
      if (name === 'vault') return tryOpenVault(x, y, z); // M22: unlock with a trial key
      if (name === 'respawn_anchor') return useAnchorAt(x, y, z) !== 'none'; // M20: charge / set-spawn / detonate
      if (isDoor(name)) { toggleDoor(x, y, z); return true; }
      if (useRedstone(x, y, z, name)) return true; // lever/button/repeater/comparator (RED)
      if (name === 'bed') return trySleep(x, y, z);
      // A full composter (level 8) is collected for bone meal, resetting to empty.
      if (name === 'composter') {
        if (Number(registries.states.props[world.getBlockState(x, y, z)]?.level ?? '0') >= 8) {
          game.give('bone_meal', 1);
          const ns = composterStateOf(0);
          if (ns >= 0) edit(x, y, z, ns);
          return true;
        }
        return false; // not full → let useItemOnBlock try to fill it
      }
      // Cake: eat one slice with any hand while hungry; the last slice removes it.
      if (name === 'cake') {
        if (!modeCaps(playerState.gameMode).survivalHunger) return false;
        const st = world.getBlockState(x, y, z);
        const bites = Number(registries.states.props[st]?.bites ?? '0');
        const r = biteCake(bites, playerState.hunger, SURVIVAL.MAX_HUNGER);
        if (!r.ate) return false;
        playerState.hunger = Math.min(SURVIVAL.MAX_HUNGER, playerState.hunger + r.hunger);
        playerState.saturation = Math.min(playerState.hunger, playerState.saturation + CAKE_SATURATION_PER_BITE * 2);
        if (r.removed) edit(x, y, z, STATE_AIR);
        else { const ns = registries.states.idByKey.get(`cake[bites=${r.nextBites}]`); if (ns !== undefined) edit(x, y, z, ns); }
        return true;
      }
      const shp = world.shapeAt(x, y, z);
      if (shp === SHAPE.FENCE_GATE || shp === SHAPE.TRAPDOOR) return toggleOpenable(x, y, z);
      const beType = blockEntityOf(name);
      if (!beType) return false;
      const be = blockEntities.create(beType, x, y, z);
      if (be.type === 'chest') { openChest(game, be.data); return true; }
      if (be.type === 'furnace' || be.type === 'smoker') { openFurnace(game, be.data); return true; }
      if (be.type === 'hopper') { openHopper(game, be.data); return true; }
      if (be.type === 'dropper') { openDispenser(game, be.data, 'Dropper'); return true; }
      if (be.type === 'dispenser') { openDispenser(game, be.data, 'Dispenser'); return true; }
      // A crafter reuses the 3×3 dispenser window to fill its grid (disabled-slot
      // toggles are a UI nicety deferred to the container-window follow-up).
      if (be.type === 'crafter') { openDispenser(game, be.data, 'Crafter'); return true; }
      if (be.type === 'brewing_stand') { openBrewing(game, be.data); return true; }
      // A script block opens its editor for creative/ops only (cheats-gated, SCR-4).
      if (be.type === 'script') {
        if (playerState.gameMode === 'creative' || game.meta.allowCommands) { openScript(game, x, y, z); return true; }
        return false;
      }
      // A world door opens its menu (create/link/enter/resize, DOR-1..4). A brief
      // post-arrival cooldown keeps a fresh traversal from re-triggering (E-36).
      if (be.type === 'world_door') {
        if (performance.now() < doorCooldownUntil) { game.print('§7Catch your breath…'); return true; }
        openDoorScreenFor(x, y, z);
        return true;
      }
      return false;
    },
    useHeldItem(fx, fy, fz) {
      const held = playerState.inventory.held();
      if (isEmpty(held)) return false;
      const heldName = registries.items.nameOf[held.item];
      // Release an axolotl from its bucket (MB.5): spawn it at the player, hand
      // back an empty water bucket.
      if (heldName === 'axolotl_bucket') {
        entities.spawn('axolotl', player.body.x, player.body.y, player.body.z, { persistent: true });
        const bucketId = registries.items.idOf.get('water_bucket');
        if (bucketId !== undefined && !modeCaps(playerState.gameMode).infiniteResources) {
          playerState.inventory.set(playerState.inventory.heldSlot(), makeStack(bucketId, 1));
        }
        return true;
      }
      // Release a fish from its bucket, handing back an empty water bucket (M18).
      const releasedFish = fishFromBucket(heldName ?? '');
      if (releasedFish) {
        const kept = playerState.inventory.held().components?.fishVariant;
        entities.spawn(releasedFish, player.body.x, player.body.y, player.body.z, {
          persistent: true, ...(typeof kept === 'number' ? { variant: kept } : {}),
        });
        const bucketId = registries.items.idOf.get('water_bucket');
        if (bucketId !== undefined && !modeCaps(playerState.gameMode).infiniteResources) {
          playerState.inventory.set(playerState.inventory.heldSlot(), makeStack(bucketId, 1));
        }
        return true;
      }
      // Throw a wind charge at your own feet to self-launch upward (M22.1).
      if (heldName === 'wind_charge') {
        const b = player.body;
        b.vy = Math.max(b.vy, windSelfLaunchImpulse());
        b.onGround = false;
        detonateWindCharge(b.x, b.y, b.z); // the burst also shoves nearby entities/blocks
        if (!modeCaps(playerState.gameMode).infiniteResources) consumeHeld();
        return true;
      }
      // Thrown into the air, an eye of ender points the way to the nearest
      // stronghold (DIM-3) — and, as in Minecraft, sometimes shatters doing it.
      if (heldName === 'ender_eye') {
        const b = player.body;
        const found = flat || atlasCity || earthWorld ? null : locateStructureType(
          'stronghold', world.seed, Math.floor(b.x / WORLD.SECTION), Math.floor(b.z / WORLD.SECTION), structEnv);
        if (!found) { game.print('§7The eye spins, then settles. Nothing out there.'); return true; }
        const dx = found.x - b.x, dz = found.z - b.z;
        const dist = Math.round(Math.hypot(dx, dz));
        game.print(`§5The eye pulls ${compassBearing(dx, dz)} — about ${dist} blocks.`);
        if (!modeCaps(playerState.gameMode).infiniteResources && eyeRng.nextInt(STRUCTURE.EYE_BREAK_ONE_IN) === 0) {
          consumeHeld();
          game.print('§7The eye shatters.');
        }
        return true;
      }
      // Hatch a sniffer egg (found by brushing suspicious blocks) into a baby sniffer (M19).
      if (heldName === 'sniffer_egg') {
        entities.spawn('sniffer', player.body.x, player.body.y, player.body.z, { baby: true, persistent: true });
        if (!modeCaps(playerState.gameMode).infiniteResources) consumeHeld();
        return true;
      }
      // Throw a splash potion along the look vector (MB.4): it arcs like the witch's
      // and shatters into a proximity-scaled cloud — a healing splash heals through
      // the host heal seam. (Tipped/lingering varieties are the brewing-stand pass.)
      if (heldName === 'splash_potion') {
        const dir = lookDir();
        const sp = POTION.THROW_SPEED;
        entities.spawnProjectile('potion', player.body.x, player.body.y + PLAYER.EYE_HEIGHT, player.body.z,
          dir.x * sp, dir.y * sp, dir.z * sp, -1, 0, { id: 'instant_health', amplifier: 0, ticks: 0 });
        if (!modeCaps(playerState.gameMode).infiniteResources) consumeHeld();
        return true;
      }
      // Boat: right-clicking water (or a block near it) floats a new boat there
      // (RED-6). A chest boat carries an inventory; both use the same physics.
      if (heldName === 'boat' || heldName === 'chest_boat') {
        const eye = { x: player.body.x, y: player.body.y + PLAYER.EYE_HEIGHT, z: player.body.z };
        const hit = raycastVoxel(
          (x, y, z) => world.isRaycastTarget(x, y, z) || world.isFluid(x, y, z),
          eye, lookDir(), REACH.BLOCK,
        );
        if (!hit) return false;
        spawnBoatAt(hit.x + 0.5, hit.y + 1, hit.z + 0.5, player.yaw, heldName as BoatKind); // on the hit cell's top face
        boatInputs.delete(boats.boats[boats.boats.length - 1]!.id); // start unmanned/still
        if (!modeCaps(playerState.gameMode).infiniteResources) consumeHeld();
        return true;
      }
      // ELT beacon (M36, SAR-2): right-click a surface to deploy an always-loaded
      // objective beacon on it; the search crew homes on its omnidirectional signal.
      if (heldName === 'elt') {
        const eye = { x: player.body.x, y: player.body.y + PLAYER.EYE_HEIGHT, z: player.body.z };
        const hit = raycastVoxel((x, y, z) => world.isRaycastTarget(x, y, z), eye, lookDir(), REACH.BLOCK);
        if (!hit) return false;
        deployBeaconAt(hit.x + 0.5, hit.y + 1, hit.z + 0.5);
        if (!modeCaps(playerState.gameMode).infiniteResources) consumeHeld();
        return true;
      }
      // Instrument readings (M53.1, ARCH §29.10, CNT-1): a use action processed at
      // this tick boundary — the sim computes the value from already-resolved world
      // state (pure `education/instruments.ts`), never mutates anything, and never
      // consumes the item (a tool, not a consumable).
      if (heldName === 'light_meter') {
        const eye = { x: player.body.x, y: player.body.y + PLAYER.EYE_HEIGHT, z: player.body.z };
        const hit = raycastVoxel((x, y, z) => world.isRaycastTarget(x, y, z), eye, lookDir(), REACH.BLOCK);
        const at = hit ?? { x: Math.floor(player.body.x), y: Math.floor(player.body.y), z: Math.floor(player.body.z) };
        const r = readLightMeter(world.getSkyLight(at.x, at.y, at.z), world.getBlockLight(at.x, at.y, at.z));
        game.showActionBar(r.text);
        for (const trig of r.triggers) fireEduTrigger({ kind: 'instrument_read', instrument: heldName, metric: trig.metric, value: trig.value });
        return true;
      }
      if (heldName === 'soil_probe') {
        const eye = { x: player.body.x, y: player.body.y + PLAYER.EYE_HEIGHT, z: player.body.z };
        const hit = raycastVoxel((x, y, z) => world.isRaycastTarget(x, y, z), eye, lookDir(), REACH.BLOCK);
        if (!hit || registries.states.blockName[world.getBlockState(hit.x, hit.y, hit.z)] !== 'farmland') return false;
        const moisture = Number(registries.states.props[world.getBlockState(hit.x, hit.y, hit.z)]?.moisture ?? '0');
        const aboveName = registries.states.blockName[world.getBlockState(hit.x, hit.y + 1, hit.z)];
        const crop = aboveName ? cropOf(aboveName) : undefined;
        const cropBE = crop?.genes && heredityOn(world) ? blockEntities.get(hit.x, hit.y + 1, hit.z) : undefined;
        const genome = cropBE?.type === 'crop' ? decodeGenome(cropBE.data.genome, crop!.genes!) : null;
        const droughtPct = genome ? Math.round(expressCrop(crop!.genes!, genome).droughtTol * 100) : null;
        const r = readSoilProbe(moisture, droughtPct);
        game.showActionBar(r.text);
        for (const trig of r.triggers) fireEduTrigger({ kind: 'instrument_read', instrument: heldName, metric: trig.metric, value: trig.value });
        return true;
      }
      if (heldName === 'signal_meter') {
        const b = player.body;
        const s = sarSignalAt(b.x, b.y, b.z);
        const r = readSignalMeter(Number.isFinite(s.rssi) ? s.rssi : null);
        game.showActionBar(r.text);
        for (const trig of r.triggers) fireEduTrigger({ kind: 'instrument_read', instrument: heldName, metric: trig.metric, value: trig.value });
        return true;
      }
      if (heldName === 'altimeter' || heldName === 'airspeed_indicator') {
        const b = player.body;
        let best: Aircraft | null = null, bestD = Infinity;
        for (const a of aircraft.aircraft) {
          const d = (a.x - b.x) ** 2 + (a.y - b.y) ** 2 + (a.z - b.z) ** 2;
          if (d < bestD) { bestD = d; best = a; }
        }
        if (!best) { game.showActionBar(heldName === 'altimeter' ? 'Altitude: no aircraft' : 'Airspeed: no aircraft'); return true; }
        const feet = meta.worldType === 'atlas' || meta.worldType === 'earth';
        const r = heldName === 'altimeter'
          ? readAltimeter(best.y - sarGroundY(best.x, best.z), feet)
          : readAirspeed(Math.hypot(best.vx, best.vy, best.vz));
        game.showActionBar(r.text);
        for (const trig of r.triggers) fireEduTrigger({ kind: 'instrument_read', instrument: heldName, metric: trig.metric, value: trig.value });
        return true;
      }
      // Bow: right-click fires an arrow (E2, M17). Power scales damage, Infinity
      // fires free, Flame lights it; needs an arrow in survival without Infinity.
      if (heldName === 'bow') return shootBow();
      // Fishing rod: first right-click casts the bobber over water, the next
      // reels it in — a bite (a scheduled tick) rolls the fishing loot (MB.3).
      if (heldName === 'fishing_rod') {
        if (fishing?.out) { reelFishingRod(); return true; }
        // Must aim at water within reach to start a cast.
        const eye = { x: player.body.x, y: player.body.y + PLAYER.EYE_HEIGHT, z: player.body.z };
        const hit = raycastVoxel(
          (x, y, z) => world.isRaycastTarget(x, y, z) || world.isFluid(x, y, z),
          eye, lookDir(), REACH.BLOCK,
        );
        if (!hit || !world.isFluid(hit.x, hit.y, hit.z)) return false;
        fishing = castRod(world.tickCount, fishRng.nextFloat());
        return true;
      }
      // Flint & steel: lighting the air cell against an obsidian frame opens a
      // nether portal (DIM-2). Consumes durability on success.
      if (heldName !== 'flint_and_steel') return false;
      if (!ignitePortalAt(fx, fy, fz)) return false;
      if (modeCaps(playerState.gameMode).survivalHunger) damageHeldTool();
      return true;
    },
    useBucket() {
      const caps = modeCaps(playerState.gameMode);
      if (!caps.canBuild) return false;
      const held = playerState.inventory.held();
      if (isEmpty(held)) return false;
      const name = registries.items.nameOf[held.item];
      const empty = name === 'bucket', water = name === 'water_bucket', lava = name === 'lava_bucket';
      const powder = name === 'powder_snow_bucket';
      if (!empty && !water && !lava && !powder) return false;

      // Buckets see through air to the fluid/solid under the crosshair.
      const eye = { x: player.body.x, y: player.body.y + PLAYER.EYE_HEIGHT, z: player.body.z };
      const hit = raycastVoxel(
        (x, y, z) => world.isRaycastTarget(x, y, z) || world.isFluid(x, y, z) || world.isLava(x, y, z),
        eye, lookDir(), REACH.BLOCK,
      );
      if (!hit) return false;
      const swap = (to: string | null): void => {
        if (caps.infiniteResources) return;
        const slot = playerState.inventory.heldSlot();
        const id = to ? registries.items.idOf.get(to) : undefined;
        playerState.inventory.set(slot, id !== undefined ? makeStack(id, 1) : emptyStack());
      };
      const st = world.getBlockState(hit.x, hit.y, hit.z);

      // Cauldron (MB.1): empty bucket scoops it out, water bucket fills it. Lava
      // buckets fall through to the pooling logic (lava cauldrons are a remainder).
      if (registries.states.blockName[st] === 'cauldron' && !lava) {
        const level = Number(registries.states.props[st]?.level ?? '0');
        const op = empty ? bucketDrain(level) : bucketFill(level);
        if (!op) return false;
        const ns = cauldronStateOf(op.level);
        if (ns < 0) return false;
        edit(hit.x, hit.y, hit.z, ns);
        swap(op.give);
        return true;
      }

      if (empty) {
        if (st === world.waterState) { edit(hit.x, hit.y, hit.z, STATE_AIR); swap('water_bucket'); return true; }
        if (st === world.lavaState) { edit(hit.x, hit.y, hit.z, STATE_AIR); swap('lava_bucket'); return true; }
        // Scoop a powder-snow block back into a bucket (MB.1).
        if (registries.states.blockName[st] === 'powder_snow') { edit(hit.x, hit.y, hit.z, STATE_AIR); swap('powder_snow_bucket'); return true; }
        const dry = world.waterloggedVariant(st, false);
        if (world.isWaterlogged(st) && dry >= 0) { edit(hit.x, hit.y, hit.z, dry); swap('water_bucket'); return true; }
        return false;
      }

      const px = hit.x + hit.nx, py = hit.y + hit.ny, pz = hit.z + hit.nz;
      // Powder-snow bucket places a powder_snow block in the empty cell (MB.1).
      if (powder) {
        const target = world.getBlockState(px, py, pz);
        if (target !== STATE_AIR && !world.isReplaceableByFluid(target)) return false;
        const ps = registries.states.defaultOf.get('powder_snow');
        if (ps === undefined) return false;
        edit(px, py, pz, ps);
        swap('bucket');
        return true;
      }

      // Water bucket onto a waterloggable block fills it rather than pooling beside it.
      if (water) {
        const wl = world.waterloggedVariant(st, true);
        if (wl >= 0 && !world.isWaterlogged(st)) { edit(hit.x, hit.y, hit.z, wl); swap('bucket'); return true; }
      }
      const target = world.getBlockState(px, py, pz);
      if (target !== STATE_AIR && !world.isReplaceableByFluid(target)) return false;
      edit(px, py, pz, water ? world.waterState : world.lavaState);
      swap('bucket');
      return true;
    },
    useItemOnBlock,
    ignitePortalAt,
    ignitePrimedTntAt: (x, y, z) => ignitePrimedTntAt(x, y, z),
    attack() {
      const mode = playerState.gameMode;
      if (mode === 'spectator' || mode === 'adventure' || playerState.dead) return false;
      const dir = lookDir();
      const ex = player.body.x, ey = player.body.y + PLAYER.EYE_HEIGHT, ez = player.body.z;
      const hit = entities.hitscan(ex, ey, ez, dir.x, dir.y, dir.z, REACH.ATTACK);
      if (!hit) return false;
      // A block closer than the mob blocks the swing (ARCH §5.4: nearest wins).
      const block = raycastVoxel((x, y, z) => world.isSolid(x, y, z), { x: ex, y: ey, z: ez }, dir, REACH.ATTACK);
      if (block && block.distance < hit.dist) return false;

      // Net mode: the server owns entity health — relay the hit for reach-validated
      // arbitration (NET-3/6). Death/knockback arrive back through the entity stream.
      if (net) {
        const netId = hit.entity.data.netId;
        // Send the held item so the server applies its real weapon damage + cooldown (C5).
        const heldNet = playerState.inventory.held();
        if (netId !== undefined) net.sendAttack(netId, isEmpty(heldNet) ? 0 : heldNet.item);
        if (modeCaps(mode).survivalHunger) playerState.addExhaustion(SURVIVAL.EXHAUST_ATTACK);
        game.attackCooldown = game.attackCooldownMax;
        return true;
      }

      const held = playerState.inventory.held();
      const def = isEmpty(held) ? undefined : registries.items.byName.get(registries.items.nameOf[held.item] ?? '');
      const base = Math.max(0, (def?.attackDamage ?? 1) + meleeDamageDelta(playerState.effects)); // strength/weakness (PLR-8)
      const dmg = modeCaps(mode).instantBreak ? 1000 : base; // creative one-shots
      entities.damage(hit.entity, dmg, makeFrame(), hit.entity.x - ex, hit.entity.z - ez, true);
      if (modeCaps(mode).survivalHunger) playerState.addExhaustion(SURVIVAL.EXHAUST_ATTACK);
      game.attackCooldown = game.attackCooldownMax;
      return true;
    },
    actionBar: actionBarState,
    eduControl,
    showActionBar(text) { actionBarState.text = text; actionBarState.serial++; },
    lastActionBar: () => actionBarState.text,
    ageEntityTo(id, ticks) { const e = entities.byId(id); if (e) e.age = ticks; },
    nameEntity(id, text) {
      const e = entities.byId(id);
      if (!e) return false;
      return nameEntity(e, text, () => {
        if (heldItemName() !== 'name_tag') return false;
        consumeHeld();
        return true;
      });
    },
    setEntityName(id, text) {
      const e = entities.byId(id);
      return e ? nameEntity(e, text, () => true) : false;
    },
    entityNametag: (id) => entityRenderer.nametagOf(id),
    heredityGenome(id) {
      const e = entities.byId(id);
      return e?.genome ? encodeGenome(e.genome) : null;
    },
    setHeredityGenome(id, b64) {
      const e = entities.byId(id);
      const genes = e ? registries.mobs.byName.get(e.type)?.genes : undefined;
      const genome = genes ? decodeGenome(b64, genes) : null;
      if (!e || !genome) return false;
      entities.setGenome(e, genome);
      return true;
    },
    heredityTraits: (id) => entities.byId(id)?.traits ?? null,
    entityTint: (id, part) => entityRenderer.tintOf(id, part),
    entityPartVisible: (id, part) => entityRenderer.partVisible(id, part),
    entityScale: (id) => entityRenderer.scaleOf(id),
    cropGenome(x, y, z) {
      const be = blockEntities.get(x, y, z);
      return be?.type === 'crop' ? be.data.genome : null;
    },
    plantGenetic(x, y, z, crop, b64) {
      const cropDef = cropOf(crop);
      if (!heredityOn(manager.world('overworld')) || !cropDef?.genes) return false;
      const cs = cropStateOf(crop, 0);
      if (cs < 0) return false;
      edit(x, y, z, cs);
      plantGenetics(x, y, z, crop, b64);
      return true;
    },
    harvest(x, y, z) {
      const state = world.getBlockState(x, y, z);
      const crop = cropOf(registries.states.blockName[state] ?? '');
      if (!crop) return { produce: 0, seeds: [] };
      const be = blockEntities.remove(x, y, z);
      const drops = cropDrops(crop, ageOf(state), x, y, z, be?.type === 'crop' ? be : undefined);
      edit(x, y, z, STATE_AIR);
      let produce = 0;
      const seeds: string[] = [];
      for (const d of drops) {
        const dn = registries.items.nameOf[d.item];
        if (dn === crop.produce) produce += d.count;
        else if (dn === crop.seed) for (let i = 0; i < d.count; i++) seeds.push(d.components?.genome ?? '');
      }
      return { produce, seeds };
    },
    overlayCount: () => cropOverlay.count,
    useFieldNotebook,
    get notebook() { return currentNotebook; },
    recordAnimalById(id) { const e = entities.byId(id); return e ? recordAnimal(e) : false; },
    recordCropAt,
    eduLessons: () => registries.lessons.list,
    // On a server the lesson is the server's (E-61): what it last told us, never evaluated here.
    eduActiveLesson: () => (net ? net.lesson?.lesson ?? null : meta.lesson?.name ?? null),
    eduActiveLessonDef: () => (net ? (net.lesson ? registries.lessons.byName.get(net.lesson.lesson) : undefined) : lessonDef),
    eduResult: () => (net ? net.lesson?.result : lessonResult),
    eduFire: (t) => fireEduTrigger(t),
    // Debug/E2E seam (ARCH §29.17): the reading TEXT for any instrument, without
    // needing it held/aimed — real play fires through `useHeldItem`/`defaultInteractMob`
    // above, which also fire the trigger; this never does (`fire()` drives that
    // separately, so every objective kind stays unit-drivable per §29.15).
    readInstrument(item, x, y, z) {
      if (item === 'light_meter') {
        const at = x !== undefined && y !== undefined && z !== undefined
          ? { x, y, z } : { x: Math.floor(player.body.x), y: Math.floor(player.body.y), z: Math.floor(player.body.z) };
        return readLightMeter(world.getSkyLight(at.x, at.y, at.z), world.getBlockLight(at.x, at.y, at.z)).text;
      }
      if (item === 'soil_probe') {
        if (x === undefined || y === undefined || z === undefined) return 'Soil: no target';
        const state = world.getBlockState(x, y, z);
        if (registries.states.blockName[state] !== 'farmland') return 'Soil: no target';
        const moisture = Number(registries.states.props[state]?.moisture ?? '0');
        const aboveName = registries.states.blockName[world.getBlockState(x, y + 1, z)];
        const crop = aboveName ? cropOf(aboveName) : undefined;
        const cropBE = crop?.genes && heredityOn(world) ? blockEntities.get(x, y + 1, z) : undefined;
        const genome = cropBE?.type === 'crop' ? decodeGenome(cropBE.data.genome, crop!.genes!) : null;
        const droughtPct = genome ? Math.round(expressCrop(crop!.genes!, genome).droughtTol * 100) : null;
        return readSoilProbe(moisture, droughtPct).text;
      }
      if (item === 'signal_meter') {
        const b = player.body;
        const s = sarSignalAt(b.x, b.y, b.z);
        return readSignalMeter(Number.isFinite(s.rssi) ? s.rssi : null).text;
      }
      if (item === 'altimeter' || item === 'airspeed_indicator') {
        const b = player.body;
        let best: Aircraft | null = null, bestD = Infinity;
        for (const a of aircraft.aircraft) {
          const d = (a.x - b.x) ** 2 + (a.y - b.y) ** 2 + (a.z - b.z) ** 2;
          if (d < bestD) { bestD = d; best = a; }
        }
        if (!best) return item === 'altimeter' ? 'Altitude: no aircraft' : 'Airspeed: no aircraft';
        const feet = meta.worldType === 'atlas' || meta.worldType === 'earth';
        return item === 'altimeter'
          ? readAltimeter(best.y - sarGroundY(best.x, best.z), feet).text
          : readAirspeed(Math.hypot(best.vx, best.vy, best.vz)).text;
      }
      if (item === 'gene_sequencer') {
        const e = x !== undefined ? entities.byId(x) : undefined;
        if (!e) return 'No genetics in this world.';
        const def = registries.mobs.byName.get(e.type);
        return readSequencer(e.type, e.id, def?.genes, e.genome, heredityOn(world)).text;
      }
      return `unknown instrument '${item}'`;
    },
    eduSubmitQuiz(answers) {
      if (net) {
        // §29.5: on a server the SERVER grades and records; the local grade is only the
        // immediate score line (the same pure grader, so the two agree).
        const def = net.lesson ? registries.lessons.byName.get(net.lesson.lesson) : undefined;
        if (!def?.quiz || !net.lesson) throw new Error('no active lesson has a quiz (E-60)');
        const graded = gradeQuiz(def.quiz, answers);
        net.sendQuizSubmit(def.name, answers.map((a) => (typeof a === 'number' ? a : a.map((row) => [...row]))));
        return { score: graded.score, n: graded.n };
      }
      if (!lessonDef?.quiz || !lessonResult) throw new Error('no active lesson has a quiz (E-60)');
      const graded = gradeQuiz(lessonDef.quiz, answers);
      const prevBest = lessonResult.quiz?.best ?? 0;
      lessonResult = {
        ...lessonResult,
        quiz: { best: Math.max(prevBest, graded.score), n: graded.n, attempts: (lessonResult.quiz?.attempts ?? 0) + 1, last: graded.score },
      };
      return { score: graded.score, n: graded.n };
    },
    openLessonScreen: () => openLessonScreen(game),
    eduRole: () => {
      if (!net) return soloRole(game.cheats);
      const me = net.players().find((q) => q.id === net.welcome.playerId);
      return isRole(me?.role) ? me.role : 'visitor';
    },
    eduRoster: () => (net ? buildRoster(net.players(), net.studentResults, net.welcome.playerId) : []),
    eduCommand: (text) => {
      if (net) net.sendCommand(text);
      else game.print('§7That needs a server — solo has no students.');
    },
    eduRaiseHand: (up) => { if (net) net.sendHand(up); },
    async eduExport(format) {
      let text: string;
      if (net) {
        const me = net.players().find((q) => q.id === net.welcome.playerId);
        const q = new URLSearchParams({ world: net.welcome.meta.id, format, sub: net.guest?.sub ?? '', name: me?.name ?? '' });
        const res = await fetch(`${net.controlOrigin}/lesson/export?${q}`);
        if (!res.ok) throw new Error(`export refused (${res.status})`);
        text = await res.text();
      } else {
        const lessons = { ...(loadedPlayer?.lessons ?? {}), ...(lessonDef && lessonResult ? { [lessonDef.name]: lessonResult } : {}) };
        const input: ExportInput = {
          world: { id: meta.id, name: meta.name }, lesson: meta.lesson ?? null, log: meta.lessonLog ?? [],
          players: Object.keys(lessons).length ? [{ sub: 'local', name: 'You', role: soloRole(game.cheats), lessons }] : [],
        };
        if (format === 'csv') {
          if (!lessonDef) throw new Error('No lesson is running in this world.');
          text = lessonCsv(input, lessonDef.name, lessonDef.objectives.map((o) => o.name));
        } else text = JSON.stringify(resultsJson(input));
      }
      if (format === 'json') lastExport = text;
      return text;
    },
    eduLastExport: () => lastExport,
    eduHandUp: () => (net ? net.players().find((q) => q.id === net.welcome.playerId)?.hand !== undefined : false),
    heredityRule(on) {
      const rules = manager.world('overworld').gamerules;
      if (on !== undefined) { rules.set(HEREDITY_RULE, String(on)); refreshCropOverlay(); }
      return heredityOn(manager.world('overworld'));
    },
    heredityPace(n) {
      const rules = manager.world('overworld').gamerules;
      if (n !== undefined) rules.set(PACE_RULE, String(n));
      return heredityPaceOf(manager.world('overworld'));
    },
    attachScriptToTarget(script) {
      if (script !== null && !isScriptName(script)) return `'${script}' is not a script name.`;
      const dir = lookDir();
      const ex = player.body.x, ey = player.body.y + PLAYER.EYE_HEIGHT, ez = player.body.z;
      const hit = entities.hitscan(ex, ey, ez, dir.x, dir.y, dir.z, REACH.BLOCK);
      if (!hit) return 'No mob under the crosshair — look at the one to attach to.';
      const e = hit.entity;
      delete e.scriptOverrideWarned;
      if (script === null) {
        delete e.scriptOverride;
        return `This ${e.type} (#${e.id}) runs its type's script again.`;
      }
      e.scriptOverride = script;
      return `Attached '${script}' to this ${e.type} (#${e.id}).`;
    },
    useEntity() {
      const mode = playerState.gameMode;
      if (mode === 'spectator' || playerState.dead) return false;
      const dir = lookDir();
      const ex = player.body.x, ey = player.body.y + PLAYER.EYE_HEIGHT, ez = player.body.z;
      const hit = entities.hitscan(ex, ey, ez, dir.x, dir.y, dir.z, REACH.BLOCK);
      // No mob under the crosshair → try to board a nearby boat (passenger binding, B4).
      if (!hit) return ridingBoatId === null ? tryMountBoat() : false;
      // A block closer than the mob blocks the reach-out (same rule as attack).
      const block = raycastVoxel((x, y, z) => world.isSolid(x, y, z), { x: ex, y: ey, z: ez }, dir, REACH.BLOCK);
      if (block && block.distance < hit.dist) return false;
      return interactMob(hit.entity);
    },
    give(item, count = 1) {
      const id = registries.items.idOf.get(item);
      if (id === undefined) return;
      giveStack(makeStack(id, count));
    },
    giveStack,
    dropItem: (stack) => {
      if (isEmpty(stack)) return;
      drops.spawn(stack, player.body.x, player.body.y + 1, player.body.z, {
        vx: -Math.sin(player.yaw) * 0.3, vy: 0.2, vz: -Math.cos(player.yaw) * 0.3, pickupDelay: 40,
      });
    },
    setGameMode(mode) {
      playerState.gameMode = mode;
      if (mode === 'survival' || mode === 'creative') meta.gameMode = mode;
      if (!modeCaps(mode).fly) player.body.flying = false;
    },
    openPlayerInventory: () => (playerState.gameMode === 'creative' ? openCreative(game) : openInventory(game)),
    openInventory: () => openInventory(game),
    openCraftingTable: () => openCraftingTable(game),
    openCreative: () => openCreative(game),
    openForge: () => openForge(game),
    forgeParams: () => forgeParams,
    forgeSeed: () => packSeed(forgeParams),
    applyForge: (params) => applyForge(params),
    forgeTilePixels: () => forgeTilePixels(),
    scriptAt: (x, y, z) => {
      const be = scriptBEAt(x, y, z);
      return be ? { source: be.data.source, autopilot: be.data.autopilot } : null;
    },
    setScriptSource: (x, y, z, source) => {
      const be = scriptBEAt(x, y, z);
      if (!be) return { ok: false, error: 'no script block here' };
      be.data.source = source;
      be.data.autopilot = null; // editing the script clears the preset
      const r = scripts.install(cellKey(x, y, z), source, 'block');
      return r.ok ? { ok: true } : { ok: false, error: r.error.message };
    },
    setScriptAutopilot: (x, y, z, preset) => {
      const be = scriptBEAt(x, y, z);
      if (be) be.data.autopilot = { ...preset };
    },
    scriptDiagnostics: (x, y, z) => (scriptBEAt(x, y, z) ? scripts.diagnostics(cellKey(x, y, z)) : null),
    runScriptBlock: (x, y, z, event = 'redstone') => {
      const be = scriptBEAt(x, y, z);
      if (!be) return { ok: false, message: 'no script block here' };
      if (be.data.autopilot) return { ok: engageScriptAutopilotAt(be), message: 'autopilot engaged' };
      return dispatchScript(be, event, redstone.powerLevelAt(x, y, z));
    },
    engageScriptAutopilot: (x, y, z) => {
      const be = scriptBEAt(x, y, z);
      return be ? engageScriptAutopilotAt(be) : false;
    },
    openEnchanting: (x, y, z) => openEnchanting(game, x, y, z),
    enchantOffers: (x, y, z) => enchantOffersAt(x, y, z),
    applyEnchant: (x, y, z, slot) => applyEnchant(x, y, z, slot),
    openSmithing: () => openSmithing(game),
    smithingOffer: () => {
      const m = heldUpgrade(smithingItems, playerState.inventory, smithingRecipes, playerState.inventory.heldSlot());
      if (!m) return null;
      const { recipe, affordable } = m;
      return { base: recipe.base, result: recipe.result, template: recipe.template, addition: recipe.addition, affordable };
    },
    doSmith: () => forgeHeld(smithingItems, playerState.inventory, smithingRecipes, playerState.inventory.heldSlot()) !== null,
    openTrade: (id) => openTrade(game, id),
    villagerInfo: (id) => {
      const e = entities.entities.find((m) => m.id === id && !m.removed);
      const def = e && registries.mobs.byName.get(e.type);
      return def?.trades ? { profession: def.trades } : null;
    },
    villagerOffers: (id) => {
      const e = entities.entities.find((m) => m.id === id && !m.removed);
      const table = e && registries.mobs.byName.get(e.type)?.trades;
      const tbl = table ? registries.trades.get(table) : undefined;
      if (!e || !tbl) return [];
      const used = usesFor(e, tbl);
      const disc = gossipDiscount(reputation(gossipFor(e.id)));
      return tbl.offers.map((o, i) => {
        const od = discountOffer(o, disc);
        return {
          buy: od.buy.map((b) => ({ name: b.item, count: b.count })),
          sell: { name: o.sell.item, count: o.sell.count },
          used: used[i]!,
          maxUses: o.maxUses,
          affordable: offerAvailable(o, used[i]!) && canAfford(registries.items, playerState.inventory, od),
        };
      });
    },
    doTrade: (id, index) => {
      const e = entities.entities.find((m) => m.id === id && !m.removed);
      const table = e && registries.mobs.byName.get(e.type)?.trades;
      const tbl = table ? registries.trades.get(table) : undefined;
      const offer = tbl?.offers[index];
      if (!e || !tbl || !offer) return false;
      const used = usesFor(e, tbl);
      const disc = gossipDiscount(reputation(gossipFor(e.id)));
      const od = discountOffer(offer, disc);
      const r = applyTrade(registries.items, playerState.inventory, [...PlayerInventory.PICKUP_RANGE], od, used[index]!);
      if (!r.ok) return false;
      used[index] = (used[index] ?? 0) + 1;
      earnGossip(e.id, 'trading', 1); // trading earns a little reputation with this villager
      if (!isEmpty(r.leftover)) game.dropItem?.(r.leftover);
      return true;
    },
    villagerActivity: (id) => {
      const e = entities.entities.find((m) => m.id === id && !m.removed);
      if (!e) return null;
      const def = registries.mobs.byName.get(e.type);
      if (!def || (def.archetype !== 'villager' && !def.trades)) return null;
      return villagerActivityAt(world.dayTime, { employed: !!def.trades, child: e.baby });
    },
    villagerReputation: (id) => reputation(gossipFor(id)),
    addVillagerGossip: (id, type, amount) => earnGossip(id, type, amount),
    shareVillagerGossip: () => shareGossipRound(),
    decayVillagerGossip: () => decayGossipRound(),
    weakenMob: (id) => {
      const e = entities.entities.find((m) => m.id === id && !m.removed);
      if (!e) return false;
      e.data.weak = 1;
      return true;
    },
    interactMob: (id) => {
      const e = entities.entities.find((m) => m.id === id && !m.removed);
      return e ? interactMob(e) : false;
    },
    finishCure: (id) => {
      const e = entities.entities.find((m) => m.id === id && !m.removed);
      if (!e || !e.data.curing) return false;
      e.data.curing = 1;
      return true;
    },
    get frozenTicks() { return frozenTicks; },
    hiveHoney: (x, y, z) => {
      const st = world.getBlockState(x, y, z);
      return registries.states.blockName[st] === 'beehive' ? honeyLevelOf(st) : -1;
    },
    pollinateHive: (x, y, z) => depositAtHive(x, y, z),
    frogEat: (id) => {
      const e = entities.entities.find((m) => m.id === id && !m.removed);
      return e && e.type === 'frog' ? frogEat(e) : '';
    },
    layEgg: (id) => {
      const e = entities.entities.find((m) => m.id === id && !m.removed);
      if (!e || e.type !== 'chicken') return false;
      layEgg(e);
      return true;
    },
    forceSpawnCycle: () => trySpawn(entities, makeSpawnCtx()),
    villagerWilling: (id) => {
      const e = entities.entities.find((m) => m.id === id && !m.removed);
      return !!e && isWilling(e.data.food ?? 0);
    },
    tryBreedVillagers: () => tryBreedVillagers(),
    villagerBed: (id) => {
      const e = entities.entities.find((m) => m.id === id && !m.removed);
      if (!e || !isVillager(e)) return null;
      if (e.data.bedSet !== 1) assignBed(e);
      return e.data.bedSet === 1 ? { x: e.data.bedX!, y: e.data.bedY!, z: e.data.bedZ! } : null;
    },
    assignVillagerJob: (id) => {
      const e = entities.entities.find((m) => m.id === id && !m.removed);
      return e && assignJobsite(e) ? e.type : null;
    },
    trySpawnIronGolem: () => trySpawnIronGolem(),
    forcePatrol: () => spawnPatrol(),
    ringBell: (x, y, z) => ringBell(x, y, z),
    summonVexes: (id) => {
      const e = entities.entities.find((m) => m.id === id && !m.removed);
      if (!e || e.type !== 'evoker') return 0;
      e.data.vexCd = 0; // ready to cast
      return summonVexesFrom(e);
    },
    fireDispenser: (x, y, z) => {
      const be = blockEntities.get(x, y, z);
      if (be?.type !== 'dispenser') return false;
      fireDispenser(be, x, y, z, facingProp(x, y, z));
      return true;
    },
    loadBrewingStand: (x, y, z, bottles, ingredient, fuel) => {
      const be = brewingBEAt(x, y, z);
      const potionId = registries.items.idOf.get('potion');
      for (let i = 0; i < 3; i++) {
        const key = bottles[i];
        be.data.slots[i] = key && potionId !== undefined ? makeStack(potionId, 1, { potion: key }) : null;
      }
      const ingId = registries.items.idOf.get(ingredient);
      be.data.slots[BREW_INGREDIENT] = ingId !== undefined ? makeStack(ingId, 1) : null;
      be.data.fuel = fuel;
      be.data.ticks = 0;
    },
    finishBrewing: (x, y, z) => {
      const be = blockEntities.get(x, y, z);
      if (!be || be.type !== 'brewing_stand') return false;
      be.data.ticks = BREW_TICKS - 1;
      return tickBrewingContainer(be.data, registries);
    },
    brewingStandBottles: (x, y, z) => {
      const be = blockEntities.get(x, y, z);
      if (!be || be.type !== 'brewing_stand') return ['', '', ''];
      return be.data.slots.slice(0, 3).map((s) => (s && !isEmpty(s) ? s.components?.potion ?? '' : ''));
    },
    get effectTint() { return screenTint(playerState.effects); },
    get nightVisionFloor() { return nightVisionFloor(playerState.effects); },
    setSignText: (x, y, z, lines) => setSignText(x, y, z, lines),
    signText: (x, y, z) => getSignText(x, y, z),
    signGlows: (x, y, z) => signGlows(x, y, z),
    signPixels: (x, y, z) => computeSignPixels(getSignText(x, y, z)),
    signBoardSize: () => ({ cols: SIGN_COLS, rows: SIGN_ROWS }),
    signTextureLitCount: (x, y, z) => computeSignLitCount(rasterizeSign(getSignText(x, y, z))),
    cowStatus: (v) => {
      if (v === undefined) return presenceStatus;
      if (v !== 'available' && v !== 'focus' && v !== 'call') { game.print('§cStatus must be available, focus, or call.'); return presenceStatus; }
      presenceStatus = v;
      net?.sendCommand(`/status ${v}`);
      return presenceStatus;
    },
    cowNoteText: (x, y, z) => {
      const be = blockEntities.get(x, y, z);
      return be?.type === 'sticky_note' ? be.data.text : null;
    },
    cowSetNote: (x, y, z, text) => {
      const be = blockEntities.create('sticky_note', x, y, z) as StickyNoteBE;
      be.data.text = normalizeNote(text);
      net?.sendBlockTextEdit(x, y, z, 'sticky_note', JSON.stringify(be.data));
      return true;
    },
    cowBoard: (x, y, z) => {
      const be = blockEntities.get(x, y, z);
      return be?.type === 'task_board' ? be.data.lines.map((l) => ({ ...l })) : null;
    },
    cowToggleTask: (x, y, z, i) => {
      const be = blockEntities.get(x, y, z);
      if (be?.type !== 'task_board') return false;
      be.data.lines = toggleTask(be.data.lines, i);
      net?.sendBlockTextEdit(x, y, z, 'task_board', JSON.stringify(be.data));
      return true;
    },
    cowTimer: (x, y, z) => {
      const be = blockEntities.get(x, y, z);
      return be?.type === 'timer' ? { endTick: be.data.endTick, preset: be.data.preset } : null;
    },
    cowTimerFace: (x, y, z) => {
      const be = blockEntities.get(x, y, z);
      return be?.type === 'timer' ? timerFace(be.data, world.tickCount) : null;
    },
    cowStartTimer: (x, y, z, preset) => {
      if (preset !== 'pomodoro25' && preset !== 'break5' && preset !== 'custom') return false;
      const be = blockEntities.create('timer', x, y, z) as TimerBE;
      be.data = startTimer(world.tickCount, preset);
      net?.sendBlockTextEdit(x, y, z, 'timer', JSON.stringify(be.data));
      return true;
    },
    cowScreenState: (x, y, z) => {
      const s = shareStates.get(cellKey(x, y, z));
      if (!s) return null;
      const isSharer = !!sharingAt && sameCell(sharingAt, { x, y, z });
      return { live: s.live, sharer: s.sharer, viewers: isSharer && shareController ? shareController.viewers().map((v) => `p${v.id}`) : [] };
    },
    cowCanShare: () => {
      // A simplified client-side approximation (D-TF-284): the server is the
      // real authority (`canShare`, §30.6) and re-checks everything on
      // `ShareStart`; this only needs to be good enough to show/hide the
      // Share button, not to be the actual gate.
      if (!net) return false;
      const me = net.players().find((p) => p.id === net.welcome.playerId);
      const role = me?.role && isRole(me.role) ? me.role : null;
      return atLeast(role, 'editor');
    },
    cowViewers: (x, y, z) => {
      const isSharer = !!sharingAt && sameCell(sharingAt, { x, y, z });
      return isSharer && shareController ? shareController.viewers().map((v) => `p${v.id}`) : [];
    },
    cowViewerStates: (x, y, z) => {
      const isSharer = !!sharingAt && sameCell(sharingAt, { x, y, z });
      return isSharer && shareController ? shareController.viewers().map((v) => ({ id: `p${v.id}`, state: v.state })) : [];
    },
    cowFakeShare: (x, y, z) => {
      if (!net || !shareController) return false;
      const canvas = document.createElement('canvas');
      canvas.width = 320; canvas.height = 180;
      const ctx = canvas.getContext('2d')!;
      let frame = 0;
      const paint = (): void => {
        ctx.fillStyle = `hsl(${(frame * 7) % 360} 70% 50%)`;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#fff';
        ctx.font = '24px sans-serif';
        ctx.fillText(`fakeShare ${frame++}`, 10, 90);
      };
      paint();
      const timer = setInterval(paint, 1000 / COW.VIDEO.fps);
      const stream = canvas.captureStream(COW.VIDEO.fps);
      const track = stream.getVideoTracks()[0];
      if (track) track.addEventListener('ended', () => clearInterval(timer));
      startSharingAt(x, y, z, stream);
      return true;
    },
    cowForceWatchFail: () => shareController?.forceWatchFail(),
    noteBlockNote: (x, y, z) => noteBlocks.get(cellKey(x, y, z)) ?? 0,
    cycleNoteBlock: (x, y, z) => cycleNoteBlock(x, y, z),
    playNoteBlock: (x, y, z) => playNote(x, y, z),
    get lastNotePlayed() { return lastNotePlayed; },
    resumeAudio: () => audio.resume(),
    audioEvents: () => audio.events(),
    lastAudio: () => audio.lastEvent(),
    clearAudio: () => audio.clearEvents(),
    playSound: (id, x, y, z, pitch) => audio.playSound(id, { x, y, z, pitch }),
    setAudioVolume: (bus, v) => {
      if (bus === 'master') audio.setMasterVolume(v);
      else if (bus === 'sound') audio.setSoundVolume(v);
      else audio.setMusicVolume(v);
    },
    audioVolumes: () => audio.volumes,
    insertDisc: (x, y, z, disc) => insertDisc(x, y, z, disc),
    jukeboxDisc: (x, y, z) => jukeboxDiscs.get(cellKey(x, y, z)) ?? null,
    discComparator: (x, y, z) => discComparatorOutput(jukeboxDiscs.get(cellKey(x, y, z))),
    get musicContext() { return musicContext; },
    applyAudioSettings: () => applyAudioSettings(),
    setSubtitles: (on) => subtitles.setEnabled(on),
    subtitleLines: () => subtitles.active(),
    advancementsDone: () => [...advancements.done],
    advancementToasts: () => [...advToasts.shown()],
    openAdvancements: () => openAdvancementsScreen(),
    setKeybind: (action, code) => { settings.keybinds = rebind(settings.keybinds, action, code); },
    keybindFor: (action) => settings.keybinds[action] ?? '',
    openStats: (onBack) => openStatsScreen(onBack),
    statCount: (category, id) => stats.get(category, id),
    statCustom: (stat) => stats.getCustom(stat),
    takeScreenshot: () => takeScreenshot(),
    screenshotCount: () => screenshotsTaken,
    toggleDebugOverlay: () => { debugOverlayOn = !debugOverlayOn; debugEl.style.display = debugOverlayOn ? 'block' : 'none'; },
    toggleChunkBorders: () => { chunkBorderLines.visible = !chunkBorderLines.visible; if (!chunkBorderLines.visible) setEdges(chunkBorderLines, []); },
    toggleHitboxes: () => { hitboxLines.visible = !hitboxLines.visible; if (!hitboxLines.visible) setEdges(hitboxLines, []); },
    debugOverlayOn: () => debugOverlayOn,
    chunkBordersOn: () => chunkBorderLines.visible,
    hitboxesOn: () => hitboxLines.visible,
    recipeBook,
    onCraft: (item, count) => { stats.bump('crafted', item, count); unlockRecipesFor(item); },
    setCrafterSlot: (x, y, z, i, item, count = 1) => {
      const be = blockEntities.create('crafter', x, y, z) as CrafterBE;
      const id = registries.items.idOf.get(item);
      be.data.items[i] = id === undefined ? null : makeStack(id, count);
    },
    toggleCrafterSlot: (x, y, z, i) => {
      const be = blockEntities.create('crafter', x, y, z) as CrafterBE;
      const s = be.data.items[i];
      be.data.disabled = toggleDisabled(be.data.disabled, i, s == null || isEmpty(s));
    },
    crafterSlots: (x, y, z) => {
      const be = blockEntities.get(x, y, z);
      if (!be || be.type !== 'crafter') return [];
      return be.data.items.map((s) => (s && !isEmpty(s) ? { name: registries.items.nameOf[s.item] ?? '?', count: s.count } : null));
    },
    fireCrafter: (x, y, z) => {
      const be = blockEntities.get(x, y, z);
      if (!be || be.type !== 'crafter') return false;
      fireCrafter(be, x, y, z, facingProp(x, y, z));
      return true;
    },
    get lastCrafted() { return lastCrafted; },
    reloadEntitiesRoundTrip: () => {
      // Exactly what a save→reload does to entities: serialize, clear, re-spawn
      // (new ids), and rebuild the villager-economy maps from entity data (MB.7).
      syncVillagerEconomy();
      const saved = entities.toJSON();
      entities.clear();
      entities.loadJSON(saved);
      restoreVillagerEconomy();
    },
    raidInfo: () => raidInfoSnapshot(),
    forceRaid: () => {
      if (raid || !raidTable) return false;
      const v = nearestVillager(player.body.x, player.body.z, RAID.ABANDON_RADIUS);
      startRaidAt(v ? { x: v.x, y: v.y, z: v.z } : { x: player.body.x, y: player.body.y, z: player.body.z }, 1);
      return raid !== null;
    },
    raidState: () => (raid ? raid.toJSON() : null),
    restoreRaid: (state) => {
      if (!raidTable) return false;
      raid = Raid.fromJSON(state, wavesFor(raidTable, difficulty));
      return true;
    },
    hurtMob: (id, amount) => {
      const e = entities.entities.find((m) => m.id === id && !m.removed);
      if (!e) return false;
      entities.damage(e, amount, makeFrame(), 0, 0, true);
      return true;
    },
    hurtPlayer: (amount, fromX, fromZ) => {
      const kbX = fromX === undefined ? 0 : player.body.x - fromX;
      const kbZ = fromZ === undefined ? 0 : player.body.z - fromZ;
      playerHandle.hurt(amount, kbX, kbZ);
    },
    equip: (item) => {
      const id = registries.items.idOf.get(item);
      if (id === undefined) return false;
      const slot = gearArmorSlotOf(item);
      playerState.inventory.set(slot === null ? INV.OFFHAND : INV.ARMOR_START + slot, makeStack(id, 1));
      return true;
    },
    useFirework: () => {
      if (!gliding || heldItemName() !== 'firework_rocket') return false;
      consumeHeld();
      fireworkBoostTicks = FIREWORK_BOOST_TICKS;
      return true;
    },
    glide: () => { gliding = true; },
    get gliding() { return gliding; },
    // Debug/E2E: force a bite on the current cast (or a fresh one) and reel,
    // returning the caught item names. Exercises the loot-roll + give path.
    fishCatch: () => {
      fishing = { out: true, biteAtTick: world.tickCount };
      return reelFishingRod();
    },
    mapStore,
    useMapInHand: () => useMapInHand(),
    explorerMap: (destination) => explorerMapStack(destination),
    createFilledMap: (scale) => {
      // Debug/E2E convenience: drop the filled map straight into the held hotbar
      // column so it renders in the map HUD regardless of a full starter hotbar.
      const mapId = makeFilledMapAtPlayer(scale);
      playerState.inventory.set(playerState.inventory.heldSlot(), filledMapStack(mapId));
      return mapId;
    },
    createTreasureMap: (scale) => {
      const mapId = makeFilledMapAtPlayer(scale);
      playerState.inventory.set(playerState.inventory.heldSlot(), filledMapStack(mapId));
      const b = player.body;
      const found = (flat || atlasCity || earthWorld) ? null
        : locateStructureType('buried_treasure', world.seed, Math.floor(b.x / WORLD.SECTION), Math.floor(b.z / WORLD.SECTION), structEnv);
      const snap = mapStore.get(mapId);
      if (snap && found) snap.target = { x: found.x, z: found.z };
      return { mapId, targetX: found?.x ?? null, targetZ: found?.z ?? null };
    },
    refreshMap: (mapId, radius = CARTO.UPDATE_RADIUS) => {
      const snap = mapStore.get(mapId);
      return snap ? snap.refresh(mapSampler, mapPalette, Math.floor(player.body.x), Math.floor(player.body.z), radius) : 0;
    },
    growAmethyst: (x, y, z, dirIndex = 4) => // 4 = up
      registries.states.blockName[world.getBlockState(x, y, z)] === 'budding_amethyst'
        ? growBudding(x, y, z, 0, dirIndex) // roll 0 < GROW_CHANCE ⇒ deterministic success
        : false,
    bloomSculk: (x, y, z, charge) => {
      const sculkState = registries.states.defaultOf.get(SCULK_BLOCK);
      const origin = bloomOrigin({ x, y, z }, sculkView);
      if (!origin || sculkState === undefined) return 0;
      const res = bloom(origin, charge, sculkView);
      for (const c of res.convert) edit(c.x, c.y, c.z, sculkState);
      return res.spent;
    },
    beaconTier: (x, y, z) => beaconTierAt(x, y, z),
    setBeaconEffect: (x, y, z, primary, secondary = null) => { beaconSel.set(cellKey(x, y, z), { primary, secondary }); },
    pulseBeacon: (x, y, z) => applyBeaconAt(x, y, z), // force one application (deterministic E2E)
    summonWither: (x, y, z) => tryBuildWither(x, y, z), // ignite a built summon frame (deterministic E2E)
    conduitFrame: (x, y, z) => conduitFrameAt(x, y, z),
    pulseConduit: (x, y, z) => applyConduitAt(x, y, z), // force one conduit application (deterministic E2E)
    shriek: (x, y, z) => triggerShriek(x, y, z), // trigger a sculk shrieker (deterministic E2E)
    emitVibration: (event, x, y, z, source) => emitVibration(event, x, y, z, source),
    wardenAngerAt: (wardenId, suspect) => angerOf(ledgerFor(wardenId), suspect),
    wardenQuarryOf: (wardenId) => wardenQuarry(ledgerFor(wardenId)),
    wardenSonicBoom: (id) => { const e = entities.entities.find((m) => m.id === id && !m.removed); if (!e) return 0; e.data.wardenEmerge = 0; return fireWardenSonicBoom(e); }, // force-emerge for a deterministic E2E
    dragonCrystals: (id) => { const e = entities.entities.find((m) => m.id === id && !m.removed); return e ? crystalsAliveNear(e.x, e.y, e.z) : 0; },
    dragonPhase: (id) => { const e = entities.entities.find((m) => m.id === id && !m.removed); return e ? dragonPhase(e.health / e.maxHealth, e.data.phaseTimer ?? 0, crystalsAliveNear(e.x, e.y, e.z)) : null; },
    trialWave: () => trialWaveTotal(trialPlayerCount()),
    trialCap: () => trialSimultaneousCap(trialPlayerCount()),
    forceTrialEject: (x, y, z) => { const st = trialData.get(cellKey(x, y, z)); if (st) { st.spawned = trialWaveTotal(trialPlayerCount()); st.mobs = []; st.state = 'active'; } }, // next tick clears → ejects the key
    growDripstone: (x, y, z) => growDripstoneTip(x, y, z, 0), // roll 0 < GROW_CHANCE ⇒ deterministic growth
    useAnchor: (x, y, z) => useAnchorAt(x, y, z), // charge / set-spawn / detonate the respawn anchor
    pulseOceanFauna: () => tickOceanFauna(), // apply dolphin grace / pufferfish poison now (deterministic E2E)
    pulseGuardians: () => {
      // Deterministic E2E: charge each nearby guardian's beam to full and fire it,
      // and apply the elder Mining Fatigue aura regardless of the 1200-gt cadence.
      const bb = player.body;
      for (const e of entities.entities) {
        if (e.removed || (e.type !== 'guardian' && e.type !== 'elder_guardian')) continue;
        const elder = e.type === 'elder_guardian';
        const dist = Math.hypot(bb.x - e.x, bb.y - e.y, bb.z - e.z);
        if (elder && elderInflictsFatigue(dist)) addEffect(playerState.effects, 'mining_fatigue', fatigueAmplifier(), fatigueTicks());
        const los = !losBlocked(e.x, e.y + 0.5, e.z, bb.x, bb.y + PLAYER.EYE_HEIGHT, bb.z);
        let charge = 0;
        for (let i = 0; i < GUARDIAN.BEAM_CHARGE_TICKS; i++) charge = beamCharge(charge, los, dist);
        if (beamFires(charge)) playerHandle.hurt(beamDamage(elder), 0, 0);
      }
    },
    zombifyHoglin: (id) => {
      // Deterministic E2E: push the hoglin past its zombify threshold, then run the
      // real (pure-gated) nether-fauna tick to convert it in the overworld.
      const e = entities.entities.find((m) => m.id === id && !m.removed && m.type === 'hoglin');
      if (!e) return false;
      e.data.owTicks = NETHER_FAUNA.HOGLIN_ZOMBIFY_TICKS;
      tickNetherFauna();
      return e.removed && entities.entities.some((m) => m.type === 'zoglin' && !m.removed);
    },
    turtleEggTick: (x, y, z) => turtleEggRandomTick(x, y, z, world.getBlockState(x, y, z)),
    sculkVibrate: (x, y, z) => { wakeSculkSensorsNear(x, y, z); redstone.tick(); },
    windCharge: (x, y, z) => detonateWindCharge(x, y, z),
    brushBlock: (x, y, z) => {
      const name = registries.states.blockName[world.getBlockState(x, y, z)] ?? '';
      return isSuspicious(name) ? brushSuspicious(x, y, z, name) : '';
    },
    snifferDig: (id) => {
      const e = entities.entities.find((m) => m.id === id && !m.removed && m.type === 'sniffer');
      return e ? digSniffer(e, 0) : ''; // roll 0 < DIG_CHANCE ⇒ a guaranteed find over diggable ground
    },
    camelDash: (id) => {
      const e = entities.entities.find((m) => m.id === id && !m.removed && m.type === 'camel');
      return e ? dashCamel(e) : false;
    },
    growTadpole: (id) => {
      const e = entities.entities.find((m) => m.id === id && !m.removed && m.type === 'tadpole');
      if (!e) return '';
      const variant = frogVariantForBiome(world.biomeNameAt(Math.floor(e.x), Math.floor(e.z)) ?? '');
      convertTadpoleToFrog(e);
      return froglightFor(variant);
    },
    oxidizeCopper: (x, y, z) => {
      const next = weatherStep(registries.states.blockName[world.getBlockState(x, y, z)] ?? '');
      if (next) setCopperBlock(x, y, z, next);
      return next;
    },
    campfireCount: (x, y, z) => {
      const be = blockEntities.get(x, y, z);
      return be?.type === 'campfire' ? be.data.slots.filter((s) => !isEmpty(s.item)).length : 0;
    },
    cookCampfire: (x, y, z) => {
      const be = blockEntities.get(x, y, z);
      if (be?.type !== 'campfire') return 0;
      let occupied = 0;
      for (const s of be.data.slots) if (!isEmpty(s.item)) { s.ticks = CAMPFIRE_COOK_TICKS - 1; occupied++; }
      tickBlockEntities(blockEntities, beEnv); // complete + pop via the real env.dropItem path
      return occupied;
    },
    rainCauldron: (x, y, z) => {
      rainFillCauldron(x, y, z, world.getBlockState(x, y, z));
      return Number(registries.states.props[world.getBlockState(x, y, z)]?.level ?? '0');
    },
    randomTick: (x, y, z) => onRandomTick(x, y, z, world.getBlockState(x, y, z)),
    placeHeld: (x, y, z) => game.placeBlock(x, y, z),
    growPlant: (x, y, z) => {
      // One deterministic growth step (roll 0 ⇒ always grows) for MB.2 plants.
      const st = world.getBlockState(x, y, z);
      const name = registries.states.blockName[st];
      if (isVine(name)) {
        const cells = vineBoneMeal(growthView, x, y, z, name!, 0);
        if (cells.length === 0) return false;
        for (const c of cells) edit(c.x, c.y, c.z, st);
        return true;
      }
      if (name !== undefined && name in VERTICAL_PLANTS) {
        const cell = verticalGrow(growthView, x, y, z, name, 0);
        if (!cell) return false;
        const ns = registries.states.defaultOf.get(name);
        if (ns === undefined) return false;
        edit(cell.x, cell.y, cell.z, ns); return true;
      }
      if (name !== undefined && name in STEM_FRUIT) {
        const res = stemGrow(growthView, x, y, z, name, ageOf(st), 0);
        if (res?.kind === 'age') { const ns = cropStateOf(name, res.age); if (ns < 0) return false; edit(x, y, z, ns); return true; }
        if (res?.kind === 'fruit') { const ns = registries.states.defaultOf.get(res.fruit); if (ns === undefined) return false; edit(res.cell.x, res.cell.y, res.cell.z, ns); return true; }
        return false;
      }
      if (name === 'nether_wart') {
        const na = netherWartGrow(growthView, x, y, z, ageOf(st), 0);
        if (na === null) return false;
        const ns = cropStateOf('nether_wart', na); if (ns < 0) return false;
        edit(x, y, z, ns); return true;
      }
      if (name !== undefined && name in AGE_PLANTS) {
        const na = agePlantGrow(name, ageOf(st), 0);
        if (na === null) return false;
        const ns = cropStateOf(name, na); if (ns < 0) return false;
        edit(x, y, z, ns); return true;
      }
      return false;
    },
    redstoneAt: (x, y, z) => {
      const st = world.getBlockState(x, y, z);
      const block = registries.states.blockName[st];
      const role = rsRoleAt(x, y, z);
      if (!block || role.kind === 'air' || role.kind === 'block') return null;
      const p = registries.states.props[st] ?? {};
      return {
        block,
        power: role.kind === 'wire' ? role.power : 0,
        lit: role.kind === 'torch' || role.kind === 'lamp' ? role.lit : false,
        powered: role.kind === 'repeater' || role.kind === 'comparator' || role.kind === 'observer' ? role.powered
          : role.kind === 'lever' || role.kind === 'button' || role.kind === 'plate' ? role.on : false,
        delay: Number(p.delay ?? '0'),
        mode: p.mode ?? '',
      };
    },
    blockPropsAt: (x, y, z) => registries.states.props[world.getBlockState(x, y, z)] ?? {},
    runCommand: (input) => executeCommand(cmdContext, input),
    print: () => {},
    tick() {
      const b = player.body;
      if (eduControl.frozen || eduControl.eyesUp !== null) {
        // Held by the instructor (§29.8): no movement, no swing, no use — chat still works.
        const i = game.input;
        i.forward = i.back = i.left = i.right = i.jump = i.sprint = i.sneak = false;
        game.breaking = false;
        game.usingItem = false;
      }
      prevX = b.x;
      prevY = b.y;
      prevZ = b.z;
      const wasOnGround = b.onGround; // pre-step ground state → Jumps takeoff detection (M24)
      const preVy = b.vy; // incoming vertical velocity → slime-bounce reflection (E-14)
      stats.add('play_time'); // M24: Time Played — one tick (20/s) per sim step
      const caps = modeCaps(playerState.gameMode);
      // Flying an aircraft (M32, AVI-2): the flight model drives; the player is
      // bound to the airframe and steers with movement input → skip normal physics
      // (the rest of the tick — entities, weather — still runs).
      const flying = flyingAircraft();
      if (flying) flyAircraftTick(flying);
      // Piloting a SAR helicopter / van (M36.3): the vehicle model drives; the player
      // is bound to it and steers with movement input, so skip normal physics.
      const heli = pilotedHeli(); if (heli) pilotHeliTick(heli);
      const van = pilotedVan(); if (van) pilotVanTick(van);
      const onVehicle = !!flying || !!heli || !!van;
      // SAR objective (M36, SAR-3): score the local searcher's dwell on the beacon.
      tickSarObjective();
      // Classroom (M37, CLS-2/3): periodic placed-vs-plan diff + completion/unlock.
      tickClassroom();
      // The active lesson's ticksOnTask + region check (Part X, §29.4, M51.7).
      tickEduLesson();
      // Riding a boat (B4): the boat drives; the player is bound to its seat and
      // steers with movement input, so skip normal walking/gravity this tick.
      if (!onVehicle && ridingBoatId !== null) {
        const rb = boats.boats.find((x) => x.id === ridingBoatId);
        if (rb) rideBoatTick(b, rb); else ridingBoatId = null;
      }
      // Riding a land mount (M19): the mount drives (steered via es.controlled); the
      // player is bound to its seat, so skip normal walking/gravity this tick.
      if (!onVehicle && ridingBoatId === null && ridingMountId !== null) {
        const rm = entities.entities.find((x) => x.id === ridingMountId && !x.removed);
        if (rm) rideMountTick(b, rm); else ridingMountId = null;
      }
      if (!onVehicle && ridingBoatId === null && ridingMountId === null && game.locked && !game.uiOpen && !playerState.dead) {
        const inFluid = isBodyInFluid((fx, fy, fz) => world.isFluid(fx, fy, fz), b.x, b.y, b.z);
        // Elytra glide (M21): a jump press while airborne starts fall-flying; the
        // look direction then drives the aerodynamics (dive→speed, climb→lift),
        // with a lit firework rocket adding a damageless forward boost.
        const hasElytra = isElytra(chestItemName());
        const jumpEdge = game.input.jump && !prevJump;
        prevJump = game.input.jump;
        if (gliding && shouldStopGliding({ hasElytra, onGround: b.onGround, inFluid })) gliding = false;
        else if (!gliding && jumpEdge &&
          canStartGliding({ hasElytra, onGround: b.onGround, inFluid, creativeFlying: caps.fly && (b.flying ?? false) })) {
          gliding = true;
        }
        // The block underfoot at tick start → slime/honey landing physics (E-14).
        const surfaceBelow = landSurface(registries.states.blockName[world.getBlockState(Math.floor(b.x), Math.floor(b.y - 0.1), Math.floor(b.z))]);
        if (gliding) {
          let v = elytraAerodynamics(b, player.yaw, player.pitch);
          if (fireworkBoostTicks > 0) { v = fireworkBoost(v, player.yaw, player.pitch); fireworkBoostTicks--; }
          b.vx = v.vx; b.vy = v.vy; b.vz = v.vz;
          collideAndMove(b, boxesAt);
          if (b.onGround) gliding = false;
        } else {
          stepPlayer(b, game.input, player.yaw, boxesAt, inFluid, {
            flying: caps.fly && b.flying,
            noClip: caps.noClip,
            // Honey saps ground speed — folded into the movement step so it slows
            // the actual walk (a post-step scale would be overwritten next tick).
            speedMul: speedMultiplier(playerState.effects) * surfaceSpeedFactor(surfaceBelow),
            jumpBoost: jumpBoost(playerState.effects),
          });
          // Levitation eases the body upward; Slow Falling caps the descent (PLR-8).
          if (!b.flying && !inFluid) {
            b.vy += levitationDelta(playerState.effects, b.vy);
            if (slowFallingActive(playerState.effects) && b.vy < PLAYER.SLOW_FALL_CAP) b.vy = PLAYER.SLOW_FALL_CAP;
          }
          // Ladder / scaffolding climb: a body in a `climbable` cell clings (MB.1).
          if (!b.flying && !inFluid) {
            const cn = registries.states.blockName[world.getBlockState(Math.floor(b.x), Math.floor(b.y + 0.2), Math.floor(b.z))] ?? '';
            if (registries.blocks.byName.get(cn)?.tags.includes(CLIMBABLE_TAG)) {
              b.vy = climbVy({ jump: game.input.jump, sneak: game.input.sneak }, b.vy);
              playerState.fallPeak = b.y; // clinging never accrues fall damage
            }
          }
          // Slime bounce + honey jump/slide (E-14). Slime reflects a landing fall
          // into a bounce (sneaking cancels it); honey caps a jump off it, and a body
          // pressed against a honey wall slides down slowly. (Honey ground-drag is
          // applied through speedMul above, before the move.)
          if (!b.flying && !inFluid) {
            if (b.onGround && !wasOnGround) {
              // Landed this tick — read the surface the body is *now* resting on.
              const landed = landSurface(registries.states.blockName[world.getBlockState(Math.floor(b.x), Math.floor(b.y - 0.1), Math.floor(b.z))]);
              const bv = bounceVelocity(landed, preVy, game.input.sneak);
              if (bv > 0) { b.vy = bv; b.onGround = false; playerState.fallPeak = b.y; }
            }
            if (wasOnGround && surfaceBelow === 'honey') b.vy = honeyJumpCap(surfaceBelow, b.vy);
            if (b.vy < 0 && !b.onGround && touchingHoneyWall(b)) {
              b.vy = honeySlideVelocity(b.vy);
              playerState.fallPeak = b.y; // sliding never accrues fall damage
            }
          }
        }
      }
      // Movement stats (M24): ground distance walked (cm, with sub-cm carry) and
      // jump takeoffs — read from the body delta this tick, teleports excluded.
      if (!playerState.dead) {
        if (b.onGround && !b.flying) {
          statDistAccum += distanceCm(b.x - prevX, b.z - prevZ);
          if (statDistAccum >= 1) { const cm = Math.floor(statDistAccum); stats.add('distance_walked', cm); statDistAccum -= cm; }
        }
        if (!b.flying && !gliding && isJumpTakeoff(wasOnGround, b.onGround, b.vy)) stats.add('jumps');
      }
      if (caps.survivalHunger && !playerState.dead) survivalTick(b);
      tickMining();
      tickEating();
      if (game.attackCooldown > 0) game.attackCooldown--;
      drops.tick(world);
      fallingBlocks.tick(fallingWorld);
      primedTnt.tick(tntWorld, detonateTnt); // fall + fuse; detonate at fuse end (RED-8)
      minecarts.tick(railWorld); // roll along rails; powered rails launch/brake (RED-5/6)
      tickDetectorRails(); // a cart on a detector rail powers it (RED-5)
      tickMinecartHazards(); // tnt-cart fuse + hopper-cart drop vacuum (RED-6)
      tickBoats(); // float + paddle boats on water (RED-6)
      if (!playerState.dead) {
        drops.collect(b.x, b.y, b.z, (s) => {
          const nm = registries.items.nameOf[s.item];
          if (nm) { fireAdvancement({ kind: 'obtain', item: nm }); stats.bump('picked_up', nm); unlockRecipesFor(nm); } // M24: obtain trigger + Items Picked Up + recipe unlock
          return insert(playerState.inventory, s, [...PlayerInventory.PICKUP_RANGE]);
        });
      }
      // Block entities (phase 6) + living entities (phase 7) + spawn attempts
      // (phase 10, on a cadence).
      runTick(world, {
        onRandomTick,
        // §27.9's neighbour bus reaching its consumer: every edit enqueued its 6 face
        // neighbours during the tick, and this hands each one to the script at that
        // cell. `depth` rides through so a chain of reacting blocks is bounded by
        // §27.10 rather than by luck.
        onNotify: (batch) => {
          // O(1) bail-out FIRST. §27.9 notifies the 6 faces of every edit, and flowing
          // water alone edits thousands of cells a tick — so in a world with no script
          // blocks (which is nearly all of them) this must cost nothing. Looking each
          // notification up in the block-entity store instead builds a key string per
          // notification and hangs the tick loop outright (D-TF-226).
          if (blockEntities.countOf('script') === 0) return;
          for (const n of batch) {
            enqueueBlockHook(n.x, n.y, n.z, 'neighbor', n.depth, { x: n.fromX, y: n.fromY, z: n.fromZ });
          }
        },
        onFluids: () => remeshChunks(world.tickFluids()),
        // `world.tickCount` still holds the COMPLETED-ticks count here (`runTick`
        // increments it only at its own end, `sim/engine/tick.ts`) — phase 6 is
        // processing the tick that is ABOUT to become `tickCount+1`, so a timer's
        // exact-equality `justFinished` check needs that value, not the
        // pre-increment one (off by one would skip its `endTick` entirely).
        onBlockEntities: () => { beEnv.tick = world.tickCount + 1; tickBlockEntities(blockEntities, beEnv); tickHoppers(); },
        // In net mode the server owns all living entities + spawns (M28.1); the
        // client runs no mob AI and instead mirrors server poses (syncNetEntities).
        onEntities: net ? undefined : () => entities.tick(makeFrame()),
        onSpawns: net ? undefined : () => { if (world.tickCount % MOB.SPAWN.INTERVAL === 0) trySpawn(entities, makeSpawnCtx()); },
      });
      if (net) {
        syncNetEntities(); // render the server-authoritative mobs (M28.1)
      } else {
        tickCures(); // zombie-villager cure conversion timers (MB.6)
        tickDrownConversion(); // submerged zombies → drowned (E1, M18)
        tickHives(); // bees deliver nectar → honey + crop growth boost (MB.5)
        tickFrogs(); // frogs eat slimes/magma cubes → froglight (MB.5)
        tickEggLaying(); // chickens lay eggs on a timer (MB.3)
        tickVillagerBreeding(); // fed villagers + a spare bed → baby (MB.7)
        tickVillagerSchedule(); // cache each villager's bed for night pathing (MB.7)
        tickGossip(); // nearby villagers share reputation; all gossip slowly decays (MB.7)
        tickGolemSpawn(); // a village summons an iron golem to guard it (MB.7)
        tickPatrol(); // a captain-led pillager patrol roams periodically (MB.6)
        tickVexSummon(); // evokers conjure vexes near the player (MB.6)
        tickRaids(); // village raid state machine (RAID, M14.2)
      }
      // Redstone runs on its own 2-gt tick (RED, M15.1): sample plates, then solve,
      // then edge-fire droppers/dispensers off the freshly-solved power (M15.2.1). In
      // net mode it stays client-local but convergent — block states agree across
      // clients via patches, so the pure solve agrees too (server-authoritative
      // redstone broadcast is an M28 remainder; M28.1c proves the determinism).
      if (world.tickCount % RED.TICK_GT === 0) { tickPressurePlates(); tickSculkSensors(); redstone.tick(); tickActivators(); tickNoteBlocks(); tickScriptBlocks(); tickTntIgnition(); tickObservers(); tickPistons(); }
      tickBeaconBlocks(); // beacons re-apply their effect on their own 80-gt cadence (M20.1)
      tickConduits(); // conduits project Conduit Power + zap hostiles on their cadence (M18.1)
      if (!net) tickWardens(); // wardens fire their armour-piercing sonic boom (M22.1)
      if (!net) tickTrialSpawners(); // trial spawners release + reward their waves (M22.1)
      if (!net) tickOceanFauna(); // dolphin grace + pufferfish poison (M18.1)
      if (!net) tickGuardians(); // guardian beams + elder Mining Fatigue aura (M18.1)
      if (!net) tickNetherFauna(); // hoglin overworld-zombify + strider water damage (M17.1)
      if (!net) tickTurtles(); // a baby turtle grown to an adult drops a scute (M18.1)
      if (!net) tickAllays(); // allay duplication cooldown decay (M18.1)
      if (!net) tickBreezes(); // breezes fire wind charges at the player (M22.1)
      if (!net) tickSniffers(); // sniffers unearth torchflower seeds (M19)
      if (!net) tickCamels(); // camel dash cooldown decay (M19)
      if (!net) tickTadpoles(); // tadpoles grow into biome-variant frogs (M19)
      // Lightning during a thunderstorm (PLR-10, overworld only) + portal travel are
      // single-player only for now (server-authoritative weather strikes + multi-dim
      // simulation are M28 remainders).
      if (!net && world.dimension === 'overworld' && world.weather.thundering && strikeRng.nextInt(WEATHER.LIGHTNING_DENOM) === 0) doLightning();
      if (!net) checkPortalTravel(b);
      updateMusicContext(); // the ambient composer follows dimension/time/mode (AUD-4)
      // Multiplayer (NET-2/3): apply server-pushed edits/patches, reconcile our body
      // against the latest server correction, then relay this tick's input frame and
      // record the resulting predicted position for the next reconciliation.
      if (net) {
        applyNetWorld();
        reconcile(b);
        const seq = net.sendInput(netButtons(), player.yaw, player.pitch, playerState.inventory.heldSlot());
        predictRing.set(seq, { x: b.x, y: b.y, z: b.z });
        if (predictRing.size > 240) predictRing.delete(predictRing.keys().next().value as number);
      }
      // Replay (REP-1): append this tick's consumed input to the recording (no-op
      // unless the recordReplay gamerule is on — so the parity path never runs it).
      game.replay?.observeLiveTick();
    },
    render(alpha) {
      const b = player.body;
      // Spectator remote camera (C4): while following a remote player, chase them
      // over the shoulder instead of rendering from our own body.
      const spectated = net && spectateId !== null ? net.remotePlayers().find((r) => r.id === spectateId) : undefined;
      const flyingCam = flyingAircraft();
      const heliCam = pilotedHeli();
      const vanCam = pilotedVan();
      if (spectated) {
        const cam = followCamera(spectated, { eyeHeight: game.eye });
        game.camera.position.set(cam.x, cam.y, cam.z);
        game.camera.rotation.set(cam.pitch, cam.yaw, 0);
      } else if (flyingCam) {
        // Chase cam (M32): sit behind + above the aircraft, along its nose (AVI-2).
        const { forward } = bodyAxes(flyingCam.yaw, flyingCam.pitch, 0);
        game.camera.position.set(
          flyingCam.x - forward.x * 7, flyingCam.y - forward.y * 7 + 2.5, flyingCam.z - forward.z * 7,
        );
        game.camera.rotation.set(flyingCam.pitch * 0.5, flyingCam.yaw, 0, 'YXZ');
      } else if (heliCam) {
        // Chase cam (M36.3): behind + above the helicopter, along its nose.
        const { forward } = bodyAxes(heliCam.yaw, heliCam.pitchTilt, 0);
        game.camera.position.set(heliCam.x - forward.x * 8, heliCam.y - forward.y * 8 + 3, heliCam.z - forward.z * 8);
        game.camera.rotation.set(heliCam.pitchTilt * 0.5, heliCam.yaw, 0, 'YXZ');
      } else if (vanCam) {
        // Chase cam (M36.3): behind + above the van at road level.
        const fx = -Math.sin(vanCam.yaw), fz = -Math.cos(vanCam.yaw);
        game.camera.position.set(vanCam.x - fx * 7, vanCam.y + 3.5, vanCam.z - fz * 7);
        game.camera.rotation.set(0.1, vanCam.yaw, 0, 'YXZ');
      } else {
        game.camera.position.set(
          prevX + (b.x - prevX) * alpha,
          prevY + (b.y - prevY) * alpha + game.eye,
          prevZ + (b.z - prevZ) * alpha,
        );
        game.camera.rotation.set(player.pitch, player.yaw, 0);
      }
      // FOV (M24): settle toward the base setting, widened while sprinting forward,
      // and only rebuild the projection when it actually moves.
      const targetFov = effectiveFov(game.settings.fov, game.input.sprint && game.input.forward);
      const nextFov = approachFov(game.camera.fov, targetFov);
      if (nextFov !== game.camera.fov) {
        game.camera.fov = nextFov;
        game.camera.updateProjectionMatrix();
      }
      // Keep the WebAudio listener on the camera so positional sounds pan (AUD-1).
      const fwd = lookDir();
      audio.setListener(game.camera.position.x, game.camera.position.y, game.camera.position.z, fwd.x, fwd.y, fwd.z);
      subtitles.tick(); // expire faded caption lines (M23)
      const pcx = Math.floor(b.x / WORLD.SECTION), pcz = Math.floor(b.z / WORLD.SECTION);
      // Adaptive render-distance cap: step it toward the fps target on a throttled
      // cadence so a weak client (esp. flying fast) pulls the view in instead of
      // outrunning the streamer. The user's setting stays the ceiling (and the UI).
      const nowMs = performance.now();
      if (nowMs - lastCapAdjust >= STREAM.CAP_ADJUST_MS) {
        effectiveRenderDist = nextRenderCap(effectiveRenderDist, game.fps, game.settings.renderDistance);
        lastCapAdjust = nowMs;
      }
      const R = effectiveRenderDist;
      const keepRadius = R + VIEW.EVICT_MARGIN;
      // Heading = last tick's ground displacement (b − prev), EMA-smoothed and
      // teleport-guarded, so prefetch aims where we're actually flying (perf).
      const rawVx = b.x - prevX, rawVz = b.z - prevZ;
      if (Math.hypot(rawVx, rawVz) <= STREAM.TELEPORT_SPEED) {
        streamVx += (rawVx - streamVx) * STREAM.HEADING_SMOOTH;
        streamVz += (rawVz - streamVz) * STREAM.HEADING_SMOOTH;
      }
      const heading = { vx: streamVx, vz: streamVz };
      genClient.pump(pcx, pcz, keepRadius, heading);
      world.evictBeyond(pcx, pcz, keepRadius);
      // Surface-only LOD (perf): while flying an aircraft high and fast, mesh just
      // the top skin so a large render distance stays affordable (nextViewLod owns
      // the hysteresis on both speed and altitude-above-terrain).
      const fa = flyingAircraft();
      const agl = fa ? fa.y - (world.surfaceY(Math.floor(fa.x), Math.floor(fa.z)) ?? fa.y) : 0;
      viewLod = nextViewLod(viewLod, !!fa, Math.hypot(streamVx, streamVz), agl);
      chunkMeshes.update(b.x, b.z, R, heading, viewLod);
      rc.sky.setViewDistance(R * WORLD.SECTION); // fog recedes with render distance (UI-6)
      // The camera far-plane must reach past the meshed terrain, or the extra chunks
      // a high render distance builds are clipped and the slider looks like a no-op.
      const wantFar = Math.max(1000, keepRadius * WORLD.SECTION * 1.3);
      if (game.camera.far !== wantFar) { game.camera.far = wantFar; game.camera.updateProjectionMatrix(); }
      // Storms darken the world (a shader-uniform tweak, no remesh — LIT-5).
      const storm = world.dimension === 'overworld' ? (world.weather.thundering ? 0.55 : world.weather.raining ? 0.75 : 1) : 1;
      // The sim owns the sky (generic cycle, or real solar for an Atlas world, SOL-1).
      const skyState = world.skyState();
      // Night vision floors the brightness to a fully-lit view (MB.4).
      rc.daylight.value = Math.max(skyState.daylight * storm, nightVisionFloor(playerState.effects));
      applyEffectOverlay(screenTint(playerState.effects)); // status-effect screen wash + HUD (MB.4)
      rc.sky.update(skyState, game.camera);
      weatherFX.update(game.camera, precipKind());
      // In net mode the server owns item drops (C6) — render its replicated list;
      // solo renders the local ItemDropSystem.
      dropRenderer.update(
        net
          ? net.remoteDrops().map((d) => ({ id: d.id, stack: { item: d.item, count: d.count }, x: d.x, y: d.y, z: d.z })) as unknown as readonly ItemDrop[]
          : drops.drops,
        performance.now(),
      );
      signRenderer.update(signText, signGlow);
      fallingRenderer.update(fallingBlocks.blocks);
      // Net mode renders the SERVER's primed TNT (M62); solo renders its own.
      primedTntRenderer.update(net
        ? net.remoteTnt().map((b) => ({ id: b.id, x: b.x, y: b.y, z: b.z, vx: 0, vy: 0, vz: 0, fuse: 0, age: b.age })) as unknown as readonly PrimedTnt[]
        : primedTnt.primed);
      // Net mode draws the SERVER's vehicles (M62); solo draws its own.
      const netVehicles = net ? net.remoteVehicles() : null;
      minecartRenderer.update(netVehicles
        ? netVehicles.filter((v) => v.family === 'cart').map((v) => ({ id: v.id, kind: v.kind, x: v.x, y: v.y, z: v.z })) as unknown as readonly Minecart[]
        : minecarts.carts);
      boatRenderer.update(netVehicles
        ? netVehicles.filter((v) => v.family === 'boat').map((v) => ({ id: v.id, kind: v.kind, x: v.x, y: v.y, z: v.z, yaw: v.yaw })) as unknown as readonly Boat[]
        : boats.boats);
      aircraftRenderer.update(aircraft.aircraft); // M32 box-rig
      helicopterRenderer.update(helicopters.helicopters); // M36.3
      vanRenderer.update(vans.vans);                       // M36.3
      { // PFD reads the flown aircraft, or the piloted helicopter (adapted to the PFD shape).
        const fa = flyingAircraft();
        if (fa) aviationHud.update(fa);
        else { const hc = pilotedHeli(); if (hc) aviationHud.update(heliAsAircraft(hc)); }
      }
      beaconRenderer.update(beacons.beacons); // M36 ELT markers
      { // M37 ghost blueprint: translucent phantom voxels (ghost) or green/red heat.
        const bp = classroom.active();
        blueprintRenderer.update(bp ? heatmap(bp, worldRead, STATE_AIR) : [], classroomHeat ? 'heat' : 'ghost');
      }
      if (sarHud.visible) sarHud.update(sarReadout()); // M36 SAR instruments
      updateDfInstruments(); // M36.2 Becker dial / handheld 9-LED DF
      if (net) {
        // Smooth remote poses to `now − RENDER_DELAY` each frame (NET-1, C2), then
        // copy the interpolated mob poses onto their shadow entities so the entity
        // renderer draws them gliding between the 10 Hz server Moves.
        net.interpolate(performance.now());
        for (const r of net.remoteEntities()) {
          const e = netEntityById.get(r.id);
          if (e) { e.x = r.x; e.y = r.y; e.z = r.z; e.yaw = r.yaw; }
        }
      }
      entityRenderer.update(entities, rc.daylight.value);
      puffs.update();
      if (net && remoteRenderer) remoteRenderer.update(net.remotePlayers()); // NET-2 avatars (interpolated)
      breakOverlay.update(mining);
      // Replay (REP-3): while the scrubber overlay is open, draw the replay world in
      // 3D instead of the live scene (the live scene above is left intact — parity).
      if (replayView.active) { replayView.sync(); replayView.render(); }
      else game.renderer.render(game.scene, game.camera);
      minimap.update();
      mapView.update();
      hud.update(game);
      if (debugOverlayOn) {
        debugEl.textContent = debugLines({
          fps: game.fps, x: b.x, y: b.y, z: b.z, yaw: player.yaw, pitch: player.pitch,
          facing: facingFromYaw(player.yaw), dimension: world.dimension,
          biome: world.biomeNameAt(Math.floor(b.x), Math.floor(b.z)),
          dayTime: world.dayTime, entities: entities.entities.filter((e) => !e.removed).length,
          section: WORLD.SECTION, renderDistance: game.settings.renderDistance, renderCap: effectiveRenderDist,
        }).join('\n');
      }
      if (chunkBorderLines.visible) updateChunkBorders();
      if (hitboxLines.visible) updateHitboxes();
      const eyeInWater = world.isFluid(Math.floor(b.x), Math.floor(b.y + game.eye), Math.floor(b.z));
      waterTint.style.display = eyeInWater ? 'block' : 'none';
    },
    saveNow() {
      // Persist the ACTIVE dimension's near-player entities + block entities and
      // its dirty sections, keyed by the current dimension (DIM-1, §3.6).
      syncVillagerEconomy(); // fold reputation + trade uses into entity data first (MB.7)
      void save.saveEntities(meta.id, currentDim, entities.toJSON(player.body.x, player.body.z, MOB.DESPAWN.SOFT));
      void save.saveBlockEntities(meta.id, currentDim, blockEntities.toJSON());
      return save.saveGame(
        meta,
        {
          x: player.body.x, y: player.body.y, z: player.body.z, yaw: player.yaw, pitch: player.pitch, dim: currentDim,
          survival: playerState.toJSON(),
          // M25 ENG-11: persist the three M24 progression trackers with the world.
          progress: {
            version: CURRENT_SAVE_VERSION,
            stats: stats.toJSON(),
            advancements: advancements.toJSON(),
            recipeBook: recipeBook.toJSON(),
          },
          // HER-7, §28.11 (save v4): absent when it is still empty, matching the
          // pre-v4 absence every migration fixture pins.
          ...(currentNotebook.animals.length || currentNotebook.crops.length ? { notebook: currentNotebook } : {}),
          // EDU-8, §29.6 (save v5): absent with no active lesson, matching the
          // pre-v5 absence every migration fixture pins.
          ...(lessonDef && lessonResult ? { lessons: { [lessonDef.name]: lessonResult } } : {}),
        },
        world.getDirtySections(),
        world.range, // section rows keyed by absolute section-Y (v2 / TAL-4)
      );
    },
  };

  let genClient = new GenClient(world);

  // ── Dimension retention + travel (DIM-1/2) ──
  interface DimBundle {
    world: World;
    entities: EntitySystem;
    blockEntities: BlockEntityStore;
    drops: ItemDropSystem;
    beSeenChunks: Set<string>;
  }
  const dimStates = new Map<DimId, DimBundle>([['overworld', { world, entities, blockEntities, drops, beSeenChunks }]]);
  const ensureDim = (dim: DimId): DimBundle => {
    let st = dimStates.get(dim);
    if (st) return st;
    st = { world: manager.world(dim), entities: makeEntities(dim), blockEntities: new BlockEntityStore(), drops: new ItemDropSystem(maxStackOf), beSeenChunks: new Set() };
    dimStates.set(dim, st);
    return st;
  };

  /** Swap the active dimension: persist the current one, tear down its render
   *  pipeline, activate the target's retained sim state, and rebuild rendering. */
  const switchDimension = (toDim: DimId, arrival: readonly [number, number, number], built: readonly [number, number, number] | null): void => {
    if (toDim === currentDim) return;
    if (!net) void game.saveNow(); // net replicas aren't persisted client-side (C7)
    chunkMeshes.dispose(); // also terminates its mesh-worker pool
    genClient.dispose();
    entityRenderer.clear();
    dropRenderer.clear();
    signRenderer.clear();
    fallingRenderer.clear();
    fallingBlocks = new FallingBlockSystem();
    primedTntRenderer.clear();
    primedTnt = new PrimedTntSystem();
    minecartRenderer.clear();
    minecarts = new MinecartSystem();
    boatRenderer.clear();
    boats = new BoatSystem();
    boatInputs.clear();
    ridingBoatId = null; // dismount on a dimension change (B4)
    ridingMountId = null; // and dismount any land mount (M19)
    tntCells.clear(); // the new dimension re-indexes its own TNT via `edit`
    pistonCells.clear(); observerCells.clear(); observerBaseline.clear(); observerPulse.clear();
    beaconBlockCells.clear(); beaconSel.clear(); conduitCells.clear(); endCrystalCells.clear();
    trialSpawnerCells.clear(); trialData.clear(); vaultUnlockedBy.clear();
    mining.active = false;

    const st = ensureDim(toDim);
    currentDim = toDim;
    manager.active = toDim;
    fireAdvancement({ kind: 'changed_dimension', to: toDim }); // M24: "We Need to Go Deeper" / Nether / The End?
    world = st.world; entities = st.entities; blockEntities = st.blockEntities; drops = st.drops; beSeenChunks = st.beSeenChunks;
    // A dimension's `EntitySystem` is a different object; without this its mobs all
    // fall back to their archetypes the moment you step through a portal (§27.6).
    attachMobScripts(entities);
    installChunkReady(world);

    chunkMeshes = new ChunkMeshes(world, renderTable, rc.scene, rc.material, rc.waterMaterial, makeMeshWorkers());
    genClient = new GenClient(world);
    rc.sky.setDimension(toDim);
    if (built) world.relightChunk(Math.floor(built[0] / WORLD.SECTION), Math.floor(built[2] / WORLD.SECTION));

    const [ax, ay, az] = arrival;
    player.body.x = ax; player.body.y = ay; player.body.z = az;
    player.body.vx = player.body.vy = player.body.vz = 0; player.body.onGround = false;
    playerState.fallPeak = ay;
    prevX = ax; prevY = ay; prevZ = az;
    wasOnGround = false;
    chunkMeshes.primeAround(ax, az);
    armEndFight(world, entities); // DIM-3: the End owes you a dragon on arrival
    portalImmune = true;
    portalTicks = 0;
  };

  /** Accumulate portal-standing time; travel at PORTAL.WAIT_GT (instant creative). */
  const checkPortalTravel = (b: Body): void => {
    if (net) return; // net mode: the server owns portal travel (C7) → a Dimension msg
    if (game.uiOpen) return; // E-24: don't travel with a screen open
    const state = world.getBlockState(Math.floor(b.x), Math.floor(b.y + 0.9), Math.floor(b.z));
    if (endGatewayStates.has(state)) {
      // A gateway is a jump WITHIN the End — no dimension switch, no dwell — but it
      // still honours the just-arrived immunity so the return gateway overhead
      // doesn't bounce you straight back out.
      if (!portalImmune) useEndGateway(b);
      return;
    }
    if (endPortalStates.has(state)) {
      // An end portal is instant — no dwell timer — but still honours the
      // just-arrived immunity so a return trip doesn't bounce straight back.
      if (portalImmune) return;
      // Coming home lands at the player's spawn (bed / world spawn), as MC does.
      const sp = playerState.spawn ?? { x: 8.5, y: 64, z: 8.5 };
      const res = manager.travelEnd(currentDim, [sp.x, sp.y, sp.z]);
      switchDimension(res.dim, res.arrival, res.built);
      return;
    }
    const inPortal = netherPortalStates.has(state);
    if (!inPortal) { portalTicks = 0; portalImmune = false; return; }
    if (portalImmune) return;
    const wait = modeCaps(playerState.gameMode).instantBreak ? 1 : PORTAL.WAIT_GT;
    if (++portalTicks < wait) return;
    const res = manager.travel(currentDim, b.x, b.y, b.z);
    switchDimension(res.dim, res.arrival, res.built);
  };

  const minimap = new MiniMap(game, document.body);
  game.minimap = minimap;
  const mapView = new MapView(game, document.body, mapStore, mapPalette, mapSampler);
  const hud = new Hud();
  // Replay (REP-1/4): the viewer overlay + record dot, and the live opt-in recorder
  // (drives nothing unless the `recordReplay` gamerule is on → parity untouched).
  // Timelapse frames capture the REPLAY view when it's on screen, else the live game.
  const replayOverlay = new ReplayOverlay(
    () => (replayView.active ? replayView.takeScreenshot() : game.takeScreenshot()),
    replayView,
    // Hangar §39.5 (M105.1) — "scrubber Save replay". `game.replay` is assigned right below;
    // this closure is only ever invoked later, on a real click.
    () => void saveCurrentReplay(game),
  );
  replayOverlay.mount(document.body);
  game.replay = new ReplayController(game, replayOverlay, replayView);
  attachInput(game);
  installDebugApi(game);
  installChat(game);

  // ── World doors (DOR-1..5, M41): traversal, the menu controller, the escape hatch,
  //    and the arrival that materialises pending return doors + places the player. ──
  const traverseTo = async (targetWorldId: string, targetDoorId: string): Promise<boolean> => {
    const t = await save.getWorld(targetWorldId);
    if (!t) { game.print('§cThis door leads nowhere — the linked world was removed.'); return false; } // E-34 inert
    writeHandoff({ targetWorldId, targetDoorId, carry: playerState.toJSON(), fromWorldId: meta.id });
    await game.saveNow(); // flush the source world before the session swap (DOR-2)
    location.href = `${location.pathname}?world=${encodeURIComponent(targetWorldId)}`;
    return true;
  };

  // Server-door traversal (M42, DOR-6): resolve the handoff over the control plane — the
  // ACL/inert check runs server-side BEFORE any teardown, so a denial/inert door is a
  // clean message with no navigation (E-37/E-34). On a grant, carry the inventory and
  // reconnect to the target world (`?server=…&world=…&ticket=…`) — the same page-nav
  // session swap M41 uses locally, generalised across world hosts.
  const traverseServer = async (x: number, y: number, z: number): Promise<boolean> => {
    if (!net?.ctl || !net.guest) return false;
    const r = await net.ctl.resolveServerTraversal(net.controlOrigin, net.guest, net.welcome.meta.id, `${x},${y},${z}`);
    if (!r.ok) {
      game.print(r.reason === 'denied' ? '§cYou do not have access to that world.'
        : r.reason === 'inert' ? '§cThis door leads nowhere — the linked world was removed.'
        : '§7There is nothing bound to this door.');
      return false;
    }
    writeHandoff({ targetWorldId: `net:${r.targetWorldId}`, targetDoorId: r.targetDoorId, carry: playerState.toJSON(), fromWorldId: meta.id });
    const url = new URL(location.href);
    url.searchParams.set('world', r.targetWorldId);
    if (r.ticket) url.searchParams.set('ticket', r.ticket); else url.searchParams.delete('ticket');
    location.href = url.toString();
    return true;
  };

  // Server storage read-out (STO-3): the account's per-world bytes, mapped into the same
  // UsageReport the local M41 screen renders (so `ui/screens/usage.ts` is shared).
  const serverUsageReport = async (n: NonNullable<typeof net>): Promise<UsageReport> => {
    const u = await n.ctl!.serverUsage(n.controlOrigin, n.guest!);
    return { worlds: u.worlds, totalBytes: u.totalBytes, quotaBytes: u.quotaBytes, warnFraction: STO.WARN_FRACTION, fraction: u.fraction, warn: u.warn };
  };

  const doorApiFor = (x: number, y: number, z: number): DoorScreenApi => {
    const be = blockEntities.create('world_door', x, y, z) as WorldDoorBE;
    return {
      meta,
      door: () => ({ ...be.data }),
      resolveTarget: () => (be.data.targetWorldId ? save.getWorld(be.data.targetWorldId) : Promise.resolve(undefined)),
      listWorlds: () => save.listWorlds(),
      createChild: async (opts) => {
        const r = await createChildWorld(save, meta, be.data, opts); // child written first (atomic)
        Object.assign(be.data, r.door);
        await game.saveNow();
      },
      bindExisting: async (targetWorldId) => {
        const r = await bindExistingWorld(save, meta, be.data, targetWorldId);
        if ('error' in r) return { ok: false, error: r.error };
        Object.assign(be.data, r.door);
        await game.saveNow();
        return { ok: true };
      },
      enter: async () => { await traverseTo(be.data.targetWorldId, be.data.targetDoorId); },
      resize: (size) => resizeWorld(save, be.data.targetWorldId, size),
    };
  };

  const openDoorScreenFor = (x: number, y: number, z: number): void => openDoorScreen(game, doorApiFor(x, y, z));

  const returnHomeImpl = async (): Promise<void> => {
    // Net (M42, DOR-5): ask the server for the highest reachable home world, then reconnect
    // to it (a player in a deleted parent's child is never stranded — E-34).
    if (net?.ctl && net.guest) {
      const home = await net.ctl.serverHome(net.controlOrigin, net.guest, net.welcome.meta.id);
      if (!home || home === net.welcome.meta.id) { game.print('§7You are already in your home world.'); return; }
      writeHandoff({ targetWorldId: `net:${home}`, targetDoorId: '', carry: playerState.toJSON(), fromWorldId: meta.id });
      const url = new URL(location.href);
      url.searchParams.set('world', home);
      url.searchParams.delete('ticket');
      location.href = url.toString();
      return;
    }
    const dest = await homeWorldId(save, meta); // highest reachable ancestor (DOR-5)
    if (dest === meta.id) { game.print('§7You are already in your home world.'); return; }
    writeHandoff({ targetWorldId: dest, targetDoorId: '', carry: playerState.toJSON(), fromWorldId: meta.id });
    await game.saveNow();
    location.href = `${location.pathname}?world=${encodeURIComponent(dest)}`;
  };

  // Imagery bake (OVL-4, M43): freeze the aligned overlays (+ any covering NAIP tiles)
  // into the ground as pinned generator input, then reload — pristine chunks regen with
  // the imagery, edited sections load from storage unchanged (a declared regen event).
  const bakeImageryImpl = async (): Promise<void> => {
    if (net || !earthWorld || !meta.earth) { advToasts.note('Bake unavailable', 'Earth (single-player) worlds only'); return; }
    const cimg = await import('./earth/imagery');
    const { buildImageryPalette } = await import('../sim/earth/imagery');
    const { loadImageryTile } = await import('./earth/tiles');
    const { tilesForBbox } = await import('../sim/earth/tiles');
    const { bboxAround } = await import('./atlas/geocode');
    const em = meta.earth;
    const baseUrl = em.contract?.url;
    const src: import('./earth/imagery').ImagerySources = {
      center: { lat: em.lat, lon: em.lon }, scaleM: em.scaleM, palette: buildImageryPalette(registries),
      decode: cimg.canvasDecode, sha256: cimg.sha256Hex,
      loadOverlayBlob: (id) => save.getOverlayBlob(id),
      loadTilePng: (z, x, y) => loadImageryTile(baseUrl, z, x, y),
    };
    const overlays = await cimg.pinOverlays(meta.overlays ?? [], src);
    // Covering NAIP tiles (M43.1) — none in today's fixture; present after John's SVC-5 regen.
    const bbox = bboxAround({ lat: em.lat, lon: em.lon }, em.extentM * 2);
    const raw: import('./earth/imagery').RawImageryTile[] = [];
    for (const { x, y } of tilesForBbox(bbox, TILE.IMAGERY_Z)) {
      const got = await loadImageryTile(baseUrl, TILE.IMAGERY_Z, x, y);
      if (got) raw.push({ z: TILE.IMAGERY_Z, x, y, png: got.png, sha256: got.sha256 });
    }
    const tiles = cimg.pinTiles(raw);
    if (overlays.length === 0 && tiles.length === 0) { advToasts.note('Nothing to bake', 'Add an overlay (or NAIP imagery) first'); return; }
    meta.imageryBake = { overlays, tiles, bakedAt: Date.now() };
    await game.saveNow();
    location.href = `${location.pathname}?world=${encodeURIComponent(meta.id)}`;
  };

  const unbakeImageryImpl = async (): Promise<void> => {
    if (!meta.imageryBake) return;
    meta.imageryBake = undefined;
    await game.saveNow();
    location.href = `${location.pathname}?world=${encodeURIComponent(meta.id)}`;
  };

  const applyDoorArrival = (hnd: DoorHandoff): void => {
    const doorState = registries.states.defaultOf.get('world_door');
    // Materialise pending return doors (DOR-1) at the world spawn on first entry.
    if (meta.pendingDoors?.length && doorState !== undefined) {
      let ox = 0;
      for (const pd of meta.pendingDoors) {
        const present = [...blockEntities.values()].some((b) => b.type === 'world_door' && b.data.doorId === pd.doorId);
        if (present) continue;
        const dx = Math.floor(sx0) + 1 + ox, dz = Math.floor(sz0);
        // Sit the door on the ground: surfaceY is the first air cell above the surface;
        // the terrainHeight fallback (surface block) needs +1 to reach that same cell.
        const dy = world.surfaceY(dx, dz) ?? world.terrainHeight(dx, dz) + 1;
        game.setBlock(dx, dy, dz, doorState);
        blockEntities.set({ type: 'world_door', x: dx, y: dy, z: dz, data: { ...pd } });
        ox++;
      }
      meta.pendingDoors = undefined;
    }
    // Arrive at the paired door (DOR-2), or the world spawn if it was destroyed (E-35).
    if (hnd.targetDoorId) {
      const door = [...blockEntities.values()].find((b) => b.type === 'world_door' && b.data.doorId === hnd.targetDoorId);
      if (door) { player.body.x = door.x + 0.5; player.body.y = door.y; player.body.z = door.z + 0.5; }
      else game.print('§eThe return door is gone — arrived at the world spawn.');
    }
    player.body.vx = player.body.vy = player.body.vz = 0; player.body.onGround = false;
    player.body.y = Math.max(player.body.y, world.range.minY + 1);
    playerState.fallPeak = player.body.y;
    prevX = player.body.x; prevY = player.body.y; prevZ = player.body.z;
    chunkMeshes.primeAround(player.body.x, player.body.z);
    doorCooldownUntil = performance.now() + DOOR.TRAVERSAL_COOLDOWN_GT * 50; // ~ms
    void game.saveNow(); // persist the materialised door + carried inventory at the new spawn
  };
  if (enter && !net) applyDoorArrival(enter); // solo: materialise + position locally
  else if (enter) {
    // Net (M42, DOR-2): the server placed the return door + dictates the spawn; just note
    // where it is (for the escape-back UI) and grant the brief post-arrival cooldown.
    const [ax, ay, az] = enter.targetDoorId.split(',').map(Number);
    game.doorArrival = enter.targetDoorId && Number.isFinite(ax) ? { x: ax!, y: ay!, z: az! } : null;
    doorCooldownUntil = performance.now() + DOOR.TRAVERSAL_COOLDOWN_GT * 50;
  }

  // Aviation (M32): a key toggles climbing into / out of the aircraft cockpit.
  if (aviationEnabled) {
    document.addEventListener('keydown', (e) => {
      if (e.code !== 'KeyG' || !game.locked || game.uiOpen) return;
      e.preventDefault();
      if (flyingAircraftId !== null) exitAircraft(); else enterAircraft();
    });
  }
  // SAR (M36, SAR-1): the map key toggles the compass + coordinate + ELT-signal
  // instruments (rides alongside the minimap's own KeyM toggle → they stay in sync).
  document.addEventListener('keydown', (e) => { if (e.code === 'KeyM') sarHud.toggle(); });
  // SAR vehicles (M36.3): H enters/exits the nearest helicopter, J the nearest van.
  const nearestWithin = <T extends { id: number; x: number; z: number }>(list: readonly T[], r: number): T | null => {
    let best: T | null = null, bd = r * r;
    for (const k of list) { const d = (k.x - player.body.x) ** 2 + (k.z - player.body.z) ** 2; if (d <= bd) { best = k; bd = d; } }
    return best;
  };
  document.addEventListener('keydown', (e) => {
    if (!game.locked || game.uiOpen || game.chatOpen) return;
    if (e.code === 'KeyH') {
      e.preventDefault();
      if (pilotedHeliId !== null) exitHeli();
      else { const h = nearestWithin(helicopters.helicopters, 8); if (h) enterHeli(h.id); }
    } else if (e.code === 'KeyJ') {
      e.preventDefault();
      if (pilotedVanId !== null) exitVan();
      else { const v = nearestWithin(vans.vans, 8); if (v) enterVan(v.id); }
    }
  });
  // Replay viewer (REP-4): F6 pops the last REP_INSTANT_SECONDS into the scrubber,
  // F7 opens the whole recording (only meaningful once something has been recorded).
  document.addEventListener('keydown', (e) => {
    if (game.uiOpen || game.chatOpen || !game.replay) return;
    if (e.code === 'F6') { e.preventDefault(); openReplayScreen(game.replay, 'instant'); }
    else if (e.code === 'F7') { e.preventDefault(); openReplayScreen(game.replay, 'full'); }
    // F8 opens the Texture Forge (M34) — a creator tool, creative/cheats-gated.
    else if (e.code === 'F8' && (playerState.gameMode === 'creative' || game.meta.allowCommands)) { e.preventDefault(); game.openForge(); }
  });
  // F9 opens the Classroom / guided-build screen (M37) — instructor tool, creative/ops.
  document.addEventListener('keydown', (e) => {
    if (game.uiOpen || game.chatOpen) return;
    if (e.code === 'F9' && (playerState.gameMode === 'creative' || game.meta.allowCommands)) { e.preventDefault(); game.openClassroom(); }
    // F10 opens the reference-overlay panel (M40.4) — Earth worlds only.
    else if (e.code === 'F10' && game.overlay) { e.preventDefault(); game.openOverlayPanel(); }
  });
  // Multiplayer chat + roster (M27): incoming server chat prints to the log; held
  // Tab reveals the connected-player list.
  if (net) {
    net.onChat = (line) => {
      game.print(`<${line.from}> ${line.text}`);
      // §30.4: `focus` mutes chat TOASTS only — the line above still always logs.
      if (presenceStatus !== 'focus') advToasts.note(line.from, line.text);
    };
    // Item pickup grant (C6): the server collected a drop for us → add it to the
    // local inventory (the drop's own Despawn removes it from every client's view).
    net.onPickup = (item, count) => giveStack(makeStack(item, count));
    // Portal travel (C7): the server moved us to another dimension → switch the local
    // replica world + reposition (the destination's edited sections re-stream).
    net.onDimension = (dim, x, y, z) => {
      for (const e of netEntityById.values()) e.removed = true; // drop old-dim shadow mobs
      netEntityById.clear();
      switchDimension(dim as DimId, [x, y, z], null);
    };
    // Mount replication: the seat roster is authoritative, so our own riding state
    // is READ from it rather than set locally. This is what makes a camel's second
    // seat real — the server refuses a seat someone else already has, and both
    // clients learn the outcome from the same snapshot.
    net.onRiders = (list) => {
      const mine = list.find((r) => r.riderId === net!.welcome.playerId);
      if (!mine) { ridingMountId = null; netRiderSeat = 0; return; }
      // Map the server entity id back to our local shadow mob.
      const shadow = netEntityById.get(mine.vehicleId);
      ridingMountId = shadow ? shadow.id : null;
      netRiderSeat = mine.seat;
    };
    installPlayerList(game, net);
  }
  startLoop(game);

  // "Simulated" badge when the Atlas city came from the fallback synthesizer (ATL-2).
  if (atlasCity?.simulated) {
    const badge = document.createElement('div');
    badge.className = 'bb-sim-badge';
    badge.textContent = '◇ SIMULATED CITY';
    badge.style.cssText = 'position:fixed;top:12px;left:12px;z-index:40;padding:4px 8px;border-radius:6px;background:rgba(180,120,20,.85);color:#fff;font:600 12px ui-monospace,monospace;pointer-events:none;';
    document.body.append(badge);
  }

  // Earth accuracy badge (ERT-3): the honest per-source fidelity claim — "±1 block"
  // only when the DEM justifies it at this scale, else "approximate relief" (E-41
  // "elevation clamped" appended when a fine scale plateaus the top).
  if (earthWorld && meta.earth) {
    const b = earthBadge(meta.earth.demSource ?? earthWorld.demSource, meta.earth.scaleM);
    const badge = document.createElement('div');
    badge.className = 'bb-earth-badge';
    // Baked-imagery provenance (OVL-4 → ERT-3 surface): the ground is real photography.
    badge.textContent = meta.imageryBake ? `⌖ ${b.text} · imagery baked` : `⌖ ${b.text}`;
    const bg = b.claim ? 'rgba(30,120,60,.85)' : 'rgba(90,90,110,.85)';
    badge.style.cssText = `position:fixed;top:12px;left:12px;z-index:40;padding:4px 8px;border-radius:6px;background:${bg};color:#fff;font:600 12px ui-monospace,monospace;pointer-events:none;`;
    document.body.append(badge);
  }

  // Reference overlays (OVL-1, M40.4): Earth-only ghost planes. The controller owns the
  // placements (meta.overlays) + the blob store + the THREE quads; it never touches the
  // sim (OVL-2/3). Restore persisted overlays now — a missing blob drops + toasts (E-40).
  if (earthWorld) {
    game.overlay = new OverlayController({
      scene: rc.scene,
      meta,
      save,
      scaleM: meta.earth?.scaleM ?? earthWorld.scaleM,
      anchorAt: () => ({ x: player.body.x, y: player.body.y, z: player.body.z }),
      persist: () => void game.saveNow(),
      toast: (head, title) => advToasts.note(head, title),
    });
    void game.overlay.loadAll();
  }

  // Route every audio event to the subtitles overlay (captioned + directioned).
  audio.onEvent = (e) => {
    if (!subtitles.isEnabled) return;
    const f = lookDir();
    subtitles.notify(e, subtitleDirection(e, game.camera.position.x, game.camera.position.z, f.x, f.z));
  };
  applyAudioSettings(); // master/sound/music gains + subtitles from saved settings

  // Autosave (ENG-9) + save on tab hide / unload (best-effort). Skipped in
  // multiplayer — the server owns persistence (NET-2), the client is a replica.
  if (!net) {
    setInterval(() => void game.saveNow(), AUTOSAVE_MS);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') void game.saveNow();
    });
    window.addEventListener('pagehide', () => void game.saveNow());
  }

  // §29.3's binding join line — shown once, on entering a world with an active
  // lesson (D-TF-191: the action bar is the M49.4 surface).
  if (lessonDef) game.showActionBar(`Lesson: ${lessonDef.title} — press J`);
  // §29.8 — our own hand, as the SERVER's roster reports it: the binding lines follow
  // what actually happened (a raise past the cap never claims success; a freeze release
  // or the instructor lowering it reads as lowered).
  if (net) {
    let handWasUp = false;
    net.onRoster = (list) => {
      const up = list.find((q) => q.id === net.welcome.playerId)?.hand !== undefined;
      if (up !== handWasUp) game.showActionBar(up ? 'Hand raised.' : 'Hand lowered.');
      handWasUp = up;
    };
  }
  // §29.8 — the instructor's controls on us. Freeze / eyes-up hold movement (the
  // server drops our frames anyway; this stops us predicting moves it will never make).
  if (net) {
    net.onLessonControl = (kind, text) => {
      if (kind === 'freeze') eduControl.frozen = true;
      else if (kind === 'unfreeze') eduControl.frozen = false;
      else if (kind === 'eyesUp') eduControl.eyesUp = text ?? '';
      else if (kind === 'eyesDown') eduControl.eyesUp = null;
      else if (kind === 'watch') eduControl.watched = true;
      else eduControl.watched = false;
    };
  }
  // On a server the lesson is the server's (§29.4, E-61): its first row for us is the
  // join, and each later row toasts exactly the objectives that just completed.
  if (net) {
    net.onLessonUpdate = (update, prev) => {
      const def = registries.lessons.byName.get(update.lesson);
      const title = (name: string): string => def?.objectives.find((o) => o.name === name)?.title ?? name;
      if (!prev) { game.showActionBar(`Lesson: ${def?.title ?? update.lesson} — press J`); return; }
      for (const [name, tick] of Object.entries(update.result.objectives)) {
        if (tick !== null && (prev.objectives[name] ?? null) === null) advToasts.note('', `Objective complete: ${title(name)}`);
      }
      if (update.result.completed !== undefined && prev.completed === undefined) {
        advToasts.note('', `Lesson complete: ${def?.title ?? update.lesson}`);
      }
    };
  }

  return game;
}

/** HUD aggregate (UI-4): hotbar, hearts, hunger, crosshair, F3 debug overlay. */
class Hud {
  private readonly hotbar = new Hotbar();
  private readonly hearts = new Hearts();
  private readonly hunger = new Hunger();
  private readonly air = new Air();
  private readonly crosshair = new Crosshair();
  private readonly bossbar = new BossBar();
  private readonly actionbar = new ActionBar();
  private readonly lessonHud = new LessonHud();
  private readonly f3 = new F3();

  constructor() {
    const bottom = document.createElement('div');
    bottom.className = 'bb-hud-bottom';
    const stats = document.createElement('div');
    stats.className = 'bb-hud-stats';
    stats.append(this.hearts.el, this.hunger.el, this.air.el);
    bottom.append(stats, this.hotbar.el);
    document.body.append(this.crosshair.el, this.bossbar.el, this.actionbar.el, this.lessonHud.el, bottom, this.f3.el);
  }

  update(game: Game): void {
    this.hotbar.update(game);
    this.hearts.update(game);
    this.hunger.update(game);
    this.air.update(game);
    this.crosshair.update(game);
    this.bossbar.update(game);
    this.actionbar.update(game);
    this.lessonHud.update(game);
    this.f3.update(game);
  }
}

/** Pause on pointer-lock release; Resume re-locks, Save & Quit returns to menu. */
function wirePause(game: Game): void {
  const canvas = game.renderer.domElement;
  let paused = false;
  const openPause = (): void => {
    paused = true;
    showPause({
      onResume: () => { paused = false; void canvas.requestPointerLock(); },
      onHelp: () => showHelp(openPause),
      onStats: () => game.openStats(openPause),
      onOptions: () => showOptions(game.settings, () => { applyGuiScale(game.settings.guiScale); game.applyAudioSettings(); }, openPause),
      // Storage read-out (STO-3) + the DOR-5 escape hatch. The escape hatch always exists
      // so a player is never stranded (it no-ops with a message on a home/root world).
      onStorage: () => game.openUsageScreen(),
      onEditor: () => showEditor(game, openPause),
      onReturnHome: () => void game.returnHome(),
      // Hangar §39.6 (M106.5, D-780 Q2). `./hangar/invite` is dynamically imported, like every
      // other module under `hangar/**` (`maybeAttachHangar`'s own header comment): the button is
      // gated here so solo/unbridged play never even evaluates the import, and the gate itself
      // uses only what boot.ts already has loaded (`atLeast`), never touching `hangar/**`.
      onInviteConnection:
        game.hangar && game.net && atLeast(game.eduRole(), 'instructor')
          ? () => {
              const hangar = game.hangar!;
              const net = game.net!;
              void (async () => {
                const { inviteConnection } = await import('./hangar/invite');
                await inviteConnection(hangar, { worldId: () => net.welcome.meta.id, print: (m) => game.print(m) });
              })();
            }
          : undefined,
      // Hangar §39.8 (M108.2, D-TF-300). The world manager is a server-worlds feature
      // (`/world/mine` is driven by `net.guest`'s identity, the same model every other
      // `/world/*` route already uses) — gated exactly like the invite button above.
      onWorldManager:
        game.hangar?.bridged() && game.net
          ? () => openWorldManagerWindow(game.hangar!, game.net!)
          : undefined,
      onSaveQuit: () => { void game.saveNow().finally(() => { location.href = location.pathname; }); },
      cheatsEnabled: () => game.cheats,
      onToggleCheats: () => {
        game.cheats = !game.cheats;
        game.meta.allowCommands = game.cheats; // persist the choice with the world
        game.print(`Cheats ${game.cheats ? 'enabled' : 'disabled'}`);
        void game.saveNow();
      },
    });
  };
  document.addEventListener('pointerlockchange', () => {
    const locked = document.pointerLockElement === canvas;
    if (locked) game.resumeAudio(); // a gesture unblocks WebAudio autoplay (AUD-1)
    // A screen (inventory/crafting) intentionally releases the lock — don't pause.
    if (!locked && !paused && !game.uiOpen) openPause();
  });
}

/**
 * Hangar §39.8 (M108.2, D-TF-300) — the marker carried in `window.open()`'s `arg` so the
 * freshly-opened SECOND window (which boots at the manifest's fixed entry, with no
 * `route=`/`server=` of its own) knows to show the world-manager screen instead of just
 * sitting there as an ordinary (solo, throwaway) game window. The opener's own `net`
 * session supplies `origin`/`guest`, since the new window has no net session of its own
 * and `/world/*` is reached over plain HTTP, never a live WebSocket.
 */
const WORLD_MANAGER_MIME = 'application/x-terraforge-worldmanager';
/** Hangar §39.10 (M109.7) — Maps' "Open in TerraForge" mime, matching Hangar's own
 *  `kernel.launchTerraforgeAtEarth`'s `application/x-hangar-earthpoint`. */
const EARTH_POINT_MIME = 'application/x-hangar-earthpoint';

/** Hangar §39.8 — open the world-manager as a genuine second window (`window/openWindow`,
 *  `multiWindow: true` in the manifest), from the pause menu or a jump-list `command/fire`. */
function openWorldManagerWindow(client: HangarClient, net: NonNullable<Game['net']>): void {
  if (!net.ctl || !net.guest) return;
  const marker = { origin: net.controlOrigin, guest: net.guest };
  void client.api.window.open({
    title: 'World Manager',
    arg: { items: [{ mime: WORLD_MANAGER_MIME, text: JSON.stringify(marker) }] },
  });
}

/**
 * Hangar §39.4 (M104.2–4) — the NET-7 pattern: unbridged TerraForge (no `?hangar=1`) loads not
 * one byte of `src/client/hangar/**`. Only this one call site ever imports it.
 */
async function maybeAttachHangar(game: Game, params: URLSearchParams, save: SaveManager): Promise<void> {
  if (params.get('hangar') !== '1') return;
  const [{ createHangarClient }, { attachHangarLifecycle }, pkg] = await Promise.all([
    import('./hangar/bridge'),
    import('./hangar/adapter'),
    import('../../package.json'),
  ]);
  const client = createHangarClient({ appId: 'terraforge', version: pkg.version });
  game.hangar = client;
  // Hangar §39.6 (M106.6) — `onTimerFinished` below reads this to decide whether the window
  // needs a real notification or the action-bar line is already enough.
  game.hangarFocused = true;
  client.onFocusChanged((focused) => {
    game.hangarFocused = focused;
  });

  // Hangar §39.5 (M105.1) — "Opening a file into TerraForge": `transfer/receive` with a
  // `.bbreplay` payload opens the scrubber; an unknown/future version is refused with the
  // existing E-33 message (`decodeReplay` already throws it), never a crash.
  client.api.transfer.onReceive((payload) => {
    void (async () => {
      const item = (payload as { items?: { mime?: string; bytes?: Uint8Array; path?: string; text?: string }[] })
        ?.items?.[0];
      if (!item) return;
      if (item.mime === WORLD_MANAGER_MIME && item.text) {
        const ctx = JSON.parse(item.text) as { origin: string; guest: import('./net/worldctl').GuestId };
        const { showWorldManager } = await import('./ui/screens/world-manager');
        showWorldManager(document.body, client, ctx);
        return;
      }
      if (item.mime === EARTH_POINT_MIME && item.text) {
        const { lat, lon } = JSON.parse(item.text) as { lat: number; lon: number };
        const { handleEarthPoint } = await import('./hangar/earthpoint');
        const { openEarthPrompt } = await import('./ui/screens/earthprompt');
        await handleEarthPoint(lat, lon, {
          listWorlds: () => save.listWorlds(),
          openWorld: (id) => { location.href = `${location.pathname}?hangar=1&world=${encodeURIComponent(id)}`; },
          confirmCreate: () => openEarthPrompt(),
          createWorld: (plat, plon) => {
            location.href = `${location.pathname}?hangar=1&world=${encodeURIComponent(newId('world'))}&earth=${plat},${plon}`;
          },
        });
        return;
      }
      const isReplay = item.mime === 'application/x-terraforge-replay' || item.path?.endsWith('.bbreplay');
      if (!isReplay) return;
      try {
        const bytes = item.bytes ?? (item.path ? await client.api.fs.read(item.path) : null);
        if (!bytes) return;
        const { decodeReplay } = await import('../sim/replay/format');
        game.replay?.openReplay(decodeReplay(bytes));
      } catch (error) {
        // E-33: a newer/garbage struct is refused with its own message, never a crash.
        game.print(String((error as Error).message ?? error));
      }
    })();
  });

  attachHangarLifecycle(client, {
    isConnected: () => Boolean(game.net),
    saveNow: () => game.saveNow(),
    pauseRender: () => {
      game.renderPaused = true;
    },
    resumeRender: () => {
      game.renderPaused = false;
    },
  });

  attachBoardSync(game);

  // Hangar §39.4.1's jump-list entry (`world-manager`) fires `command/fire` at THIS,
  // already-running window — it is this window's own job to then open the second one.
  client.onCommand((id) => {
    if (id === 'world-manager' && game.net) openWorldManagerWindow(client, game.net);
  });
}

/**
 * Hangar §39.7 (M107.3) — keep every linked `task_board` reconciled for as long as this
 * session is bridged, independent of whether that board's own screen is open: the
 * List→board direction (`reminders/changed` → `read` → diff → `BlockTextEdit`) is a
 * background concern, not a per-screen one. Runs one full reconcile per link right away
 * too — "on window open: one full reconcile before any incremental op," where the
 * "window" this covers is the bridged session itself (the taskboard SCREEN's own
 * on-open reconcile, in `useBlock`, is the other half of that same rule).
 *
 * No-op without a live `net` session: linking only ever happens on a real multiplayer
 * world (`POST /world/board-link` is gated the same way), so solo play has nothing to
 * reconcile and `game.meta.boardLinks` stays empty by construction.
 */
function attachBoardSync(game: Game): void {
  const hangar = game.hangar;
  if (!hangar) return;

  const hooks = {
    mintLineId: () => newId('line'),
    maxLines: COW.BOARD_LINES,
    maxChars: COW.LINE_MAX,
  };

  const reconcileOne = (link: BoardLink): void => {
    const be = game.blockEntities.get(link.boardCell.x, link.boardCell.y, link.boardCell.z);
    if (!be || be.type !== 'task_board') return; // the block is gone — nothing to sync
    const before = JSON.stringify(be.data.lines);
    void import('./hangar/boardlink').then(({ reconcileWithRead }) =>
      reconcileWithRead(hangar, link, be.data.lines, hooks).then((result) => {
        const links = game.meta.boardLinks ?? [];
        const idx = links.findIndex(
          (l) => l.boardCell.x === link.boardCell.x && l.boardCell.y === link.boardCell.y && l.boardCell.z === link.boardCell.z,
        );
        if (idx < 0) return; // unlinked while this reconcile was in flight
        if (JSON.stringify(result.boardLines) !== before) {
          be.data.lines = result.boardLines;
          game.net?.sendBlockTextEdit(link.boardCell.x, link.boardCell.y, link.boardCell.z, 'task_board', JSON.stringify(be.data), true);
        }
        game.meta.boardLinks = [...links.slice(0, idx), { ...links[idx]!, mapping: result.mapping }, ...links.slice(idx + 1)];
        void game.saveNow();
      }),
    );
  };

  for (const link of game.meta.boardLinks ?? []) reconcileOne(link);
  hangar.api.reminders.onChanged((listId) => {
    for (const link of game.meta.boardLinks ?? []) if (link.listId === listId) reconcileOne(link);
  });
}

/** Hangar §39.5 (M105.1) — "scrubber Save replay": the real shell write when bridged, the
 *  pre-existing download otherwise. No-op with nothing to save yet. */
async function saveCurrentReplay(game: Game): Promise<void> {
  const rep = game.replay?.currentReplay();
  if (!rep) return;
  const bytes = encodeReplay(rep);
  const files = await import('./hangar/files');
  if (game.hangar?.bridged()) {
    await files.saveReplayBridged(game.hangar, game.meta.name, bytes);
  } else {
    files.downloadBytes(bytes, `${files.worldSlug(game.meta.name)}.bbreplay`, 'application/x-terraforge-replay');
  }
}

/**
 * Hangar §39.8 (M108.2, D-TF-300) — `?hangar=1&route=world-manager`: a standalone entry
 * point into the world-manager screen alone, no save/world/game ever touched. This is
 * the SAME screen the real `window.open()` click path shows (over a throwaway solo game
 * it has no choice but to also boot, per `window/openWindow`'s fixed-entry-URL limit) —
 * here it is reached directly, which is what the E2E drives against the in-memory
 * multi-world server (`server/worlds-runner.ts`, :8791), since that server's HTTP origin
 * differs from the page's own (`origin=` overrides the same-origin default a colocated
 * deploy would use).
 */
async function bootWorldManagerWindow(params: URLSearchParams): Promise<void> {
  const [{ createHangarClient }, wc, { showWorldManager }, pkg] = await Promise.all([
    import('./hangar/bridge'),
    import('./net/worldctl'),
    import('./ui/screens/world-manager'),
    import('../../package.json'),
  ]);
  const client = createHangarClient({ appId: 'terraforge', version: pkg.version });
  const origin = params.get('origin') ?? location.origin;
  const guest = wc.guestId(params.get('name') || 'Player', Number(params.get('hue') ?? '210') || 210);
  showWorldManager(document.body, client, { origin, guest });
}

export async function boot(): Promise<void> {
  const params = new URLSearchParams(location.search);
  if (params.get('hangar') === '1' && params.get('route') === 'world-manager') {
    await bootWorldManagerWindow(params);
    return;
  }
  const save = await SaveManager.open();
  const registries = loadClientRegistries();
  const settings = loadSettings();
  applyGuiScale(settings.guiScale);

  // A pending world-door handoff (DOR-2): a traversal navigated here with the player's
  // carried inventory; it's applied only to the world it targets (single-use, cleared).
  const handoff = takeHandoff();
  const start = async (meta: WorldMeta, isNew: boolean): Promise<void> => {
    const enter = handoff && handoff.targetWorldId === meta.id ? handoff : undefined;
    const game = await startGame(save, registries, settings, meta, isNew, undefined, enter);
    wirePause(game);
    await maybeAttachHangar(game, params, save);
  };

  // Multiplayer join (NET-2/7): `?server=ws://…` dynamically imports the net client
  // (solo never fetches that chunk), performs the Hello→Welcome handshake, then
  // boots a replica world from the server's meta/seed.
  const serverUrl = params.get('server');
  if (serverUrl) {
    const { NetSession, fetchTicket } = await import('./net/client');
    const wc = await import('./net/worldctl');
    // Auth (NET-5, C1): if the browser has a server session, fetch a single-use WS
    // ticket — its blessed name/hue win over the URL. An open server (or a not-yet-
    // logged-in browser) returns null and we join as a guest with the URL params.
    const auth = params.get('join') ? null : await fetchTicket(serverUrl);
    const name = auth?.name || params.get('name') || `Player${Math.floor(Math.random() * 1000)}`;
    const hue = auth ? auth.hue : Number(params.get('hue') ?? '210') || 210;
    const guest = wc.guestId(name, hue);
    // Route the socket to a specific server world (M42, DOR-6): the WS query names the
    // world + the guest sub the manager re-checks the ACL against (a bare connect lands
    // in the seeded root). A door traversal reconnects here with `?world=&ticket=`.
    const targetWorld = params.get('world') ?? '';
    const sep = serverUrl.includes('?') ? '&' : '?';
    const wsUrl = `${serverUrl}${sep}sub=${encodeURIComponent(guest.sub)}${targetWorld ? `&world=${encodeURIComponent(targetWorld)}` : ''}`;
    const net = await NetSession.connect(wsUrl, {
      name, hue, viewRadius: settings.renderDistance, token: auth?.ticket ?? params.get('ticket') ?? params.get('join') ?? undefined,
    });
    net.controlOrigin = wc.httpOrigin(serverUrl);
    net.guest = guest;
    net.ctl = wc;
    const meta = metaFromWelcome(net.welcome, params);
    // A door traversal reconnected here carrying the player's inventory (DOR-2): apply it
    // only to the world it targets (the server-namespaced id); the server spawn dictates
    // position, and pending-door materialisation stays a solo concern (gated in startGame).
    const enter = handoff && handoff.targetWorldId === meta.id ? handoff : undefined;
    const game = await startGame(save, registries, settings, meta, true, net, enter);
    wirePause(game);
    await maybeAttachHangar(game, params, save);
    return;
  }

  const worldId = params.get('world');
  if (worldId) {
    // Direct-boot a named world (E2E + quick dev).
    let meta = await save.getWorld(worldId);
    const isNew = !meta;
    if (!meta) {
      meta = defaultMeta(worldId, params);
      await save.putWorld(meta);
    }
    await start(meta, isNew);
  } else {
    showMenu(save, (meta, isNew) => void start(meta, isNew), registries.lessons.list);
  }
}
